import React, { useState, useEffect, useCallback, useRef, createContext, useContext, useMemo  } from 'react';
import {
  Lock, Unlock, ChevronRight, ChevronLeft, Check, X, Flame, Zap, Target,
  Trophy, Star, Award, Shield, Clock, AlertTriangle, ChefHat, Users,
  TrendingUp, BarChart3, Settings, LogOut, User, BookOpen, FileText,
  PlayCircle, CheckCircle2, XCircle, MinusCircle, Sparkles, Crown, Skull, Eye,
  ArrowRight, ArrowLeft, Plus, Camera, ClipboardCheck, Search, Filter,
  Briefcase, Calendar, AlertCircle, Loader2, Send, ThumbsUp,
  Edit3, Trash2, Copy, ArrowUp, ArrowDown, Save, RotateCcw, Database, Layers, GripVertical,
  Phone, Palette, Archive, Gift, Info, Cake, Bell, LogIn,
  CheckSquare, Paperclip, MessageSquare, AlertOctagon, Hourglass, Image as ImageIcon, Video, Flag, ListChecks, MoreVertical, Maximize2,
  EyeOff, BellRing, Download, Upload, Tag, Coffee, RefreshCw, Activity,
  Volume2, VolumeX, Menu, Home, CornerDownLeft
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { createPortal } from 'react-dom';

// ═══════════════════════════════════════════════════════════════
// СТИЛИ И ШРИФТЫ
// ═══════════════════════════════════════════════════════════════

const FONTS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap');

.font-display { font-family: 'Anton', sans-serif; letter-spacing: 0.02em; }
.font-body { font-family: 'Manrope', sans-serif; }
.font-mono { font-family: 'JetBrains Mono', monospace; }

* { -webkit-tap-highlight-color: transparent; }
body { font-family: 'Manrope', sans-serif; }

.grain {
  background-image:
    radial-gradient(rgba(255,255,255,0.012) 1px, transparent 1px),
    radial-gradient(rgba(255,255,255,0.008) 1px, transparent 1px);
  background-size: 3px 3px, 5px 5px;
  background-position: 0 0, 1px 1px;
}

.glow-red { box-shadow: 0 0 24px -4px rgba(232, 58, 44, 0.4), inset 0 1px 0 rgba(255,255,255,0.04); }
.glow-gold { box-shadow: 0 0 24px -4px rgba(255, 184, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.04); }
.glow-cyan { box-shadow: 0 0 24px -4px rgba(0, 229, 255, 0.35), inset 0 1px 0 rgba(255,255,255,0.04); }
.glow-green { box-shadow: 0 0 24px -4px rgba(0, 199, 129, 0.35), inset 0 1px 0 rgba(255,255,255,0.04); }

.dossier-corner::before, .dossier-corner::after {
  content: ''; position: absolute; width: 14px; height: 14px;
  border-color: #E83A2C; border-style: solid;
}
.dossier-corner::before { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
.dossier-corner::after { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

@keyframes pulse-red { 0%,100% { box-shadow: 0 0 0 0 rgba(232,58,44,0.4); } 50% { box-shadow: 0 0 0 8px rgba(232,58,44,0); } }
.pulse-red { animation: pulse-red 2s infinite; }

@keyframes scan { 0% { transform: translateY(-100%); } 100% { transform: translateY(100vh); } }
.scan-line::after {
  content: ''; position: absolute; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(232,58,44,0.6), transparent);
  animation: scan 4s linear infinite; pointer-events: none;
}

@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.fade-in { animation: fadeIn 0.35s ease-out; }

@keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-4px)} 75%{transform:translateX(4px)} }
.shake { animation: shake 0.4s; }

@keyframes glow-pulse { 0%,100%{filter: drop-shadow(0 0 4px rgba(255,184,0,0.4))} 50%{filter: drop-shadow(0 0 16px rgba(255,184,0,0.7))} }
.glow-pulse { animation: glow-pulse 2.5s ease-in-out infinite; }

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: #0A0B0D; }
::-webkit-scrollbar-thumb { background: #2A2E36; border-radius: 3px; }

.diagonal-stripes {
  background-image: repeating-linear-gradient(
    45deg, transparent, transparent 10px, rgba(232,58,44,0.04) 10px, rgba(232,58,44,0.04) 11px
  );
}

/* ═══════ ИНТЕРАКТИВНОСТЬ — hover/press эффекты ═══════ */
/* iOS-fix: dvh для корректной высоты под Safari */
@supports (height: 100dvh) {
  .min-h-screen { min-height: 100dvh !important; }
}

/* hover применяем только на устройствах, где есть hover (десктоп с мышью) */
/* На тач-устройствах :hover «залипает» после тапа — это бесит */
@media (hover: hover) and (pointer: fine) {
  .hover-lift { transition: transform 0.18s ease-out, box-shadow 0.18s ease-out; }
  .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 10px 25px -10px rgba(232,58,44,0.25); }
}
.hover-lift:active { transform: translateY(0) scale(0.99); transition-duration: 0.05s; }

button { transition: transform 0.1s ease-out, background-color 0.15s ease-out, border-color 0.15s ease-out, color 0.15s ease-out; }
button:active:not(:disabled) { transform: scale(0.97); }

/* На тач-устройствах увеличиваем минимальный touch-target до 44px невидимым padding */
@media (hover: none) and (pointer: coarse) {
  .touch-target { position: relative; }
  .touch-target::after {
    content: '';
    position: absolute;
    inset: -8px;
    min-width: 44px;
    min-height: 44px;
  }
}

.tap-bounce:active { animation: tapBounce 0.3s; }
@keyframes tapBounce { 0%{transform:scale(1)} 30%{transform:scale(0.94)} 100%{transform:scale(1)} }

@keyframes ping-once {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(2); opacity: 0; }
}
.ping-once { animation: ping-once 0.6s ease-out; }

@keyframes tilt { 0%,100%{transform:rotate(-1deg)} 50%{transform:rotate(1deg)} }
.tilt-hover:hover { animation: tilt 0.4s ease-in-out; }

@keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
.float-anim { animation: float 3s ease-in-out infinite; }

/* ═══════ НОВЫЕ АНИМАЦИИ ITER 3E ═══════ */

/* Shimmer — бегущая блестящая полоса для прогресс-баров и skeleton */
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.shimmer-bg {
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.08) 50%,
    rgba(255,255,255,0) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.8s infinite;
}

/* Прогресс-бар с заливкой и блеском */
@keyframes progress-fill {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}
.progress-fill {
  transform-origin: left;
  animation: progress-fill 0.9s cubic-bezier(0.16, 1, 0.3, 1);
}
.progress-shine {
  position: relative;
  overflow: hidden;
}
.progress-shine::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255,255,255,0.35) 50%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: shine-sweep 2.5s ease-in-out infinite;
}
@keyframes shine-sweep {
  0% { transform: translateX(-100%); }
  60%,100% { transform: translateX(100%); }
}

/* Skeleton loader — пульсирующий блок */
@keyframes skeleton-pulse { 0%,100%{ opacity: 0.4; } 50%{ opacity: 0.7; } }
.skeleton-block {
  background: linear-gradient(90deg, #15171B 0%, #1F2128 50%, #15171B 100%);
  background-size: 200% 100%;
  animation: shimmer 1.8s infinite, skeleton-pulse 1.8s infinite;
  border-radius: 6px;
}

/* Анимация числа — пульсация при изменении */
@keyframes number-pop {
  0% { transform: scale(1); color: inherit; }
  30% { transform: scale(1.25); color: #FFB800; }
  100% { transform: scale(1); color: inherit; }
}
.number-pop { animation: number-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }

/* Конфетти — частица падает с вращением */
@keyframes confetti-fall {
  0% { transform: translate3d(0, -20vh, 0) rotate(0deg); opacity: 1; }
  100% { transform: translate3d(var(--dx, 0), 110vh, 0) rotate(720deg); opacity: 0; }
}

/* 3D-tilt карточек на hover (только desktop) */
@media (hover: hover) and (pointer: fine) {
  .tilt-3d {
    transform-style: preserve-3d;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .tilt-3d:hover {
    transform: perspective(800px) rotateX(2deg) rotateY(-2deg) translateZ(10px);
  }
}

/* Magnetic button — притягивание (управляется JS-классом + transform) */
.magnetic-btn {
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

/* Gradient background для героических блоков */
@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.gradient-hero {
  background: linear-gradient(135deg, #E83A2C 0%, #FFB800 50%, #E83A2C 100%);
  background-size: 200% 200%;
  animation: gradient-shift 8s ease infinite;
}
.gradient-dev {
  background: linear-gradient(135deg, #FFB800 0%, #FF7849 50%, #FFB800 100%);
  background-size: 200% 200%;
  animation: gradient-shift 6s ease infinite;
}
.gradient-cosmic {
  background: linear-gradient(135deg, #1A1535 0%, #2D1B69 50%, #3A1C5D 100%);
  background-size: 200% 200%;
  animation: gradient-shift 12s ease infinite;
}

/* Vibrate — для критических кнопок (например "удалить навсегда") */
@keyframes vibrate {
  0%,100% { transform: translate(0); }
  10%,30%,50%,70%,90% { transform: translate(-1px, 1px); }
  20%,40%,60%,80% { transform: translate(1px, -1px); }
}
.vibrate:hover { animation: vibrate 0.3s linear; }

/* Glow border — пульсирующая обводка */
@keyframes glow-border {
  0%,100% { box-shadow: 0 0 0 1px rgba(255,184,0,0.4), 0 0 12px rgba(255,184,0,0.15); }
  50% { box-shadow: 0 0 0 1px rgba(255,184,0,0.7), 0 0 24px rgba(255,184,0,0.4); }
}
.glow-border { animation: glow-border 2.5s ease-in-out infinite; }

/* Slide-in справа/слева — для transition при смене экрана */
@keyframes slide-in-right { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
@keyframes slide-in-left  { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
.slide-in-right { animation: slide-in-right 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
.slide-in-left  { animation: slide-in-left 0.4s cubic-bezier(0.16, 1, 0.3, 1); }

/* Анимация для иконок (icon-spin, icon-bounce, icon-wiggle) */
@keyframes icon-bounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
.icon-bounce { animation: icon-bounce 1.2s ease-in-out infinite; }

@keyframes icon-wiggle {
  0%,100%{transform:rotate(0deg)}
  25%{transform:rotate(-7deg)}
  75%{transform:rotate(7deg)}
}
.icon-wiggle:hover { animation: icon-wiggle 0.4s ease-in-out; }

@keyframes icon-spin-once { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
.icon-spin-once:hover { animation: icon-spin-once 0.6s ease-in-out; }

/* Bump — лёгкий «толчок» при чекинге пункта */
@keyframes bump { 0%{transform:scale(1)} 50%{transform:scale(1.06)} 100%{transform:scale(1)} }
.bump { animation: bump 0.3s ease-out; }

/* Уважаем prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* подсветка карточки при наведении (только desktop) */
@media (hover: hover) and (pointer: fine) {
  .card-glow { position: relative; transition: all 0.2s; }
  .card-glow:hover { border-color: #FFB800 !important; box-shadow: 0 0 0 1px rgba(255,184,0,0.4), 0 8px 30px -10px rgba(255,184,0,0.3); }
}

/* предотвращаем zoom на input focus в iOS (font-size минимум 16px) */
input, select, textarea { font-size: 16px; }
@media (min-width: 768px) {
  input, select, textarea { font-size: 14px; }
}

/* безопасные зоны для iPhone с челкой */
.safe-bottom { padding-bottom: max(1.25rem, env(safe-area-inset-bottom)); }
.safe-top { padding-top: max(1.25rem, env(safe-area-inset-top)); }

/* ═══════ FALLBACK ДЛЯ ARBITRARY TAILWIND VALUES (без компилятора) ═══════ */
/* Базовые BG */
.bg-\\[\\#0A0B0D\\] { background-color: #0A0B0D !important; }
.bg-\\[\\#0A0B0D\\]\\/95 { background-color: rgba(10,11,13,0.95) !important; }
.bg-\\[\\#15171B\\] { background-color: #15171B !important; }
.bg-\\[\\#15171B\\]\\/95 { background-color: rgba(21,23,27,0.95) !important; }
.bg-\\[\\#1F0A0A\\] { background-color: #1F0A0A !important; }
.bg-\\[\\#0A1F12\\] { background-color: #0A1F12 !important; }
.bg-\\[\\#2A2E36\\] { background-color: #2A2E36 !important; }
.bg-\\[\\#E83A2C\\] { background-color: #E83A2C !important; }
.bg-\\[\\#E83A2C\\]\\/10 { background-color: rgba(232,58,44,0.1) !important; }
.bg-\\[\\#E83A2C\\]\\/20 { background-color: rgba(232,58,44,0.2) !important; }
.bg-\\[\\#FF4A3C\\] { background-color: #FF4A3C !important; }
.bg-\\[\\#FFB800\\] { background-color: #FFB800 !important; }
.bg-\\[\\#FFB800\\]\\/5 { background-color: rgba(255,184,0,0.05) !important; }
.bg-\\[\\#FFB800\\]\\/10 { background-color: rgba(255,184,0,0.1) !important; }
.bg-\\[\\#FFB800\\]\\/20 { background-color: rgba(255,184,0,0.2) !important; }
.bg-\\[\\#FFC820\\] { background-color: #FFC820 !important; }
.bg-\\[\\#00C781\\] { background-color: #00C781 !important; }
.bg-\\[\\#00C781\\]\\/5 { background-color: rgba(0,199,129,0.05) !important; }
.bg-\\[\\#00C781\\]\\/10 { background-color: rgba(0,199,129,0.1) !important; }
.bg-\\[\\#00C781\\]\\/20 { background-color: rgba(0,199,129,0.2) !important; }
.bg-\\[\\#00D78F\\] { background-color: #00D78F !important; }
.bg-\\[\\#00E5FF\\] { background-color: #00E5FF !important; }
.bg-\\[\\#00E5FF\\]\\/10 { background-color: rgba(0,229,255,0.1) !important; }

/* Hover BG */
.hover\\:bg-\\[\\#FF4A3C\\]:hover { background-color: #FF4A3C !important; }
.hover\\:bg-\\[\\#FFC820\\]:hover { background-color: #FFC820 !important; }
.hover\\:bg-\\[\\#00D78F\\]:hover { background-color: #00D78F !important; }
.hover\\:bg-\\[\\#1F0A0A\\]:hover { background-color: #1F0A0A !important; }
.hover\\:bg-\\[\\#2A2E36\\]:hover { background-color: #2A2E36 !important; }

/* TEXT colors */
.text-\\[\\#F2F2F4\\] { color: #F2F2F4 !important; }
.text-\\[\\#E83A2C\\] { color: #E83A2C !important; }
.text-\\[\\#FFB800\\] { color: #FFB800 !important; }
.text-\\[\\#00C781\\] { color: #00C781 !important; }
.text-\\[\\#00E5FF\\] { color: #00E5FF !important; }
.text-\\[\\#FF3B30\\] { color: #FF3B30 !important; }
.hover\\:text-\\[\\#E83A2C\\]:hover { color: #E83A2C !important; }

/* BORDER colors */
.border-\\[\\#15171B\\] { border-color: #15171B !important; }
.border-\\[\\#2A2E36\\] { border-color: #2A2E36 !important; }
.border-\\[\\#E83A2C\\] { border-color: #E83A2C !important; }
.border-\\[\\#E83A2C\\]\\/30 { border-color: rgba(232,58,44,0.3) !important; }
.border-\\[\\#E83A2C\\]\\/40 { border-color: rgba(232,58,44,0.4) !important; }
.border-\\[\\#FFB800\\] { border-color: #FFB800 !important; }
.border-\\[\\#FFB800\\]\\/30 { border-color: rgba(255,184,0,0.3) !important; }
.border-\\[\\#FFB800\\]\\/40 { border-color: rgba(255,184,0,0.4) !important; }
.border-\\[\\#00C781\\] { border-color: #00C781 !important; }
.border-\\[\\#00C781\\]\\/20 { border-color: rgba(0,199,129,0.2) !important; }
.border-\\[\\#00C781\\]\\/30 { border-color: rgba(0,199,129,0.3) !important; }
.border-\\[\\#00C781\\]\\/40 { border-color: rgba(0,199,129,0.4) !important; }
.border-\\[\\#00E5FF\\] { border-color: #00E5FF !important; }
.border-l-\\[\\#E83A2C\\] { border-left-color: #E83A2C !important; }

/* Hover border */
.hover\\:border-\\[\\#E83A2C\\]:hover { border-color: #E83A2C !important; }
.hover\\:border-\\[\\#FFB800\\]:hover { border-color: #FFB800 !important; }
.hover\\:border-\\[\\#FFB800\\]\\/40:hover { border-color: rgba(255,184,0,0.4) !important; }
.hover\\:border-\\[\\#00C781\\]:hover { border-color: #00C781 !important; }
.hover\\:border-\\[\\#00C781\\]\\/60:hover { border-color: rgba(0,199,129,0.6) !important; }
.focus\\:border-\\[\\#E83A2C\\]:focus { border-color: #E83A2C !important; }
.focus\\:border-\\[\\#FFB800\\]:focus { border-color: #FFB800 !important; }

/* Hover text */
.hover\\:text-\\[\\#E83A2C\\]:hover { color: #E83A2C !important; }
.hover\\:text-\\[\\#FFB800\\]:hover { color: #FFB800 !important; }
.hover\\:text-\\[\\#FFC820\\]:hover { color: #FFC820 !important; }

/* Hover bg для админки */
.hover\\:bg-\\[\\#FFB800\\]\\/10:hover { background-color: rgba(255,184,0,0.1) !important; }

/* FILL */
.fill-\\[\\#FFB800\\] { fill: #FFB800 !important; }

/* GRADIENTS — устанавливаем CSS-переменные, которые читает bg-gradient-* */
.from-\\[\\#0A0B0D\\] { --tw-gradient-from: #0A0B0D !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#15171B\\] { --tw-gradient-from: #15171B !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#E83A2C\\] { --tw-gradient-from: #E83A2C !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#E83A2C\\]\\/20 { --tw-gradient-from: rgba(232,58,44,0.2) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#FFB800\\] { --tw-gradient-from: #FFB800 !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#FFB800\\]\\/20 { --tw-gradient-from: rgba(255,184,0,0.2) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#FFB800\\]\\/30 { --tw-gradient-from: rgba(255,184,0,0.3) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.from-\\[\\#00C781\\]\\/20 { --tw-gradient-from: rgba(0,199,129,0.2) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent) !important; }
.via-\\[\\#0A0B0D\\] { --tw-gradient-stops: var(--tw-gradient-from), #0A0B0D, var(--tw-gradient-to, transparent) !important; }
.to-\\[\\#0F1116\\] { --tw-gradient-to: #0F1116 !important; }
.to-\\[\\#8B0F08\\] { --tw-gradient-to: #8B0F08 !important; }
.to-\\[\\#A87800\\] { --tw-gradient-to: #A87800 !important; }
.to-\\[\\#FF6B5C\\] { --tw-gradient-to: #FF6B5C !important; }
.to-\\[\\#E83A2C\\]\\/5 { --tw-gradient-to: rgba(232,58,44,0.05) !important; }
.to-\\[\\#FFB800\\]\\/5 { --tw-gradient-to: rgba(255,184,0,0.05) !important; }
.to-\\[\\#FFB800\\]\\/10 { --tw-gradient-to: rgba(255,184,0,0.1) !important; }
.to-\\[\\#00C781\\]\\/5 { --tw-gradient-to: rgba(0,199,129,0.05) !important; }

/* Accent для checkbox */
.accent-\\[\\#E83A2C\\] { accent-color: #E83A2C !important; }

/* Поддержка bg-gradient-to-* через CSS-переменные */
.bg-gradient-to-r { background-image: linear-gradient(to right, var(--tw-gradient-stops, var(--tw-gradient-from, transparent), var(--tw-gradient-to, transparent))) !important; }
.bg-gradient-to-br { background-image: linear-gradient(to bottom right, var(--tw-gradient-stops, var(--tw-gradient-from, transparent), var(--tw-gradient-to, transparent))) !important; }
.bg-gradient-to-t { background-image: linear-gradient(to top, var(--tw-gradient-stops, var(--tw-gradient-from, transparent), var(--tw-gradient-to, transparent))) !important; }

/* Placeholder colors (Tailwind core handles these but на всякий) */
.placeholder-gray-700::placeholder { color: #374151 !important; }
.placeholder-gray-600::placeholder { color: #4B5563 !important; }

/* ═══════ ТЕМА: переменные ═══════ */
:root {
  --accent: #FFB800;
  --primary: #E83A2C;
}

/* Класс accent-themed применяет переменную акцента вместо жёсткого жёлтого.
   Используется на ключевых интерактивных элементах (CTA-кнопки, активные плитки и т.д.) */
.themed-bg-accent { background-color: var(--accent) !important; color: #0A0B0D !important; }
.themed-bg-accent:hover { filter: brightness(1.1); }
.themed-text-accent { color: var(--accent) !important; }
.themed-border-accent { border-color: var(--accent) !important; }
.themed-border-accent-soft { border-color: color-mix(in srgb, var(--accent) 40%, transparent) !important; }
.themed-bg-accent-soft { background-color: color-mix(in srgb, var(--accent) 10%, transparent) !important; }

/* Новогодняя тема: тонкие красно-зелёные подсветки */
.theme-newyear .glow-red { box-shadow: 0 0 30px rgba(0, 199, 129, 0.18), 0 0 60px rgba(232, 58, 44, 0.15) !important; }

/* ═══════ Снежинки ═══════ */
@keyframes snowfall {
  0%   { transform: translateY(-10vh) translateX(0); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { transform: translateY(110vh) translateX(40px); opacity: 0; }
}
.snowflake {
  position: fixed;
  top: 0;
  color: rgba(255,255,255,0.85);
  font-size: 14px;
  pointer-events: none;
  z-index: 5;
  animation: snowfall linear infinite;
  text-shadow: 0 0 4px rgba(0,229,255,0.5);
}

/* ═══════════════ LOGIN SCREEN REDESIGN ═══════════════ */

/* Анимированные градиентные орбы фона — медленно плавают */
@keyframes orb-drift-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -40px) scale(1.05); }
  66% { transform: translate(-20px, 30px) scale(0.95); }
}
@keyframes orb-drift-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-40px, 30px) scale(1.1); }
}
@keyframes orb-drift-3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(20px, 20px) scale(1.08); }
  75% { transform: translate(-30px, -10px) scale(0.92); }
}
.login-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.45;
  will-change: transform;
  pointer-events: none;
}
.login-orb-1 { animation: orb-drift-1 18s ease-in-out infinite; }
.login-orb-2 { animation: orb-drift-2 22s ease-in-out infinite; }
.login-orb-3 { animation: orb-drift-3 16s ease-in-out infinite; }

/* Edge-fade сетка фона — едва заметная */
.login-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
  pointer-events: none;
}

/* Глассморфизм для основной карточки формы */
.login-card-glass {
  background: linear-gradient(145deg, rgba(21,23,27,0.85) 0%, rgba(15,17,21,0.95) 100%);
  backdrop-filter: blur(24px) saturate(140%);
  -webkit-backdrop-filter: blur(24px) saturate(140%);
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow:
    0 32px 64px -16px rgba(0,0,0,0.7),
    0 8px 24px rgba(0,0,0,0.4),
    inset 0 1px 0 rgba(255,255,255,0.05);
}

/* Анимация появления карточки с лёгким лифтом */
@keyframes card-enter {
  from { opacity: 0; transform: translateY(24px) scale(0.97); filter: blur(8px); }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}
.card-enter { animation: card-enter 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }

/* Лого «дыхание» — мягкое масштабирование */
@keyframes logo-breathe {
  0%, 100% { transform: scale(1); box-shadow: 0 0 40px rgba(232,58,44,0.45), 0 0 80px rgba(232,58,44,0.2); }
  50%      { transform: scale(1.03); box-shadow: 0 0 60px rgba(232,58,44,0.65), 0 0 120px rgba(232,58,44,0.35); }
}
.logo-breathe { animation: logo-breathe 4.5s ease-in-out infinite; }

/* Лучи вокруг лого — медленное вращение */
@keyframes logo-rays {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.logo-rays { animation: logo-rays 60s linear infinite; }

/* Glitch для заголовка при первом появлении */
@keyframes title-glitch {
  0%   { transform: translate(0); opacity: 0; }
  20%  { transform: translate(-2px, 0); opacity: 0.6; }
  40%  { transform: translate(2px, 0); opacity: 1; }
  60%  { transform: translate(-1px, 0); }
  80%  { transform: translate(1px, 0); }
  100% { transform: translate(0); opacity: 1; }
}
.title-glitch { animation: title-glitch 0.9s cubic-bezier(0.16, 1, 0.3, 1) both; }

/* Typewriter для tagline */
@keyframes typewriter {
  from { width: 0; }
  to   { width: 100%; }
}
@keyframes caret-blink {
  0%, 100% { border-right-color: rgba(255,184,0,0.8); }
  50%      { border-right-color: transparent; }
}
.typewriter {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  border-right: 2px solid rgba(255,184,0,0.8);
  animation: typewriter 1.6s steps(20, end) 0.5s both, caret-blink 0.7s step-end infinite 2.1s;
}

/* Плавающий лейбл для input полей */
.float-label-wrap {
  position: relative;
}
.float-label-wrap input,
.float-label-wrap select {
  width: 100%;
  padding: 18px 14px 8px 14px;
  background: rgba(10,11,13,0.6);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  color: white;
  font-size: 14px;
  transition: all 0.2s ease;
  outline: none;
}
.float-label-wrap input:focus,
.float-label-wrap select:focus {
  border-color: var(--primary, #E83A2C);
  background: rgba(10,11,13,0.85);
  box-shadow: 0 0 0 3px rgba(232,58,44,0.12);
}
.float-label-wrap label {
  position: absolute;
  left: 14px;
  top: 14px;
  font-size: 12px;
  color: rgba(255,255,255,0.45);
  pointer-events: none;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: ui-monospace, monospace;
  letter-spacing: 0.05em;
}
.float-label-wrap input:focus + label,
.float-label-wrap input:not(:placeholder-shown) + label,
.float-label-wrap select:focus + label,
.float-label-wrap select:not([data-empty="true"]) + label {
  top: 4px;
  font-size: 9px;
  color: var(--primary, #E83A2C);
  letter-spacing: 0.1em;
}

/* Mode toggle с плавающим индикатором */
.mode-toggle {
  position: relative;
  display: flex;
  background: rgba(10,11,13,0.8);
  border-radius: 12px;
  padding: 4px;
  border: 1px solid rgba(255,255,255,0.06);
}
.mode-toggle-indicator {
  position: absolute;
  top: 4px;
  bottom: 4px;
  border-radius: 8px;
  background: linear-gradient(135deg, #E83A2C 0%, #C12818 100%);
  box-shadow: 0 4px 12px rgba(232,58,44,0.4);
  transition: left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 0;
}
.mode-toggle button {
  flex: 1;
  position: relative;
  z-index: 1;
  padding: 11px 0;
  background: transparent;
  border: 0;
  font-family: ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.4);
  cursor: pointer;
  transition: color 0.25s ease;
}
.mode-toggle button.active { color: white; }

/* Primary button с pulse эффектом */
.btn-primary-glow {
  position: relative;
  background: linear-gradient(135deg, #E83A2C 0%, #C12818 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 12px;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  letter-spacing: 0.15em;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(232,58,44,0.35), 0 1px 0 rgba(255,255,255,0.1) inset;
}
.btn-primary-glow:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(232,58,44,0.55), 0 1px 0 rgba(255,255,255,0.15) inset;
}
.btn-primary-glow:active:not(:disabled) {
  transform: translateY(0);
}
.btn-primary-glow:disabled {
  background: linear-gradient(135deg, #2A2E36 0%, #1A1E26 100%);
  color: rgba(255,255,255,0.3);
  cursor: not-allowed;
  box-shadow: none;
}
.btn-primary-glow::before {
  content: '';
  position: absolute;
  top: 0; left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.6s ease;
}
.btn-primary-glow:hover::before { left: 100%; }

/* OAuth кнопки — приподнятый стиль */
.oauth-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  font-family: ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.04);
  color: white;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.oauth-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(255,255,255,0.16);
  background: rgba(255,255,255,0.07);
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
}
.oauth-btn-yandex { background: linear-gradient(135deg, #FC3F1D 0%, #DD2906 100%); border-color: rgba(255,255,255,0.1); }
.oauth-btn-yandex:hover { background: linear-gradient(135deg, #FF5535 0%, #EC3919 100%); box-shadow: 0 8px 24px rgba(252,63,29,0.4); }
.oauth-btn-vk { background: linear-gradient(135deg, #0077FF 0%, #005FCC 100%); border-color: rgba(255,255,255,0.1); }
.oauth-btn-vk:hover { background: linear-gradient(135deg, #1A85FF 0%, #0F70E0 100%); box-shadow: 0 8px 24px rgba(0,119,255,0.4); }

/* Разделитель «ИЛИ» с градиентными линиями */
.divider-or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
}
.divider-or .line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
}
.divider-or .text {
  font-family: ui-monospace, monospace;
  font-size: 9px;
  letter-spacing: 0.25em;
  color: rgba(255,255,255,0.3);
}

/* Подсветка курсором — spotlight follow */
.spotlight-bg {
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(232,58,44,0.15) 0%, transparent 60%);
  pointer-events: none;
  filter: blur(40px);
  transform: translate(-50%, -50%);
  transition: opacity 0.5s ease;
  will-change: transform;
}

/* Анимация эмблемы — лучи */
@keyframes ray-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.ray { animation: ray-rotate linear infinite; transform-origin: center; }

@media (max-width: 640px) {
  .pl-safe { padding-left: 16px; }
  .pr-safe { padding-right: 16px; }
  .pt-safe-5 { padding-top: 20px; }
}
.z-999 {z-index:999}
`;

// ═══════════════════════════════════════════════════════════════
// КОНТЕНТ — РАНГИ, БЕЙДЖИ
// ═══════════════════════════════════════════════════════════════

// SVG-логотип мафиозной шляпы (федора)
// Снежинки для новогодней темы
function Snowfall({ count = 30 }) {
  // Генерим один раз, чтобы анимация не сбрасывалась при ререндерах
  const flakes = React.useMemo(() => {
    const symbols = ['❄', '❅', '❆', '✻', '✼', '✿'];
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      duration: 8 + Math.random() * 12,
      delay: -Math.random() * 20,
      size: 8 + Math.random() * 14,
      symbol: symbols[Math.floor(Math.random() * symbols.length)],
      opacity: 0.4 + Math.random() * 0.5,
    }));
  }, [count]);
  return (
    <div aria-hidden className="fixed inset-0 pointer-events-none z-[5]">
      {flakes.map(f => (
        <span
          key={f.id}
          className="snowflake"
          style={{
            left: `${f.left}%`,
            fontSize: `${f.size}px`,
            opacity: f.opacity,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
          }}
        >
          {f.symbol}
        </span>
      ))}
    </div>
  );
}

function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Проверяем, не в standalone-режиме ли уже (установлено)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Уже установлено, баннер не нужен
    }

    // Для iOS: проверяем navigator.standalone (необязательно, но можно)
    if (window.navigator.standalone) {
      return; // Установлено через iOS Safari
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Предотвращаем автоматический показ мини-инфобара
      setDeferredPrompt(e); // Сохраняем событие для вызова позже
      setShowBanner(true); // Показываем наш баннер
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Скрываем баннер, если приложение было установлено
    window.addEventListener('appinstalled', () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt(); // Показываем диалог установки
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Можно запомнить в localStorage, чтобы не показывать некоторое время
    localStorage.setItem('pwa_banner_dismissed', Date.now());
  };

  if (!showBanner) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: '#15171B',
      color: '#fff',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 1000,
      borderTop: '1px solid #2A2E36',
      fontFamily: 'Manrope, sans-serif',
    }}>
      <div style={{ flex: 1, marginRight: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
          Установить приложение
        </div>
        <div style={{ fontSize: 12, color: '#9B9BA4' }}>
          Быстрый доступ с главного экрана
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleInstallClick} style={{
          background: '#E83A2C',
          border: 'none',
          color: '#fff',
          padding: '10px 16px',
          borderRadius: 8,
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: 13,
          fontFamily: 'monospace',
        }}>
          Установить
        </button>
        <button onClick={handleDismiss} style={{
          background: 'transparent',
          border: '1px solid #2A2E36',
          color: '#9B9BA4',
          padding: '10px 16px',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 13,
          fontFamily: 'monospace',
        }}>
          Позже
        </button>
      </div>
    </div>
  );
}

function FedoraHat({ className = "w-8 h-8", color = "currentColor" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Поля шляпы (низ) */}
      <ellipse cx="32" cy="46" rx="28" ry="5" fill={color} opacity="0.95" />
      {/* Тулья (верх) */}
      <path
        d="M 14 44 C 14 28, 18 16, 32 16 C 46 16, 50 28, 50 44 Z"
        fill={color}
      />
      {/* Вмятина сверху */}
      <path
        d="M 22 18 Q 32 24, 42 18"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      {/* Лента */}
      <rect x="14" y="38" width="36" height="5" fill="rgba(0,0,0,0.45)" />
      {/* Блик на ленте */}
      <rect x="36" y="39" width="4" height="3" fill="rgba(255,255,255,0.15)" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// БРЕНДЫ — два основных раздела академии
// ═══════════════════════════════════════════════════════════════

const BRANDS = {
  shawarmafia: {
    id: 'shawarmafia',
    name: 'Шаурмафия',
    short: 'Шаурмафия',
    description: 'Соло-смены. Шаурма, картофель, соусы.',
    color: '#E83A2C',
    icon: '🥙',
  },
  rollomafia: {
    id: 'rollomafia',
    name: 'Ролломафия',
    short: 'Ролломафия',
    description: 'Команда 3–4 человека. Роллы, пицца, касса.',
    color: '#FFB800',
    icon: '🍣',
  },
};

const BRAND_LIST = Object.values(BRANDS);

const getBrandById = (id) => BRANDS[id] || null;

// ─── Каталог категорий меню кухни ───
// Хранится в БД под STORAGE_KEYS.KITCHEN_CATEGORIES.
// Каждая категория имеет: id, name, brands (массив — где она доступна), order, hidden
const DEFAULT_KITCHEN_CATEGORIES = [
  { id: 'cat_shawarma', name: 'Шаурма',  icon: '🥙', brands: ['shawarmafia'],               order: 1, hidden: false },
  { id: 'cat_rolls',    name: 'Роллы',   icon: '🍣', brands: ['rollomafia'],                order: 2, hidden: false },
  { id: 'cat_pizza',    name: 'Пицца',   icon: '🍕', brands: ['rollomafia'],                order: 3, hidden: false },
  { id: 'cat_snacks',   name: 'Снэки',   icon: '🍟', brands: ['shawarmafia', 'rollomafia'], order: 4, hidden: false },
  { id: 'cat_drinks',   name: 'Напитки', icon: '🥤', brands: ['shawarmafia', 'rollomafia'], order: 5, hidden: false },
  { id: 'cat_sauces',   name: 'Соусы',   icon: '🥣', brands: ['shawarmafia', 'rollomafia'], order: 6, hidden: false },
  { id: 'cat_combo',    name: 'Комбо',   icon: '🎁', brands: ['shawarmafia', 'rollomafia'], order: 7, hidden: false },
];

// ─── Уведомления: настройки и типы определены позже в файле, рядом с NotificationContext ───

// ═══════════════════════════════════════════════════════════════
// ПРОФИЛЬ РАЗРАБОТЧИКА — захардкожен, создаётся автоматически
// ═══════════════════════════════════════════════════════════════
// Аккаунт TarasovWeb — единственный с возможностью раздавать админ-права.
// При первом старте приложения создаётся в БД, при последующих — пароль и данные обновляются на эти значения.
// НИ ПРИ КАКИХ ОБСТОЯТЕЛЬСТВАХ не может быть удалён или понижен — защищено и на фронте, и на сервере.
const DEV_ACCOUNT = {
  email: 'info@tarasovweb.ru',
  password: '12boroda12!',                            // хешируется при создании, на диск в открытом виде не пишется
  name: 'TarasovWeb Developer',
  fullName: 'TarasovWeb Developer',
  phone: '+79109221337',
  birthDate: '1999-02-04',
  gender: '',
};

// Маркер аккаунта разработчика — по email и явному флагу `isDeveloperAccount`.
// Email — основной идентификатор, флаг — дополнительная страховка.
const DEV_EMAIL = DEV_ACCOUNT.email.toLowerCase();
const isDevAccount = (u) => !!u && (
  (u.email || '').toLowerCase() === DEV_EMAIL || u.isDeveloperAccount === true
);

// ═══════════════════════════════════════════════════════════════
// МОДУЛИ СИСТЕМЫ — можно включать/выключать целыми разделами
// ═══════════════════════════════════════════════════════════════
// Хранится в БД под STORAGE_KEYS.MODULES. Управляется только разработчиком.
// Если модуль выключен — соответствующие пункты меню/экраны/кнопки скрываются у ВСЕХ пользователей.
const DEFAULT_MODULES = {
  tasks:        { enabled: true,  label: 'Задачи',           description: 'Управление задачами руководитель → админ' },
  exit:         { enabled: true,  label: 'Exit-интервью',    description: 'Кнопка «Уволиться» с опросом' },
  regulations:  { enabled: true,  label: 'Регламенты',       description: 'Документы и правила для стажёров' },
  techCards:    { enabled: true,  label: 'Меню кухни',       description: 'Тех-карты блюд для обучения' },
  checklist:    { enabled: true,  label: 'Чек-лист',         description: 'Шаблон практической части обучения' },
  tests:        { enabled: true,  label: 'Тесты в курсах',   description: 'Тесты после прохождения модулей' },
  messages:     { enabled: true,  label: 'Сообщения',        description: 'Личные сообщения админ → стажёр' },
  notifications:{ enabled: true,  label: 'Уведомления',      description: 'In-app и email уведомления' },
  contacts:     { enabled: true,  label: 'Контакты',         description: 'Телефоны и адреса заведений' },
};

// ═══════════════════════════════════════════════════════════════
// КАТАЛОГ ГРАНУЛЯРНЫХ ПРАВ — для тоглов в панели разработчика
// ═══════════════════════════════════════════════════════════════
// Хранятся в user.permissions = { canCreateTasks: true, ... }
// Если поле отсутствует — берутся дефолты по isAdmin/isDirector (обратная совместимость).
// Менять права может ТОЛЬКО разработчик.
// ═══════════════════════════════════════════════════════════════
// КАТАЛОГ ПРАВ — расширенный список, сгруппированный по категориям
// ═══════════════════════════════════════════════════════════════
// Категории помогают сворачивать длинный список в UI.
// Старые права (canEditContent, canEditMenu) ОСТАВЛЕНЫ для обратной совместимости —
// если у юзера в БД они выставлены, они работают как агрегаторы:
// canEditContent = canEditCourses + canEditRegulations + canEditChecklists + canEditTechCards.
const PERMISSIONS_CATALOG = [
  // ─── КОНТЕНТ ───
  { id: 'canEditCourses',      label: 'Редактировать курсы',           desc: 'Создавать/изменять программы обучения, модули, уроки, тесты',   category: 'content' },
  { id: 'canEditRegulations',  label: 'Редактировать регламенты',      desc: 'Создавать и редактировать должностные инструкции',              category: 'content' },
  { id: 'canEditChecklists',   label: 'Редактировать чек-листы',       desc: 'Изменять шаблон практической части обучения',                   category: 'content' },
  { id: 'canEditTechCards',    label: 'Редактировать тех-карты',       desc: 'Меню кухни: позиции, ингредиенты, ошибки, шаги приготовления',  category: 'content' },
  { id: 'canEditContacts',     label: 'Редактировать контакты',        desc: 'Изменять справочник телефонов и адресов',                       category: 'content' },
  { id: 'canEditExitQuestions',label: 'Редактировать exit-вопросы',    desc: 'Менять список вопросов в exit-интервью',                        category: 'content' },

  // ─── ПОЛЬЗОВАТЕЛИ ───
  { id: 'canCreateUsers',      label: 'Создавать пользователей',       desc: 'Добавлять новых сотрудников через админку',                     category: 'users' },
  { id: 'canEditUsers',        label: 'Редактировать пользователей',   desc: 'Менять данные пользователей (БЕЗ выдачи админ-прав)',           category: 'users' },
  { id: 'canDeleteUsers',      label: 'Удалять пользователей',         desc: 'Удалять аккаунты (с их прогрессом)',                            category: 'users' },
  { id: 'canSendMessages',     label: 'Отправлять сообщения',          desc: 'Личные сообщения стажёрам и другим админам',                    category: 'users' },

  // ─── ВИДИМОСТЬ ───
  { id: 'canSeeAllTrainees',   label: 'Видеть всех стажёров',          desc: 'Полный список и статистика (vs только свой бренд)',             category: 'visibility' },
  { id: 'canSeeStats',         label: 'Видеть статистику',             desc: 'Дашборд с ТОПами, ошибками, прогрессом',                        category: 'visibility' },
  { id: 'canSeeExitResponses', label: 'Видеть exit-ответы',            desc: 'Доступ к ответам уволенных сотрудников',                        category: 'visibility' },

  // ─── ЗАДАЧИ ───
  { id: 'canCreateTasks',      label: 'Создавать задачи',              desc: 'Ставить задачи другим админам',                                 category: 'tasks' },
  { id: 'canEditAllTasks',     label: 'Видеть все задачи',             desc: 'Видеть полный модуль задач (а не только свои)',                 category: 'tasks' },

  // ─── СИСТЕМА ───
  { id: 'canBackupContent',    label: 'Делать бэкапы',                 desc: 'Скачивать и загружать JSON-бэкапы контента',                    category: 'system' },
  { id: 'canEditSystemSettings', label: 'Системные настройки',         desc: 'Регистрация, OAuth, темы, лимиты email',                        category: 'system' },
];

// Категории для группировки в UI (порядок важен)
const PERMISSION_CATEGORIES = [
  { id: 'content',    label: 'КОНТЕНТ',      color: '#FFB800', icon: 'BookOpen' },
  { id: 'users',      label: 'ПОЛЬЗОВАТЕЛИ', color: '#3B82F6', icon: 'Users' },
  { id: 'visibility', label: 'ВИДИМОСТЬ',    color: '#00C781', icon: 'Eye' },
  { id: 'tasks',      label: 'ЗАДАЧИ',       color: '#FF7849', icon: 'CheckSquare' },
  { id: 'system',     label: 'СИСТЕМА',      color: '#A855F7', icon: 'Settings' },
];

// Получение эффективных прав пользователя — учитывает явные permissions + fallback по isAdmin
// Получает все эффективные права пользователя.
// Приоритет (от высшего к низшему):
//   1. Разработчик — все права
//   2. Руководитель — все права
//   3. Кастомная роль (если user.customRoleId указан) — права из роли + user.permissions override
//   4. Обычный админ — дефолты по типу админа + user.permissions override
//   5. Стажёр — никаких прав
// Параметр roles нужен только для кейса (3). Если он не передан — кастомные роли игнорируются.
function getEffectivePermissions(user, roles = []) {
  if (!user) return {};
  if (isDevAccount(user)) {
    return PERMISSIONS_CATALOG.reduce((acc, p) => { acc[p.id] = true; return acc; }, {});
  }
  if (user.isDirector) {
    return PERMISSIONS_CATALOG.reduce((acc, p) => { acc[p.id] = true; return acc; }, {});
  }
  // Кастомная роль — переопределяет дефолты для админов
  if (user.customRoleId && Array.isArray(roles)) {
    const role = roles.find(r => r.id === user.customRoleId);
    if (role && role.permissions) {
      // Если у роли есть baseAdmin=true и юзер не админ — всё равно даём права (роль делает админом по факту)
      // Если baseAdmin=false — права применяются только если юзер уже админ
      if (role.baseAdmin || user.isAdmin) {
        return { ...role.permissions, ...(user.permissions || {}) };
      }
    }
  }
  // Обычный админ — дефолты по типу
  if (user.isAdmin) {
    const defaults = {
      // КОНТЕНТ — админ редактирует всё по умолчанию
      canEditCourses:        true,
      canEditRegulations:    true,
      canEditChecklists:     true,
      canEditTechCards:      true,
      canEditContacts:       true,
      canEditExitQuestions:  true,
      // ПОЛЬЗОВАТЕЛИ — редактирует и создаёт, но не удаляет (удаление = опасная операция)
      canCreateUsers:        true,
      canEditUsers:          true,
      canDeleteUsers:        false,
      canSendMessages:       true,
      // ВИДИМОСТЬ — видит всё про обучение
      canSeeAllTrainees:     true,
      canSeeStats:           true,
      canSeeExitResponses:   true,
      // ЗАДАЧИ — по умолчанию НЕ может ставить/видеть все (только Руководитель)
      canCreateTasks:        false,
      canEditAllTasks:       false,
      // СИСТЕМА — бэкапы и настройки НЕ доступны (это уровень разработчика/руководителя)
      canBackupContent:      false,
      canEditSystemSettings: false,
      // ─── Обратная совместимость со старыми именами прав ───
      // Если у юзера в БД permissions содержит canEditContent: false — это перекроет все canEdit*
      // ниже логика учитывает старые поля как «зонтичные» (если явно установлены)
    };
    const userPerms = user.permissions || {};
    const merged = { ...defaults, ...userPerms };
    // Обратная совместимость: старое canEditContent (если установлено) → влияет на новые
    if (userPerms.canEditContent === false) {
      merged.canEditCourses = false;
      merged.canEditRegulations = false;
      merged.canEditChecklists = false;
      merged.canEditTechCards = false;
    }
    if (userPerms.canEditMenu === false) {
      merged.canEditTechCards = false;
    }
    return merged;
  }
  return {};
}

function hasPermission(user, permissionId, roles = []) {
  return !!getEffectivePermissions(user, roles)[permissionId];
}

// ═══════════════════════════════════════════════════════════════
// СИСТЕМНЫЕ НАСТРОЙКИ — единый объект, редактируется разработчиком
// ═══════════════════════════════════════════════════════════════
const DEFAULT_SYSTEM_CONFIG = {
  allowRegistration:        true,         // открыта ли регистрация (фронт + бэк)
  allowOAuthYandex:         true,         // показывать кнопку «Войти через Яндекс»
  allowOAuthVk:             true,         // показывать кнопку «Войти через ВК»
  requireEmailVerification: false,        // требовать ли подтверждение email перед входом
  brandColor:               '#E83A2C',    // основной акцент UI
  smtpDailyLimit:           150,          // лимит писем (визуально для разработчика, реальный лимит — в env)
  maintenanceMode:          false,        // режим «технические работы» — все кроме разработчика видят заглушку
  maintenanceMessage:       'Идут технические работы. Скоро вернёмся!',
};

// Каноничные роли — для подсказок при создании курса и регламента
const CANONICAL_ROLES = {
  shawarmafia: ['Повар-кассир'],
  rollomafia: ['Локальный администратор', 'Пиццмейкер', 'Суши-мейкер'],
  shared: ['Курьер', 'Старший администратор', 'Младший администратор'],
};

const RANKS = [
  { id: 'recruit', title: 'СТАЖЁР', minXp: 0, color: '#A4A8B2', icon: '🎯' },
  { id: 'sixer', title: 'УЧЕНИК', minXp: 500, color: '#00E5FF', icon: '⚡' },
  { id: 'soldier', title: 'МАСТЕР', minXp: 1500, color: '#E83A2C', icon: '🔥' },
  { id: 'shift_soldier', title: 'СПЕЦИАЛИСТ', minXp: 3000, color: '#FFB800', icon: '👑' },
  { id: 'brigadir', title: 'СТАРШИЙ', minXp: 5000, color: '#FFB800', icon: '⭐' },
  { id: 'capo', title: 'АДМИН', minXp: 8000, color: '#FFB800', icon: '💎' },
];

const getRank = (xp) => {
  let r = RANKS[0];
  for (const rank of RANKS) if (xp >= rank.minXp) r = rank;
  return r;
};
const getNextRank = (xp) => RANKS.find(r => r.minXp > xp) || null;

const BADGES = {
  first_blood: { id: 'first_blood', title: 'Первая кровь', desc: 'Сдал первый тест', icon: '🎯' },
  perfectionist: { id: 'perfectionist', title: 'Перфекционист', desc: 'Тест на 100%', icon: '💯' },
  lightning: { id: 'lightning', title: 'Молния', desc: 'Прошёл модуль за <15 мин', icon: '⚡' },
  streak3: { id: 'streak3', title: 'Стрик 3', desc: '3 дня подряд', icon: '🔥' },
  saucer: { id: 'saucer', title: 'Соусье', desc: 'Знаешь все соусы', icon: '🌶' },
  cleancut: { id: 'cleancut', title: 'Чистый рез', desc: 'Идеальный финал', icon: '🔪' },
  godfather: { id: 'godfather', title: 'Крёстный отец', desc: 'Завершил курс', icon: '👑' },
};

// ═══════════════════════════════════════════════════════════════
// КОНТЕНТ — КУРС ШАУРМАФИЯ / ПОВАР-КАССИР
// ═══════════════════════════════════════════════════════════════

const DEFAULT_COURSE = {
  id: 'shawarmafia_cashier',
  brand: 'Шаурмафия',
  role: 'Повар-кассир',
  title: 'Специалист — Шаурмафия',
  passing: 85,
  modules: [
    {
      id: 'm1',
      title: 'Досье на Семью',
      subtitle: 'История, кодекс, локации',
      duration: 15,
      icon: 'Briefcase',
      xp: 200,
      lessons: [
        {
          id: 'l1_1',
          title: 'История Шаурмафии',
          type: 'text',
          duration: 5,
          content: `Шаурмафия — это не просто сеть. Это Семья. Мы делаем шаурму с 2018 года и за это время превратили её из уличной еды в продукт, за которым выстраиваются очереди.

Наш принцип простой: качество, скорость, дерзость. Мы не делаем "дёшево и сердито". Мы делаем так, чтобы гость возвращался — и приводил друзей.

В Семье каждый отвечает за свою смену как за личную репутацию. Один косяк — это не "ой, бывает". Это пятно на всей бригаде.

ПРАВИЛА СЕМЬИ:
1. Никогда не выдавай гостю то, что не съел бы сам
2. Если очередь больше 5 человек — звони админу, не геройствуй
3. Не споришь с гостем при других гостях
4. Касса всегда в порядке. Расхождения объясняются ДО конца смены
5. Стандарт = закон. Своих рецептов в смену не приносим`
        },
        {
          id: 'l1_2',
          title: 'Наши локации',
          type: 'text',
          duration: 4,
          content: `Сейчас Шаурмафия работает на 5 точках:

📍 Шаурмафия-1 — ул. Тверская
📍 Шаурмафия-2 — м. Авиамоторная
📍 Шаурмафия-3 — ТРЦ "Европейский"
📍 Шаурмафия-4 — Старый Арбат
📍 Шаурмафия-5 — м. Сокольники

Каждая точка работает в режиме "один в смене". Это значит ты сам:
— открываешь смену
— принимаешь товар
— готовишь
— обслуживаешь кассу
— решаешь конфликты
— закрываешь и инкассируешь

Помощь старшего админа — на телефоне, 24/7.`
        },
        {
          id: 'l1_3',
          title: 'Кодекс Семьи',
          type: 'text',
          duration: 3,
          content: `5 ПРАВИЛ КОТОРЫЕ НЕЛЬЗЯ НАРУШАТЬ:

🔴 Воровство = увольнение в течение часа. Без обсуждения.

🔴 Наркотики/алкоголь на смене = увольнение и чёрный список по всем нашим точкам.

🔴 Хамство гостю = первое предупреждение и разбор. Второе — увольнение.

🔴 Несоблюдение санитарии = первое предупреждение. Повторное в течение 30 дней — увольнение.

🔴 Подделка отчётности (отчёт, инкассация, остатки) = увольнение и заявление в полицию при умышленном.`
        }
      ],
      test: {
        passing: 75,
        questions: [
          { type: 'single', q: 'С какого года работает Шаурмафия?', options: ['2015', '2018', '2020', '2022'], correct: 1 },
          { type: 'single', q: 'Сколько точек у Шаурмафии сейчас?', options: ['3', '4', '5', '7'], correct: 2 },
          { type: 'multi', q: 'Какие правила Семьи нельзя нарушать? (выбери все)', options: ['Воровство', 'Опоздание на 5 минут', 'Хамство гостю', 'Алкоголь на смене', 'Забыл подписать чек'], correct: [0,2,3] },
          { type: 'truefalse', q: 'Если очередь 6+ человек — справляйся сам, админа не дёргай.', correct: false },
          { type: 'single', q: 'Сколько человек работает на одной точке Шаурмафии в смене?', options: ['1', '2', '3', '4'], correct: 0 },
          { type: 'single', q: 'Что делать с расхождением в кассе?', options: ['Переписать в следующую смену', 'Объяснить до конца смены', 'Списать и забыть', 'Никому не говорить'], correct: 1 },
        ]
      }
    },
    {
      id: 'm_dress',
      title: 'Дресс-код Семьи',
      subtitle: 'Униформа · значки · детали',
      duration: 18,
      icon: 'Shield',
      xp: 200,
      lessons: [
        {
          id: 'l_dress_1',
          title: 'Форма Шаурмафии — официальный дресс-код',
          type: 'text',
          duration: 5,
          content: `Дресс-код в Семье — это не «надел что попало». Это твоя визитка перед гостем. Сорок секунд хватает гостю, чтобы оценить точку — и в основном он смотрит на тебя.

ОБЯЗАТЕЛЬНО (выдаётся на точке):
• Чёрная фирменная футболка / худи Шаурмафии — чистая, без пятен, размер «по фигуре», не балахон
• Чёрный фартук с лого «М» — завязан крест-накрест на спине, не болтается
• Чёрная кепка или фирменная бандана — все волосы собраны и убраны под неё
• Закрытая обувь, тёмная, с нескользящей подошвой — кроссовки или клоги
• Тёмные брюки/джинсы без рваностей и принтов

ЛИЧНЫЕ ВЕЩИ:
• Часы — снять перед сменой (моются плохо)
• Кольца — снять, кроме обручального (если плоское и без камня)
• Браслеты, цепочки на руках — снять
• Серьги-гвоздики — допустимо. Висячие — снять
• Длинные ногти, накладные, гель-лак с блёстками — запрещено для смены на кухне

ПОДГОТОВКА К СМЕНЕ:
• Душ накануне — обязательно
• Свежий пот не маскируется парфюмом — он усиливается
• Парфюм нейтральный или вообще без него — гость не должен чувствовать твой запах поверх еды
• Жвачка во рту во время работы — нет`
        },
        {
          id: 'l_dress_2',
          title: 'Чего категорически нельзя',
          type: 'text',
          duration: 4,
          content: `🚫 КРАСНЫЕ ЛИНИИ:

❌ Шорты любой длины — даже летом, даже «жарко»
❌ Юбки, платья — мы не в офисе
❌ Майки на бретелях, футболки с открытыми плечами
❌ Сандалии, кроксы открытые, сланцы — это травмоопасно при кипящем масле
❌ Распущенные длинные волосы — пожарная и санитарная норма
❌ Видимая нижняя одежда (трусы из-под штанов, бра под прозрачной футболкой)
❌ Громкие принты, символика, надписи на одежде
❌ Татуировки на лице — должны быть прикрыты, если возможно
❌ Резкий парфюм, парфюм-шлейф
❌ Жирные, немытые волосы

ШТРАФ-СИСТЕМА:
• 1-е нарушение — устное, отправляешься переодеться (запасная форма в подсобке)
• 2-е нарушение в течение месяца — письменное предупреждение
• 3-е — увольнение`
        },
        {
          id: 'l_dress_3',
          title: 'Бейдж и узнаваемость',
          type: 'text',
          duration: 4,
          content: `Каждый сотрудник носит бейдж. Это правило ради гостя — он должен знать кому пожаловаться, кого похвалить и к кому обратиться.

БЕЙДЖ:
• Слева на груди (на фартуке)
• Имя крупно, шрифт читаем
• Должность мелко («Повар-кассир»)
• Бейдж выдаётся в первый день, в день увольнения возвращается

ПОВЕДЕНЧЕСКИЕ МЕЛОЧИ:
• Никогда не вытирай руки о фартук — есть полотенце
• Не убирай пряди волос пальцами на смене — мой руки сразу
• Не садись на рабочую поверхность (стол, прилавок)
• Не ешь и не пей за стойкой при гостях
• Если жуёшь — отвернись или отойди`
        },
        {
          id: 'l_dress_4',
          title: 'Состояние одежды в течение смены',
          type: 'text',
          duration: 5,
          content: `Чистая форма утром — не повод носить её в крови от помидоров вечером.

ПЛАН-ОБХОД:
• Каждые 2 часа — взгляд на себя в зеркало в подсобке (висит у входа)
• Замаран фартук — поменял (запасные лежат в шкафу)
• Замаралась футболка — попроси сменную в чате смены или используй запасной фартук как прикрытие
• Кепка съехала — поправил
• На обуви жирные следы — протёр

ВЕЧЕРОМ:
• Грязная форма — в фирменный мешок (висит в подсобке)
• Не уноси домой — у нас централизованная стирка
• Если унёс случайно — постирай сам и верни в первый же день

ЕСЛИ ТЫ В СОМНЕНИИ — посмотри на себя глазами гостя. Захочешь ли ты, чтобы такой человек собирал тебе еду?`
        }
      ],
      test: {
        passing: 80,
        questions: [
          { type: 'multi', q: 'Что обязательно входит в форму повара-кассира? (выбери всё верное)', options: ['Чёрная футболка/худи', 'Шорты в жару', 'Кепка или бандана', 'Закрытая обувь', 'Сандалии', 'Чёрный фартук'], correct: [0,2,3,5] },
          { type: 'truefalse', q: 'Резкий парфюм допустим, если ты не успел принять душ.', correct: false },
          { type: 'single', q: 'Сколько нарушений дресс-кода в месяц до увольнения?', options: ['1', '2', '3', '5'], correct: 2 },
          { type: 'multi', q: 'Что нельзя делать со своей одеждой/собой на смене?', options: ['Жевать жвачку при гостях', 'Носить бейдж', 'Сидеть на рабочей поверхности', 'Вытирать руки о фартук', 'Поправлять волосы пальцами на смене'], correct: [0,2,3,4] },
          { type: 'truefalse', q: 'Грязный фартук можно носить до конца смены — гость не заметит.', correct: false },
          { type: 'single', q: 'Где находится бейдж?', options: ['В кармане брюк', 'Слева на груди (на фартуке)', 'На спине', 'В подсобке'], correct: 1 },
          { type: 'truefalse', q: 'Длинные распущенные волосы на смене допустимы, если они чистые.', correct: false },
        ]
      }
    },
    {
      id: 'm2',
      title: 'Внешний вид и гигиена',
      subtitle: 'Форма, руки, СанПиН',
      duration: 25,
      icon: 'Shield',
      xp: 250,
      lessons: [
        {
          id: 'l2_1',
          title: 'Форма Шаурмафии',
          type: 'text',
          duration: 6,
          content: `СТРОГИЙ ДРЕСС-КОД:

✅ Чёрная фирменная футболка / худи
✅ Чёрный фартук с лого
✅ Чёрная кепка / бандана (волосы убраны под неё)
✅ Закрытая обувь с нескользящей подошвой
✅ Чистые брюки тёмных тонов

❌ Шорты, юбки выше колена
❌ Сандалии, кроксы, сланцы
❌ Распущенные волосы
❌ Длинные ногти, накладные ногти
❌ Кольца, браслеты, цепи на руках
❌ Резкий парфюм
❌ Жвачка во рту во время работы

🔴 ЗАПРЕЩЕНО приходить на смену в немытой форме. Если форма испачкалась — в подсобке есть запасная.`
        },
        {
          id: 'l2_2',
          title: 'Мытьё рук — правильно',
          type: 'text',
          duration: 5,
          content: `КАК ПРАВИЛЬНО МЫТЬ РУКИ (40 секунд минимум):

1. Намочить руки тёплой водой
2. Жидкое мыло на ладонь
3. Намылить ладони друг о друга — 5 секунд
4. Тыльную сторону кистей — по 5 секунд каждую
5. Между пальцами — 10 секунд
6. Большие пальцы кругами — 5 секунд каждый
7. Подушечки пальцев в ладонь — 5 секунд каждую
8. Смыть тёплой водой
9. Вытереть одноразовым полотенцем
10. Полотенцем закрыть кран

КОГДА МЫТЬ ОБЯЗАТЕЛЬНО:
🟥 Перед началом смены
🟥 После туалета (всегда!)
🟥 После касания лица, волос, телефона
🟥 После работы с сырыми продуктами
🟥 После уборки
🟥 После курения / еды`
        },
        {
          id: 'l2_3',
          title: 'Что делать если порезался или болеешь',
          type: 'text',
          duration: 4,
          content: `🩹 ПОРЕЗ:
1. Немедленно отойти от продуктов
2. Промыть рану водой
3. Обработать перекисью / антисептиком (аптечка под кассой)
4. Пластырь + резиновая перчатка сверху
5. Если кровь не останавливается 5 минут — звонить админу

🤧 БОЛЕЗНЬ:
Если у тебя:
— температура
— кашель
— насморк
— симптомы ОРВИ
— расстройство ЖКТ

🔴 НЕ ВЫХОДИ НА СМЕНУ. Звони админу за 4+ часа до смены, чтоб успели заменить.

🔴 НИКОГДА не работаешь с продуктами, если у тебя гнойничковые поражения кожи на руках, чирьи, открытые раны без обработки.`
        }
      ],
      test: {
        passing: 85,
        questions: [
          { type: 'multi', q: 'Что входит в форму повара-кассира? (выбери всё верное)', options: ['Чёрная футболка', 'Шорты в жару', 'Кепка/бандана', 'Закрытая обувь', 'Сандалии', 'Фартук'], correct: [0,2,3,5] },
          { type: 'single', q: 'Сколько секунд минимум должно длиться мытьё рук?', options: ['10 секунд', '20 секунд', '40 секунд', '90 секунд'], correct: 2 },
          { type: 'truefalse', q: 'Можно работать с лёгкой простудой если выпил парацетамол.', correct: false },
          { type: 'multi', q: 'Когда мыть руки обязательно?', options: ['Перед сменой', 'После туалета', 'После касания телефона', 'Только в начале и в конце смены', 'После курения'], correct: [0,1,2,4] },
          { type: 'single', q: 'Что делать при порезе?', options: ['Терпеть, доделать заказ', 'Отойти, промыть, антисептик, пластырь, перчатка', 'Замотать салфеткой и работать', 'Уйти со смены'], correct: 1 },
          { type: 'truefalse', q: 'Жвачку на смене жевать можно если негромко.', correct: false },
        ]
      }
    },
    {
      id: 'm3',
      title: 'Технологические карты',
      subtitle: 'Все блюда на смене',
      duration: 90,
      icon: 'ChefHat',
      xp: 500,
      type: 'techcards',
      lessons: [
        { id: 'l3_1', title: 'Шаурма классическая', type: 'techcard', techCardId: 'shawarma_classic', duration: 12 },
        { id: 'l3_2', title: 'Шаурма с курицей', type: 'techcard', techCardId: 'shawarma_chicken', duration: 10 },
        { id: 'l3_3', title: 'Шаурма Мега', type: 'techcard', techCardId: 'shawarma_mega', duration: 12 },
        { id: 'l3_4', title: 'Соус «Дон»', type: 'techcard', techCardId: 'sauce_don', duration: 6 },
        { id: 'l3_5', title: 'Картофель фри', type: 'techcard', techCardId: 'fries', duration: 5 },
      ],
      test: {
        passing: 85,
        questions: [
          { type: 'single', q: 'Сколько грамм курицы в Шаурме классической?', options: ['100 г', '120 г', '140 г', '150 г'], correct: 1 },
          { type: 'multi', q: 'Какие соусы входят в Шаурму классическую?', options: ['Соус «Дон»', 'Сырный', 'Чесночный', 'Острый', 'Барбекю'], correct: [0,2] },
          { type: 'order', q: 'Расставь шаги сборки шаурмы по порядку:', items: ['Подогрев лаваша', 'Соус «Дон»', 'Курица', 'Овощи', 'Сыр', 'Сворачивание', 'Гриль 90 сек', 'Разрез по диагонали'], correct: [0,1,2,3,4,5,6,7] },
          { type: 'single', q: 'Минимальная температура гриля для шаурмы?', options: ['180 °C', '200 °C', '220 °C', '250 °C'], correct: 2 },
          { type: 'truefalse', q: 'Капусту перед использованием нужно отжать от лишней влаги.', correct: true },
          { type: 'match', q: 'Сопоставь ингредиент с граммовкой (Шаурма классическая):', pairs: [['Курица','120 г'],['Лаваш','90 г'],['Помидор','40 г'],['Соус «Дон»','25 г'],['Сыр','20 г']] },
          { type: 'single', q: 'Что делать если соус «Дон» закончился, а в очереди 3 человека?', options: ['Заменить на чесночный, не сказав гостю', 'Сообщить, предложить альтернативу с компенсацией', 'Отказать всем', 'Делать без соуса'], correct: 1 },
          { type: 'truefalse', q: 'Можно отдать шаурму гостю если разрез не диагональный — главное вкус.', correct: false },
        ]
      }
    },
    {
      id: 'm4',
      title: 'Касса и приём заказа',
      subtitle: 'iiko, оплаты, агрегаторы',
      duration: 50,
      icon: 'FileText',
      xp: 300,
      lessons: [
        {
          id: 'l4_1',
          title: 'Скрипт приёма заказа',
          type: 'text',
          duration: 5,
          content: `СКРИПТ ШАУРМАФИИ:

👁 Гость подходит:
"Привет, Шаурмафия. Что будем делать?"

📝 Принимаешь заказ. ОБЯЗАТЕЛЬНО уточни:
— "С собой или здесь?"
— "Соус нормальный или острый?"
— "Лук добавляем?"

🧾 Повторяешь заказ:
"Значит так: классическая без лука, картошка фри, кола 0.5. Итого 480 рублей. Картой?"

💳 Принимаешь оплату:
"Спасибо. Чек на почту/телефон или печатать?"

⏱ Отдаёшь:
"Ваш заказ 3 минуты. Готовим — позову."

✅ Когда готово:
"Спасибо за заказ. Вкусной шаурмы. Заходи ещё."

🔴 НИКОГДА не говори:
— "Не знаю, спросите у админа"
— "Ну вот так"
— "Сейчас, я один"
— ничего, что снимает с тебя ответственность`
        },
        {
          id: 'l4_2',
          title: 'Оплаты: наличные, терминал, СБП',
          type: 'text',
          duration: 6,
          content: `💵 НАЛИЧНЫЕ:
1. Озвучить сумму
2. Принять купюры, пересчитать ВСЛУХ перед гостем
3. Пробить чек
4. Сдачу — крупными купюрами поверх мелочи
5. Никогда не убирай деньги в карман — только в кассу

💳 ТЕРМИНАЛ:
1. Сумма должна совпадать с чеком
2. Терминал поворачивай к гостю экраном
3. После оплаты — отдай чек терминала + чек ресторана
4. Если оплата не прошла — НЕ повторяй сразу, спроси гостя
5. При возврате — только при админе

📱 СБП (QR-код):
1. Открой в iiko вкладку оплат
2. Сгенерируй QR
3. Покажи гостю
4. Жди подтверждения В СИСТЕМЕ (не на словах гостя)
5. Только потом — выдача

🔴 КРАСНЫЕ ФЛАГИ:
— Гость показывает "скрин оплаты" — НЕ принимаем
— Терминал не печатает чек — НЕ выдаём заказ до решения админа
— Сумма в терминале меньше чека — стоп, разбор`
        }
      ],
      test: {
        passing: 80,
        questions: [
          { type: 'single', q: 'Гость показал скрин оплаты на телефоне. Что делать?', options: ['Выдать заказ — раз заплатил', 'Не принимать, ждать подтверждение в системе', 'Спросить пароль от банка', 'Позвонить в банк'], correct: 1 },
          { type: 'multi', q: 'Что обязательно уточнить при приёме заказа?', options: ['С собой или здесь', 'Соус нормальный или острый', 'Возраст гостя', 'Лук добавляем', 'Семейное положение'], correct: [0,1,3] },
          { type: 'truefalse', q: 'При оплате наличными деньги можно положить в карман, потом разберёмся.', correct: false },
          { type: 'situation', q: 'Терминал не печатает чек после оплаты. Гость требует заказ. Действия?', options: ['Выдать заказ — оплата прошла', 'Сказать гостю подождать, звонить админу, не выдавать пока не решим', 'Дать заказ бесплатно за беспокойство', 'Отменить оплату самостоятельно'], correct: 1 },
          { type: 'truefalse', q: 'Сдачу выдаём крупными купюрами поверх мелочи.', correct: true },
        ]
      }
    }
  ]
};

// Массив курсов — каждый закреплён за брендом и ролью
const DEFAULT_COURSES = [DEFAULT_COURSE];

// Резолвит курс для пользователя по совпадению (бренд × роль)
// Возвращает все курсы, доступные пользователю по его брендам и роли.
// user.brands — массив id брендов; user.role — должность (если не указана, отдаём все курсы по бренду).
function getCoursesForUser(user, courses) {
  if (!user || !courses?.length) return [];
  if (user.isAdmin) return courses; // админ видит всё

  // Нормализация для нечёткого сравнения:
  // нижний регистр, удаление дефисов/пробелов/трим/замена ё→е
  const norm = s => (s || '').toString().trim().toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[-‑–—_]/g, ' ')      // все виды дефиса → пробел
    .replace(/\s+/g, ' ')           // двойные пробелы → одинарный
    .trim();

  const brandIds = (user.brands && user.brands.length ? user.brands : (user.brand ? [user.brand] : [])).map(norm);

  // matchBrand: курс.brand может быть как id ('shawarmafia') так и название ('Шаурмафия')
  const matchBrand = (cBrand) => {
    const cb = norm(cBrand);
    if (brandIds.includes(cb)) return true;
    return brandIds.some(bid => {
      const b = BRANDS[bid];
      return b && norm(b.name) === cb;
    });
  };

  // matchRole: точное совпадение нормализованных строк ИЛИ "содержит" в обе стороны
  // (чтобы "Повар-кассир" совпал с "повар кассир", "повар", "кассир повара" и т.п.)
  const matchRole = (cRole, uRole) => {
    if (!uRole) return true;
    if (!cRole) return false;
    const a = norm(cRole);
    const b = norm(uRole);
    if (a === b) return true;
    // partial match: либо роль курса содержит роль юзера, либо наоборот
    if (a.length >= 3 && b.length >= 3 && (a.includes(b) || b.includes(a))) return true;
    return false;
  };

  return courses.filter(c => {
    if (!matchBrand(c.brand)) return false;
    return matchRole(c.role, user.role);
  });
}

// Совместимость со старым API — возвращает первый подходящий курс
function getCourseForUser(user, courses) {
  const list = getCoursesForUser(user, courses);
  return list[0] || null;
}

// ═══════════════════════════════════════════════════════════════
// ТЕХНОЛОГИЧЕСКИЕ КАРТЫ
// ═══════════════════════════════════════════════════════════════

const DEFAULT_TECHCARDS = {
  shawarma_classic: {
    id: 'shawarma_classic',
    name: 'Шаурма классическая',
    weight: '380 г',
    time: '4 мин',
    level: 1,
    color: '#E83A2C',
    ingredients: [
      { name: 'Лаваш армянский', amount: '1 шт', grams: '90 г', critical: false },
      { name: 'Курица гриль', amount: '', grams: '120 г', critical: true },
      { name: 'Лук маринованный', amount: '', grams: '30 г' },
      { name: 'Помидор свежий', amount: '', grams: '40 г' },
      { name: 'Огурец свежий', amount: '', grams: '30 г' },
      { name: 'Капуста бело-краснокочанная', amount: '', grams: '35 г' },
      { name: 'Соус фирменный «Дон»', amount: '', grams: '25 г', critical: true },
      { name: 'Соус чесночный', amount: '', grams: '15 г' },
      { name: 'Сыр сулугуни тёртый', amount: '', grams: '20 г' },
    ],
    steps: [
      'Лаваш на гриль 8 секунд одной стороной — должен стать горячим, не пересушенным',
      'Соус «Дон» зигзагом по центру лаваша — НЕ экономь',
      'Курица в центр, не дальше 2 см от края',
      'Овощи послойно: помидор → огурец → капуста (отжатая!)',
      'Лук маринованный + соус чесночный',
      'Сыр сулугуни сверху',
      'Сворачивание: сначала боковины, потом снизу — туго',
      'На гриль 90 сек / 220 °C обеими сторонами',
      'Разрез по диагонали, упаковка'
    ],
    mistakes: [
      { title: 'Мало соуса «Дон»', desc: 'Главная жалоба гостей. Зигзаг на всю ширину лаваша.' },
      { title: 'Капуста не отжата', desc: 'Лаваш расползётся уже к моменту выдачи. Всегда отжимай.' },
      { title: 'Гриль ниже 200 °C', desc: 'Корочка не схватится, лаваш будет сырой и расползётся.' },
      { title: 'Нарезка не диагональная', desc: 'Выглядит дёшево. Только диагональ, под 45°.' },
      { title: 'Курица холодная из холодильника', desc: 'Гость почувствует. Используй только курицу с гриля или повторно прогретую.' }
    ]
  },
  shawarma_chicken: {
    id: 'shawarma_chicken',
    name: 'Шаурма с курицей',
    weight: '320 г',
    time: '3.5 мин',
    level: 1,
    color: '#E83A2C',
    ingredients: [
      { name: 'Лаваш армянский', amount: '1 шт', grams: '90 г' },
      { name: 'Курица гриль', amount: '', grams: '140 г', critical: true },
      { name: 'Помидор свежий', amount: '', grams: '40 г' },
      { name: 'Огурец свежий', amount: '', grams: '30 г' },
      { name: 'Соус чесночный', amount: '', grams: '20 г' },
    ],
    steps: [
      'Лаваш на гриль 8 секунд',
      'Соус чесночный по центру',
      'Курица — больше чем в классической',
      'Помидор + огурец',
      'Сворачивание туго',
      'Гриль 80 сек / 220 °C',
      'Разрез по диагонали'
    ],
    mistakes: [
      { title: 'Меньше курицы чем в карте', desc: '140 г, не 120. Это её ключевое отличие.' },
      { title: 'Лук добавлен', desc: 'В этой шаурме лука НЕТ. Если просят — отдельная позиция.' }
    ]
  },
  shawarma_mega: {
    id: 'shawarma_mega',
    name: 'Шаурма Мега',
    weight: '550 г',
    time: '5 мин',
    level: 2,
    color: '#FFB800',
    ingredients: [
      { name: 'Лаваш XL', amount: '1 шт', grams: '120 г' },
      { name: 'Курица гриль', amount: '', grams: '180 г', critical: true },
      { name: 'Бекон жареный', amount: '', grams: '40 г', critical: true },
      { name: 'Помидор', amount: '', grams: '50 г' },
      { name: 'Огурец', amount: '', grams: '40 г' },
      { name: 'Капуста', amount: '', grams: '40 г' },
      { name: 'Соус «Дон»', amount: '', grams: '30 г', critical: true },
      { name: 'Соус чесночный', amount: '', grams: '20 г' },
      { name: 'Соус острый', amount: '', grams: '15 г' },
      { name: 'Сыр сулугуни', amount: '', grams: '30 г' },
    ],
    steps: [
      'Лаваш XL на гриль 10 секунд',
      'Все 3 соуса параллельными зигзагами',
      'Курица + бекон вперемешку',
      'Все овощи',
      'Двойная порция сыра',
      'Сворачивание особенно туго (большая)',
      'Гриль 100 секунд',
      'Разрез на 2 части по диагонали',
      'Двойная упаковка чтоб не развалилась'
    ],
    mistakes: [
      { title: 'Один из 3 соусов забыт', desc: 'В Меге обязательно ВСЕ три. Это её фишка.' },
      { title: 'Сворачивание слабое', desc: 'Из-за веса развалится в руках гостя. Туго.' },
      { title: 'Один разрез вместо двух', desc: 'Большую шаурму режем на 2 части — иначе не съесть.' }
    ]
  },
  sauce_don: {
    id: 'sauce_don',
    name: 'Соус «Дон» (фирменный)',
    weight: '500 мл партия',
    time: '5 мин',
    level: 1,
    color: '#E83A2C',
    isComponent: true,
    ingredients: [
      { name: 'Майонез 67%', amount: '', grams: '300 г' },
      { name: 'Сметана 20%', amount: '', grams: '100 г' },
      { name: 'Кетчуп томатный', amount: '', grams: '50 г' },
      { name: 'Чеснок свежий', amount: '', grams: '15 г', critical: true },
      { name: 'Зелень укроп', amount: '', grams: '10 г' },
      { name: 'Специи смесь «Дон»', amount: '', grams: '5 г', critical: true },
      { name: 'Соль', amount: 'по вкусу', grams: '~3 г' },
    ],
    steps: [
      'Майонез + сметана в чашу — перемешать вилкой',
      'Кетчуп — добавить, перемешать до однородности',
      'Чеснок через пресс — добавить',
      'Укроп мелко рубленный',
      'Специи «Дон» — точно по граммовке (передоз = ядерный соус)',
      'Соль по вкусу, размешать',
      'Дать настояться 15 минут перед использованием',
      'Маркировка: дата+время+инициалы'
    ],
    mistakes: [
      { title: 'Передоз специй', desc: 'Соус становится горьким. 5 г = одна мерная ложка. Не на глаз.' },
      { title: 'Без маркировки', desc: 'Срок годности соуса 24 часа с приготовления. Без маркировки = выкинь.' },
      { title: 'Не дал настояться', desc: 'Свежий соус суховат. 15 минут — и вкус раскрывается.' }
    ]
  },
  fries: {
    id: 'fries',
    name: 'Картофель фри',
    weight: '150 г',
    time: '3 мин',
    level: 1,
    color: '#FFB800',
    ingredients: [
      { name: 'Картофель фри замороженный', amount: '', grams: '180 г сырой / 150 г готовой' },
      { name: 'Соль', amount: '', grams: '2 г' },
      { name: 'Масло фритюрное', amount: '', grams: 'фритюр' },
    ],
    steps: [
      'Замороженный картофель в корзину фритюра — не размораживать!',
      'Опустить во фритюр 175 °C',
      'Жарить ровно 3 минуты',
      'Поднять, дать стечь маслу 10 секунд',
      'Высыпать в коробку, посолить сразу пока горячая',
      'Подача в течение 30 секунд после посолки'
    ],
    mistakes: [
      { title: 'Картофель размороженный', desc: 'Будет вялый и впитает много масла. ТОЛЬКО замороженный.' },
      { title: 'Жарка дольше 3 мин', desc: 'Пересушит, станет тёмной. Таймер обязательно.' },
      { title: 'Соль после остывания', desc: 'Не прилипнет. Соли в первые 30 секунд после фритюра.' },
      { title: 'Долгое лежание', desc: 'Через 5 минут — мусор. Не отдавай, пожарь свежую.' }
    ]
  },
};

// ═══════════════════════════════════════════════════════════════
// ПРАКТИЧЕСКИЙ ЧЕК-ЛИСТ
// ═══════════════════════════════════════════════════════════════

const DEFAULT_CHECKLIST = [
  { section: 'Рабочее место', items: [
    'Знает расположение всех инвентарей',
    'Может включить гриль и шаурму-машину',
    'Знает где аптечка, огнетушитель, ключи',
    'Понимает зоны "грязно/чисто"'
  ]},
  { section: 'Открытие смены', items: [
    'Принял смену по чек-листу',
    'Проверил температуру холодильников',
    'Подготовил рабочее место',
    'Включил кассу, открыл смену в iiko',
    'Проверил остатки соусов и заготовок'
  ]},
  { section: 'Приём заказа', items: [
    'Принял заказ от гостя в живую',
    'Принял заказ через агрегатор',
    'Корректно пробил по кассе',
    'Принял оплату наличными и терминалом',
    'Закрыл проблемный кейс (отмена/возврат)'
  ]},
  { section: 'Приготовление', items: [
    'Приготовил Шаурму классическую под контролем',
    'Приготовил Шаурму с курицей',
    'Приготовил картофель фри',
    'Приготовил соус "Дон"',
    'Соблюдает санитарию во время готовки',
    'Скорость: целевая (≤4 мин на шаурму)'
  ]},
  { section: 'Обслуживание', items: [
    'Использует скрипты приветствия',
    'Реагирует на жалобу адекватно',
    'Закрывает заказ в системе после выдачи'
  ]},
  { section: 'Завершение смены', items: [
    'Закрыл смену в кассе',
    'Сдал инкассацию',
    'Убрал рабочее место по стандарту'
  ]},
  { section: 'Критические ситуации', items: [
    'Знает что делать при пожаре',
    'Знает что делать при потере связи',
    'Знает что делать при отключении света',
    'Знает контакт администратора 24/7',
    'Знает регламент при пьяном госте'
  ]},
];

// ═══════════════════════════════════════════════════════════════
// РЕГЛАМЕНТЫ — должностные инструкции
// ═══════════════════════════════════════════════════════════════

const DEFAULT_REGULATIONS = [
  // ─── Шаурмафия ───
  {
    id: 'reg_shawarmafia_cashier',
    brand: 'shawarmafia',
    title: 'Повар-кассир Шаурмафии',
    subtitle: 'Соло-смена · меню Шаурмафии · касса',
    icon: '🥙',
    rights: [
      'Принимать решения по операционным вопросам в рамках смены без согласования',
      'Отказать в обслуживании агрессивному или нетрезвому гостю',
      'Закрыть точку при угрозе безопасности (с обязательным уведомлением старшего администратора)',
      'Запросить замену смены за 4+ часа до её начала при болезни/форс-мажоре',
      'Получать оплачиваемые перерывы согласно ТК',
    ],
    duties: [
      'Открытие и закрытие смены по чек-листу',
      'Приготовление блюд строго по технологическим картам',
      'Соблюдение санитарных норм СанПиН',
      'Приём заказа, кассовые операции, инкассация',
      'Поддержание чистоты рабочего места в течение смены',
      'Учёт остатков, своевременный заказ продуктов через ответственного',
      'Корректное оформление возвратов с подтверждением старшего администратора',
      'Сообщение о ЧП немедленно по протоколу',
    ],
    privileges: [
      'Бесплатное питание во время смены (1 блюдо из меню)',
      'Скидка 30% на продукцию вне смены',
      'Бонусы за выполнение KPI по выручке точки',
      'Премия за безупречные тайные проверки',
      'Возможность роста до старшего администратора через 6 месяцев работы',
    ],
    penalties: [
      { title: 'Опоздание на смену 1–15 мин', amount: '500 ₽' },
      { title: 'Опоздание на смену 15+ мин', amount: '1 500 ₽ + разбор' },
      { title: 'Нарушение дресс-кода (первое)', amount: 'Предупреждение' },
      { title: 'Повторное нарушение дресс-кода', amount: '1 000 ₽' },
      { title: 'Несоблюдение технологической карты', amount: '2 000 ₽ за инцидент' },
      { title: 'Грубое отношение к гостю', amount: '3 000 ₽ + разбор' },
      { title: 'Нарушение санитарии (фото-фиксация)', amount: '3 000 ₽' },
      { title: 'Расхождение в кассе свыше 200 ₽ необъяснимое', amount: 'Покрытие из ЗП + разбор' },
      { title: 'Воровство, наркотики, алкоголь на смене', amount: 'Увольнение по статье + заявление' },
    ],
  },
  // ─── Ролломафия ───
  {
    id: 'reg_rollomafia_local_admin',
    brand: 'rollomafia',
    title: 'Локальный администратор Ролломафии',
    subtitle: 'Управление сменой и командой 3-4 человека',
    icon: '👔',
    rights: [
      'Распределять обязанности внутри смены между членами команды',
      'Принимать решения по конфликтам с гостями до 5 000 ₽',
      'Делать кадровые рекомендации старшему администратору',
      'Корректировать графики смен с согласия команды и старшего администратора',
      'Получать оплачиваемый отпуск 28 календарных дней',
    ],
    duties: [
      'Открытие и закрытие точки, инкассация',
      'Контроль соблюдения технологий пиццмейкером и суши-мейкером',
      'Контроль кассовой дисциплины',
      'Учёт остатков и заказ продукции',
      'Работа с агрегаторами доставки',
      'Ведение ежедневной отчётности',
      'Обучение новых сотрудников на смене',
      'Реагирование на жалобы гостей в течение 15 минут',
      'Поддержание стандартов сервиса и санитарии',
    ],
    privileges: [
      'Оклад + % от выручки точки',
      'Бесплатное питание (без ограничений по позициям)',
      'Скидка 50% вне смены для семьи',
      'Корпоративный телефон',
      'Премия за результаты тайных проверок',
      'Карьерный рост до старшего администратора',
    ],
    penalties: [
      { title: 'Опоздание', amount: '1 000 ₽ за каждое' },
      { title: 'Ошибка в кассовом отчёте', amount: '2 000 ₽' },
      { title: 'Срыв смены без замены', amount: '5 000 ₽' },
      { title: 'Невыполнение KPI 2 месяца подряд', amount: 'Понижение в должности' },
      { title: 'Утаивание ЧП от старшего администратора', amount: '5 000 ₽ + разбор' },
      { title: 'Манипуляции с отчётностью', amount: 'Увольнение по статье' },
    ],
  },
  {
    id: 'reg_rollomafia_pizzamaker',
    brand: 'rollomafia',
    title: 'Пиццмейкер',
    subtitle: 'Производство пиццы и горячих позиций',
    icon: '🍕',
    rights: [
      'Отказаться готовить из явно испорченного сырья',
      'Запрашивать перерывы согласно ТК',
      'Использовать собственные ножи (после согласования с администратором)',
      'Предлагать улучшения в меню старшему администратору',
    ],
    duties: [
      'Приготовление пиццы по технологическим картам',
      'Контроль температуры печи и заготовок',
      'Маркировка всех заготовок (дата+время)',
      'Поддержание чистоты пицца-станции',
      'Своевременное предупреждение о заканчивающихся ингредиентах',
      'Соблюдение санитарных норм',
      'Помощь команде в часы пиковой нагрузки',
    ],
    privileges: [
      'Бесплатное питание на смене',
      'Скидка 30% вне смены',
      'Премия за минимальный процент брака',
      'Карьерный рост до локального администратора',
    ],
    penalties: [
      { title: 'Опоздание 1–15 мин', amount: '500 ₽' },
      { title: 'Нарушение технологии пиццы (фото-фиксация)', amount: '2 000 ₽' },
      { title: 'Заготовки без маркировки', amount: '1 500 ₽' },
      { title: 'Использование просроченного сырья', amount: '3 000 ₽ + разбор' },
      { title: 'Грязное рабочее место в конце смены', amount: '1 500 ₽' },
    ],
  },
  {
    id: 'reg_rollomafia_sushimaker',
    brand: 'rollomafia',
    title: 'Суши-мейкер',
    subtitle: 'Производство роллов, суши, японского меню',
    icon: '🍣',
    rights: [
      'Отказаться работать с сырой рыбой при сомнениях в свежести (с фото-фиксацией)',
      'Использовать собственный набор ножей (с согласования)',
      'Запрашивать обновление заготовок риса каждые 4 часа',
      'Перерывы согласно ТК',
    ],
    duties: [
      'Приготовление роллов и суши по технологическим картам',
      'Контроль температуры и кислотности риса',
      'Жёсткое соблюдение санитарных норм для сырой рыбы',
      'Маркировка всех заготовок и контейнеров',
      'Замена перчаток каждые 30 минут или после любой смены задачи',
      'Поддержание стерильности рабочей зоны',
      'Своевременное предупреждение о необходимости заказа продуктов',
    ],
    privileges: [
      'Бесплатное питание (включая роллы)',
      'Скидка 30% вне смены',
      'Премия за минимальный брак',
      'Доплата за работу с премиум-меню',
      'Карьерный рост до локального администратора',
    ],
    penalties: [
      { title: 'Нарушение санитарии (критично!)', amount: '5 000 ₽ + разбор' },
      { title: 'Заготовки без маркировки', amount: '2 000 ₽' },
      { title: 'Использование риса с истёкшим сроком', amount: '3 000 ₽' },
      { title: 'Нарушение технологии ролла', amount: '2 000 ₽ за инцидент' },
      { title: 'Опоздание', amount: '500 ₽ / 1 500 ₽' },
    ],
  },
  // ─── Общие роли ───
  {
    id: 'reg_courier',
    brand: 'shared',
    title: 'Курьер',
    subtitle: 'Доставка заказов гостям',
    icon: '🛵',
    rights: [
      'Выбирать оптимальный маршрут доставки',
      'Отказаться от заказа, если адрес явно опасный (с уведомлением)',
      'Перерывы между заказами в горячий сезон',
      'Бесплатная мойка экипировки еженедельно',
    ],
    duties: [
      'Доставка заказа в обозначенный срок (до 60 минут)',
      'Бережное обращение с термо-сумкой и заказом',
      'Соблюдение ПДД и скоростного режима',
      'Вежливое общение с гостем',
      'Возврат денежных средств при наличной оплате — в кассу до конца смены',
      'Фотофиксация заказа при сомнительных ситуациях',
      'Уход за велосипедом/мотоциклом/электросамокатом',
    ],
    privileges: [
      'Оплата за каждую доставку + чаевые лично',
      'Компенсация связи и амортизации транспорта',
      'Бесплатное питание во время смены',
      'Бонус за быстрые доставки (до 30 минут)',
      'Корпоративная экипировка',
    ],
    penalties: [
      { title: 'Опоздание с доставкой > 90 минут', amount: '500 ₽ за случай' },
      { title: 'Жалоба гостя на хамство', amount: '2 000 ₽' },
      { title: 'Повреждение заказа по вине курьера', amount: 'Стоимость заказа' },
      { title: 'Кража заказа / денежных средств', amount: 'Увольнение + заявление' },
      { title: 'ДТП по вине курьера', amount: 'Согласно ПДД + разбор' },
    ],
  },
  {
    id: 'reg_senior_admin',
    brand: 'shared',
    title: 'Старший администратор',
    subtitle: 'Управление несколькими точками',
    icon: '⭐',
    rights: [
      'Приём и увольнение линейных сотрудников',
      'Назначение премий и применение штрафов в рамках регламента',
      'Перевод сотрудников между точками',
      'Принятие операционных решений суммой до 30 000 ₽ без согласования',
      'Доступ к финансовой отчётности всех точек',
      'Утверждение графиков смен',
    ],
    duties: [
      'Контроль работы всех вверенных точек',
      'Ежедневный анализ отчётности и KPI',
      'Решение конфликтных ситуаций с гостями',
      'Обучение и развитие администраторов точек',
      'Контроль закупок и заказов',
      'Проведение тайных проверок',
      'Ведение кадровой документации',
      'Реакция на ЧП 24/7',
      'Ежемесячные планёрки с командой',
    ],
    privileges: [
      'Оклад + % от общей выручки',
      'Корпоративный автомобиль или компенсация',
      'Корпоративный телефон',
      'Бесплатное питание на любой точке',
      'Скидка 70% для семьи',
      'Расширенный отпуск',
      'Бонусы за достижение KPI квартала',
      'Возможность партнёрства в новых точках',
    ],
    penalties: [
      { title: 'Срыв сроков отчётности', amount: '3 000 ₽' },
      { title: 'Невыполнение KPI квартала', amount: 'Премия не выплачивается' },
      { title: 'Утаивание серьёзных проблем на точках', amount: '10 000 ₽ + разбор' },
      { title: 'Конфликт интересов / нечестные сделки', amount: 'Увольнение по статье' },
      { title: 'Нарушение трудового законодательства', amount: 'Личная ответственность' },
    ],
  },
  {
    id: 'reg_junior_admin',
    brand: 'shared',
    title: 'Младший администратор',
    subtitle: 'Помощник старшего администратора',
    icon: '🔰',
    rights: [
      'Замена старшего администратора в его отсутствие в рамках инструкций',
      'Делать рекомендации по кадровым вопросам',
      'Доступ к операционной отчётности точек',
      'Согласовывать мелкие операционные решения до 5 000 ₽',
    ],
    duties: [
      'Помощь старшему администратору в текущих задачах',
      'Контроль выполнения регламентов на точках',
      'Ведение части документации',
      'Проведение проверок чек-листов на точках',
      'Реагирование на оперативные обращения линейных сотрудников',
      'Подготовка отчётов',
    ],
    privileges: [
      'Оклад + переменная часть',
      'Бесплатное питание',
      'Скидка 50% вне смены',
      'Корпоративная связь',
      'Карьерный рост до старшего администратора через 6–12 месяцев',
    ],
    penalties: [
      { title: 'Срыв сроков задач от старшего администратора', amount: '1 500 ₽' },
      { title: 'Невыполнение проверок чек-листов', amount: '2 000 ₽' },
      { title: 'Передача неполной информации руководству', amount: '3 000 ₽ + разбор' },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════
// STORAGE HOOKS
// ═══════════════════════════════════════════════════════════════

const STORAGE_KEYS = {
  USERS: 'mafia:users',
  PROGRESS: (uid) => `mafia:progress:v1:${uid}`,
  CHECKLIST: (uid) => `mafia:checklist:v1:${uid}`,
  MESSAGES: (uid) => `mafia:messages:v1:${uid}`,
  CONTENT_COURSES: 'mafia:content:courses:v1',
  CONTENT_TECHCARDS: 'mafia:content:techcards:v1',
  CONTENT_CHECKLIST: 'mafia:content:checklist:v1',
  CONTENT_REGULATIONS: 'mafia:content:regulations:v1',
  CONTENT_SETTINGS: 'mafia:content:settings:v1',
  CONTENT_CONTACTS: 'mafia:content:contacts:v1',
  CONTENT_EXIT_QUESTIONS: 'mafia:content:exit_questions:v1',
  EXIT_RESPONSES: 'mafia:exit_responses:v1',
  TASKS: 'mafia:tasks:v1',                              // задачи от Руководителя → Администраторам
  KITCHEN_CATEGORIES: 'mafia:content:kitchen_categories:v1',  // категории меню кухни
  NOTIFICATIONS: (userId) => `mafia:notifications:v1:${userId}`,        // лента уведомлений пользователя
  NOTIF_SETTINGS: (userId) => `mafia:notif_settings:v1:${userId}`,      // настройки уведомлений
  MODULES: 'mafia:modules:v1',                  // флаги включения модулей (задачи, exit, тех-карты и т.д.)
  SYSTEM_CONFIG: 'mafia:system_config:v1',      // системные настройки (OAuth, лимиты, цвета бренда)
  AUDIT_LOG: 'mafia:audit_log:v1',              // журнал значимых действий
  ROLES: 'mafia:roles:v1',                      // кастомные роли (создаются разработчиком)
};

const PRIVATE_STORAGE_PREFIXES = [
  'mafia:progress:v1:',
  'mafia:checklist:v1:',
  'mafia:messages:v1:',
  'mafia:notifications:v1:',
  'mafia:notif_settings:v1:',
];

const storageSharedFlag = (key, shared) => {
  if (typeof shared === 'boolean') return shared;
  return !PRIVATE_STORAGE_PREFIXES.some(prefix => String(key || '').startsWith(prefix));
};

// ─── Простая хеш-функция для паролей ───
// Использует Web Crypto API (SHA-256). Это НЕ замена bcrypt, но для пилота с доверенным
// окружением даёт защиту от случайного просмотра паролей в БД. На бэкенде с сервером
// следует заменить на bcrypt / argon2.
async function hashPassword(password) {
  if (!password) return '';
  const enc = new TextEncoder().encode(password + 'mafia_salt_v1');
  const hashBuf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function verifyPassword(password, hash) {
  if (!hash) return false;
  const calc = await hashPassword(password);
  return calc === hash;
}

// Валидация email
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');
}

// ─── ФИО / телефон / дата рождения (правила совпадают с сервером) ───
const RU_NAME_WORD = /^[А-ЯЁ][а-яё]+(?:-[А-ЯЁ][а-яё]+)?$/;
function normalizeRuFullName(raw) {
  return String(raw || '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(w => w.split('-').map(p => p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p).join('-'))
    .join(' ');
}
// Возвращает текст ошибки или '' если всё верно
function checkRuFullName(raw) {
  const v = normalizeRuFullName(raw);
  if (!v) return 'Укажите фамилию, имя и отчество';
  if (/[a-z]/i.test(v)) return 'ФИО пишется русскими буквами';
  if (/[^А-Яа-яЁё\s-]/.test(v)) return 'В ФИО допустимы только русские буквы и дефис';
  const parts = v.split(' ');
  if (parts.length < 3) return 'Нужны фамилия, имя и отчество полностью';
  if (!parts.every(w => RU_NAME_WORD.test(w))) return 'Проверьте написание: каждое слово — минимум 2 буквы';
  return '';
}
function normalizeRuPhone(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '8') d = '7' + d.slice(1);
  if (d.length === 10) d = '7' + d;
  return d.length === 11 && d[0] === '7' ? '+' + d : '';
}
// Маска ввода: +7 (999) 123-45-67
function formatRuPhoneInput(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (!d) return '';
  if (d[0] === '8') d = '7' + d.slice(1);
  if (d[0] !== '7') d = '7' + d;
  d = d.slice(0, 11);
  const p = d.slice(1);
  let out = '+7';
  if (p.length) out += ' (' + p.slice(0, 3);
  if (p.length >= 3) out += ')';
  if (p.length > 3) out += ' ' + p.slice(3, 6);
  if (p.length > 6) out += '-' + p.slice(6, 8);
  if (p.length > 8) out += '-' + p.slice(8, 10);
  return out;
}
function checkBirthDate(v) {
  if (!v) return 'Укажите дату рождения';
  const d = new Date(v);
  if (isNaN(d.getTime())) return 'Некорректная дата';
  const age = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (age < 14 || age > 100) return 'Проверьте дату рождения';
  return '';
}
// Дата от провайдера: Яндекс — YYYY-MM-DD, ВК — D.M.YYYY (год может быть скрыт)
function providerBirthdayToIso(b) {
  const v = String(b || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return '';
}
// Каких личных данных не хватает (для экрана дозаполнения)
function missingProfileFields(u) {
  if (!u || u.isAdmin || isDevAccount(u)) return [];
  const miss = [];
  if (checkRuFullName(u.fullName || u.name)) miss.push('fullName');
  if (checkBirthDate(u.birthDate)) miss.push('birthDate');
  if (!normalizeRuPhone(u.phone)) miss.push('phone');
  return miss;
}

// ─── Дефолтные контакты и exit-вопросы ───

const DEFAULT_CONTACTS = {
  intro: 'Контакты экстренной связи, ответственных лиц и дни рождения коллег. Береги номера — в нужный момент они спасут смену.',
  sections: [
    {
      id: 'emergency',
      title: 'Экстренная связь',
      icon: '🚨',
      color: '#E83A2C',
      items: [
        { id: 'em1', label: 'Полиция', value: '102' },
        { id: 'em2', label: 'Скорая помощь', value: '103' },
        { id: 'em3', label: 'Пожарная служба', value: '101' },
        { id: 'em4', label: 'Единая служба спасения', value: '112' },
      ],
    },
    {
      id: 'managers',
      title: 'Руководство 24/7',
      icon: '⭐',
      color: '#FFB800',
      items: [
        { id: 'mg1', label: 'Старший администратор', value: '+7 (___) ___-__-__' },
        { id: 'mg2', label: 'Младший администратор', value: '+7 (___) ___-__-__' },
        { id: 'mg3', label: 'Технический отдел', value: '+7 (___) ___-__-__' },
      ],
    },
    {
      id: 'services',
      title: 'Сервисные службы',
      icon: '🔧',
      color: '#00E5FF',
      items: [
        { id: 'sv1', label: 'Поставщик продуктов', value: '' },
        { id: 'sv2', label: 'Ремонт оборудования', value: '' },
        { id: 'sv3', label: 'Уборка/санитария', value: '' },
      ],
    },
  ],
};

const DEFAULT_EXIT_INTRO = 'Мы, как работодатели, старающиеся соответствовать стандартам найма, при планируемом уходе с должности сотрудника, просим заполнить exit-анкету, которая поможет нам установить причинно-следственную связь, и, возможно, улучшить условия труда в будущем.\n\nНужно ответить на несколько вопросов в текстовом формате в удобное время.';

const DEFAULT_EXIT_QUESTIONS = [
  { id: 'eq1', text: 'Какая причина ухода?', type: 'text' },
  { id: 'eq2', text: 'Оцени, насколько устраивают текущие условия работы от 1 до 10', type: 'rating10' },
  { id: 'eq3', text: 'Опиши то, что нравится', type: 'text' },
  { id: 'eq4', text: 'Опиши то, что не нравится', type: 'text' },
  { id: 'eq5', text: 'Что бы ты порекомендовал(а) исправить из условий работы для общего комфорта будущего потенциального администратора?', type: 'text' },
  { id: 'eq6', text: 'Есть ли в рабочем коллективе друзья?', type: 'text' },
  { id: 'eq7', text: 'Есть ли в рабочем коллективе враги?', type: 'text' },
  { id: 'eq8', text: 'Можем ли мы что-то предпринять, чтобы решение было пересмотрено, если причина ухода созависима с условиями работы?', type: 'yesno' },
  { id: 'eq9', text: 'Если можем, то что именно?', type: 'text' },
];

// Возраст по дате рождения (YYYY-MM-DD)
function calcAge(birthDate) {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

// Сколько дней до следующего ДР (отрицательно = сегодня прошло, NaN = некорректная дата)
function daysUntilBirthday(birthDate) {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const next = new Date(today.getFullYear(), d.getMonth(), d.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  return Math.round((next - today) / (24 * 3600 * 1000));
}

// Форматирование "10 января"
const MONTHS_RU = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
function formatBirthDay(birthDate) {
  if (!birthDate) return '—';
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;
}
/*
const safeGet = async (key, shared) => {
  try {
    const sharedFlag = storageSharedFlag(key, shared);
    const params = new URLSearchParams({ shared: String(sharedFlag), owner_id: sharedFlag ? '' : (key.split(':').pop() || '') });
    const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?${params}`, { credentials: 'include' });
    if (!resp.ok) return null;
    const json = await resp.json();
    return json?.value ?? null;
  } catch (e) {
    return null;
  }
};*/

const safeGet = async (key, shared) => {
  try {
    const sharedFlag = storageSharedFlag(key, shared);
    const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=${sharedFlag}`, { credentials: 'include' });
    if (!resp.ok) return null;
    const json = await resp.json();
    return json?.value ?? null;
  } catch (e) {
    return null;
  }
};

const safeSet = async (key, value, shared) => {
  try {
    const sharedFlag = storageSharedFlag(key, shared);
    const resp = await fetch(`/api/kv/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value, shared: sharedFlag, owner_id: sharedFlag ? '' : (key.split(':').pop() || '') }),
    });
    return resp.ok;
  } catch (e) {
    console.error('[safeSet]', e.message);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════
// CONTENT CONTEXT — динамический контент с возможностью редактирования
// ═══════════════════════════════════════════════════════════════

const ContentContext = createContext(null);

const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
};

// ═══════════════════════════════════════════════════════════════
// NOTIFICATIONS CONTEXT — личная лента уведомлений + настройки
// ═══════════════════════════════════════════════════════════════

const NotificationContext = createContext(null);
const useNotifications = () => useContext(NotificationContext);

// Единая шина событий реального времени. Сокет открывается ОДИН раз в корне приложения,
// экраны (чаты и т.п.) подписываются сюда, а не открывают свои соединения.
const realtimeBus = typeof window !== 'undefined' ? new EventTarget() : null;
function useRealtime(handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!realtimeBus) return;
    const fn = (e) => { try { ref.current?.(e.detail); } catch {} };
    realtimeBus.addEventListener('rt', fn);
    return () => realtimeBus.removeEventListener('rt', fn);
  }, []);
}

// Куда ведёт уведомление. У старых записей link мог быть пустым — достраиваем по типу.
function resolveNotifLink(notif, isAdmin) {
  if (notif?.link?.screen) return { screen: notif.link.screen, ctx: notif.link.ctx || {} };
  if (notif?.type === 'message') {
    return isAdmin
      ? { screen: 'adminMessages', ctx: notif.from ? { userId: notif.from } : {} }
      : { screen: 'messages', ctx: {} };
  }
  return null;
}

// Короткий сигнал уведомления (WebAudio, без файлов)
function playNotifBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880;
    o.type = 'sine';
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    o.start();
    o.stop(ctx.currentTime + 0.16);
    setTimeout(() => ctx.close().catch(() => {}), 400);
  } catch {}
}

// Дефолтные настройки — все типы включены, звук отключён (нужен явный opt-in из-за политики браузеров)
const DEFAULT_NOTIF_SETTINGS = {
  inAppEnabled: true,                  // показывать всплывашки
  emailEnabled: true,                  // присылать письма (по умолчанию да)
  soundEnabled: false,                 // звук — opt-in
  // Типы:
  tasks: true,                         // задачи
  taskComments: true,                  // комментарии к задачам
  taskStatus: true,                    // смена статуса задач
  messages: true,                      // личные сообщения от руководителя
  mentions: true,                      // упоминания @
  system: true,                        // системные (welcome, ДР и т.п.)
};

// Типы уведомлений
const NOTIF_TYPES = {
  task_assigned:   { label: 'НОВАЯ ЗАДАЧА',        icon: '📋', color: '#7C5CFF', setting: 'tasks' },
  task_comment:    { label: 'КОММЕНТАРИЙ',          icon: '💬', color: '#00E5FF', setting: 'taskComments' },
  task_status:     { label: 'СТАТУС ЗАДАЧИ',        icon: '🔄', color: '#FFB800', setting: 'taskStatus' },
  message:         { label: 'СООБЩЕНИЕ',            icon: '✉️', color: '#E83A2C', setting: 'messages' },
  mention:         { label: 'УПОМИНАНИЕ',           icon: '@',  color: '#FF7849', setting: 'mentions' },
  system:          { label: 'СИСТЕМА',              icon: 'ℹ️', color: '#9B9BA4', setting: 'system' },
};

// ═══════════════════════════════════════════════════════════════
// CONFIRM CONTEXT — кастомный модал вместо window.confirm
// ═══════════════════════════════════════════════════════════════

const ConfirmContext = createContext(null);
const useConfirm = () => useContext(ConfirmContext);

function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, msg: '', resolver: null, danger: false });

  const confirm = useCallback((msg, opts = {}) => {
    return new Promise(resolve => {
      setState({ open: true, msg, resolver: resolve, danger: opts.danger || false });
    });
  }, []);

  const handle = (val) => {
    if (state.resolver) state.resolver(val);
    setState({ open: false, msg: '', resolver: null, danger: false });
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm fade-in" onClick={() => handle(false)}>
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5 max-w-sm w-full pop-in shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                state.danger ? 'bg-[#E83A2C]/20 text-[#E83A2C]' : 'bg-[#FFB800]/20 text-[#FFB800]'
              }`}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 pt-0.5">
                <div className="font-display text-lg tracking-wider text-white mb-1">ПОДТВЕРДИ</div>
                <div className="text-sm text-gray-300 leading-relaxed">{state.msg}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handle(false)}
                className="flex-1 bg-[#0A0B0D] border border-[#2A2E36] hover:border-gray-500 text-gray-300 font-mono text-xs tracking-wider py-3 rounded-lg"
              >
                ОТМЕНА
              </button>
              <button
                onClick={() => handle(true)}
                className={`flex-1 font-mono text-xs tracking-wider py-3 rounded-lg ${
                  state.danger
                    ? 'bg-[#E83A2C] hover:bg-[#FF4A3C] text-white'
                    : 'bg-[#FFB800] hover:bg-[#FFC820] text-black'
                }`}
              >
                ПОДТВЕРДИТЬ
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// Простой генератор уникальных ID
const genId = (prefix = 'id') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Глубокое клонирование (для безопасного редактирования вложенных структур)
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// ─── sanitizeTechCards ───
// Очищает «битые» карточки блюд, которые могли быть созданы багом ранней версии
// duplicateCard (превращала step-строки в объекты вида {0:'Л',1:'а',2:'в',...}).
// Возвращает копию карт с корректной структурой; оригинал не меняется.
// Также гарантирует что обязательные поля есть.
function sanitizeTechCards(cards) {
  if (!cards || typeof cards !== 'object') return cards;
  const out = {};
  for (const [id, card] of Object.entries(cards)) {
    if (!card || typeof card !== 'object') continue;
    const fixed = { ...card };

    // steps: должен быть массивом строк
    if (Array.isArray(fixed.steps)) {
      fixed.steps = fixed.steps.map(s => {
        if (typeof s === 'string') return s;
        // Битый случай: объект {0:'Л', 1:'а', 2:'в', ..., id:'step_...'}
        // Распознаём по наличию числовых ключей и собираем строку.
        if (s && typeof s === 'object') {
          const keys = Object.keys(s).filter(k => /^\d+$/.test(k)).sort((a,b) => +a - +b);
          if (keys.length > 0) return keys.map(k => s[k]).join('');
          // Иначе пытаемся достать что-то осмысленное
          return s.text || s.title || s.description || '';
        }
        return String(s ?? '');
      }).filter(s => s.length > 0);
    } else {
      fixed.steps = [];
    }

    // ingredients и mistakes — массивы объектов; убираем у них «лишний» id если был дополнен
    if (Array.isArray(fixed.ingredients)) {
      fixed.ingredients = fixed.ingredients.map(i => {
        if (typeof i !== 'object' || i === null) return i;
        const { id: _ignoreId, ...rest } = i;
        return rest;
      });
    } else {
      fixed.ingredients = [];
    }
    if (Array.isArray(fixed.mistakes)) {
      fixed.mistakes = fixed.mistakes.map(m => {
        if (typeof m !== 'object' || m === null) return m;
        const { id: _ignoreId, ...rest } = m;
        return rest;
      });
    } else {
      fixed.mistakes = [];
    }

    out[id] = fixed;
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════
// УТИЛИТЫ
// ═══════════════════════════════════════════════════════════════

const calcOverallProgress = (progress, course) => {
  if (!progress) return 0;
  const totalLessons = course.modules.reduce((s, m) => s + m.lessons.length + 1, 0);
  let done = 0;
  course.modules.forEach(m => {
    m.lessons.forEach(l => { if (progress.lessons?.[l.id]) done++; });
    if (progress.tests?.[m.id]?.passed) done++;
  });
  return Math.round((done / totalLessons) * 100);
};

const isModuleUnlocked = (moduleIdx, progress, course) => {
  if (moduleIdx === 0) return true;
  const prevModule = course.modules[moduleIdx - 1];
  return progress?.tests?.[prevModule.id]?.passed === true;
};

const getReadinessStatus = (progress, course) => {
  const overall = calcOverallProgress(progress, course);
  const allTestsPassed = course.modules.every(m => progress?.tests?.[m.id]?.passed);
  const finalApproved = progress?.finalApproved === true;
  const checklistDone = progress?.checklistApproved === true;

  if (finalApproved && checklistDone) return { status: 'shift_soldier', label: 'СПЕЦИАЛИСТ', color: 'green' };
  if (allTestsPassed && !finalApproved) return { status: 'on_exam', label: 'НА ЭКЗАМЕНЕ', color: 'orange' };
  if (overall > 0) return { status: 'training', label: 'НА ОБУЧЕНИИ', color: 'blue' };
  return { status: 'candidate', label: 'КАНДИДАТ', color: 'gray' };
};

// ═══════════════════════════════════════════════════════════════
// ОСНОВНОЙ КОМПОНЕНТ
// ═══════════════════════════════════════════════════════════════

export default function MafiaAcademy() {
  const [screen, setScreen] = useState('login');
  const [ctx, setCtx] = useState({});
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  // Публичный счётчик пользователей — нужен на LoginScreen чтобы понять «первый ли это юзер».
  // Грузится без auth с endpoint /api/auth/usersCount (отдаёт только число, никаких PII).
  const [publicUsersCount, setPublicUsersCount] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        const r = await fetch(`${apiUrl}/auth/usersCount`);
        if (r.ok) {
          const body = await r.json();
          if (typeof body.count === 'number') setPublicUsersCount(body.count);
        }
      } catch {}
    })();
  }, []);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [progress, setProgress] = useState(null);
  const [practicalChecklist, setPracticalChecklist] = useState(null);
  const [toast, setToast] = useState(null);
  const [authAction, setAuthAction] = useState(null);   // { type:'verify-email'|'reset-password', token } - из URL

  // Контент (динамический, грузится из БД с fallback на defaults)
  const [courses, setCourses] = useState(DEFAULT_COURSES);
  const [techCards, setTechCards] = useState(DEFAULT_TECHCARDS);
  const [checklistTemplate, setChecklistTemplate] = useState(DEFAULT_CHECKLIST);
  const [regulations, setRegulations] = useState(DEFAULT_REGULATIONS);
  const [contacts, setContacts] = useState(DEFAULT_CONTACTS);
  const [exitQuestions, setExitQuestions] = useState({ intro: DEFAULT_EXIT_INTRO, questions: DEFAULT_EXIT_QUESTIONS });
  const [exitResponses, setExitResponses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [kitchenCategories, setKitchenCategories] = useState(DEFAULT_KITCHEN_CATEGORIES);
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [systemConfig, setSystemConfig] = useState(DEFAULT_SYSTEM_CONFIG);
  const [roles, setRoles] = useState([]);
  // ─── Уведомления — личные для текущего пользователя ───
  const [notifications, setNotifications] = useState([]);
  const [notifSettings, setNotifSettings] = useState(DEFAULT_NOTIF_SETTINGS);
  const [notifToast, setNotifToast] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settings, setSettings] = useState({
    // — Регистрация —
    allowRegistration: true,
    requireEmail: true,
    requirePassword: true,
    requireFullName: true,                    // ФИО обязательно
    requireGender: false,                     // пол при регистрации
    requirePhone: false,                      // телефон при регистрации
    requireBirthDate: true,                   // дата рождения обязательна
    welcomeMessage: 'Добро пожаловать в Академию!',
    // — Тексты стартового экрана —
    loginTitle: 'MAFIA',
    loginSubtitle: 'ACADEMY',
    loginTagline: 'БАЗА ПОДГОТОВКИ · ШАУРМАФИЯ · РОЛЛОМАФИЯ',
    loginEmailPlaceholder: 'ivan@example.com',
    loginPasswordPlaceholder: '••••••',
    loginNamePlaceholder: 'Иванов Иван Иванович',
    registerHint: 'Доступ ко второму направлению админ откроет позже.',
    firstAdminHint: '⚡ ПЕРВЫЙ РЕГИСТРИРУЮЩИЙСЯ ПОЛУЧАЕТ ПРАВА АДМИНА',
    loginButtonText: 'ВОЙТИ',
    registerButtonText: 'СОЗДАТЬ АККАУНТ',
    // — Тема оформления —
    theme: 'classic',                         // classic | newyear
    accentColor: '#FFB800',                   // золотой → можно сменить на фуксию #E91E63 и т.п.
    primaryColor: '#E83A2C',                  // красный (главный) — обычно не трогаем
  });

  // ─── Загрузка общего контента из БД (курсы, техкарты, регламенты, настройки и т.д.) ───
  // Вынесено в отдельную функцию: нужно и при восстановлении сессии на старте,
  // и сразу после логина/регистрации — иначе свежезалогиненный юзер видит дефолтный контент до F5.
  const loadSharedContent = useCallback(async () => {
    try {
    // ═══ ЗАГРУЗКА КОНТЕНТА (теперь есть JWT-куки) ═══
    
    // Курсы
    const coursesResp = await fetch('/api/kv/mafia:content:courses:v1?shared=true', { credentials: 'include' });
    if (coursesResp.ok) {
      const json = await coursesResp.json();
      // console.log("[init] setting courses:", json?.value?.length);
      if (json?.value && Array.isArray(json.value) && json.value.length > 0) setCourses(json.value);
    }
    
    // Техкарты
    const tcResp = await fetch('/api/kv/mafia:content:techcards:v1?shared=true', { credentials: 'include' });
    if (tcResp.ok) {
      const json = await tcResp.json();
      if (json?.value) setTechCards(sanitizeTechCards(json.value));
    }
    
    // Чек-лист
    const clResp = await fetch('/api/kv/mafia:content:checklist:v1?shared=true', { credentials: 'include' });
    if (clResp.ok) {
      const json = await clResp.json();
      if (json?.value) setChecklistTemplate(json.value);
    }
    
    // Регламенты
    const regsResp = await fetch('/api/kv/mafia:content:regulations:v1?shared=true', { credentials: 'include' });
    if (regsResp.ok) {
      const json = await regsResp.json();
      if (json?.value && Array.isArray(json.value)) setRegulations(json.value);
    }
    
    // Настройки
    const settingsResp = await fetch('/api/kv/mafia:content:settings:v1?shared=true', { credentials: 'include' });
    if (settingsResp.ok) {
      const json = await settingsResp.json();
      if (json?.value) setSettings(s => ({ ...s, ...json.value }));
    }
    
    // Контакты
    const contactsResp = await fetch('/api/kv/mafia:content:contacts:v1?shared=true', { credentials: 'include' });
    if (contactsResp.ok) {
      const json = await contactsResp.json();
      if (json?.value) setContacts(json.value);
    }
    
    // Exit-вопросы
    const exitQResp = await fetch('/api/kv/mafia:content:exit_questions:v1?shared=true', { credentials: 'include' });
    if (exitQResp.ok) {
      const json = await exitQResp.json();
      if (json?.value) setExitQuestions(json.value);
    }
    
    // Exit-ответы
    const exitRResp = await fetch('/api/kv/mafia:exit_responses:v1?shared=true', { credentials: 'include' });
    if (exitRResp.ok) {
      const json = await exitRResp.json();
      if (json?.value && Array.isArray(json.value)) setExitResponses(json.value);
    }
    
    // Задачи
    const tasksResp = await fetch('/api/kv/mafia:tasks:v1?shared=true', { credentials: 'include' });
    if (tasksResp.ok) {
      const json = await tasksResp.json();
      if (json?.value && Array.isArray(json.value)) setTasks(json.value);
    }
    
    // Категории кухни
    const catsResp = await fetch('/api/kv/mafia:content:kitchen_categories:v1?shared=true', { credentials: 'include' });
    if (catsResp.ok) {
      const json = await catsResp.json();
      if (json?.value && Array.isArray(json.value)) setKitchenCategories(json.value);
    }
    
    // Модули
    const modulesResp = await fetch('/api/kv/mafia:modules:v1?shared=true', { credentials: 'include' });
    if (modulesResp.ok) {
      const json = await modulesResp.json();
      if (json?.value && typeof json.value === 'object') {
        const merged = { ...DEFAULT_MODULES };
        for (const k of Object.keys(json.value)) {
          if (merged[k]) merged[k] = { ...merged[k], enabled: !!json.value[k].enabled };
        }
        setModules(merged);
      }
    }
    
    // Системный конфиг
    const sysResp = await fetch('/api/kv/mafia:system_config:v1?shared=true', { credentials: 'include' });
    if (sysResp.ok) {
      const json = await sysResp.json();
      if (json?.value && typeof json.value === 'object') setSystemConfig({ ...DEFAULT_SYSTEM_CONFIG, ...json.value });
    }
    
    // Аудит
    const auditResp = await fetch('/api/kv/mafia:audit_log:v1?shared=true', { credentials: 'include' });
    if (auditResp.ok) {
      const json = await auditResp.json();
      if (json?.value && Array.isArray(json.value)) setAuditLog(json.value);
    }
    
    // Роли
    const rolesResp = await fetch('/api/kv/mafia:roles:v1?shared=true', { credentials: 'include' });
    if (rolesResp.ok) {
      const json = await rolesResp.json();
      if (json?.value && Array.isArray(json.value)) setRoles(json.value);
    }
    } catch (e) {
      console.error('[content] загрузка не удалась:', e);
    }
  }, []);

    // Загрузка пользователей и контента при старте
  useEffect(() => {
    (async () => {
      let data = await safeGet(STORAGE_KEYS.USERS);
      if (!Array.isArray(data)) data = [];

      // ─── ВОССТАНОВЛЕНИЕ СЕССИИ ───
      try {
        let serverUser = null;
        try {
          const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
          const r = await fetch(`${apiUrl}/auth/me`, { credentials: 'include' });
          if (r.ok) {
            const body = await r.json();
            serverUser = body.user;
          }
        } catch (e) {}

        const effectiveUserId = serverUser?.id || null;

        if (effectiveUserId) {
          // ═══ ЗАГРУЗКА КОНТЕНТА (теперь есть JWT-куки) ═══
          await loadSharedContent();

          // Загружаем свежий users из БД
          let allUsers = data || [];
          try {
            const resp = await fetch('/api/kv/mafia:users?shared=true', { credentials: 'include' });
            if (resp.ok) {
              const json = await resp.json();
              const freshUsers = json.value;
              if (Array.isArray(freshUsers) && freshUsers.length > 0) {
                allUsers = freshUsers;
              }
            }
          } catch (e) {
            console.error('[session] failed to load users:', e.message);
          }
          
          let u = allUsers.find(x => x.id === effectiveUserId);
          if (!u && serverUser) {
            u = serverUser;
            allUsers = [serverUser, ...allUsers.filter(x => x.id !== serverUser.id)];
          }
          setUsers(allUsers);
          
          setContentReady(true);
          if (u) {
            if (!u.isAdmin) {
              // Прогресс. Если записи в БД ещё нет (юзер только зарегистрировался и не
              // проходил уроков) — ставим пустой объект, иначе Dashboard навсегда
              // застревает на скелетоне «СИНХРОНИЗАЦИЯ ДОСЬЕ...».
              let loadedProgress = null;
              try {
                const pkey = STORAGE_KEYS.PROGRESS(u.id);
                const presp = await fetch(`/api/kv/${encodeURIComponent(pkey)}?shared=false&owner_id=${u.id}`, { credentials: 'include' });
                if (presp.ok) {
                  const pjson = await presp.json();
                  if (pjson?.value) loadedProgress = pjson.value;
                }
              } catch {}
              setProgress(loadedProgress || { lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
              try {
                const ckey = STORAGE_KEYS.CHECKLIST(u.id);
                const cresp = await fetch(`/api/kv/${encodeURIComponent(ckey)}?shared=false&owner_id=${u.id}`, { credentials: 'include' });
                if (cresp.ok) {
                  const cjson = await cresp.json();
                  if (cjson?.value) setPracticalChecklist(cjson.value);
                }
              } catch {}
            }
            
            setCurrentUserId(u.id);
            const params = new URLSearchParams(window.location.search);
            const screenParam = params.get('screen');
            const path = window.location.pathname;
            
            if (screenParam) {
              setScreen(screenParam);
              const ctxParam = params.get('ctx');
              if (ctxParam) {
                try { setCtx(JSON.parse(atob(ctxParam))); } catch {}
              }
            } else if (path !== '/' && path !== '/favicon.svg') {
              const parts = path.split('/').filter(Boolean);
              if (parts.length > 0) {
                setScreen(parts[0]);
                if (parts[1]) {
                  try { setCtx(JSON.parse(atob(parts[1]))); } catch {}
                }
              }
            } else {
              setScreen(u.isAdmin ? 'admin' : 'dashboard');
            }
          } else {
            console.warn('❌ юзер с id', effectiveUserId, 'не найден в users');
            setCurrentUserId(effectiveUserId);
            setScreen('dashboard');
          }
        } else {
		  setContentReady(true);
          // console.log('сессии нет, остаёмся на login');
        }
      } catch (e) {
        console.error('[session/restore] критическая ошибка:', e);
      }
	  setLoading(false);
	  
	  
      
    })();
  }, []);
  
  // ─── Всплывашки: таймеры через ref, чтобы новая не гасилась таймером старой ───
  const toastTimerRef = useRef(null);
  const notifToastTimerRef = useRef(null);
  const notifSettingsRef = useRef(DEFAULT_NOTIF_SETTINGS);
  const notificationsRef = useRef([]);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  const showNotifToast = useCallback((n) => {
    const st = notifSettingsRef.current || DEFAULT_NOTIF_SETTINGS;
    const typeInfo = NOTIF_TYPES[n.type];
    if (!st.inAppEnabled) return;
    if (typeInfo?.setting && st[typeInfo.setting] === false) return;
    setNotifToast(n);
    if (notifToastTimerRef.current) clearTimeout(notifToastTimerRef.current);
    notifToastTimerRef.current = setTimeout(() => setNotifToast(null), 6000);
    if (st.soundEnabled) playNotifBeep();
  }, []);

  // WebSocket: одно соединение на приложение. Личность сервер берёт из JWT-куки.
  useEffect(() => {
    if (!currentUserId) return;
    let ws;
    let reconnectTimer;
    let pingInterval;
    let disposed = false;
    let attempt = 0;

    const connect = () => {
      if (disposed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      try {
        ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
      } catch { return; }

      ws.onopen = () => {
        attempt = 0;
        pingInterval = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
        }, 30000);
      };

      ws.onmessage = (event) => {
        let data;
        try { data = JSON.parse(event.data); } catch { return; }
        if (data.type === 'new_notification' && data.notification?.id) {
          const n = data.notification;
          // Дубли отсекаем по id (React может вызвать updater позже, поэтому сверяемся с ref)
          if (!notificationsRef.current.some(x => x.id === n.id)) {
            notificationsRef.current = [n, ...notificationsRef.current];
            setNotifications(prev => prev.some(x => x.id === n.id) ? prev : [n, ...prev].slice(0, 100));
            showNotifToast(n);
          }
        }
        realtimeBus?.dispatchEvent(new CustomEvent('rt', { detail: data }));
      };

      ws.onclose = () => {
        if (pingInterval) clearInterval(pingInterval);
        if (disposed) return;
        attempt += 1;
        reconnectTimer = setTimeout(connect, Math.min(30000, 3000 * attempt));
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingInterval) clearInterval(pingInterval);
      try { ws && ws.close(); } catch {}
    };
  }, [currentUserId, showNotifToast]);

    // ─── Журнал аудита ───
  // Пишет запись о значимом действии — кто, когда, что сделал
  const appendAuditLog = useCallback(async (entry) => {
    if (!entry || !entry.action) return;
    const record = {
      id: 'log_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      at: Date.now(),
      actorId: entry.actorId || currentUserId || '',
      actorName: entry.actorName || '',
      action: entry.action,                  // 'user_created', 'permission_changed', 'content_deleted', etc.
      target: entry.target || '',            // что/кого затронуло
      details: entry.details || '',
    };
    setAuditLog(prev => {
      const next = [record, ...prev].slice(0, 500);     // храним последние 500 записей
      safeSet(STORAGE_KEYS.AUDIT_LOG, next).catch(() => {});
      return next;
    });
  }, [currentUserId]);
  
  const getActorName = () => users.find(u => u.id === currentUserId)?.name || 'Система';
  
  // API для редактирования контента
  const updateCourses = useCallback(async (newCourses) => {
    setCourses(newCourses);
    const key = STORAGE_KEYS.CONTENT_COURSES;
    await fetch(`/api/kv/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newCourses, shared: true, owner_id: '' }),
    }).catch(() => {});
    appendAuditLog({ action: 'courses_updated', actorName: getActorName(), details: `Обновлены курсы (${newCourses?.length || 0} шт.)` });
  }, [appendAuditLog, getActorName()]);

	const updateCourse = useCallback(async (courseId, patch) => {
	  const newCourses = courses.map(c => c.id === courseId ? (typeof patch === 'function' ? patch(c) : { ...c, ...patch }) : c);
	  setCourses(newCourses);
	  const key = STORAGE_KEYS.CONTENT_COURSES;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newCourses, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'course_updated', actorName: getActorName(), details: `Изменён курс ${courseId}` });
	}, [courses, appendAuditLog, getActorName()]);

  const updateTechCards = useCallback(async (newTC) => {
	  setTechCards(newTC);
	  const key = STORAGE_KEYS.CONTENT_TECHCARDS;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newTC, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'techcards_updated', actorName: getActorName(), details: `Техкарты обновлены (${Object.keys(newTC || {}).length || 0} шт.)` });
	}, [appendAuditLog, getActorName()]);

	const updateKitchenCategories = useCallback(async (newCats) => {
	  setKitchenCategories(newCats);
	  const key = STORAGE_KEYS.KITCHEN_CATEGORIES;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: "PUT", credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ value: newCats, shared: true, owner_id: "" }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'kitchen_categories_updated', actorName: getActorName(), details: `Категории меню обновлены (${newCats?.length || 0} шт.)` });
	}, [appendAuditLog, getActorName()]);

  // ─── Модули системы ───
  const updateModules = useCallback(async (newModules) => {
	  setModules(newModules);
	  const key = STORAGE_KEYS.MODULES;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newModules, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'modules_updated', actorName: getActorName(), details: `Модули системы обновлены` });
	}, [appendAuditLog, getActorName()]);

  // ─── Системные настройки ───
  const updateSystemConfig = useCallback(async (patch) => {
	  const next = { ...systemConfig, ...patch };
	  setSystemConfig(next);
	  const key = STORAGE_KEYS.SYSTEM_CONFIG;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: next, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'system_config_updated', actorName: getActorName(), details: `Системный конфиг обновлён: ${JSON.stringify(patch)}` });
	}, [systemConfig, appendAuditLog, getActorName()]);

  // ─── Кастомные роли ───
  const updateRoles = useCallback(async (newRoles) => {
	  setRoles(newRoles);
	  const key = STORAGE_KEYS.ROLES;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newRoles, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'roles_updated', actorName: getActorName(), details: `Роли обновлены (${newRoles?.length || 0} шт.)` });
	}, [appendAuditLog, getActorName()]);

  const updateChecklistTemplate = useCallback(async (newCL) => {
	  setChecklistTemplate(newCL);
	  const key = STORAGE_KEYS.CONTENT_CHECKLIST;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newCL, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'checklist_updated', actorName: getActorName(), details: `Чек-лист обновлён (${newCL?.length || 0} разделов)` });
	}, [appendAuditLog, getActorName()]);

	const updateRegulations = useCallback(async (newRegs) => {
	  setRegulations(newRegs);
	  const key = STORAGE_KEYS.CONTENT_REGULATIONS;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newRegs, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'regulations_updated', actorName: getActorName(), details: `Регламенты обновлены (${newRegs?.length || 0} шт.)` });
	}, [appendAuditLog, getActorName()]);

  const updateRegulation = useCallback(async (regId, patch) => {
    const next = regulations.map(r => r.id === regId ? (typeof patch === 'function' ? patch(r) : { ...r, ...patch }) : r);
    setRegulations(next);
    const key = STORAGE_KEYS.CONTENT_REGULATIONS;
    await fetch(`/api/kv/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: next, shared: true, owner_id: '' }),
    }).catch(() => {});
  }, [regulations]);

  const updateSettings = useCallback(async (patch) => {
	  const next = { ...settings, ...patch };
	  setSettings(next);
	  const key = STORAGE_KEYS.CONTENT_SETTINGS;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: next, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'settings_updated', actorName: getActorName(), details: `Настройки обновлены: ${JSON.stringify(patch)}` });
	}, [settings, appendAuditLog, getActorName()]);

  const updateContacts = useCallback(async (newContacts) => {
	  setContacts(newContacts);
	  const key = STORAGE_KEYS.CONTENT_CONTACTS;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newContacts, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'contacts_updated', actorName: getActorName(), details: `Контакты обновлены (${newContacts?.sections?.length || 0} разделов)` });
	}, [appendAuditLog, getActorName()]);

  const updateExitQuestions = useCallback(async (newEq) => {
	  setExitQuestions(newEq);
	  const key = STORAGE_KEYS.CONTENT_EXIT_QUESTIONS;
	  await fetch(`/api/kv/${encodeURIComponent(key)}`, {
		method: 'PUT', credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ value: newEq, shared: true, owner_id: '' }),
	  }).catch(() => {});
	  appendAuditLog({ action: 'exit_questions_updated', actorName: getActorName(), details: `Exit-анкета обновлена` });
	}, [appendAuditLog, getActorName()]);

  const addExitResponse = useCallback(async (resp) => {
    const next = [...exitResponses, resp];
    setExitResponses(next);
    const key = STORAGE_KEYS.EXIT_RESPONSES;
    await fetch(`/api/kv/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: next, shared: true, owner_id: '' }),
    }).catch(() => {});
  }, [exitResponses]);

  const deleteExitResponse = useCallback(async (id) => {
    const next = exitResponses.filter(r => r.id !== id);
    setExitResponses(next);
    const key = STORAGE_KEYS.EXIT_RESPONSES;
    await fetch(`/api/kv/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: next, shared: true, owner_id: '' }),
    }).catch(() => {});
  }, [exitResponses]);

  // ─── Управление задачами (Руководитель → Администраторам) ───
  // ─── Утилита: записать уведомление напрямую в БД (без зависимости от состояния) ───
  // Используется триггерами в createTask/updateTask/addTaskComment.
  // Не обновляет локальный стейт получателя — это сделает либо его текущая вкладка
  // через collisions при следующем безопасном чтении, либо подгрузится при логине.
  const persistNotification = useCallback(async (targetUserId, notif) => {
    if (!targetUserId) return;
    try {
      const existing = await safeGet(STORAGE_KEYS.NOTIFICATIONS(targetUserId), false) || [];
      const newNotif = {
        id: 'n_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
        type: notif.type || 'system',
        title: notif.title || '',
        text: notif.text || '',
        link: notif.link || null,
        from: notif.from || null,
        read: false,
        at: Date.now(),
      };
      const updated = [newNotif, ...existing].slice(0, 100);
      await safeSet(STORAGE_KEYS.NOTIFICATIONS(targetUserId), updated, false);
      // Если уведомление для текущего пользователя — обновим стейт и покажем toast
      if (targetUserId === currentUserId) {
        setNotifications(updated);
        if (notifSettings.inAppEnabled) {
          setNotifToast(newNotif);
          setTimeout(() => setNotifToast(t => t?.id === newNotif.id ? null : t), 6000);
          if (notifSettings.soundEnabled) {
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const o = ctx.createOscillator();
              const g = ctx.createGain();
              o.connect(g); g.connect(ctx.destination);
              o.frequency.value = 880; o.type = 'sine';
              g.gain.setValueAtTime(0.001, ctx.currentTime);
              g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
              g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
              o.start(); o.stop(ctx.currentTime + 0.16);
            } catch {}
          }
        }
      }

      // ─── Email-триггер ───
      // Проверяем настройки ПОЛУЧАТЕЛЯ (а не отправителя), но настройки лежат в его личном KV.
      // Без серверной авторизации мы не можем спросить чужие настройки безопасно.
      // Поэтому правило: на сервере есть лимит, на фронте мы вызываем notify «оптимистично»,
      // а сервер сам решает (по типу события) — мгновенно или в дайджест.
      // Если получатель отключил email-уведомления, он может сделать это на стороне сервера
      // в будущих версиях (пока — единая политика).
      try {
        const target = users.find(u => u.id === targetUserId);
        if (target?.email && newNotif.type !== 'system') {
          const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
          const subject = newNotif.title || 'Уведомление Mafia Academy';
          const summary = newNotif.text || '';
          // Ссылка на акадею — фронт сам разберётся куда вести
          const link = window.location.origin + '/';
          // Не ждём ответа — отправка email не должна блокировать UI
          fetch(`${apiUrl}/email/notify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: newNotif.type,
              recipient_email: target.email,
              recipient_name: target.name || target.fullName || '',
              subject,
              summary,
              link,
            }),
          }).catch(() => {});
        }
      } catch (e) { /* email — не критично */ }
    } catch (e) {
      console.warn('[notif] persist failed:', e.message);
    }
  }, [currentUserId, notifSettings, users]);

  const createTask = useCallback(async (task) => {
    const newTask = {
      id: 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      title: task.title || 'Без названия',
      description: task.description || '',
      assigneeId: task.assigneeId || '',
      createdBy: task.createdBy || '',
      priority: task.priority || 'medium',     // low | medium | high | critical
      status: 'active',                         // active | review | done | cancelled
      deadline: task.deadline || null,          // timestamp
      todos: Array.isArray(task.todos) ? task.todos : [],  // [{id,text,done}]
      attachments: Array.isArray(task.attachments) ? task.attachments : [],  // [{id,name,type,data}]
      comments: [],                             // [{id,authorId,text,attachments,at}]
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const next = [newTask, ...tasks];
    setTasks(next);
    await safeSet(STORAGE_KEYS.TASKS, next);

    // ─── Уведомление исполнителю о новой задаче ───
    if (newTask.assigneeId && newTask.assigneeId !== newTask.createdBy) {
      await persistNotification(newTask.assigneeId, {
        type: 'task_assigned',
        title: 'Новая задача',
        text: newTask.title,
        link: { screen: 'adminMyTask', ctx: { taskId: newTask.id } },
        from: newTask.createdBy,
      });
    }
    return newTask;
  }, [tasks, persistNotification]);

  const updateTask = useCallback(async (taskId, patchObj) => {
    const prevTask = tasks.find(t => t.id === taskId);
    const next = tasks.map(t => t.id === taskId ? { ...t, ...patchObj, updatedAt: Date.now() } : t);
    setTasks(next);
    await safeSet(STORAGE_KEYS.TASKS, next);

    // ─── Уведомление при смене статуса ───
    if (prevTask && patchObj.status && prevTask.status !== patchObj.status) {
      const updatedTask = next.find(t => t.id === taskId);
      const statusLabels = { active: 'возвращена в работу', review: 'отправлена на проверку', done: 'завершена', cancelled: 'отменена' };
      const statusText = statusLabels[patchObj.status] || patchObj.status;

      // Уведомляем ОБЕИХ участников (исключая того, кто инициировал смену — мы его не знаем точно,
      // но в большинстве случаев это автор измененений и он же отправитель)
      const recipients = [prevTask.assigneeId, prevTask.createdBy].filter(Boolean);
      for (const rid of recipients) {
        if (rid === currentUserId) continue; // не уведомляем того, кто кликнул
        await persistNotification(rid, {
          type: 'task_status',
          title: 'Статус задачи: ' + statusText,
          text: updatedTask.title,
          link: { screen: rid === prevTask.assigneeId ? 'adminMyTask' : 'directorTask', ctx: { taskId } },
          from: currentUserId,
        });
      }
    }
  }, [tasks, persistNotification, currentUserId]);

  const deleteTask = useCallback(async (taskId) => {
    const next = tasks.filter(t => t.id !== taskId);
    setTasks(next);
    await safeSet(STORAGE_KEYS.TASKS, next);
  }, [tasks]);

  const addTaskComment = useCallback(async (taskId, comment) => {
    const task = tasks.find(t => t.id === taskId);
    const next = tasks.map(t => {
      if (t.id !== taskId) return t;
      const newComment = {
        id: 'c_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
        authorId: comment.authorId || '',
        text: comment.text || '',
        attachments: Array.isArray(comment.attachments) ? comment.attachments : [],
        at: Date.now(),
      };
      return { ...t, comments: [...(t.comments || []), newComment], updatedAt: Date.now() };
    });
    setTasks(next);
    await safeSet(STORAGE_KEYS.TASKS, next);

    // ─── Уведомление всем участникам кроме автора комментария ───
    if (task) {
      const authorId = comment.authorId || '';
      const recipients = [task.assigneeId, task.createdBy]
        .filter(Boolean)
        .filter(id => id !== authorId);
      for (const rid of recipients) {
        await persistNotification(rid, {
          type: 'task_comment',
          title: 'Новый комментарий',
          text: (comment.text || '').slice(0, 80) + ((comment.text || '').length > 80 ? '...' : ''),
          link: { screen: rid === task.assigneeId ? 'adminMyTask' : 'directorTask', ctx: { taskId } },
          from: authorId,
        });
      }
    }
  }, [tasks, persistNotification]);

  // ─── Резервное копирование контента ───
  // Экспортирует ВЕСЬ редактируемый контент в один JSON-объект.
  // НЕ включает: пользователей, прогресс стажёров, сессии, чек-листы (это пользовательские данные, не контент).
  const exportContent = useCallback(() => {
    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      content: {
        courses, techCards, checklistTemplate, regulations,
        contacts, exitQuestions, settings, kitchenCategories,
      },
    };
  }, [courses, techCards, checklistTemplate, regulations, contacts, exitQuestions, settings, kitchenCategories]);

  // Импорт — заменяет контент данными из бэкапа. Возвращает { ok, error?, applied? }
    const importContent = useCallback(async (backup) => {
    if (!backup || typeof backup !== 'object') return { ok: false, error: 'Файл повреждён или не является бэкапом' };
    if (!backup.content || typeof backup.content !== 'object') return { ok: false, error: 'В файле нет блока content' };
    if (backup.version !== 1) return { ok: false, error: 'Неподдерживаемая версия бэкапа: ' + backup.version };

    const c = backup.content;
    const applied = [];
    try {
      // console.log('[import] courses:', Array.isArray(c.courses), c.courses?.length);
      if (Array.isArray(c.courses))               { setCourses(c.courses);                     await safeSet(STORAGE_KEYS.CONTENT_COURSES, c.courses);                     applied.push('Курсы'); }
      // console.log('[import] techCards:', typeof c.techCards, c.techCards ? Object.keys(c.techCards).length : 0);
      if (c.techCards && typeof c.techCards === 'object') { const cleanTC = sanitizeTechCards(c.techCards); setTechCards(cleanTC);          await safeSet(STORAGE_KEYS.CONTENT_TECHCARDS, cleanTC);                 applied.push('Меню кухни'); }
      // console.log('[import] kitchenCategories:', Array.isArray(c.kitchenCategories), c.kitchenCategories?.length);
      if (Array.isArray(c.kitchenCategories))     { setKitchenCategories(c.kitchenCategories); await safeSet(STORAGE_KEYS.KITCHEN_CATEGORIES, c.kitchenCategories);         applied.push('Категории');  }
      if (Array.isArray(c.checklistTemplate))     { setChecklistTemplate(c.checklistTemplate); await safeSet(STORAGE_KEYS.CONTENT_CHECKLIST, c.checklistTemplate);         applied.push('Чек-лист'); }
      if (Array.isArray(c.regulations))           { setRegulations(c.regulations);              await safeSet(STORAGE_KEYS.CONTENT_REGULATIONS, c.regulations);             applied.push('Регламенты'); }
      if (c.contacts)                              { setContacts(c.contacts);                    await safeSet(STORAGE_KEYS.CONTENT_CONTACTS, c.contacts);                    applied.push('Контакты'); }
      if (c.exitQuestions)                         { setExitQuestions(c.exitQuestions);          await safeSet(STORAGE_KEYS.CONTENT_EXIT_QUESTIONS, c.exitQuestions);         applied.push('Exit-интервью'); }
      if (c.settings)                              { setSettings(c.settings);                    await safeSet(STORAGE_KEYS.CONTENT_SETTINGS, c.settings);                    applied.push('Настройки'); }
      // console.log('[import] done, applied:', applied);
      return { ok: true, applied };
    } catch (e) {
      console.error('[import] error:', e);
      return { ok: false, error: e.message };
    }
  }, []);

	const contentValue = {
		courses, techCards, checklistTemplate, regulations, settings,
		contacts, exitQuestions, exitResponses,
		tasks, kitchenCategories,
		modules, systemConfig, auditLog, roles,
		updateCourses, updateCourse, updateTechCards, updateChecklistTemplate,
		updateRegulations, updateRegulation, updateSettings, updateKitchenCategories,
		updateContacts, updateExitQuestions, addExitResponse, deleteExitResponse,
		createTask, updateTask, deleteTask, addTaskComment,
		updateModules, updateSystemConfig, appendAuditLog, updateRoles,
		exportContent, importContent,
		hasPerm: (user, permId) => hasPermission(user, permId, roles),
		getPerms: (user) => getEffectivePermissions(user, roles),
		getCourseFor: (u) => getCourseForUser(u, courses),
		getCourseById: (id) => courses.find(c => c.id === id) || null,
		getRegulationById: (id) => regulations.find(r => r.id === id) || null,
		getRegulationsFor: (u) => {
		  if (!u) return [];
		  if (u.isAdmin) return regulations;
		  const userBrands = (u.brands || [u.brand]).filter(Boolean);
		  return regulations.filter(r => r.brand === 'shared' || userBrands.includes(r.brand));
		},
	  };

    // Загрузка прогресса при логине
  useEffect(() => {
    if (!currentUserId) return;
    const isAdmin = users.find(u => u.id === currentUserId)?.isAdmin;
    if (isAdmin) return; // админам прогресс не нужен
    (async () => {
      // Загружаем прогресс
      try {
        const pkey = STORAGE_KEYS.PROGRESS(currentUserId);
        const presp = await fetch(`/api/kv/${encodeURIComponent(pkey)}?shared=false&owner_id=${currentUserId}`, { credentials: 'include' });
        if (presp.ok) {
          const pjson = await presp.json();
          setProgress(pjson?.value || { lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        } else {
          setProgress({ lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        }
      } catch {
        setProgress({ lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
      }
      // Загружаем чеклист
      try {
        const ckey = STORAGE_KEYS.CHECKLIST(currentUserId);
        const cresp = await fetch(`/api/kv/${encodeURIComponent(ckey)}?shared=false&owner_id=${currentUserId}`, { credentials: 'include' });
        if (cresp.ok) {
          const cjson = await cresp.json();
          setPracticalChecklist(cjson?.value || {});
        } else {
          setPracticalChecklist({});
        }
      } catch {
        setPracticalChecklist({});
      }
      // ─── Уведомления ───
      try {
        const nkey = STORAGE_KEYS.NOTIFICATIONS(currentUserId);
        const nresp = await fetch(`/api/kv/${encodeURIComponent(nkey)}?shared=false&owner_id=${currentUserId}`, { credentials: 'include' });
        if (nresp.ok) {
          const njson = await nresp.json();
          if (Array.isArray(njson?.value)) setNotifications(njson.value);
        }
      } catch {}
      try {
        const nskey = STORAGE_KEYS.NOTIF_SETTINGS(currentUserId);
        const nsresp = await fetch(`/api/kv/${encodeURIComponent(nskey)}?shared=false&owner_id=${currentUserId}`, { credentials: 'include' });
        if (nsresp.ok) {
          const nsjson = await nsresp.json();
          if (nsjson?.value) setNotifSettings(s => ({ ...s, ...nsjson.value }));
        }
      } catch {}
    })();
  }, [currentUserId]);

  // ─── Функции работы с уведомлениями ───
  const addNotification = useCallback(async (targetUserId, notif) => {
    if (!targetUserId) return;
    try {
      const key = STORAGE_KEYS.NOTIFICATIONS(targetUserId);
      // Загружаем существующие
      const existingResp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${targetUserId}`, { credentials: 'include' });
      let existing = [];
      if (existingResp.ok) {
        const json = await existingResp.json();
        if (Array.isArray(json?.value)) existing = json.value;
      }
      
      const newNotif = {
        id: 'n_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
        type: notif.type || 'system',
        title: notif.title || '',
        text: notif.text || '',
        link: notif.link || null,
        from: notif.from || null,
        read: false,
        at: Date.now(),
      };
      const updated = [newNotif, ...existing].slice(0, 100);
      
      // Сохраняем
      await fetch(`/api/kv/${encodeURIComponent(key)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: updated, shared: false, owner_id: targetUserId }),
      });
      
      if (targetUserId === currentUserId) {
        setNotifications(updated);
        showNotifToast(newNotif);
      }
    } catch (e) {
      console.warn('[notif] failed to save:', e.message);
    }
  }, [currentUserId, showNotifToast]);

  const markNotificationRead = useCallback(async (notifId) => {
    if (!currentUserId) return;
    const target = notifications.find(n => n.id === notifId);
    if (!target || target.read) return;       // уже прочитано — лишняя запись не нужна
    const updated = notifications.map(n => n.id === notifId ? { ...n, read: true } : n);
    setNotifications(updated);
    await safeSet(STORAGE_KEYS.NOTIFICATIONS(currentUserId), updated, false);
  }, [currentUserId, notifications]);

  const markNotificationsReadWhere = useCallback(async (pred) => {
    if (!currentUserId) return;
    if (!notifications.some(n => !n.read && pred(n))) return;
    const updated = notifications.map(n => (!n.read && pred(n)) ? { ...n, read: true } : n);
    setNotifications(updated);
    await safeSet(STORAGE_KEYS.NOTIFICATIONS(currentUserId), updated, false);
  }, [currentUserId, notifications]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!currentUserId) return;
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    await safeSet(STORAGE_KEYS.NOTIFICATIONS(currentUserId), updated, false);
  }, [currentUserId, notifications]);

  const clearNotifications = useCallback(async () => {
    if (!currentUserId) return;
    setNotifications([]);
    await safeSet(STORAGE_KEYS.NOTIFICATIONS(currentUserId), [], false);
  }, [currentUserId]);

  useEffect(() => { notifSettingsRef.current = notifSettings; }, [notifSettings]);

  const updateNotifSettings = useCallback(async (patch) => {
    if (!currentUserId) return;
    const updated = { ...notifSettings, ...patch };
    setNotifSettings(updated);
    await safeSet(STORAGE_KEYS.NOTIF_SETTINGS(currentUserId), updated, false);
  }, [currentUserId, notifSettings]);
  
  const notifValue = {
    notifications, setNotifications, notifSettings,
    unreadCount: notifications.filter(n => !n.read).length,
    addNotification, markNotificationRead, markAllNotificationsRead, markNotificationsReadWhere, clearNotifications, updateNotifSettings,
  };
  
  const showToast = useCallback((msg, type = 'success') => {
    if (!msg) return;
    setToast({ msg, type, id: Date.now() });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), type === 'error' ? 4500 : 3000);
  }, []);

  const updateUsers = useCallback(async (newUsers) => {
	  const oldUsers = users;
	  setUsers(newUsers);
	  await safeSet(STORAGE_KEYS.USERS, newUsers);
	  
	  const added = newUsers.filter(nu => !oldUsers.find(ou => ou.id === nu.id));
	  const removed = oldUsers.filter(ou => !newUsers.find(nu => nu.id === ou.id));
	  
	  if (added.length > 0) {
		appendAuditLog({ action: 'users_added', actorName: getActorName(), details: `Добавлены: ${added.map(u => u.name).join(', ')}` });
	  }
	  if (removed.length > 0) {
		appendAuditLog({ action: 'users_removed', actorName: getActorName(), details: `Удалены: ${removed.map(u => u.name).join(', ')}` });
	  }
	}, [users, appendAuditLog, getActorName()]);

  const updateProgress = useCallback(async (newProgress) => {
    setProgress(newProgress);
    const key = STORAGE_KEYS.PROGRESS(currentUserId);
    try {
      await fetch(`/api/kv/${encodeURIComponent(key)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: newProgress, shared: false, owner_id: currentUserId }),
      });
    } catch (e) {
      console.error('[updateProgress]', e);
    }
  }, [currentUserId]);

  const updateChecklist = useCallback(async (newChecklist) => {
    setPracticalChecklist(newChecklist);
    const key = STORAGE_KEYS.CHECKLIST(currentUserId);
    try {
      await fetch(`/api/kv/${encodeURIComponent(key)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: newChecklist, shared: false, owner_id: currentUserId }),
      });
    } catch (e) {
      console.error('[updateChecklist]', e);
    }
  }, [currentUserId]);

  // ─── Регистрация нового пользователя ───
  // Поддерживает обычную регистрацию + регистрацию через OAuth (Яндекс/ВК).
  // Если переданы oauthProvider/oauthSub — пароль может быть пустым (вход только через OAuth).
  // Дополнительные поля: fullName, position, avatarUrl — заполняются при OAuth-регистрации.
  const handleRegister = useCallback(async ({
    email, password, name, fullName, brand, gender, phone, birthDate, position,
    avatarUrl, oauthProvider, oauthSub, oauthSignupToken,
  }) => {
    email = (email || '').trim().toLowerCase();
    name = (name || fullName || '').trim();
    gender = gender || '';
    phone = (phone || '').trim();
    birthDate = (birthDate || '').trim();
    position = (position || '').trim();
    const isOAuth = !!(oauthProvider && oauthSub && oauthSignupToken);
    if (!isValidEmail(email)) return { ok: false, error: 'Некорректный email' };
    const fioErr = checkRuFullName(name);
    if (fioErr) return { ok: false, error: fioErr };
    name = normalizeRuFullName(name);
    fullName = name;
    if (!isOAuth && password && password.length < 6) return { ok: false, error: 'Пароль минимум 6 символов' };
    if (birthDate) {
      const d = new Date(birthDate);
      if (isNaN(d.getTime())) return { ok: false, error: 'Некорректная дата рождения' };
      const age = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 14 || age > 100) return { ok: false, error: 'Проверьте дату рождения' };
    }

    // ═══════════════════════════════════════════════════════════════
    // ОБЫЧНАЯ РЕГИСТРАЦИЯ ЧЕРЕЗ СЕРВЕР (email + password)
    // ═══════════════════════════════════════════════════════════════
    // Сервер хеширует пароль через bcrypt, создаёт юзера, выдаёт JWT в cookie.
    // isAdmin/isDirector в body игнорируются сервером — все новые юзеры стажёры.
    if (!isOAuth) {
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        const r = await fetch(`${apiUrl}/auth/register`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, password, name, fullName, brand, gender, phone, birthDate,
            role: position,
          }),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          return { ok: false, error: body.message || 'Не удалось зарегистрироваться' };
        }
        const newUser = body.user;

        // Подгружаем свежий список users
        try {
          const data = await safeGet(STORAGE_KEYS.USERS);
          if (Array.isArray(data)) setUsers(data);
        } catch {}

        // Контент грузится только при старте с живой сессией — после регистрации
        // её ещё не было, поэтому подтягиваем курсы/регламенты/настройки здесь.
        await loadSharedContent();

        // Welcome email
        try {
          const apiKey = import.meta?.env?.VITE_API_KEY || '';
          const headers = { 'Content-Type': 'application/json' };
          if (apiKey) headers['X-API-Key'] = apiKey;
          await fetch(`${apiUrl}/auth/send-welcome`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ userId: newUser.id, email: newUser.email, name: newUser.name, isAdmin: false }),
          });
        } catch {}

        setProgress({ lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        setPracticalChecklist({});
        setCurrentUserId(newUser.id);
        setScreen('dashboard');
        return { ok: true, user: newUser };
      } catch (e) {
        console.error('[register]', e);
        return { ok: false, error: 'Ошибка сети. Попробуйте ещё раз.' };
      }
    }

    // OAuth без серверной JWT-сессии запрещён: иначе устройство снова становится источником истины.
    if (!isOAuth) return { ok: false, error: 'Сессия входа истекла. Войдите через соцсеть ещё раз.' };
        // ═══════════════════════════════════════════════════════════════
    // OAUTH-РЕГИСТРАЦИЯ ЧЕРЕЗ СЕРВЕР (Яндекс/ВК)
    // ═══════════════════════════════════════════════════════════════
    if (isOAuth) {
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        // Регистрируем через сервер (без пароля)
        const r = await fetch(`${apiUrl}/auth/register`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, password: '', name, fullName, brand, gender, phone, birthDate,
            role: position, oauthSignupToken,
          }),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          return { ok: false, error: body.message || 'Не удалось зарегистрироваться' };
        }
        const newUser = body.user;
        // Привязка к Яндексу/ВК уже сделана сервером по одноразовому токену

        try {
          const data = await safeGet(STORAGE_KEYS.USERS);
          setUsers(Array.isArray(data) && data.length ? data : [newUser]);
        } catch { setUsers(prev => [...prev.filter(u => u.id !== newUser.id), newUser]); }
        await loadSharedContent();
        setProgress({ lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        setPracticalChecklist({});
        setCurrentUserId(newUser.id);
        setScreen('dashboard');
        return { ok: true, user: newUser };
      } catch (e) {
        console.error('[oauth-register]', e);
        return { ok: false, error: 'Ошибка сети' };
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // OAUTH-РЕГИСТРАЦИЯ (выключена до перевода на серверный JWT)
    // ═══════════════════════════════════════════════════════════════
    if (users.find(u => u.email?.toLowerCase() === email)) {
      return { ok: false, error: 'Этот email уже зарегистрирован' };
    }
    const newUser = {
      id: 'u_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      email,
      name,
      fullName: fullName || name,
      gender, phone, birthDate,
      passwordHash: '',
      avatar: avatarUrl || '',
      brands: brand ? [brand] : [],
      role: position || '',
      isAdmin: false,
      isDirector: false,
      joinedAt: Date.now(),
      lastLoginAt: Date.now(),
      level: 1,
      streakDays: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      emailVerified: true,
      oauthProviders: [{ provider: oauthProvider, sub: oauthSub, linkedAt: Date.now() }],
    };
    let baseUsers = users;
    try {
      const fromDb = await safeGet(STORAGE_KEYS.USERS);
      if (Array.isArray(fromDb) && fromDb.length >= users.length) baseUsers = fromDb;
    } catch {}
    if (baseUsers.find(u => (u.email || '').toLowerCase() === email)) {
      return { ok: false, error: 'Этот email уже зарегистрирован (параллельная регистрация)' };
    }
    const newUsers = [...baseUsers, newUser];
    await updateUsers(newUsers);

    setProgress({ lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
    setPracticalChecklist({});
    setCurrentUserId(newUser.id);
    setScreen('dashboard');
    return { ok: true, user: newUser, emailSent: false };
  }, [users, updateUsers, loadSharedContent]);

  // ─── Вход по email + паролю ───
  const handleLogin = useCallback(async ({ email, password, oauthBypass = false }) => {
    email = (email || '').trim().toLowerCase();

    // OAuth-флоу
    if (oauthBypass && password && password.startsWith('__oauth__:')) {
      const [, provider, sub] = password.split(':');
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        const r = await fetch(`${apiUrl}/auth/me`, {
          method: 'GET',
          credentials: 'include',
        });
        const body = await r.json().catch(() => ({}));
        if (r.ok && body.user) {
          setUsers(prev => {
            const exists = prev.find(u => u.id === body.user.id);
            return exists ? prev.map(u => u.id === body.user.id ? { ...u, ...body.user } : u) : [...prev, body.user];
          });
          setCurrentUserId(body.user.id);
		  
		  safeGet(STORAGE_KEYS.USERS).then(data => {
		    if (Array.isArray(data)) setUsers(data);
	      }).catch(() => {});
		  
          await loadSharedContent();
          setScreen(body.user.isAdmin ? 'admin' : 'dashboard');
          return { ok: true };
        }
      } catch {}
      return { ok: false, error: 'OAuth-вход не удался' };
    }

    // Обычный логин
    try {
      const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      const r = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        return { ok: false, error: body.message || 'Неверный email или пароль' };
      }
      const user = body.user;
      if (!user) {
        return { ok: false, error: 'Ошибка авторизации' };
      }

      // Сразу добавляем юзера в users
      setUsers(prev => {
        const exists = prev.find(u => u.id === user.id);
        return exists ? prev.map(u => u.id === user.id ? { ...u, ...user } : u) : [...prev, user];
      });

      // Фоново подгружаем полный список из БД
      safeGet(STORAGE_KEYS.USERS).then(data => {
        if (Array.isArray(data)) setUsers(data);
      }).catch(() => {});

      // Контент (курсы, регламенты, настройки) грузится в стартовом эффекте только
      // при уже существующей сессии — после свежего логина её не было, догружаем сейчас.
      await loadSharedContent();

      // Предзагружаем прогресс
      if (!user.isAdmin) {
        safeGet(STORAGE_KEYS.PROGRESS(user.id)).then(p => {
          setProgress(p || { lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        }).catch(() => {});
        safeGet(STORAGE_KEYS.CHECKLIST(user.id)).then(c => {
          setPracticalChecklist(c || {});
        }).catch(() => {});
      }

      setCurrentUserId(user.id);
      setScreen(user.isAdmin ? 'admin' : 'dashboard');
      return { ok: true };
    } catch (e) {
      console.error('[login]', e);
      return { ok: false, error: 'Ошибка сети. Попробуйте ещё раз.' };
    }
  }, [users, safeGet, loadSharedContent]);

  // ─── OAuth-логин по ID юзера (без проверки пароля) ───
  // Используется в OAuthFinalizeScreen когда мы уже идентифицировали юзера через провайдера.
  // ВАЖНО: проверяет что у юзера есть запись oauthProviders для этого provider:sub.
  // Это защита от подмены — нельзя просто передать чужой userId и зайти.
  const handleOAuthLoginById = useCallback(async ({ userId, provider, sub }) => {
    try {
      const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      // Проверяем сессию через сервер
      const r = await fetch(`${apiUrl}/auth/me`, {
        method: 'GET',
        credentials: 'include',
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.user && body.user.id === userId) {
        try {
          const data = await safeGet(STORAGE_KEYS.USERS);
          if (Array.isArray(data)) setUsers(data);
        } catch {}
        await loadSharedContent();
        setCurrentUserId(body.user.id);
        setScreen(body.user.isAdmin ? 'admin' : 'dashboard');
        return { ok: true };
      }
      // Если сессии нет — пробуем обычный логин через /auth/login с OAuth-данными
      return { ok: false, error: 'OAuth-сессия не найдена. Войдите по email и паролю.' };
    } catch (e) {
      return { ok: false, error: 'Ошибка сети' };
    }
  }, [loadSharedContent]);

  const handleLogout = async () => {
    // Сообщаем серверу — он почистит JWT cookies
    try {
      const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      await fetch(`${apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.warn('[logout] server unreachable, clearing locally:', e.message);
    }
    setCurrentUserId(null);
    setProgress(null);
    setPracticalChecklist(null);
    setNotifications([]);
    setNotifToast(null);
    setScreen('login');
    setCtx({});
  };

  // Запрос на восстановление пароля — обращается к backend (если есть)
  const handleRequestPasswordReset = useCallback(async ({ email }) => {
    email = (email || '').trim().toLowerCase();
    if (!isValidEmail(email)) return { ok: false, error: 'Некорректный email' };
    try {
      const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      const apiKey = import.meta?.env?.VITE_API_KEY || '';
      const headers = { 'Content-Type': 'application/json' };
      if (apiKey) headers['X-API-Key'] = apiKey;
      const resp = await fetch(`${apiUrl}/auth/request-password-reset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { ok: false, error: err.error || 'Сервис восстановления недоступен' };
      }
      const data = await resp.json();
      return { ok: true, sent: !!data.sent, skipped: !!data.skipped };
    } catch (e) {
      return { ok: false, error: 'Backend недоступен. Восстановление пароля работает только в режиме MySQL с подключенным SMTP.' };
    }
  }, []);


	const navigate = (s, c = {}) => {
	  setCtx(c);
	  setScreen(s);
	  window.scrollTo(0, 0);
	  
	  // Строим URL
	  const ctxPart = Object.keys(c).length > 0 ? '/' + btoa(JSON.stringify(c)) : '';
	  const url = `/${s}${ctxPart}`;
	  
	  // Сохраняем в историю
	  window.history.pushState({ screen: s, ctx: c }, '', url);
	};

  // Восстановление экрана из URL при загрузке
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const screenParam = params.get('screen');
    if (screenParam && !loading) {
      setScreen(screenParam);
    }
  }, [loading]);

	useEffect(() => {
	  const handlePopState = (e) => {
		if (e.state?.screen) {
		  setScreen(e.state.screen);
		  if (e.state.ctx) setCtx(e.state.ctx);
		} else {
		  const params = new URLSearchParams(window.location.search);
		  const screenParam = params.get('screen');
		  const path = window.location.pathname;
		  
		  if (screenParam) {
			setScreen(screenParam);
		  } else if (path !== '/' && path !== '/favicon.svg') {
			const parts = path.split('/').filter(Boolean);
			if (parts.length > 0) setScreen(parts[0]);
		  } else {
			// Ищем пользователя по currentUserId
			const u = users.find(x => x.id === currentUserId);
			setScreen(u?.isAdmin ? 'admin' : 'dashboard');
		  }
		}
	  };
	  window.addEventListener('popstate', handlePopState);
	  return () => window.removeEventListener('popstate', handlePopState);
	}, [currentUserId, users]);

  // ─── Reconciliation: когда users подгрузились, сверяем currentUserId с серверным списком. ───
  useEffect(() => {
    if (loading || !contentReady) return;
    if (!currentUserId) return;
    if (users.length === 0) return;  // ещё не загрузились — ждём
    const u = users.find(x => x.id === currentUserId);
    if (!u) {
      console.warn('[session/reconcile] currentUserId', currentUserId, 'не найден в подгруженных users');
      setCurrentUserId(null);
      setScreen('login');
    } else {
      // Юзер найден. Если он админ, а screen остался 'dashboard' — переключим на 'admin'.
      // (Делаем это только если screen ещё 'dashboard' — чтобы не сбивать ручную навигацию)
      if (u.isAdmin && screen === 'dashboard') {
        setScreen('admin');
      }
    }
  }, [loading, currentUserId, users.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Завершение урока
  const completeLesson = useCallback(async (lessonId) => {
    if (progress.lessons[lessonId]) return;
    const newProgress = {
      ...progress,
      lessons: { ...progress.lessons, [lessonId]: { completed: true, completedAt: Date.now() } },
      xp: progress.xp + 50
    };
    await updateProgress(newProgress);
    showToast('+50 XP — урок пройден');
  }, [progress, updateProgress, showToast]);

  const completeTest = useCallback(async (moduleId, score, passing, perfect, course) => {
    const passed = score >= passing;
    const moduleData = course.modules.find(m => m.id === moduleId);
    const prevTest = progress.tests?.[moduleId] || {};
    const prevAttempts = prevTest.attempts || 0;
    // XP начисляется за первое прохождение и за первый идеальный результат;
    // утешительные +50 — только за первую попытку. Пересдачи не «фармят» опыт.
    let xpGain = 0;
    if (passed && !prevTest.passed) xpGain += moduleData?.xp || 0;
    if (perfect && !prevTest.perfect) xpGain += 200;
    if (!passed && prevAttempts === 0) xpGain += 50;
    const newBadges = [...(progress.badges || [])];
    if (passed && !progress.badges?.includes('first_blood')) newBadges.push('first_blood');
    if (perfect && !progress.badges?.includes('perfectionist')) newBadges.push('perfectionist');
    if (moduleId === 'm3' && passed && !progress.badges?.includes('saucer')) newBadges.push('saucer');

    const allModulesPassed = course.modules.every(m =>
      m.id === moduleId ? (passed || progress.tests?.[m.id]?.passed) : progress.tests?.[m.id]?.passed
    );
    let finalApproved = progress.finalApproved;
    if (allModulesPassed && perfect && !finalApproved) {
      finalApproved = true;
      if (!newBadges.includes('godfather')) newBadges.push('godfather');
    }

    const newProgress = {
      ...progress,
      // Лучший результат не затирается неудачной пересдачей
      tests: { ...progress.tests, [moduleId]: {
        score: Math.max(score, prevTest.score || 0),
        lastScore: score,
        passed: passed || !!prevTest.passed,
        attempts: prevAttempts + 1,
        lastAt: Date.now(),
        perfect: perfect || !!prevTest.perfect,
      } },
      xp: progress.xp + xpGain,
      badges: newBadges,
      finalApproved
    };
    await updateProgress(newProgress);
  }, [progress, updateProgress]); // БЕЗ course

  // Парсим URL — если есть ?verify=... или ?reset=..., показываем спец-экран
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const verify = params.get('verify');
      const reset = params.get('reset');
      const oauth = params.get('oauth');
      const oauthToken = params.get('token');
      const oauthReason = params.get('reason');
      if (verify) {
        setAuthAction({ type: 'verify-email', token: verify });
      } else if (reset) {
        setAuthAction({ type: 'reset-password', token: reset });
      } else if (oauth === 'success' && oauthToken) {
        setAuthAction({ type: 'oauth-finalize', token: oauthToken });
      } else if (oauth === 'error') {
        setAuthAction({ type: 'oauth-error', reason: oauthReason || 'unknown' });
      }
    } catch (e) { /* noop */ }
  }, []);

  // Применяю тему: накатываю CSS-переменные на :root + добавляю класс на body.
  // ВАЖНО: этот useEffect должен быть ДО любых early-return, иначе нарушаются правила хуков.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', settings.accentColor || '#FFB800');
    root.style.setProperty('--primary', settings.primaryColor || '#E83A2C');
    // Снимаем все theme-* классы и добавляем актуальный
    document.body.classList.remove('theme-classic', 'theme-newyear');
    document.body.classList.add(`theme-${settings.theme || 'classic'}`);
  }, [settings.accentColor, settings.primaryColor, settings.theme]);

  // Ctrl/Cmd+K или «/» — меню и поиск по функциям
  useEffect(() => {
    if (!currentUserId) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K' || e.key === 'л' || e.key === 'Л')) {
        e.preventDefault();
        setMenuOpen(o => !o);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        setMenuOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentUserId]);
  useEffect(() => { setMenuOpen(false); }, [screen, currentUserId]);
  const navUiValue = useMemo(() => ({ openMenu: () => setMenuOpen(true), menuOpen }), [menuOpen]);

  if (loading || !contentReady) {
    return (
      <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center">
        <style>{FONTS_CSS}</style>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E83A2C] to-[#8B0F08] mb-4 glow-red float-anim glow-pulse">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
          <div className="font-mono text-xs text-gray-500 tracking-widest">ЗАГРУЗКА БАЗЫ...</div>
        </div>
      </div>
    );
  }
  
  const currentUser = users.find(u => u.id === currentUserId);
  const profileMissing = missingProfileFields(currentUser);
  const appReady = !!(currentUser && profileMissing.length === 0 && (!systemConfig?.maintenanceMode || isDevAccount(currentUser)) && (currentUser.isAdmin || progress));
  const homeScreen = currentUser?.isAdmin ? 'admin' : 'dashboard';
  const navItems = appReady ? buildNavItems({
    user: currentUser,
    hasPerm: contentValue.hasPerm,
    modules,
    course: currentUser.isAdmin ? null : getCourseForUser(currentUser, courses),
    progress,
    techCards,
    regulations: contentValue.getRegulationsFor(currentUser),
  }) : [];
  // Экраны стажёра с нижней панелью (там шляпа уже в центре панели)
  const TRAINEE_TABBAR_SCREENS = ['dashboard', 'techcards', 'regulations', 'contacts', 'stats', 'messages', 'profile'];
  const showTraineeHat = appReady && !currentUser.isAdmin && ['module', 'lesson', 'techcard', 'exitInterview'].includes(screen);
  const showAdminDock = appReady && currentUser.isAdmin && screen !== 'adminMessages';
  return (
    <ContentContext.Provider value={contentValue}>
    <NotificationContext.Provider value={notifValue}>
    <NavUiContext.Provider value={navUiValue}>
    <ConfirmProvider>
    <div className={`min-h-screen bg-[#0A0B0D] text-[#F2F2F4] font-body grain ${showAdminDock ? 'pad-dock' : ''} ${showTraineeHat ? 'has-hat-float' : ''}`}>
      <style>{FONTS_CSS}</style>
      {settings.theme === 'newyear' && <Snowfall />}

      {toast && toast.msg && (
        <div className="app-toast" role="status" aria-live="polite" key={toast.id}>
          <button
            type="button"
            onClick={() => setToast(null)}
            className={`app-toast-inner ${toast.type === 'error' ? 'is-error' : 'is-ok'}`}
          >
            {toast.type === 'error' ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
            <span className="min-w-0">{toast.msg}</span>
          </button>
        </div>
      )}

      {/* Всплывашка-уведомление (другой стиль чем обычный toast) */}
      {notifToast && currentUser && (
        <NotificationToast
          notif={notifToast}
          onDismiss={() => setNotifToast(null)}
          onClick={() => {
            const target = resolveNotifLink(notifToast, !!currentUser.isAdmin);
            markNotificationRead(notifToast.id);
            setNotifToast(null);
            if (target) navigate(target.screen, target.ctx);
          }}
        />
      )}

      {authAction && (
        <AuthActionScreen
          action={authAction}
          onDone={() => { setAuthAction(null); try { window.history.replaceState({}, '', window.location.pathname); } catch {} }}
          handleLogin={handleLogin}
          handleRegister={handleRegister}
          handleOAuthLoginById={handleOAuthLoginById}
          users={users}
        />
      )}

      {loading && !authAction && (
        <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#E83A2C] mx-auto mb-4" />
            <div className="font-mono text-xs text-gray-500 tracking-widest">ЗАГРУЗКА СЕССИИ...</div>
          </div>
        </div>
      )}

      {!loading && !authAction && !currentUserId && (
        <LoginScreen
          onLogin={handleLogin}
          onRegister={handleRegister}
          onRequestReset={handleRequestPasswordReset}
          usersCount={publicUsersCount ?? users.length}
        />
      )}

      {/* Состояние "сессия восстановлена, но users ещё не подгрузились" */}
      {!loading && !authAction && currentUserId && !currentUser && (
        <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#E83A2C] mx-auto mb-4" />
            <div className="font-mono text-xs text-gray-500 tracking-widest">СИНХРОНИЗАЦИЯ...</div>
          </div>
        </div>
      )}
	  
      {/* ─── MAINTENANCE MODE ─── */}
      {/* Если разработчик включил режим тех. работ — показываем заглушку всем кроме него. */}
      {!loading && !authAction && currentUser && !isDevAccount(currentUser) && systemConfig?.maintenanceMode && (
        <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center p-5">
          <div className="max-w-md w-full bg-[#15171B] border border-[#FFB800]/40 rounded-2xl p-8 text-center">
            <Settings className="w-12 h-12 text-[#FFB800] mx-auto mb-4 animate-spin" style={{ animationDuration: '4s' }} />
            <div className="font-display text-2xl tracking-wider text-white mb-3">ТЕХНИЧЕСКИЕ РАБОТЫ</div>
            <div className="text-sm text-gray-400 leading-relaxed mb-6 whitespace-pre-line">
              {systemConfig.maintenanceMessage || 'Идут технические работы. Скоро вернёмся!'}
            </div>
            <button
              onClick={handleLogout}
              className="px-5 py-2.5 bg-[#0A0B0D] hover:bg-[#1F0A0A] border border-[#2A2E36] hover:border-[#E83A2C] rounded-lg text-sm text-gray-400 hover:text-[#E83A2C] font-mono tracking-wider transition"
            >
              <LogOut className="w-3.5 h-3.5 inline mr-2" />
              ВЫЙТИ
            </button>
          </div>
        </div>
      )}

      {!loading && !authAction && currentUser && (!systemConfig?.maintenanceMode || isDevAccount(currentUser)) && profileMissing.length > 0 && (
        <ProfileCompletionScreen
          user={currentUser}
          missing={profileMissing}
          onLogout={handleLogout}
          onSaved={(fresh) => setUsers(prev => prev.map(u => u.id === fresh.id ? { ...u, ...fresh } : u))}
        />
      )}

      {!loading && !authAction && currentUser && (!systemConfig?.maintenanceMode || isDevAccount(currentUser)) && profileMissing.length === 0 && (
        <div key={screen} className="fade-in">
          {currentUser.isAdmin ? (
            <>
              {screen === 'admin' && <AdminDashboard users={users} navigate={navigate} onLogout={handleLogout} currentUser={currentUser} notifications={notifications} />}
              {screen === 'adminTrainee' && <AdminTrainee userId={ctx.userId} users={users} navigate={navigate} />}
              {screen === 'adminChecklist' && <AdminChecklist userId={ctx.userId} users={users} navigate={navigate} adminName={currentUser.name} />}
              {screen === 'adminContent' && <AdminContentHub navigate={navigate} onLogout={handleLogout} currentUser={currentUser} users={users} auditLog={auditLog} />}
              {screen === 'devPanel' && <DevPanel navigate={navigate} currentUser={currentUser} users={users} updateUsers={updateUsers} showToast={showToast} ctx={ctx} />}
              {screen === 'adminCourses' && <AdminCoursesList navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminCourse' && <AdminCourseEditor courseId={ctx.courseId} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminModule' && <AdminModuleEditor courseId={ctx.courseId} moduleId={ctx.moduleId} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminLesson' && <AdminLessonEditor courseId={ctx.courseId} moduleId={ctx.moduleId} lessonId={ctx.lessonId} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminTest' && <AdminTestEditor courseId={ctx.courseId} moduleId={ctx.moduleId} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminTechCards' && <AdminTechCardsList navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminTechCard' && <AdminTechCardEditor cardId={ctx.cardId} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminChecklistTpl' && <AdminChecklistTemplate navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminUsers' && <AdminUsersList users={users} updateUsers={updateUsers} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminUser' && <AdminUserEditor userId={ctx.userId} users={users} updateUsers={updateUsers} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminRegulations' && <AdminRegulationsList navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminRegulation' && <AdminRegulationEditor regId={ctx.regId} navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminStats' && <StatsDashboard users={users} navigate={navigate} onLogout={handleLogout} currentUser={currentUser} />}
              {screen === 'adminSettings' && <AdminSettings navigate={navigate} currentUser={currentUser} />}
              {screen === 'adminStartScreen' && <AdminStartScreenEditor navigate={navigate} currentUser={currentUser} />}
              {screen === 'adminContacts' && <AdminContactsEditor navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminExitQuestions' && <AdminExitInterviewEditor navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminExitResponses' && <AdminExitResponses navigate={navigate} showToast={showToast} currentUser={currentUser} />}
              {screen === 'adminMessages' && <AdminMessages users={users} navigate={navigate} currentUser={currentUser} showToast={showToast} initialUserId={ctx.userId} />}
              {screen === 'directorTasks' && <DirectorTasksList users={users} navigate={navigate} currentUser={currentUser} showToast={showToast} />}
              {screen === 'directorTask' && <DirectorTaskEditor taskId={ctx.taskId} users={users} navigate={navigate} currentUser={currentUser} showToast={showToast} />}
              {screen === 'adminMyTasks' && <AdminMyTasksList users={users} navigate={navigate} currentUser={currentUser} showToast={showToast} />}
              {screen === 'adminMyTask' && <AdminMyTaskView taskId={ctx.taskId} users={users} navigate={navigate} currentUser={currentUser} showToast={showToast} />}
            </>
          ) : !progress ? (
            <div className="max-w-2xl mx-auto p-5 fade-in">
              <div className="flex items-center justify-between mb-6">
                <div className="space-y-2 flex-1">
                  <Skeleton width="40%" height={10} />
                  <Skeleton width="60%" height={24} />
                </div>
                <Skeleton width={48} height={48} rounded={false} className="rounded-lg" />
              </div>
              <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5 mb-4 space-y-3">
                <Skeleton width="70%" height={18} />
                <Skeleton width="40%" height={14} />
                <div className="pt-3 border-t border-[#2A2E36] space-y-2">
                  <Skeleton width="50%" height={12} />
                  <Skeleton width="100%" height={6} />
                </div>
              </div>
              <div className="space-y-2.5">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 flex items-center gap-3">
                    <Skeleton width={44} height={44} rounded={false} className="rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton width="70%" height={14} />
                      <Skeleton width="50%" height={10} />
                      <Skeleton width="100%" height={4} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6">
                <div className="font-mono text-[10px] text-gray-600 tracking-widest">СИНХРОНИЗАЦИЯ ДОСЬЕ...</div>
              </div>
            </div>
          ) : (
            <>
              {screen === 'dashboard' && <Dashboard user={currentUser} progress={progress} navigate={navigate} onLogout={handleLogout} users={users} />}
              {screen === 'regulations' && <RegulationsScreen key={ctx.regId || 'list'} user={currentUser} navigate={navigate} initialRegId={ctx.regId} />}
              {screen === 'contacts' && <ContactsScreen user={currentUser} navigate={navigate} users={users} />}
              {screen === 'stats' && <TraineeStatsScreen user={currentUser} users={users} navigate={navigate} />}
              {screen === 'messages' && <TraineeMessagesScreen user={currentUser} navigate={navigate} showToast={showToast} />}
              {screen === 'exitInterview' && <ExitInterviewScreen user={currentUser} navigate={navigate} />}
              {screen === 'module' && <ModuleScreen user={currentUser} moduleId={ctx.moduleId} progress={progress} navigate={navigate} />}
              {screen === 'lesson' && <LessonScreen user={currentUser} lessonCtx={ctx} progress={progress} navigate={navigate} onComplete={completeLesson} />}
              {screen === 'techcard' && <TechCardScreen techCardId={ctx.techCardId} navigate={navigate} backTo={ctx.backTo || 'dashboard'} backCtx={ctx.backCtx} />}
              {screen === 'test' && <TestScreen key={`${ctx.moduleId}_${ctx.retry || ''}`} user={currentUser} moduleId={ctx.moduleId} navigate={navigate} onComplete={completeTest} progress={progress} />}
              {screen === 'profile' && <ProfileScreen user={currentUser} progress={progress} navigate={navigate} onLogout={handleLogout} users={users} updateUsers={updateUsers} showToast={showToast} />}
              {screen === 'techcards' && <TechCardsList navigate={navigate} />}
            </>
          )}
        </div>
      )}
      {showTraineeHat && !menuOpen && <FloatingDock variant="trainee" onHome={() => navigate('dashboard')} />}
      {showAdminDock && !menuOpen && (
        <FloatingDock variant="admin" raised={screen === 'adminChecklist'} onHome={() => navigate('admin')} onMenu={() => setMenuOpen(true)} />
      )}
      {appReady && (
        <CommandMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          items={navItems}
          navigate={navigate}
          homeScreen={homeScreen}
          onLogout={handleLogout}
          userName={currentUser.name}
        />
      )}
	  <PwaInstallBanner />
    </div>
    </ConfirmProvider>
    </NavUiContext.Provider>
    </NotificationContext.Provider>
    </ContentContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: ЛОГИН
// ═══════════════════════════════════════════════════════════════

function LoginScreen({ onLogin, onRegister, onRequestReset, usersCount }) {
  const { settings, systemConfig } = useContent();
  const [mode, setMode] = useState(usersCount === 0 ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [brand, setBrand] = useState('shawarmafia');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [resetSent, setResetSent] = useState(null);     // null | {ok, message}
  const [showReset, setShowReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);  // toggle "глаз"

  // OAuth-провайдеры — подгружаем с сервера какие из них включены в .env
  const [oauthProviders, setOAuthProviders] = useState({ yandex: false, vk: false });
  useEffect(() => {
    (async () => {
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        const r = await fetch(`${apiUrl}/auth/oauth-providers`);
        if (r.ok) setOAuthProviders(await r.json());
      } catch { /* noop — если сервер недоступен, кнопок просто не будет */ }
    })();
  }, []);
  
  // ─── Spotlight follow effect ───
  // Лёгкое световое пятно следует за курсором, привязано к контейнеру.
  // На мобиле эффект отключается (нет hover).
  const containerRef = useRef(null);
  const spotlightRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return;
    const container = containerRef.current;
    const spotlight = spotlightRef.current;
    if (!container || !spotlight) return;
    let rafId = null;
    const handleMove = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const rect = container.getBoundingClientRect();
        spotlight.style.left = (e.clientX - rect.left) + 'px';
        spotlight.style.top = (e.clientY - rect.top) + 'px';
        spotlight.style.opacity = '1';
      });
    };
    const handleLeave = () => { spotlight.style.opacity = '0'; };
    container.addEventListener('mousemove', handleMove);
    container.addEventListener('mouseleave', handleLeave);
    return () => {
      container.removeEventListener('mousemove', handleMove);
      container.removeEventListener('mouseleave', handleLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  
  const [touched, setTouched] = useState({});
  const fioError = checkRuFullName(fullName);
  const phoneError = normalizeRuPhone(phone) ? '' : 'Введите номер в формате +7 (999) 123-45-67';
  const birthError = checkBirthDate(birthDate);

  const submit = async () => {
    setError('');

    if (mode === 'register') {
      setTouched({ fullName: true, phone: true, birthDate: true });
      if (fioError) { setError(fioError); return; }
      if (settings.requireGender && !gender) {
        setError('Выберите пол');
        return;
      }
      if (birthError) { setError(birthError); return; }
      if (phoneError) { setError(phoneError); return; }
    }

    setBusy(true);
    try {
      if (mode === 'login') {
        const res = await onLogin({ email, password });
        if (!res.ok) setError(res.error);
      } else {
        const res = await onRegister({
          email, password,
          name: normalizeRuFullName(fullName),
          gender, phone: normalizeRuPhone(phone), birthDate,
          brand,
        });
        if (!res.ok) { setError(res.error); return; }
        // Регистрация прошла — handleRegister уже залогинил пользователя,
        // но мы покажем красивый экран успеха перед переходом
        setRegistered({ name: normalizeRuFullName(fullName), email, isAdmin: res.user?.isAdmin, emailSent: res.emailSent });
      }
    } finally {
      setBusy(false);
    }
  };

  // ФИО, телефон и дата рождения обязательны всегда — это данные сотрудника
  const requireRegName = true;
  const requireRegGender = !!settings.requireGender;
  const requireRegPhone = true;
  const requireRegBirthDate = true;
  const requireRegEmail = settings.requireEmail !== false;
  const requireRegPassword = settings.requirePassword !== false;

  // Кнопка активна
  const canSubmit = mode === 'login'
    ? email && password
    : ((!requireRegEmail || email) &&
       (!requireRegPassword || password) &&
       !fioError &&
       (!requireRegGender || gender) &&
       !phoneError &&
       !birthError);

  // Экран запроса восстановления пароля
  if (showReset) {
    const submitReset = async () => {
      setError('');
      setBusy(true);
      try {
        const res = await onRequestReset({ email });
        if (!res.ok) { setError(res.error); return; }
        setResetSent({ skipped: !!res.skipped });
      } finally {
        setBusy(false);
      }
    };

    if (resetSent) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-md fade-in">
            <div className="bg-[#15171B] border border-[#00C781]/40 rounded-2xl p-8 pop-in">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00C781]/20 mb-4">
                  <Send className="w-8 h-8 text-[#00C781]" />
                </div>
                <h1 className="font-display text-2xl tracking-wider text-white mb-2">ЗАПРОС ПРИНЯТ</h1>
                <p className="text-sm text-gray-300 mb-5 leading-relaxed">
                  Если такой email зарегистрирован в системе — на него отправлено письмо со ссылкой для восстановления пароля.
                  <br /><br />
                  <strong className="text-white">Проверь почту</strong>, в том числе папку «Спам». Ссылка действует <strong className="text-white">1 час</strong>.
                  <br /><br />
                  <span className="text-xs text-gray-500">Если письмо не пришло за 5 минут — возможно, email не зарегистрирован или администратор ещё не подключил отправку писем. В этом случае можно попросить администратора сменить пароль вручную.</span>
                </p>
                <button
                  onClick={() => { setShowReset(false); setResetSent(null); setError(''); }}
                  className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono text-xs tracking-wider py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" /> К ВХОДУ
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md fade-in">
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-6">
            <button onClick={() => { setShowReset(false); setError(''); }} className="text-gray-500 hover:text-white text-xs flex items-center gap-1 mb-4 font-mono tracking-wider">
              <ChevronLeft className="w-3 h-3" /> К ВХОДУ
            </button>
            <h1 className="font-display text-2xl tracking-wider text-white mb-2">ЗАБЫЛ ПАРОЛЬ?</h1>
            <p className="text-sm text-gray-400 mb-5 leading-relaxed">
              Введи email, на который зарегистрирован аккаунт. Мы отправим ссылку для сброса пароля.
            </p>

            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitReset()}
              placeholder={settings.loginEmailPlaceholder || 'ivan@example.com'}
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white placeholder-gray-700 outline-none transition"
              autoFocus
            />

            {error && (
              <div className="mt-4 p-3 bg-[#1F0A0A] border border-[#E83A2C] rounded-lg text-sm text-[#FF3B30] flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={submitReset}
              disabled={busy || !email}
              className="w-full mt-5 bg-[#FFB800] hover:bg-[#FFC820] disabled:bg-gray-800 disabled:text-gray-600 text-black font-mono tracking-wider py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              ОТПРАВИТЬ ССЫЛКУ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Если пользователь успешно зарегистрировался — показываем экран успеха
  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md fade-in">
          <div className="bg-[#15171B] border border-[#00C781]/40 rounded-2xl p-8 relative dossier-corner overflow-hidden pop-in">
            {/* Декор */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#00C781]/10 to-transparent pointer-events-none"></div>

            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00C781] to-[#005C3D] mb-5 shadow-lg" style={{ boxShadow: '0 0 40px rgba(0,199,129,0.4)' }}>
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-display text-3xl tracking-wider text-white mb-1">ДОБРО ПОЖАЛОВАТЬ</h1>
              <div className="font-display text-xl tracking-wider text-[#00C781] mb-4">{registered.name.toUpperCase()}</div>

              {registered.isAdmin && (
                <div className="bg-[#FFB800]/10 border border-[#FFB800]/40 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 justify-center">
                    <Crown className="w-4 h-4 text-[#FFB800]" />
                    <span className="text-sm text-[#FFB800] font-mono tracking-wider">ТЫ ПЕРВЫЙ — ПОЛУЧАЕШЬ АДМИНКУ</span>
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-300 leading-relaxed mb-4">
                Аккаунт создан, ты уже в системе.
                {registered.emailSent && (
                  <div className="mt-3 p-3 bg-[#0A1F12] border border-[#00C781]/30 rounded-lg flex items-start gap-2 text-left">
                    <Send className="w-4 h-4 text-[#00C781] flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-gray-300">
                      Письмо с приветствием отправлено на <span className="font-mono text-white">{registered.email}</span>. Перейди по ссылке в письме, чтобы подтвердить email — это понадобится для восстановления пароля.
                    </div>
                  </div>
                )}
                {!registered.emailSent && (
                  <div className="mt-3 p-3 bg-[#15171B] border border-[#2A2E36] rounded-lg flex items-start gap-2 text-left">
                    <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-gray-500">
                      Письмо не отправлено — SMTP не настроен. Это не страшно, аккаунт работает, но восстановление пароля по email пока недоступно.
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setRegistered(null)}
                className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono tracking-wider py-3.5 rounded-lg transition glow-red flex items-center justify-center gap-2"
              >
                НАЧАТЬ <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* ─── ФОНОВЫЕ СЛОИ ─── */}

      {/* Сетка с edge-fade */}
      <div className="login-grid-bg" />

      {/* Анимированные градиентные орбы — задают «глубину» сцене */}
      <div className="login-orb login-orb-1" style={{
        width: 480, height: 480,
        top: '-15%', left: '-10%',
        background: 'radial-gradient(circle, rgba(232,58,44,0.55) 0%, transparent 70%)',
      }} />
      <div className="login-orb login-orb-2" style={{
        width: 400, height: 400,
        bottom: '-10%', right: '-10%',
        background: 'radial-gradient(circle, rgba(255,184,0,0.4) 0%, transparent 70%)',
      }} />
      <div className="login-orb login-orb-3" style={{
        width: 300, height: 300,
        top: '40%', right: '5%',
        background: 'radial-gradient(circle, rgba(139,15,8,0.45) 0%, transparent 70%)',
      }} />

      {/* Spotlight за курсором (только desktop) */}
      <div ref={spotlightRef} className="spotlight-bg" style={{ opacity: 0 }} />

      {/* ─── ОСНОВНОЙ КОНТЕНТ ─── */}
      <div className="w-full max-w-md relative z-10 card-enter">
        {/* Логотип с эмблемой, лучами и дыханием */}
        <div className="text-center mb-8">
          <div className="relative inline-flex items-center justify-center mb-5">
            {/* Декоративные лучи — медленно вращаются вокруг лого */}
            <svg className="absolute logo-rays" width="160" height="160" viewBox="0 0 160 160" style={{ opacity: 0.18 }}>
              {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30) * Math.PI / 180;
                const x1 = 80 + Math.cos(angle) * 52;
                const y1 = 80 + Math.sin(angle) * 52;
                const x2 = 80 + Math.cos(angle) * 76;
                const y2 = 80 + Math.sin(angle) * 76;
                return (
                  <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="#FFB800" strokeWidth="1.5" strokeLinecap="round" />
                );
              })}
            </svg>
            {/* Внешнее кольцо */}
            <div className="absolute w-[120px] h-[120px] rounded-full border border-[#FFB800]/20" />
            {/* Лого квадрат */}
            <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-[#E83A2C] via-[#C12818] to-[#8B0F08] flex items-center justify-center logo-breathe dossier-corner">
              {/* Внутренний highlight */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
              <FedoraHat className="w-14 h-14 text-white relative z-10" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }} />
              {/* Маленькая точка-«звезда» в углу */}
              <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-white/80 glow-pulse" />
            </div>
          </div>

          <h1 className="font-display text-6xl tracking-wider text-white title-glitch leading-none mb-1">
            {settings.loginTitle || 'MAFIA'}
          </h1>
          <h2 className="font-display text-3xl tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-r from-[#FFB800] via-[#FFC820] to-[#FFB800] title-glitch leading-none"
            style={{ animationDelay: '0.15s' }}>
            {settings.loginSubtitle || 'ACADEMY'}
          </h2>
          <div className="mt-4 inline-block">
            <div className="typewriter font-mono text-[10px] text-[#FFB800]/70 tracking-[0.35em]">
              {settings.loginTagline || 'БАЗА ПОДГОТОВКИ'}
            </div>
          </div>
        </div>

        <div className="login-card-glass rounded-2xl p-6 relative dossier-corner scan-line overflow-hidden">
          {/* Mode toggle */}
          <div className="mode-toggle mb-5">
            {settings.allowRegistration && (
              <div className="mode-toggle-indicator" style={{
                left: mode === 'login' ? '4px' : 'calc(50% + 0px)',
                width: 'calc(50% - 4px)',
              }} />
            )}
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className={mode === 'login' ? 'active' : ''}
            >
              ВХОД
            </button>
            {settings.allowRegistration && (
              <button
                onClick={() => { setMode('register'); setError(''); }}
                className={mode === 'register' ? 'active' : ''}
              >
                РЕГИСТРАЦИЯ
              </button>
            )}
          </div>

          {/* ─── Соц-авторизация ─── */}
          {/* Показываем только если: (а) провайдер настроен в .env, (б) разработчик не выключил его в systemConfig */}
          {((oauthProviders.yandex && systemConfig?.allowOAuthYandex !== false) ||
            (oauthProviders.vk && systemConfig?.allowOAuthVk !== false)) && (
            <>
              <div className="space-y-2 mb-4">
                {oauthProviders.yandex && systemConfig?.allowOAuthYandex !== false && (
                  <button
                    type="button"
                    onClick={() => { window.location.href = '/api/auth/yandex/start'; }}
                    className="oauth-btn oauth-btn-yandex"
                  >
                    <span className="w-5 h-5 rounded-full bg-white text-[#FC3F1D] flex items-center justify-center font-bold text-sm" style={{ fontFamily: 'Arial, sans-serif' }}>Я</span>
                    {mode === 'register' ? 'РЕГИСТРАЦИЯ ЧЕРЕЗ ЯНДЕКС' : 'ВОЙТИ ЧЕРЕЗ ЯНДЕКС'}
                  </button>
                )}
                {oauthProviders.vk && systemConfig?.allowOAuthVk !== false && (
                  <button
                    type="button"
                    onClick={() => { window.location.href = '/api/auth/vk/start'; }}
                    className="oauth-btn oauth-btn-vk"
                  >
                    <span className="w-5 h-5 rounded bg-white text-[#0077FF] flex items-center justify-center font-bold text-[11px]" style={{ fontFamily: 'Arial, sans-serif' }}>VK</span>
                    {mode === 'register' ? 'РЕГИСТРАЦИЯ ЧЕРЕЗ ВК' : 'ВОЙТИ ЧЕРЕЗ ВК'}
                  </button>
                )}
              </div>
              <div className="divider-or">
                <div className="line" />
                <div className="text">ИЛИ ПО EMAIL</div>
                <div className="line" />
              </div>
            </>
          )}

          {mode === 'register' && requireRegName && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ФИО ПОЛНОСТЬЮ, ПО-РУССКИ</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder={settings.loginNamePlaceholder || 'Иванов Иван Иванович'}
                className={`w-full bg-[#0A0B0D] border rounded-lg px-4 py-3 text-white placeholder-gray-700 focus:outline-none transition ${touched.fullName && fioError ? 'border-[#E83A2C]' : 'border-[#2A2E36]'}`}
                style={{ outline: 'none' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => { e.target.style.borderColor = ''; setTouched(t => ({ ...t, fullName: true })); if (fullName.trim()) setFullName(normalizeRuFullName(fullName)); }}
                autoFocus
                autoComplete="name"
                autoCapitalize="words"
                lang="ru"
              />
              <div className={`field-hint ${touched.fullName && fioError ? 'is-error' : ''}`}>
                {touched.fullName && fioError ? fioError : 'Фамилия, имя и отчество — как в паспорте'}
              </div>
            </>
          )}

          {mode === 'register' && requireRegGender && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ПОЛ</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[{ id: 'male', label: 'Мужской', icon: '♂' }, { id: 'female', label: 'Женский', icon: '♀' }].map(g => (
                  <button
                    key={g.id}
                    onClick={() => setGender(g.id)}
                    className={`p-2.5 rounded-lg border text-center transition ${
                      gender === g.id ? 'themed-border-accent themed-bg-accent-soft' : 'border-[#2A2E36] bg-[#0A0B0D] hover:border-gray-600'
                    }`}
                  >
                    <div className="text-xl">{g.icon}</div>
                    <div className="text-xs text-white">{g.label}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {mode === 'register' && requireRegPhone && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ТЕЛЕФОН</label>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={e => setPhone(formatRuPhoneInput(e.target.value))}
                placeholder="+7 (___) ___-__-__"
                className={`w-full bg-[#0A0B0D] border rounded-lg px-4 py-3 text-white placeholder-gray-700 focus:outline-none transition ${touched.phone && phoneError ? 'border-[#E83A2C]' : 'border-[#2A2E36]'} mb-1`}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => { e.target.style.borderColor = ''; setTouched(t => ({ ...t, phone: true })); }}
                autoComplete="tel"
              />
              {touched.phone && phoneError ? <div className="field-hint is-error">{phoneError}</div> : <div className="mb-3" />}
            </>
          )}

          {mode === 'register' && requireRegBirthDate && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ДАТА РОЖДЕНИЯ</label>
              <input
                type="date"
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                max={new Date().toISOString().slice(0,10)}
                className="w-full bg-[#0A0B0D] border border-[#2A2E36] rounded-lg px-4 py-3 text-white placeholder-gray-700 focus:outline-none transition mb-3 min-h-[48px]"
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => { e.target.style.borderColor = ''; setTouched(t => ({ ...t, birthDate: true })); }}
              />
            </>
          )}

          {(mode === 'login' || requireRegEmail) && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()}
                placeholder={settings.loginEmailPlaceholder || 'ivan@example.com'}
                className="w-full bg-[#0A0B0D] border border-[#2A2E36] rounded-lg px-4 py-3 text-white placeholder-gray-700 focus:outline-none transition mb-3"
                onFocus={e => e.target.style.borderColor = '#E83A2C'}
                onBlur={e => e.target.style.borderColor = ''}
                autoComplete="email"
                autoFocus={mode === 'login'}
              />
            </>
          )}

          {(mode === 'login' || requireRegPassword) && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ПАРОЛЬ</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                  placeholder={mode === 'register' ? 'Минимум 6 символов' : (settings.loginPasswordPlaceholder || '••••••')}
                  className="w-full bg-[#0A0B0D] border border-[#2A2E36] rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-700 focus:outline-none transition"
                  onFocus={e => e.target.style.borderColor = '#E83A2C'}
                  onBlur={e => e.target.style.borderColor = ''}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 min-touch flex items-center justify-center text-gray-500 hover:text-white transition"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword
                    ? <EyeOff className="w-5 h-5" />
                    : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </>
          )}

          {mode === 'register' && (
            <>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2 mt-4">ВЫБЕРИ НАПРАВЛЕНИЕ</label>
              <div className="grid grid-cols-2 gap-2">
                {BRAND_LIST.map(b => (
                  <button
                    key={b.id}
                    onClick={() => setBrand(b.id)}
                    className={`p-3 rounded-lg border text-left transition ${
                      brand === b.id ? 'themed-border-accent themed-bg-accent-soft' : 'border-[#2A2E36] bg-[#0A0B0D] hover:border-gray-600'
                    }`}
                  >
                    <div className="text-2xl mb-1">{b.icon}</div>
                    <div className="font-bold text-sm text-white">{b.name}</div>
                    <div className="text-[10px] text-gray-500">{b.description}</div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-gray-600 mt-2">
                {settings.registerHint || 'Доступ ко второму направлению админ откроет позже.'}
              </div>
            </>
          )}

          {error && (
            <div className="mt-4 p-3 bg-[#1F0A0A] border border-[#E83A2C] rounded-lg text-sm text-[#FF3B30] flex items-start gap-2 shake">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <MagneticButton
            onClick={submit}
            disabled={busy || !canSubmit}
            strength={0.18}
            className="w-full mt-5 bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:bg-gray-800 disabled:text-gray-600 text-white font-mono tracking-wider py-3.5 rounded-lg glow-red disabled:glow-red disabled:shadow-none flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? (settings.loginButtonText || 'ВОЙТИ') : (settings.registerButtonText || 'СОЗДАТЬ АККАУНТ'))}
            {!busy && <ArrowRight className="w-4 h-4" />}
          </MagneticButton>

          {mode === 'login' && onRequestReset && (
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => { setShowReset(true); setError(''); setResetSent(null); }}
                className="text-xs text-gray-500 hover:text-[#FFB800] font-mono tracking-wider transition"
              >
                Забыл пароль?
              </button>
            </div>
          )}

          {usersCount === 0 && mode === 'register' && (
            <div className="text-[10px] themed-text-accent font-mono text-center mt-4 tracking-wider">
              {settings.firstAdminHint || '⚡ ПЕРВЫЙ РЕГИСТРИРУЮЩИЙСЯ ПОЛУЧАЕТ ПРАВА АДМИНА'}
            </div>
          )}

          <div className="text-[10px] text-gray-700 font-mono text-center mt-4 tracking-wider">
            В БАЗЕ: {usersCount} {usersCount === 1 ? 'СОТРУДНИК' : 'СОТРУДНИКОВ'}
          </div>
        </div>

        <div className="text-center mt-6 text-[10px] text-gray-700 font-mono tracking-wider">
          v2.1 · ВНУТРЕННЕЕ ИСПОЛЬЗОВАНИЕ
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUTH ACTIONS — экраны верификации email и сброса пароля
// ═══════════════════════════════════════════════════════════════

function AuthActionScreen({ action, onDone, handleLogin, handleRegister, handleOAuthLoginById, users }) {
  // Внутренний компонент, чтобы хуки были стабильны
  if (action.type === 'verify-email')    return <VerifyEmailScreen token={action.token} onDone={onDone} />;
  if (action.type === 'reset-password')  return <ResetPasswordScreen token={action.token} onDone={onDone} />;
  if (action.type === 'oauth-finalize')  return <OAuthFinalizeScreen token={action.token} onDone={onDone} handleLogin={handleLogin} handleRegister={handleRegister} handleOAuthLoginById={handleOAuthLoginById} users={users} />;
  if (action.type === 'oauth-error')     return <OAuthErrorScreen reason={action.reason} onDone={onDone} />;
  return null;
}

function OAuthErrorScreen({ reason, onDone }) {
  const messages = {
    bad_state: 'Срок действия запроса истёк. Попробуйте войти ещё раз.',
    token_exchange: 'Не удалось получить токен от провайдера.',
    profile_fetch: 'Не удалось получить данные профиля.',
    missing_code: 'Провайдер не вернул код авторизации.',
    access_denied: 'Вы отменили вход.',
    internal: 'Внутренняя ошибка. Попробуйте позже.',
  };
  const msg = messages[reason] || `Ошибка авторизации: ${reason}`;
  return (
    <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center p-5">
      <div className="max-w-md w-full bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-[#E83A2C] mx-auto mb-4" />
        <h1 className="font-display text-xl text-white mb-3">ОШИБКА ВХОДА</h1>
        <p className="text-sm text-gray-400 mb-6">{msg}</p>
        <button onClick={onDone} className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono text-xs tracking-wider px-4 py-3 rounded-lg transition">
          ВЕРНУТЬСЯ К ВХОДУ
        </button>
      </div>
    </div>
  );
}

// ─── Финализация OAuth: обменивает handoff-токен на данные профиля,
//     далее либо логинит существующего юзера, либо предлагает дозаполнить профиль ───
function OAuthFinalizeScreen({ token, onDone, handleLogin, handleRegister, handleOAuthLoginById, users }) {
  const [state, setState] = useState('loading');   // loading | login_existing | needs_signup | error
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);    // { provider, sub, email, name, avatarUrl, ... }

  // Дозаполнение (B.2)
  const [phone, setPhone] = useState('');
  const [brand, setBrand] = useState('shawarmafia');
  const [position, setPosition] = useState('');
  const [busy, setBusy] = useState(false);
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [emailInput, setEmailInput] = useState('');  // если провайдер не дал email — заполнит юзер

  // Загружаем данные с бэка
  useEffect(() => {
    (async () => {
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        const resp = await fetch(`${apiUrl}/auth/oauth-finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.ok) {
          setError(data.error === 'invalid_or_expired'
            ? 'Срок действия токена истёк. Попробуйте войти ещё раз.'
            : (data.error || 'Не удалось завершить вход'));
          setState('error');
          return;
        }
        setProfile(data);
        // Имя от провайдера подставляем, только если оно уже по-русски — иначе пусть человек введёт сам
        setFullName(/[A-Za-z]/.test(data.name || '') ? '' : normalizeRuFullName(data.name || ''));
        setBirthDate(providerBirthdayToIso(data.birthday));
        setEmailInput(data.email && !String(data.email).endsWith('@vk.tarasovweb.ru') ? data.email : '');

        // КРИТИЧНО: подгружаем СВЕЖИЙ список users из БД,
        // а не используем переданный пропсом — он мог быть пустой или устаревший
        // (race: пользователь только что зарегистрировался через ВК, его данные ещё не дошли).
        let freshUsers = users;
        try {
          const fromDb = await safeGet(STORAGE_KEYS.USERS);
          if (Array.isArray(fromDb) && fromDb.length >= (users?.length || 0)) {
            freshUsers = fromDb;
          }
        } catch (e) {
          console.warn('[oauth-finalize] не удалось рефрешнуть users:', e.message);
        }

        // Кейс 1: сервер нашёл пользователя (по привязке или по email) и уже выдал сессию.
        // Недостающие ФИО/телефон/дату рождения человек дозаполнит на следующем экране.
        if (data.linkedUserId) {
          await loginExisting(data.linkedUserId, data);
          return;
        }
        // Кейс 2: нового человека сервер пускает только с одноразовым пропуском
        if (!data.signupToken) {
          setError('Не удалось подготовить регистрацию. Попробуйте войти ещё раз.');
          setState('error');
          return;
        }
        setState('needs_signup');
      } catch (e) {
        setError(e.message || 'Сетевая ошибка');
        setState('error');
      }
    })();
  }, [token]);  // НЕ зависим от users — мы сами рефрешим внутри

  // Привязать стороннюю учётку к нашему пользователю + залогинить
  // Привязка стороннего аккаунта к нашему юзеру + логин по userId
    const linkAndLogin = async (userId, prof) => {
    // Привязку и выдачу сессии сервер делает сам в /auth/oauth-finalize.
    await new Promise(r => setTimeout(r, 150));
    
    // 4. Проверяем сессию и логиним
    	try {
      const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      const r = await fetch(`${apiUrl}/auth/me`, {
        method: 'GET',
        credentials: 'include',
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.user) {
        // Логиним через родителя — только там доступны setUsers/setCurrentUserId/setScreen
        const res = await handleOAuthLoginById({
          userId: body.user.id,
          provider: prof?.provider,
          sub: prof?.sub,
        });
        if (res?.ok) {
          onDone();
          return;
        }
      }
    } catch {}
    
    // Фолбэк — перезагружаем страницу
    window.location.href = '/';
  };

  const loginExisting = linkAndLogin; // тот же путь

  // Завершение регистрации (B.2)
  const completeSignup = async () => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedEmail || !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      setError('Введите корректный email');
      return;
    }
    const fioErr = checkRuFullName(fullName);
    if (fioErr) { setError(fioErr); return; }
    const bErr = checkBirthDate(birthDate);
    if (bErr) { setError(bErr); return; }
    if (!normalizeRuPhone(phone)) {
      setError('Введите номер телефона в формате +7 (999) 123-45-67');
      return;
    }
    if (!brand) { setError('Выберите кухню'); return; }
    if (!position.trim()) { setError('Укажите должность'); return; }

    setError('');
    setBusy(true);
    try {
      // Регистрируем без пароля, с предзаполненными данными от OAuth
      const res = await handleRegister({
        email: trimmedEmail,
        password: '',                                  // пароля нет — вход только через OAuth
        fullName: normalizeRuFullName(fullName),
        phone: normalizeRuPhone(phone),
        birthDate,
        brand,
        position: position.trim(),
        avatarUrl: profile.avatarUrl || '',
        oauthProvider: profile.provider,
        oauthSub: profile.sub,
        oauthSignupToken: profile.signupToken,
      });
      if (!res?.ok) {
        setError(res?.error || 'Не удалось завершить регистрацию');
        setBusy(false);
        return;
      }
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#E83A2C] mx-auto mb-4" />
          <div className="font-mono text-xs text-gray-500 tracking-widest">ВХОД ЧЕРЕЗ {(profile?.provider || '').toUpperCase() || 'ПРОВАЙДЕР'}...</div>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center p-5">
        <div className="max-w-md w-full bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[#E83A2C] mx-auto mb-4" />
          <h1 className="font-display text-xl text-white mb-3">ОШИБКА</h1>
          <p className="text-sm text-gray-400 mb-6">{error}</p>
          <button onClick={onDone} className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono text-xs tracking-wider px-4 py-3 rounded-lg transition">
            ВЕРНУТЬСЯ К ВХОДУ
          </button>
        </div>
      </div>
    );
  }

  // state === 'needs_signup' — экран дозаполнения профиля
  const providerLabel = profile?.provider === 'yandex' ? 'Яндекс' : (profile?.provider === 'vk' ? 'ВКонтакте' : 'провайдер');

  return (
    <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center p-5">
      <div className="max-w-md w-full bg-[#15171B] border border-[#2A2E36] rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5 pb-5 border-b border-[#2A2E36]">
          {profile?.avatarUrl && (
            <img src={profile.avatarUrl} alt="" className="w-12 h-12 rounded-full border border-[#2A2E36]" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] text-[#FFB800] tracking-widest">ВХОД ЧЕРЕЗ {providerLabel.toUpperCase()}</div>
            <div className="text-white font-bold truncate">{profile?.name}</div>
            <div className="text-xs text-gray-500 truncate">{profile?.email}</div>
          </div>
        </div>

        <h1 className="font-display text-xl text-white mb-2">ЗАВЕРШИТЕ РЕГИСТРАЦИЮ</h1>
        <p className="text-sm text-gray-500 mb-5">
          Чтобы войти в Mafia Academy, заполните данные сотрудника. Это требуется один&nbsp;раз.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">
              EMAIL *
              {!profile?.email && <span className="ml-2 text-[#FFB800] normal-case">(провайдер не передал — введите вручную)</span>}
            </label>
            <input
              type="email"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="ivan@example.com"
              autoComplete="email"
              readOnly={!!profile?.email}
              className={`w-full bg-[#0A0B0D] border ${profile?.email ? 'border-[#2A2E36] opacity-70 cursor-not-allowed' : 'border-[#FFB800]/40 focus:border-[#E83A2C]'} rounded-lg px-4 py-3 text-white text-sm outline-none transition`}
            />
            {profile?.email && (
              <div className="text-[10px] text-gray-600 mt-1">
                Email получен от {profile?.provider === 'yandex' ? 'Яндекса' : 'ВКонтакте'} — изменить нельзя
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">ФИО ПОЛНОСТЬЮ, ПО-РУССКИ *</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              onBlur={() => fullName.trim() && setFullName(normalizeRuFullName(fullName))}
              placeholder="Иванов Иван Иванович"
              autoCapitalize="words"
              lang="ru"
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white text-sm outline-none transition"
            />
            {fullName && checkRuFullName(fullName) && <div className="field-hint is-error">{checkRuFullName(fullName)}</div>}
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">ДАТА РОЖДЕНИЯ *</label>
            <input
              type="date"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white text-sm outline-none transition min-h-[48px]"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">ТЕЛЕФОН *</label>
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={e => setPhone(formatRuPhoneInput(e.target.value))}
              placeholder="+7 (999) 123-45-67"
              autoComplete="tel"
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white text-sm outline-none transition"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">КУХНЯ *</label>
            <div className="grid grid-cols-2 gap-2">
              {BRAND_LIST.map(b => (
                <button
                  key={b.id}
                  onClick={() => {
                    setBrand(b.id);
                    // Сбрасываем должность, если она не из канонических ролей нового бренда —
                    // это убирает рассинхронизацию (например, выбран "Повар-кассир" + переключение на Ролломафию)
                    const canonical = CANONICAL_ROLES[b.id] || [];
                    if (position && !canonical.includes(position)) {
                      setPosition('');
                    }
                  }}
                  className={`px-3 py-2.5 rounded-lg text-xs font-mono tracking-wider transition flex items-center justify-center gap-1.5 border-2 ${
                    brand === b.id ? '' : 'opacity-50'
                  }`}
                  style={brand === b.id
                    ? { background: b.color + '20', color: b.color, borderColor: b.color }
                    : { background: '#0A0B0D', borderColor: '#2A2E36', color: '#5F5F68' }}
                >
                  <span>{b.icon}</span> {b.name.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">ДОЛЖНОСТЬ *</label>
            {/* Кнопки канонических ролей для выбранного бренда — выбор одним кликом без опечаток */}
            <div className="flex flex-wrap gap-2 mb-2">
              {(CANONICAL_ROLES[brand] || []).map(role => {
                const active = position === role;
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setPosition(role)}
                    className={`px-3 py-2 rounded-lg text-xs font-mono tracking-wider transition border ${
                      active ? 'bg-[#E83A2C] border-[#E83A2C] text-white' : 'bg-[#0A0B0D] border-[#2A2E36] text-gray-400 hover:border-[#E83A2C]/50'
                    }`}
                  >
                    {role.toUpperCase()}
                  </button>
                );
              })}
            </div>
            {/* Поле для редактирования / ввода нестандартной должности */}
            <input
              type="text"
              value={position}
              onChange={e => setPosition(e.target.value)}
              placeholder="Или впишите вручную"
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white text-sm outline-none transition"
              list="canonical-roles-list"
            />
            <datalist id="canonical-roles-list">
              {(CANONICAL_ROLES[brand] || []).map(role => (
                <option key={role} value={role} />
              ))}
            </datalist>
            <div className="text-[10px] text-gray-600 mt-1">
              ⚠ Если должности нет в списке — попросите администратора создать соответствующий курс
            </div>
          </div>

          {error && (
            <div className="bg-[#E83A2C]/10 border border-[#E83A2C]/40 text-[#FF4A3C] text-xs px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            onClick={completeSignup}
            disabled={busy}
            className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:opacity-50 text-white font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {busy ? 'СОЗДАЁМ ПРОФИЛЬ...' : 'ЗАВЕРШИТЬ И ВОЙТИ'}
          </button>
          <button
            onClick={onDone}
            className="w-full text-gray-500 hover:text-white text-xs font-mono tracking-wider py-2 transition"
          >
            ОТМЕНА
          </button>
        </div>
      </div>
    </div>
  );
}

// Обязательное дозаполнение профиля: ФИО по-русски, дата рождения, телефон.
// Показывается стажёру, если чего-то не хватает (вход через Яндекс/ВК, старые аккаунты,
// аккаунты, созданные админом).
function ProfileCompletionScreen({ user, missing, onSaved, onLogout }) {
  const initialName = checkRuFullName(user.fullName || user.name) ? '' : normalizeRuFullName(user.fullName || user.name);
  const [fullName, setFullName] = useState(initialName);
  const [birthDate, setBirthDate] = useState(checkBirthDate(user.birthDate) ? '' : user.birthDate);
  const [phone, setPhone] = useState(normalizeRuPhone(user.phone) ? formatRuPhoneInput(user.phone) : '');
  const [touched, setTouched] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const errs = {
    fullName: checkRuFullName(fullName),
    birthDate: checkBirthDate(birthDate),
    phone: normalizeRuPhone(phone) ? '' : 'Введите номер в формате +7 (999) 123-45-67',
  };
  const valid = !errs.fullName && !errs.birthDate && !errs.phone;

  const save = async () => {
    setTouched({ fullName: true, birthDate: true, phone: true });
    if (!valid) return;
    setBusy(true);
    setError('');
    try {
      const api = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      const r = await fetch(`${api}/auth/complete-profile`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullName: normalizeRuFullName(fullName), birthDate, phone: normalizeRuPhone(phone) }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setError(body.message || 'Не удалось сохранить'); return; }
      onSaved(body.user);
    } catch {
      setError('Нет связи с сервером. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, input, hint) => (
    <div className={missing.includes(key) ? 'need-field' : ''}>
      <label className="block text-sm text-gray-300 mb-1.5">{label}</label>
      {input}
      <div className={`field-hint ${touched[key] && errs[key] ? 'is-error' : ''}`}>
        {touched[key] && errs[key] ? errs[key] : hint}
      </div>
    </div>
  );
  const inputCls = (key) => `w-full bg-[#0A0B0D] border rounded-xl px-4 py-3.5 text-white outline-none transition focus:border-[#FFB800] ${touched[key] && errs[key] ? 'border-[#E83A2C]' : 'border-[#2A2E36]'}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 pt-safe-5 safe-bottom">
      <div className="w-full max-w-md card-enter">
        <div className="login-card-glass rounded-3xl p-6 sm:p-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="hat-badge"><FedoraHat className="w-7 h-7" /></div>
            <div>
              <div className="font-display text-2xl tracking-wide text-white leading-tight">Последний шаг</div>
              <div className="text-sm text-gray-400">Заполните данные сотрудника — это нужно один раз</div>
            </div>
          </div>

          <div className="space-y-4">
            {field('fullName', 'Фамилия, имя и отчество',
              <input
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                onBlur={() => { setTouched(t => ({ ...t, fullName: true })); if (fullName.trim()) setFullName(normalizeRuFullName(fullName)); }}
                placeholder="Иванов Иван Иванович"
                autoComplete="name"
                autoCapitalize="words"
                lang="ru"
                className={inputCls('fullName')}
              />,
              'Полностью и русскими буквами, как в паспорте')}
            {field('birthDate', 'Дата рождения',
              <input
                type="date"
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, birthDate: true }))}
                max={new Date().toISOString().slice(0, 10)}
                className={inputCls('birthDate') + ' min-h-[52px]'}
              />,
              'Поздравим вас в этот день')}
            {field('phone', 'Номер телефона',
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={e => setPhone(formatRuPhoneInput(e.target.value))}
                onBlur={() => setTouched(t => ({ ...t, phone: true }))}
                placeholder="+7 (999) 123-45-67"
                autoComplete="tel"
                className={inputCls('phone')}
              />,
              'Для связи со сменой и руководителем')}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-[#1F0A0A] border border-[#E83A2C]/60 rounded-xl text-sm text-[#FF6B5C] flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <button onClick={save} disabled={busy} className="btn-primary-glow w-full mt-6 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {busy ? 'СОХРАНЯЕМ…' : 'СОХРАНИТЬ И ПРОДОЛЖИТЬ'}
          </button>
          <button onClick={onLogout} className="w-full mt-2 py-2.5 text-sm text-gray-500 hover:text-white transition">
            Выйти из аккаунта
          </button>
        </div>
      </div>
    </div>
  );
}

function VerifyEmailScreen({ token, onDone }) {
  const [state, setState] = useState('loading');   // loading | ok | error
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
        const resp = await fetch(`${apiUrl}/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.ok) {
          setError(data.error || 'Не удалось подтвердить email');
          setState('error');
          return;
        }
        setEmail(data.email || '');
        setState('ok');
      } catch (e) {
        setError('Backend недоступен. Проверь, что сервер работает.');
        setState('error');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in">
        <div className="bg-[#15171B] border rounded-2xl p-8 text-center pop-in" style={{
          borderColor: state === 'ok' ? 'rgba(0,199,129,0.4)' : state === 'error' ? 'rgba(232,58,44,0.4)' : '#2A2E36',
        }}>
          {state === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#FFB800] mx-auto mb-4" />
              <div className="font-display text-2xl tracking-wider text-white mb-2">ПРОВЕРКА...</div>
              <div className="text-sm text-gray-400">Проверяем твою ссылку</div>
            </>
          )}
          {state === 'ok' && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00C781] to-[#005C3D] mb-5" style={{ boxShadow: '0 0 40px rgba(0,199,129,0.4)' }}>
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-display text-3xl tracking-wider text-white mb-2">EMAIL ПОДТВЕРЖДЁН</h1>
              {email && <div className="text-sm text-gray-400 font-mono mb-4">{email}</div>}
              <div className="text-sm text-gray-300 leading-relaxed mb-6">
                Отлично! Теперь ты сможешь восстановить пароль по email, если когда-нибудь его забудешь.
              </div>
              <button
                onClick={onDone}
                className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono tracking-wider py-3.5 rounded-lg transition glow-red flex items-center justify-center gap-2"
              >
                К АКАДЕМИИ <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
          {state === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#1F0A0A] border border-[#E83A2C] mb-5">
                <XCircle className="w-10 h-10 text-[#E83A2C]" />
              </div>
              <h1 className="font-display text-2xl tracking-wider text-white mb-2">НЕ ПОЛУЧИЛОСЬ</h1>
              <div className="text-sm text-gray-400 leading-relaxed mb-5">{error}</div>
              <div className="text-xs text-gray-600 mb-5">
                Возможно, ссылка устарела или уже использована. Зайди в систему обычным способом — ничего страшного не случилось.
              </div>
              <button
                onClick={onDone}
                className="w-full bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-white font-mono text-xs tracking-wider py-3 rounded-lg transition"
              >
                К ВХОДУ
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetPasswordScreen({ token, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState('form');   // form | loading | ok | error
  const [error, setError] = useState('');
  const [showP1, setShowP1] = useState(false);
  const [showP2, setShowP2] = useState(false);

  const submit = async () => {
  setError('');
  if (password.length < 6) { setError('Пароль минимум 6 символов'); return; }
  if (password !== confirm) { setError('Пароли не совпадают'); return; }

  setState('loading');
  try {
    const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
    const resp = await fetch(`${apiUrl}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword: password }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.ok) {
      setError(data.error || 'Не удалось сбросить пароль');
      setState('error');
      return;
    }
    setState('ok');
  } catch (e) {
    setError('Backend недоступен.');
    setState('error');
  }
};

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in">
        <div className="bg-[#15171B] border rounded-2xl p-6 pop-in" style={{
          borderColor: state === 'ok' ? 'rgba(0,199,129,0.4)' : state === 'error' ? 'rgba(232,58,44,0.4)' : '#2A2E36',
        }}>
          {state === 'ok' ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00C781] to-[#005C3D] mb-5" style={{ boxShadow: '0 0 40px rgba(0,199,129,0.4)' }}>
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-display text-2xl tracking-wider text-white mb-2">ПАРОЛЬ ОБНОВЛЁН</h1>
              <div className="text-sm text-gray-300 leading-relaxed mb-6">
                Теперь войди с новым паролем.
              </div>
              <button
                onClick={onDone}
                className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono tracking-wider py-3.5 rounded-lg transition glow-red flex items-center justify-center gap-2"
              >
                К ВХОДУ <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E83A2C] to-[#8B0F08] mb-3 glow-red">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-display text-2xl tracking-wider text-white">НОВЫЙ ПАРОЛЬ</h1>
                <div className="text-xs text-gray-500 mt-1">Придумай и подтверди</div>
              </div>

              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">НОВЫЙ ПАРОЛЬ</label>
              <div className="relative mb-3">
                <input
                  type={showP1 ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Минимум 6 символов"
                  className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-700 outline-none transition"
                  autoComplete="new-password"
                  autoFocus
                />
                <button type="button" onClick={() => setShowP1(v => !v)} tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 min-touch flex items-center justify-center text-gray-500 hover:text-white transition"
                  aria-label={showP1 ? 'Скрыть пароль' : 'Показать пароль'}>
                  {showP1 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ПОДТВЕРДИ ПАРОЛЬ</label>
              <div className="relative">
                <input
                  type={showP2 ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                  placeholder="Повтори"
                  className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-700 outline-none transition"
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowP2(v => !v)} tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 min-touch flex items-center justify-center text-gray-500 hover:text-white transition"
                  aria-label={showP2 ? 'Скрыть пароль' : 'Показать пароль'}>
                  {showP2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-[#1F0A0A] border border-[#E83A2C] rounded-lg text-sm text-[#FF3B30] flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={submit}
                disabled={state === 'loading' || !password || !confirm}
                className="w-full mt-5 bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:bg-gray-800 disabled:text-gray-600 text-white font-mono tracking-wider py-3.5 rounded-lg transition glow-red disabled:glow-red disabled:shadow-none flex items-center justify-center gap-2"
              >
                {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'ОБНОВИТЬ ПАРОЛЬ'}
                {state !== 'loading' && <ArrowRight className="w-4 h-4" />}
              </button>

              <button
                onClick={onDone}
                className="w-full mt-3 text-xs text-gray-500 hover:text-white font-mono tracking-wider py-2 transition"
              >
                Отмена — войти обычным способом
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Заглушка когда у стажёра нет привязанного курса
function NoCourseAssigned({ user, onLogout, navigate }) {
  const { courses } = useContent();

  // Покажем какие курсы доступны для бренда юзера (даже если роль не совпала)
  const norm = s => (s || '').toString().trim().toLowerCase().replace(/ё/g, 'е');
  const userBrand = norm((user.brands && user.brands[0]) || user.brand || '');
  const availableForBrand = (courses || []).filter(c => {
    const cb = norm(c.brand);
    if (cb === userBrand) return true;
    const b = BRANDS[userBrand];
    return b && norm(b.name) === cb;
  });

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <div className="max-w-md text-center bg-[#15171B] border border-[#FFB800]/40 rounded-2xl p-8 fade-in">
        <AlertTriangle className="w-12 h-12 text-[#FFB800] mx-auto mb-4" />
        <div className="font-display text-2xl tracking-wider text-white mb-2">КУРС НЕ НАЗНАЧЕН</div>
        <div className="text-sm text-gray-400 leading-relaxed mb-3">
          Для твоей должности <span className="text-white font-semibold">«{user.role || '(не указана)'}»</span> в бренде <span className="text-white font-semibold">«{BRANDS[userBrand]?.name || user.brand || '?'}»</span> курс ещё не создан.
        </div>

        {availableForBrand.length > 0 && (
          <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 mb-4 text-left">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-2">ДОСТУПНЫЕ КУРСЫ ПО ТВОЕМУ БРЕНДУ:</div>
            <div className="space-y-1">
              {availableForBrand.map(c => (
                <div key={c.id} className="text-xs text-gray-300">
                  • <span className="text-white">{c.title || c.name}</span>
                  {c.role && <span className="text-gray-500"> · должность: <span className="text-[#FFB800]">«{c.role}»</span></span>}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-gray-600 mt-3 font-mono leading-relaxed">
              Возможно у тебя в профиле указана должность чуть-чуть иначе, чем у курса (опечатка, лишний пробел). Проверь точное название должности у администратора.
            </div>
          </div>
        )}

        <div className="font-mono text-[10px] text-gray-600 tracking-wider mb-5">
          Попроси администратора создать курс или назначить тебе должность из&nbsp;списка выше.
        </div>

        <div className="flex flex-col gap-2">
          {navigate && (
            <button
              onClick={() => navigate('profile')}
              className="w-full bg-[#0A0B0D] hover:bg-[#2A2E36] border border-[#2A2E36] hover:border-[#FFB800] text-white text-xs font-mono tracking-wider px-4 py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              <Edit3 className="w-3.5 h-3.5" /> ОТКРЫТЬ ПРОФИЛЬ
            </button>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              className="w-full bg-[#0A0B0D] hover:bg-[#E83A2C]/10 border border-[#E83A2C]/40 hover:border-[#E83A2C] text-[#E83A2C] text-xs font-mono tracking-wider px-4 py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> ВЫЙТИ ИЗ АККАУНТА
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: DASHBOARD СТАЖЁРА
// ═══════════════════════════════════════════════════════════════

function AdminDashboardHero({ currentUser, trainees, notifications, navigate }) {
  const hour = new Date().getHours();
  const greeting = hour < 6 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер';
  const firstName = (currentUser?.name || '').split(' ')[0] || 'Админ';
  
  // Онлайн: заходили < 1 часа назад
  const now = Date.now();
  const onlineCount = trainees.filter(t => {
    const lastActive = t.lastActiveAt || t.lastLoginAt;
    return lastActive && (now - lastActive) < 3600000;
  }).length;
  
  // Непрочитанные сообщения
  const unreadMessages = notifications?.filter(n => !n.read && n.type === 'message').length || 0;
  
  // Новые за неделю
  const weekAgo = now - 7 * 24 * 3600000;
  const newThisWeek = trainees.filter(t => t.joinedAt && t.joinedAt > weekAgo).length;

  return (
    <div className="relative overflow-hidden rounded-2xl p-5 pop-in"
      style={{
        background: 'linear-gradient(135deg, rgba(255,184,0,0.08) 0%, rgba(232,58,44,0.06) 60%, rgba(0,0,0,0) 100%)',
        border: '1px solid rgba(255,184,0,0.15)',
      }}>
      {/* Декоративный glow */}
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.25) 0%, transparent 70%)', filter: 'blur(20px)' }} />
      <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(232,58,44,0.12) 0%, transparent 70%)', filter: 'blur(25px)' }} />

      <div className="relative flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.25em] mb-1 flex items-center gap-1.5">
            {greeting.toUpperCase()} · {new Date().toLocaleDateString('ru-RU', { weekday: 'long' }).toUpperCase()}
          </div>
          <h2 className="font-display text-2xl text-white tracking-wider mb-3 truncate">MAFIA ACADEMY</h2>

          {/* Мини-метрики */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 bg-[#15171B]/80 rounded-lg px-3 py-1.5 border border-[#2A2E36]">
              <Users className="w-3.5 h-3.5 text-[#00C781]" />
              <span className="text-xs text-gray-300">
                <span className="text-[#00C781] font-bold">{onlineCount}</span>
                <span className="text-gray-500 ml-1">онлайн</span>
              </span>
            </div>
            {unreadMessages > 0 && (
              <div className="flex items-center gap-1.5 bg-[#15171B]/80 rounded-lg px-3 py-1.5 border border-[#2A2E36]">
                <MessageSquare className="w-3.5 h-3.5 text-[#FFB800]" />
                <span className="text-xs text-gray-300">
                  <span className="text-[#FFB800] font-bold">{unreadMessages}</span>
                  <span className="text-gray-500 ml-1">новых сообщений</span>
                </span>
              </div>
            )}
            {newThisWeek > 0 && (
              <div className="flex items-center gap-1.5 bg-[#15171B]/80 rounded-lg px-3 py-1.5 border border-[#2A2E36]">
                <Sparkles className="w-3.5 h-3.5 text-[#3B82F6]" />
                <span className="text-xs text-gray-300">
                  <span className="text-[#3B82F6] font-bold">+{newThisWeek}</span>
                  <span className="text-gray-500 ml-1">за неделю</span>
                </span>
              </div>
            )}
          </div>

          {/* Быстрые действия */}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              onClick={() => navigate('adminMessages')}
              className="px-3 py-1.5 bg-[#FFB800]/15 border border-[#FFB800]/30 hover:border-[#FFB800] rounded-lg text-[10px] text-[#FFB800] font-mono tracking-wider transition flex items-center gap-1"
            >
              <Send className="w-3 h-3" /> СООБЩЕНИЯ
            </button>
            <button
              onClick={() => navigate('adminUsers')}
              className="px-3 py-1.5 bg-[#E83A2C]/15 border border-[#E83A2C]/30 hover:border-[#E83A2C] rounded-lg text-[10px] text-[#E83A2C] font-mono tracking-wider transition flex items-center gap-1"
            >
              <Users className="w-3 h-3" /> БАЗА СОТРУДНИКОВ
            </button>
            {currentUser?.isDirector && (
              <button
                onClick={() => navigate('directorTasks')}
                className="px-3 py-1.5 bg-[#00C781]/15 border border-[#00C781]/30 hover:border-[#00C781] rounded-lg text-[10px] text-[#00C781] font-mono tracking-wider transition flex items-center gap-1"
              >
                <CheckSquare className="w-3 h-3" /> ЗАДАЧИ
              </button>
            )}
          </div>
        </div>

        {/* Бейдж админа */}
        <div className="flex-shrink-0 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E83A2C] to-[#8B0F08] flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
            <Shield className="w-8 h-8 text-white relative z-10" />
          </div>
          <div className="font-mono text-[8px] text-gray-500 tracking-wider mt-1.5 uppercase">
            {currentUser?.isDirector ? 'Руководитель' : 'Админ'}
          </div>
        </div>
      </div>
    </div>
  );
}

// DashboardHero — hero-блок в верхней части дашборда стажёра.
// Приветствие зависит от времени суток, отдельно показывает стрик с мини-календарём.
function DashboardHero({ user, progress }) {
  const hour = new Date().getHours();
  const greeting = hour < 6 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер';
  const greetingEmoji = hour < 6 ? '🌙' : hour < 12 ? '☀️' : hour < 18 ? '👋' : '🌆';
  const firstName = (user.name || '').split(' ')[0] || user.name || 'Друг';
  const streak = progress?.streakDays || 0;
  const xp = progress?.xp || 0;
  const today = new Date(); today.setHours(0,0,0,0);

  // Построим визуализацию streak: 7 дней назад → сегодня.
  // Точная история не хранится, но если streak ≥ N, то последние N дней — активные.
  // Это аппроксимация но честная: streak=5 значит 5 дней подряд активности.
  const dayLabels = ['П','В','С','Ч','П','С','В'];
  const todayIdx = today.getDay() === 0 ? 6 : today.getDay() - 1;

  return (
    <div className="relative overflow-hidden rounded-2xl p-5 pop-in"
      style={{
        background: 'linear-gradient(135deg, rgba(255,184,0,0.08) 0%, rgba(232,58,44,0.06) 60%, rgba(0,0,0,0) 100%)',
        border: '1px solid rgba(255,184,0,0.15)',
      }}>
      {/* Декоративный glow */}
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.25) 0%, transparent 70%)', filter: 'blur(20px)' }} />

      <div className="relative flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.25em] mb-1 flex items-center gap-1.5">
            <span className="text-base leading-none">{greetingEmoji}</span> {greeting.toUpperCase()}
          </div>
          <h2 className="font-display text-2xl text-white tracking-wider mb-3 truncate">{firstName.toUpperCase()}</h2>

          {/* Мини-стрик-календарь — 7 дней */}
          <div className="flex items-center gap-2">
            <Flame className={`w-4 h-4 ${streak > 0 ? 'text-[#FF7849] icon-bounce' : 'text-gray-700'}`} />
            <div className="flex gap-1">
              {dayLabels.map((label, i) => {
                // i = 0 (Пн) ... 6 (Вс), todayIdx — сегодня
                const dayOffsetFromToday = todayIdx - i;  // 0 = сегодня, 1 = вчера, ...
                const isInStreak = dayOffsetFromToday >= 0 && dayOffsetFromToday < streak;
                const isToday = dayOffsetFromToday === 0;
                return (
                  <div key={i} className="flex flex-col items-center gap-0.5">
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center transition"
                      style={{
                        background: isInStreak ? (isToday ? '#FFB800' : 'rgba(255,120,73,0.7)') : 'rgba(42,46,54,0.5)',
                        boxShadow: isInStreak && isToday ? '0 0 8px rgba(255,184,0,0.6)' : 'none',
                      }}
                    >
                      {isInStreak && <Check className="w-3 h-3" style={{ color: isToday ? '#000' : '#fff' }} />}
                    </div>
                    <span className={`font-mono text-[8px] ${isToday ? 'text-[#FFB800] font-bold' : 'text-gray-600'}`}>{label}</span>
                  </div>
                );
              })}
            </div>
            <div className="ml-2 text-xs">
              {streak > 0 ? (
                <span className="text-[#FF7849] font-mono font-bold">{streak}</span>
              ) : (
                <span className="text-gray-600 font-mono text-[10px]">начни сегодня</span>
              )}
              {streak > 0 && <span className="text-gray-600 font-mono text-[10px] ml-1">{streak === 1 ? 'день' : 'дн'}</span>}
            </div>
          </div>
        </div>

        {/* XP бейдж справа */}
        <div className="flex-shrink-0 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFB800] via-[#FFC820] to-[#FFB800] flex items-center justify-center relative overflow-hidden glow-pulse">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
            <Zap className="w-7 h-7 text-black relative z-10" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
          </div>
          <div className="font-display text-lg text-white leading-none mt-1.5">
            <AnimatedCounter value={xp} duration={900} />
          </div>
          <div className="font-mono text-[8px] text-gray-500 tracking-wider mt-0.5">XP</div>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ user, progress, navigate, onLogout, users = [] }) {
  const { getCourseFor } = useContent();
  const course = getCourseFor(user);
  if (!course) return <NoCourseAssigned user={user} onLogout={onLogout} navigate={navigate} />;
  const overall = calcOverallProgress(progress, course);
  const rank = getRank(progress.xp);
  const nextRank = getNextRank(progress.xp);
  const status = getReadinessStatus(progress, course);
  const xpToNext = nextRank ? nextRank.minXp - progress.xp : 0;
  const xpProgress = nextRank ? ((progress.xp - rank.minXp) / (nextRank.minXp - rank.minXp)) * 100 : 100;

  // Текущий модуль (первый незавершённый)
  const currentModuleIdx = course.modules.findIndex(m => !progress.tests?.[m.id]?.passed);
  const currentModule = currentModuleIdx >= 0 ? course.modules[currentModuleIdx] : null;

  return (
    <div className="max-w-2xl mx-auto pb-24">
      {/* Header */}
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate('profile')} className="flex-shrink-0 rounded-xl overflow-hidden ring-1 ring-white/10" aria-label="Профиль">
              <Avatar user={user} size={44} />
            </button>
            <div className="min-w-0">
              <div className="font-bold text-white text-sm truncate">{user.name}</div>
              <div className="font-mono text-[10px] text-gray-500 tracking-wider flex items-center gap-1">
                <span style={{ color: rank.color }}>{rank.icon} {rank.title}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationCenter navigate={navigate} currentUserId={user.id} users={users} />
            <button onClick={onLogout} className="text-gray-600 hover:text-white p-2 min-touch flex items-center justify-center" aria-label="Выйти">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-5 py-5 space-y-5">
        {/* ─── HERO: приветствие + время дня + streak ─── */}
        <DashboardHero user={user} progress={progress} />

        {/* Большая карточка прогресса */}
        <div className="bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#2A2E36] rounded-2xl p-5 relative dossier-corner overflow-hidden fade-in">
          {/* Декоративные орбы фона */}
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(232,58,44,0.15) 0%, transparent 70%)', filter: 'blur(10px)' }} />
          <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${rank.color}20 0%, transparent 70%)`, filter: 'blur(15px)' }} />

          <div className="absolute inset-0 diagonal-stripes opacity-50 pointer-events-none"></div>

          <div className="relative">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-[10px] text-gray-500 tracking-[0.2em]">КОНТРАКТ № 001</div>
                <div className="font-display text-2xl tracking-wider text-white mt-1">{course.title.toUpperCase()}</div>
              </div>
              <StatusBadge status={status} />
            </div>

            <div className="flex items-end gap-3 mt-5">
              <div className="font-display text-6xl text-white leading-none">{overall}<span className="text-2xl text-gray-600">%</span></div>
              <div className="flex-1 pb-2">
                <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-1">АДАПТАЦИЯ</div>
                <div className="h-2 bg-[#0A0B0D] rounded-full overflow-hidden border border-[#2A2E36]">
                  <div
                    className="h-full bg-gradient-to-r from-[#E83A2C] to-[#FF6B5C] transition-all duration-500"
                    style={{ width: `${overall}%` }}
                  />
                </div>
              </div>
            </div>

            {/* XP bar — анимированный */}
            <div className="mt-5 pt-4 border-t border-[#2A2E36]">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-[#FFB800] icon-bounce" />
                  <span className="font-mono text-xs text-white">
                    <AnimatedCounter value={progress.xp} duration={900} /> XP
                  </span>
                </div>
                {nextRank && (
                  <span className="font-mono text-[10px] text-gray-500 tracking-wider">
                    ДО {nextRank.title}: <AnimatedCounter value={xpToNext} duration={500} />
                  </span>
                )}
              </div>
              <ProgressBar value={xpProgress} max={100} color="#FFB800" height={6} shimmer />
            </div>
          </div>
        </div>

        {/* Миссия дня */}
        {currentModule && (
          <div
            onClick={() => navigate('module', { moduleId: currentModule.id })}
            className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-5 relative cursor-pointer hover:border-[#E83A2C] transition group fade-in hover-lift"
          >
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-[#E83A2C]" />
              <span className="font-mono text-[10px] text-[#E83A2C] tracking-[0.2em]">МИССИЯ ДНЯ</span>
            </div>
            <div className="font-display text-xl text-white tracking-wider mb-1">{currentModule.title.toUpperCase()}</div>
            <div className="text-xs text-gray-500 mb-3">{currentModule.subtitle}</div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 font-mono text-[10px] text-gray-500 tracking-wider">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{currentModule.duration} МИН</span>
                <span className="flex items-center gap-1 text-[#FFB800]"><Zap className="w-3 h-3" />+{currentModule.xp} XP</span>
              </div>
              <ChevronRight className="w-5 h-5 text-[#E83A2C] group-hover:translate-x-1 transition" />
            </div>
          </div>
        )}

        {/* Список модулей */}
        <div>
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="font-display text-lg tracking-wider text-white">ОПЕРАЦИИ</h3>
            <span className="font-mono text-[10px] text-gray-600 tracking-wider">
              {course.modules.filter(m => progress.tests?.[m.id]?.passed).length} / {course.modules.length}
            </span>
          </div>
          <div className="space-y-3 relative">
            {course.modules.length > 1 && (
              /* Вертикальная линия-маршрут слева, по центру иконок модулей */
              <div className="absolute left-[26px] top-12 bottom-12 w-0.5 pointer-events-none" style={{
                background: 'linear-gradient(to bottom, #00C781 0%, #00C781 var(--route-progress, 0%), #2A2E36 var(--route-progress, 0%), #2A2E36 100%)',
                // CSS-переменная пересчитывается из доли пройденных модулей
                ['--route-progress']: `${Math.max(0, (course.modules.filter((m, i) => progress.tests?.[m.id]?.passed).length / course.modules.length) * 100)}%`,
              }} />
            )}
            {course.modules.length === 0 ? (
              <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center">
                <BookOpen className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                <div className="text-gray-400 text-sm mb-1">Курс пока пустой</div>
                <div className="text-xs text-gray-600 font-mono tracking-wider">МОДУЛИ ПОЯВЯТСЯ ПОСЛЕ ИХ СОЗДАНИЯ АДМИНОМ</div>
              </div>
            ) : course.modules.map((mod, idx) => {
              const unlocked = isModuleUnlocked(idx, progress, course);
              const moduleTest = progress.tests?.[mod.id];
              const passed = moduleTest?.passed;
              const lessonsDone = mod.lessons.filter(l => progress.lessons?.[l.id]).length;
              return (
                <ModuleCard
                  key={mod.id}
                  mod={mod}
                  idx={idx}
                  unlocked={unlocked}
                  passed={passed}
                  lessonsDone={lessonsDone}
                  totalLessons={mod.lessons.length}
                  testScore={moduleTest?.score}
                  onClick={() => unlocked && navigate('module', { moduleId: mod.id })}
                />
              );
            })}
          </div>
        </div>

        {/* Бейджи preview */}
        {progress.badges && progress.badges.length > 0 && (
          <div>
            <h3 className="font-display text-lg tracking-wider text-white mb-3 px-1">ДОСТИЖЕНИЯ</h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {progress.badges.map((bid, i) => {
                const b = BADGES[bid];
                if (!b) return null;
                return (
                  <div
                    key={bid}
                    className="flex-shrink-0 w-24 bg-[#15171B] border border-[#2A2E36] rounded-xl p-3 text-center hover-lift pop-in cursor-default"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <div className="text-2xl mb-1 glow-pulse">{b.icon}</div>
                    <div className="font-mono text-[9px] text-white tracking-wider">{b.title.toUpperCase()}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <BottomNav active="home" navigate={navigate} />
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    green: 'border-[#00C781] text-[#00C781] bg-[#00C781]/10',
    orange: 'border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10',
    blue: 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10',
    gray: 'border-gray-600 text-gray-500 bg-gray-900',
  };
  return (
    <div className={`px-2.5 py-1 rounded-md border font-mono text-[9px] tracking-[0.15em] ${colors[status.color]}`}>
      {status.label}
    </div>
  );
}

function ModuleCard({ mod, idx, unlocked, passed, lessonsDone, totalLessons, testScore, onClick }) {
  const totalSteps = totalLessons + 1;
  const doneSteps = lessonsDone + (passed ? 1 : 0);
  const pct = (doneSteps / totalSteps) * 100;
  const inProgress = unlocked && !passed && doneSteps > 0;

  return (
    <div
      onClick={onClick}
      className={`bg-[#15171B] border rounded-xl p-4 transition relative overflow-hidden hover-lift group ${
        unlocked
          ? passed
            ? 'border-[#00C781]/30 hover:border-[#00C781]/60 cursor-pointer'
            : 'border-[#2A2E36] hover:border-[#E83A2C] cursor-pointer'
          : 'border-[#15171B] opacity-50 cursor-not-allowed'
      } ${mod.critical ? 'border-l-2 border-l-[#E83A2C]' : ''} ${inProgress ? 'glow-border' : ''}`}
    >
      {passed && (
        <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,199,129,0.18) 0%, transparent 70%)' }} />
      )}
      <div className="flex items-center gap-3 relative">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 font-display text-lg transition-all ${
          passed ? 'bg-[#00C781]/20 text-[#00C781]' :
          unlocked ? 'bg-[#E83A2C]/20 text-[#E83A2C]' :
          'bg-[#2A2E36] text-gray-700'
        } ${passed ? 'group-hover:scale-110' : ''}`} style={{ transitionDuration: '0.25s' }}>
          {passed ? <Check className="w-5 h-5" /> : unlocked ? (idx + 1).toString().padStart(2, '0') : <Lock className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-white text-sm truncate">{mod.title}</div>
            {mod.critical && (
              <span className="font-mono text-[8px] text-[#E83A2C] border border-[#E83A2C] px-1 py-0.5 rounded tracking-wider">КРИТ</span>
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">{mod.subtitle}</div>
          {unlocked && (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1">
                <ProgressBar
                  value={pct}
                  max={100}
                  color={passed ? '#00C781' : '#E83A2C'}
                  height={4}
                  shimmer={inProgress}
                />
              </div>
              <span className="font-mono text-[9px] text-gray-500 tracking-wider flex-shrink-0">
                {doneSteps}/{totalSteps}
              </span>
            </div>
          )}
        </div>
        {unlocked && (
          <div className="text-right flex-shrink-0">
            {passed && testScore && (
              <div className="font-mono text-xs text-[#00C781] mb-0.5">{testScore}%</div>
            )}
            <ChevronRight className="w-4 h-4 text-gray-600 inline-block transition-transform group-hover:translate-x-1" style={{ transitionDuration: '0.2s' }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// НАВИГАЦИЯ: шляпа «на главную», меню и поиск по функциям
// ═══════════════════════════════════════════════════════════════
const NavUiContext = createContext({ openMenu: () => {}, menuOpen: false });
const useNavUi = () => useContext(NavUiContext);

const normSearch = (v) => String(v || '').toLowerCase().replace(/ё/g, 'е').trim();

// Реестр разделов с учётом ролей, прав и включённых модулей
function buildNavItems({ user, hasPerm, modules, course, progress, techCards, regulations }) {
  if (!user) return [];
  const items = [];
  const add = (it) => items.push(it);
  const on = (id) => modules?.[id]?.enabled !== false;

  if (user.isAdmin) {
    add({ id: 'home', group: 'Главное', label: 'Стажёры', hint: 'Прогресс и статусы команды', icon: Users, screen: 'admin', keywords: 'главная дашборд контроль стажеры список' });
    add({ id: 'content', group: 'Главное', label: 'Редактор контента', hint: 'Все разделы академии', icon: Database, screen: 'adminContent', keywords: 'контент хаб редактор' });
    if (hasPerm(user, 'canSendMessages')) add({ id: 'msg', group: 'Главное', label: 'Сообщения', hint: 'Чат со стажёрами', icon: MessageSquare, screen: 'adminMessages', keywords: 'чат написать переписка' });
    if (on('tasks')) {
      if (hasPerm(user, 'canEditAllTasks')) add({ id: 'dtasks', group: 'Главное', label: 'Управление задачами', hint: 'Поручения команде', icon: CheckSquare, screen: 'directorTasks', keywords: 'задачи поручения руководитель' });
      add({ id: 'mytasks', group: 'Главное', label: 'Мои задачи', hint: 'Поручения от руководителя', icon: ListChecks, screen: 'adminMyTasks', keywords: 'задачи мои поручения' });
    }
    if (hasPerm(user, 'canSeeStats')) add({ id: 'stats', group: 'Главное', label: 'Статистика', hint: 'Топы, ошибки, прогресс', icon: BarChart3, screen: 'adminStats', keywords: 'аналитика рейтинг цифры' });

    if (hasPerm(user, 'canEditCourses')) add({ id: 'courses', group: 'Контент', label: 'Курсы', hint: 'Модули, уроки, тесты', icon: BookOpen, screen: 'adminCourses', keywords: 'обучение программа уроки тесты модули' });
    if (on('techCards') && hasPerm(user, 'canEditTechCards')) add({ id: 'tc', group: 'Контент', label: 'Техкарты', hint: 'Меню кухни', icon: ChefHat, screen: 'adminTechCards', keywords: 'блюда рецепты меню кухня' });
    if (on('regulations') && hasPerm(user, 'canEditRegulations')) add({ id: 'regs', group: 'Контент', label: 'Регламенты и инструкции', hint: 'Права, обязанности, видеоинструкции', icon: Shield, screen: 'adminRegulations', keywords: 'инструкции видео должностные правила штрафы' });
    if (on('checklist') && hasPerm(user, 'canEditChecklists')) add({ id: 'cl', group: 'Контент', label: 'Чек-лист практики', hint: 'Шаблон практического допуска', icon: ClipboardCheck, screen: 'adminChecklistTpl', keywords: 'практика допуск чеклист' });
    if (on('contacts') && hasPerm(user, 'canEditContacts')) add({ id: 'contacts', group: 'Контент', label: 'Контакты', hint: 'Справочник телефонов', icon: Phone, screen: 'adminContacts', keywords: 'телефоны справочник' });
    if (on('exit') && hasPerm(user, 'canEditExitQuestions')) add({ id: 'exitq', group: 'Контент', label: 'Exit-анкета', hint: 'Вопросы при уходе', icon: FileText, screen: 'adminExitQuestions', keywords: 'увольнение уход анкета' });
    if (on('exit') && hasPerm(user, 'canSeeExitResponses')) add({ id: 'exitr', group: 'Контент', label: 'Ответы exit-анкеты', hint: 'База анкет ушедших', icon: Archive, screen: 'adminExitResponses', keywords: 'увольнение ответы ушедшие' });

    if (hasPerm(user, 'canEditUsers') || hasPerm(user, 'canCreateUsers')) add({ id: 'users', group: 'Система', label: 'Пользователи', hint: 'Команда и стажёры, доступы', icon: User, screen: 'adminUsers', keywords: 'сотрудники аккаунты добавить стажера пароль' });
    if (hasPerm(user, 'canEditSystemSettings')) {
      add({ id: 'settings', group: 'Система', label: 'Настройки регистрации', hint: 'Регистрация и доступ', icon: Settings, screen: 'adminSettings', keywords: 'настройки регистрация доступ' });
      add({ id: 'theme', group: 'Система', label: 'Тема и стартовый экран', hint: 'Оформление и тексты', icon: Palette, screen: 'adminStartScreen', keywords: 'тема цвет оформление новый год логин' });
    }
    if (isDevAccount(user)) {
      add({ id: 'dev', group: 'Система', label: 'Панель разработчика', hint: 'Права, роли, модули', icon: Activity, screen: 'devPanel', keywords: 'разработчик роли права модули' });
      add({ id: 'logs', group: 'Система', label: 'Журнал действий', hint: 'Кто и что менял', icon: FileText, screen: 'devPanel', ctx: { tab: 'logs' }, keywords: 'аудит логи история' });
    }
    return items;
  }

  // ─── Стажёр ───
  add({ id: 'home', group: 'Разделы', label: 'Главная', hint: 'Курс и миссия дня', icon: Home, screen: 'dashboard', keywords: 'курс операции прогресс' });
  if (on('techCards')) add({ id: 'tc', group: 'Разделы', label: 'Техкарты', hint: 'Рецепты и шаги блюд', icon: ChefHat, screen: 'techcards', keywords: 'блюда рецепты меню кухня карты' });
  if (on('regulations')) add({ id: 'regs', group: 'Разделы', label: 'Регламенты и инструкции', hint: 'Права, обязанности, видео', icon: Shield, screen: 'regulations', keywords: 'право правила инструкции видео штрафы' });
  add({ id: 'msg', group: 'Разделы', label: 'Сообщения', hint: 'Чат с руководителями', icon: MessageSquare, screen: 'messages', keywords: 'чат написать руководитель' });
  if (on('contacts')) add({ id: 'contacts', group: 'Разделы', label: 'Контакты', hint: 'Кому звонить на смене', icon: Phone, screen: 'contacts', keywords: 'телефоны позвонить' });
  add({ id: 'stats', group: 'Разделы', label: 'Рейтинг', hint: 'Моё место среди стажёров', icon: Trophy, screen: 'stats', keywords: 'рейтинг топ статистика место' });
  add({ id: 'profile', group: 'Разделы', label: 'Профиль', hint: 'Мои данные, достижения, уведомления', icon: User, screen: 'profile', keywords: 'досье данные телефон настройки уведомления звук' });
  if (on('exit')) add({ id: 'exit', group: 'Разделы', label: 'Exit-анкета', hint: 'Если планируете уйти', icon: FileText, screen: 'exitInterview', keywords: 'увольнение уход анкета' });

  (course?.modules || []).forEach((m, idx) => {
    const unlocked = progress ? isModuleUnlocked(idx, progress, course) : idx === 0;
    add({
      id: 'mod_' + m.id, group: 'Курс', label: m.title, hint: unlocked ? (progress?.tests?.[m.id]?.passed ? 'Сдан' : m.subtitle || 'Модуль') : 'Откроется после предыдущего модуля',
      icon: unlocked ? BookOpen : Lock, screen: unlocked ? 'module' : null, ctx: { moduleId: m.id },
      keywords: [m.subtitle, ...(m.lessons || []).map(l => l.title)].join(' '), searchOnly: true, disabled: !unlocked,
    });
  });
  if (on('techCards')) Object.values(techCards || {}).forEach(c => c?.name && add({
    id: 'card_' + c.id, group: 'Техкарты', label: c.name, hint: c.time || 'Техкарта', icon: ChefHat,
    screen: 'techcard', ctx: { techCardId: c.id, backTo: 'techcards' }, searchOnly: true,
  }));
  if (on('regulations')) (regulations || []).forEach(r => r?.title && add({
    id: 'reg_' + r.id, group: 'Регламенты', label: r.title, hint: (r.instructions || []).length ? `Инструкций: ${r.instructions.length}` : 'Регламент',
    icon: Shield, screen: 'regulations', ctx: { regId: r.id }, searchOnly: true,
  }));
  return items;
}

function CommandMenu({ open, onClose, items, navigate, homeScreen, onLogout, userName }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(''); setActive(0);
    // На телефоне не открываем клавиатуру сразу — меню чаще листают, чем ищут
    if (window.matchMedia('(hover: hover)').matches) setTimeout(() => inputRef.current?.focus(), 30);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const query = normSearch(q);
  const results = useMemo(() => {
    if (!query) return items.filter(i => !i.searchOnly);
    const words = query.split(/\s+/);
    return items
      .map(i => {
        const label = normSearch(i.label);
        const hay = normSearch(`${i.label} ${i.hint || ''} ${i.keywords || ''} ${i.group}`);
        if (!words.every(w => hay.includes(w))) return null;
        const score = (label.startsWith(query) ? 3 : 0) + (label.includes(query) ? 2 : 0) + (i.searchOnly ? 0 : 1);
        return { i, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 40)
      .map(x => x.i);
  }, [items, query]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const go = (it) => {
    if (!it || it.disabled || !it.screen) return;
    onClose();
    navigate(it.screen, it.ctx || {});
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(results.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const groups = [];
  results.forEach((it, idx) => {
    let g = groups.find(x => x.name === it.group);
    if (!g) { g = { name: it.group, list: [] }; groups.push(g); }
    g.list.push({ it, idx });
  });

  return (
    <div className="cmd-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onKeyDown={onKey}>
      <div className="cmd-panel" role="dialog" aria-modal="true" aria-label="Меню и поиск">
        <div className="sheet-grip sm:hidden" />
        <div className="flex items-center gap-3 px-4 pt-3 pb-3">
          <button type="button" className="hat-home-inline" onClick={() => { onClose(); navigate(homeScreen); }} aria-label="На главную">
            <FedoraHat className="w-7 h-7" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-gray-500">Вы вошли как</div>
            <div className="text-sm text-white font-semibold truncate">{userName}</div>
          </div>
          <button type="button" onClick={onClose} className="min-touch flex items-center justify-center rounded-xl text-gray-400 hover:text-white hover:bg-white/5" aria-label="Закрыть">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 pb-2">
          <div className="cmd-search">
            <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Найти раздел, блюдо, модуль…"
              className="cmd-input"
              enterKeyHint="go"
              aria-label="Поиск по функциям"
            />
            {q ? (
              <button type="button" onClick={() => { setQ(''); inputRef.current?.focus(); }} className="text-gray-500 hover:text-white p-1" aria-label="Очистить"><X className="w-4 h-4" /></button>
            ) : (
              <span className="kbd hidden sm:inline-flex">Ctrl K</span>
            )}
          </div>
        </div>

        <div className="cmd-list" ref={listRef}>
          {results.length === 0 && (
            <div className="px-6 py-10 text-center">
              <div className="text-sm text-gray-300">Ничего не нашлось по запросу «{q}»</div>
              <div className="text-xs text-gray-500 mt-1">Попробуйте другое слово: «инструкция», «рейтинг», «шаурма»</div>
            </div>
          )}
          {groups.map(g => (
            <div key={g.name} className="pb-1">
              <div className="cmd-group">{g.name}</div>
              {g.list.map(({ it, idx }) => {
                const Icon = it.icon || ChevronRight;
                return (
                  <button
                    key={it.id}
                    type="button"
                    data-idx={idx}
                    onMouseMove={() => setActive(idx)}
                    onClick={() => go(it)}
                    disabled={it.disabled}
                    className={`cmd-item ${idx === active ? 'is-active' : ''}`}
                  >
                    <span className="cmd-item-icon"><Icon className="w-4 h-4" /></span>
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block text-sm text-white truncate">{it.label}</span>
                      {it.hint && <span className="block text-xs text-gray-500 truncate">{it.hint}</span>}
                    </span>
                    {idx === active && !it.disabled && <CornerDownLeft className="w-3.5 h-3.5 text-gray-500 hidden sm:block" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="cmd-footer">
          <button type="button" onClick={() => { onClose(); onLogout(); }} className="text-sm text-gray-400 hover:text-[#E83A2C] flex items-center gap-2 px-2 py-2 rounded-lg">
            <LogOut className="w-4 h-4" /> Выйти
          </button>
          <span className="text-xs text-gray-600 hidden sm:block">↑↓ выбрать · Enter открыть · Esc закрыть</span>
        </div>
      </div>
    </div>
  );
}

// Кнопка-шляпа «на главную»
function HatHomeButton({ onClick, size = 'md', label = 'На главную', className = '' }) {
  return (
    <button type="button" onClick={onClick} className={`hat-btn hat-btn-${size} ${className}`} aria-label={label} title={label}>
      <FedoraHat className="hat-btn-svg" />
    </button>
  );
}

// Плавающая шляпа + поиск для экранов без нижней панели
function FloatingDock({ variant, onHome, onMenu, raised }) {
  if (variant === 'admin') {
    return (
      <div className={`admin-dock ${raised ? 'is-raised' : ''}`}>
        <button type="button" onClick={onMenu} className="admin-dock-search" aria-label="Меню и поиск">
          <Search className="w-4 h-4" />
          <span className="hidden sm:inline">Поиск и меню</span>
          <span className="kbd hidden md:inline-flex">Ctrl K</span>
        </button>
        <HatHomeButton onClick={onHome} size="lg" />
      </div>
    );
  }
  return (
    <div className="hat-float">
      <HatHomeButton onClick={onHome} size="sm" />
    </div>
  );
}

function BottomNav({ active, navigate }) {
  const { openMenu } = useNavUi();
  const notif = useNotifications();
  const { modules } = useContent();
  const unreadMsg = (notif?.notifications || []).filter(n => n.type === 'message' && !n.read).length;
  const on = (id) => modules?.[id]?.enabled !== false;
  // Разделы, которых нет на панели, подсвечивают «Меню»
  const inMenu = ['contacts', 'stats', 'profile'].includes(active);
  const Tab = ({ id, icon: Icon, label, screen, badge }) => {
    const isActive = id === active;
    return (
      <button
        type="button"
        onClick={() => navigate(screen)}
        className={`tabbar-item ${isActive ? 'is-active' : ''}`}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="relative">
          <Icon className="w-[22px] h-[22px]" />
          {badge > 0 && <span className="tabbar-badge">{badge > 9 ? '9+' : badge}</span>}
        </span>
        <span className="tabbar-label">{label}</span>
      </button>
    );
  };
  return (
    <nav className="tabbar" aria-label="Основная навигация">
      <div className="tabbar-inner">
        {on('techCards') ? <Tab id="cards" icon={ChefHat} label="Техкарты" screen="techcards" /> : <Tab id="stats" icon={Trophy} label="Рейтинг" screen="stats" />}
        {on('regulations') ? <Tab id="regs" icon={Shield} label="Регламент" screen="regulations" /> : <Tab id="profile" icon={User} label="Профиль" screen="profile" />}
        <div className="tabbar-hat-slot">
          <button
            type="button"
            onClick={() => navigate('dashboard')}
            className={`tabbar-hat ${active === 'home' ? 'is-active' : ''}`}
            aria-label="На главную"
          >
            <FedoraHat className="w-8 h-8" />
          </button>
          <span className={`tabbar-label ${active === 'home' ? 'text-[#FFB800]' : ''}`}>Главная</span>
        </div>
        <Tab id="messages" icon={MessageSquare} label="Чат" screen="messages" badge={unreadMsg} />
        <button type="button" onClick={openMenu} className={`tabbar-item ${inMenu ? 'is-active' : ''}`} aria-label="Меню и поиск">
          <Menu className="w-[22px] h-[22px]" />
          <span className="tabbar-label">Меню</span>
        </button>
      </div>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: МОДУЛЬ
// ═══════════════════════════════════════════════════════════════

function ModuleScreen({ user, moduleId, progress, navigate }) {
  const { getCourseFor } = useContent();
  const course = getCourseFor(user);
  // console.log('ModuleScreen course:', course?.id, 'moduleId:', moduleId);
  if (!course) return <NoCourseAssigned user={user} />;
  const mod = course.modules.find(m => m.id === moduleId);
  if (!mod) return null;

  const lessonsDone = mod.lessons.filter(l => progress.lessons?.[l.id]).length;
  const allLessonsDone = lessonsDone === mod.lessons.length;
  const testPassed = progress.tests?.[mod.id]?.passed;

  return (
    <div className="max-w-2xl mx-auto pb-24 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('dashboard')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К ОПЕРАЦИЯМ
        </button>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[10px] text-[#E83A2C] tracking-[0.2em]">МОДУЛЬ {mod.id.replace('m', '').padStart(2, '0')}</span>
          {mod.critical && (
            <span className="font-mono text-[9px] text-[#E83A2C] border border-[#E83A2C] px-1.5 py-0.5 rounded tracking-wider">КРИТИЧЕСКИЙ</span>
          )}
        </div>
        <h1 className="font-display text-3xl tracking-wider text-white">{mod.title.toUpperCase()}</h1>
        <p className="text-sm text-gray-500 mt-1">{mod.subtitle}</p>

        {/* ─── Навигационная лента уроков с прогрессом ─── */}
        {mod.lessons.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#15171B]">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[9px] text-gray-600 tracking-[0.2em]">ПРОГРЕСС МОДУЛЯ</span>
              <span className="font-mono text-[10px] text-white">
                {lessonsDone}/{mod.lessons.length}{testPassed ? ' + тест' : ''}
              </span>
            </div>
            {/* Полоса с точками-уроками */}
            <div className="relative flex items-center gap-1">
              {mod.lessons.map((lesson, i) => {
                const done = progress.lessons?.[lesson.id];
                return (
                  <button
                    key={lesson.id}
                    onClick={() => navigate('lesson', { moduleId: mod.id, lessonId: lesson.id })}
                    className="flex-1 h-1.5 rounded-full transition relative group"
                    style={{ background: done ? '#00C781' : '#2A2E36' }}
                    title={`${i + 1}. ${lesson.title}`}
                  >
                    {done && (
                      <div className="absolute inset-0 rounded-full" style={{
                        background: '#00C781',
                        boxShadow: '0 0 6px rgba(0,199,129,0.6)',
                      }} />
                    )}
                  </button>
                );
              })}
              {/* Точка теста в конце */}
              <div
                className="w-3 h-3 rounded-full flex-shrink-0 ml-1 transition"
                style={{
                  background: testPassed ? '#FFB800' : (allLessonsDone ? '#E83A2C' : '#2A2E36'),
                  boxShadow: testPassed ? '0 0 8px rgba(255,184,0,0.6)' : (allLessonsDone ? '0 0 6px rgba(232,58,44,0.6)' : 'none'),
                }}
                title={testPassed ? 'Тест пройден' : (allLessonsDone ? 'Готов к тесту' : 'Тест в конце модуля')}
              />
            </div>
          </div>
        )}
      </div>

      <div className="py-5 space-y-3">
        <div className="text-xs text-gray-600 font-mono tracking-wider mb-2">УРОКИ</div>

        {mod.lessons.length === 0 && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-6 text-center text-sm text-gray-500">
            В этом модуле пока нет уроков.
          </div>
        )}

        {mod.lessons.map((lesson, idx) => {
          const done = progress.lessons?.[lesson.id];
          return (
            <div
              key={lesson.id}
              onClick={() => navigate('lesson', { moduleId: mod.id, lessonId: lesson.id })}
              className={`bg-[#15171B] border rounded-xl p-4 cursor-pointer transition hover-lift group pop-in ${
                done ? 'border-[#00C781]/30' : 'border-[#2A2E36] hover:border-[#E83A2C]'
              }`}
              style={{ animationDelay: `${idx * 0.05}s` }}
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 font-mono text-xs transition-transform group-hover:scale-110 ${
                  done ? 'bg-[#00C781]/20 text-[#00C781]' : 'bg-[#0A0B0D] text-gray-500'
                }`} style={{ transitionDuration: '0.25s' }}>
                  {done ? <Check className="w-4 h-4" /> : (idx + 1).toString().padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">{lesson.title}</div>
                  <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-0.5 flex items-center gap-2 flex-wrap">
                    {lesson.type === 'techcard' && <ChefHat className="w-3 h-3" />}
                    {lesson.type === 'text' && <FileText className="w-3 h-3" />}
                    <span>{lesson.duration} МИН</span>
                    <span>·</span>
                    <span>{lesson.type === 'techcard' ? 'ТЕХКАРТА' : 'УРОК'}</span>
                    {lesson.videoUrl && (
                      <span className="flex items-center gap-0.5 text-[#E83A2C]">
                        <span>·</span>
                        <PlayCircle className="w-3 h-3" />
                        <span>ВИДЕО</span>
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0 transition-transform group-hover:translate-x-1" style={{ transitionDuration: '0.2s' }} />
              </div>
            </div>
          );
        })}

        {/* Test */}
        <div className="pt-4">
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-2">ЭКЗАМЕН ПО МОДУЛЮ</div>
          <div
            onClick={() => allLessonsDone && navigate('test', { moduleId: mod.id })}
            className={`rounded-xl p-5 border-2 transition relative overflow-hidden ${
              testPassed
                ? 'border-[#00C781]/40 bg-[#00C781]/5'
                : allLessonsDone
                  ? 'border-[#FFB800]/40 bg-[#FFB800]/5 cursor-pointer hover:border-[#FFB800]'
                  : 'border-[#2A2E36] bg-[#15171B] opacity-60 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                testPassed ? 'bg-[#00C781]/20 text-[#00C781]' :
                allLessonsDone ? 'bg-[#FFB800]/20 text-[#FFB800]' :
                'bg-[#2A2E36] text-gray-700'
              }`}>
                {testPassed ? <Check className="w-5 h-5" /> : allLessonsDone ? <Trophy className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <div className="font-display text-lg tracking-wider text-white">
                  {testPassed ? 'СДАН' : allLessonsDone ? 'ГОТОВ К СДАЧЕ' : 'ПРОЙДИ ВСЕ УРОКИ'}
                </div>
                <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-0.5">
                  {mod.test.questions.length} ВОПРОСОВ · ПРОХОДНОЙ {mod.test.passing}%
                  {testPassed && progress.tests[mod.id].score && ` · РЕЗУЛЬТАТ ${progress.tests[mod.id].score}%`}
                </div>
              </div>
              {(allLessonsDone || testPassed) && <ChevronRight className="w-5 h-5 text-gray-500" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: УРОК
// ═══════════════════════════════════════════════════════════════

function LessonScreen({ user, lessonCtx, progress, navigate, onComplete }) {
  const { getCourseFor } = useContent();
  const course = getCourseFor(user);
  if (!course) return <NoCourseAssigned user={user} />;
  const mod = course.modules.find(m => m.id === lessonCtx.moduleId);
  const lesson = mod?.lessons.find(l => l.id === lessonCtx.lessonId);
  if (!lesson) return null;

  // Если это техкарта — отдельный экран
  if (lesson.type === 'techcard') {
    return <TechCardScreen
      techCardId={lesson.techCardId}
      navigate={navigate}
      backTo="module"
      backCtx={{ moduleId: mod.id }}
      onComplete={() => onComplete(lesson.id)}
      isCompleted={!!progress.lessons?.[lesson.id]}
    />;
  }

  const isCompleted = !!progress.lessons?.[lesson.id];

  // ─── Прогресс чтения ───
  // Считает позицию scroll внутри статьи. Узкая полоса сверху, заливается по мере прокрутки.
  const [readProgress, setReadProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const winH = window.innerHeight;
      const docH = document.documentElement.scrollHeight - winH;
      if (docH <= 0) { setReadProgress(100); return; }
      const pct = Math.max(0, Math.min(100, (window.scrollY / docH) * 100));
      setReadProgress(pct);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="max-w-2xl mx-auto pb-32 fade-in">
      {/* Прогресс чтения — узкая полоса сверху */}
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-transparent z-50 pointer-events-none">
        <div
          className="h-full transition-[width] duration-150"
          style={{
            width: `${readProgress}%`,
            background: 'linear-gradient(90deg, #E83A2C 0%, #FFB800 100%)',
            boxShadow: '0 0 6px rgba(232,58,44,0.5)',
          }}
        />
      </div>

      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('module', { moduleId: mod.id })} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К МОДУЛЮ
        </button>
        <div className="font-mono text-[10px] text-gray-500 tracking-[0.2em]">{mod.title.toUpperCase()}</div>
        <h1 className="font-display text-2xl tracking-wider text-white mt-1">{lesson.title.toUpperCase()}</h1>
        {/* Метаданные урока: длительность, тип */}
        <div className="flex items-center gap-3 mt-2 font-mono text-[10px] text-gray-500 tracking-wider">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {lesson.duration} МИН</span>
          {lesson.type === 'text' && <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> УРОК</span>}
          {lesson.videoUrl && <span className="flex items-center gap-1 text-[#E83A2C]"><PlayCircle className="w-3 h-3" /> ВИДЕО</span>}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {lesson.videoUrl && (
          <div className="fade-in">
            <div className="flex items-center gap-2 mb-2 px-1">
              <PlayCircle className="w-4 h-4 text-[#E83A2C]" />
              <div className="font-mono text-[10px] text-[#E83A2C] tracking-[0.2em]">ВИДЕО</div>
            </div>
            <VideoPlayer url={lesson.videoUrl} />
          </div>
        )}

        {/* Фотографии к уроку — для ознакомления стажёров */}
        {Array.isArray(lesson.photos) && lesson.photos.length > 0 && (
          <LessonPhotosViewer photos={lesson.photos} />
        )}

        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-6 md:p-8 relative dossier-corner">
          <div className="prose prose-invert max-w-none">
            {lesson.content.split('\n\n').map((para, i) => (
              <p key={i} className="text-gray-200 leading-relaxed text-[16px] md:text-[17px] whitespace-pre-line mb-5 last:mb-0"
                style={{ lineHeight: 1.7, letterSpacing: '0.005em' }}>
                {para}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0B0D] to-transparent pt-8 px-5 safe-bottom">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => {
              if (!isCompleted) onComplete(lesson.id);
              navigate('module', { moduleId: mod.id });
            }}
            className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono tracking-wider py-3.5 rounded-lg glow-red flex items-center justify-center gap-2"
          >
            {isCompleted ? 'НАЗАД К МОДУЛЮ' : 'УРОК ПРОЙДЕН'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: ТЕХКАРТА
// ═══════════════════════════════════════════════════════════════

// StepsTimeline — таймлайн шагов рецепта с вертикальной линией и чек-боксами.
// Чекеные шаги — локально в state (не сохраняется в БД, это «инструмент в моменте»).
// При перепрохождении/повторном открытии чек-бокс сбрасывается.
function StepsTimeline({ steps, cardId }) {
  const [checked, setChecked] = useState({});
  useEffect(() => { setChecked({}); }, [cardId]);

  const toggle = (i) => setChecked(prev => ({ ...prev, [i]: !prev[i] }));
  const doneCount = Object.values(checked).filter(Boolean).length;
  const pct = steps.length > 0 ? (doneCount / steps.length) * 100 : 0;

  return (
    <div className="fade-in">
      {/* Header с прогрессом */}
      <div className="mb-3 flex items-center justify-between">
        <div className="font-mono text-[10px] text-gray-500 tracking-wider">
          ПОМЕЧЕНО {doneCount} / {steps.length}
        </div>
        <button
          onClick={() => setChecked({})}
          disabled={doneCount === 0}
          className="font-mono text-[10px] text-gray-600 hover:text-[#FFB800] tracking-wider transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <RotateCcw className="w-3 h-3" /> СБРОС
        </button>
      </div>
      {steps.length > 0 && (
        <div className="mb-4">
          <ProgressBar value={pct} max={100} color="#00C781" height={3} shimmer />
        </div>
      )}

      <div className="relative">
        {/* Вертикальная линия слева */}
        {steps.length > 1 && (
          <div className="absolute left-[18px] top-8 bottom-8 w-0.5 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, #00C781 0%, #00C781 ${pct}%, #2A2E36 ${pct}%, #2A2E36 100%)`,
              transition: 'background 0.4s ease',
            }}
          />
        )}

        <div className="space-y-2">
          {steps.map((step, i) => {
            const isChecked = !!checked[i];
            return (
              <button
                key={i}
                onClick={() => toggle(i)}
                className={`w-full text-left bg-[#15171B] border rounded-xl p-4 flex gap-3 hover-lift transition pop-in relative ${
                  isChecked ? 'border-[#00C781]/40 opacity-70' : 'border-[#2A2E36] hover:border-[#E83A2C]'
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-display text-sm border-2 transition relative z-10 ${
                  isChecked
                    ? 'bg-[#00C781] border-[#00C781] text-white'
                    : 'bg-[#0A0B0D] border-[#E83A2C] text-[#E83A2C]'
                }`} style={{ transitionDuration: '0.25s' }}>
                  {isChecked ? <Check className="w-4 h-4 bump" /> : (i + 1)}
                </div>
                <div className={`text-sm leading-relaxed pt-2 transition ${
                  isChecked ? 'text-gray-500 line-through decoration-[#00C781]/40' : 'text-gray-200'
                }`}>{step}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Финальная плашка когда все шаги проверены */}
      {doneCount === steps.length && steps.length > 0 && (
        <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[#00C781]/20 to-[#00C781]/5 border border-[#00C781]/40 pop-in text-center">
          <CheckCircle2 className="w-8 h-8 text-[#00C781] mx-auto mb-2 icon-bounce" />
          <div className="font-display text-base text-white tracking-wider">ВСЕ ШАГИ ВЫПОЛНЕНЫ</div>
          <div className="font-mono text-[10px] text-gray-400 tracking-wider mt-1">ОПЕРАЦИЯ ЗАВЕРШЕНА</div>
        </div>
      )}
    </div>
  );
}

function TechCardScreen({ techCardId, navigate, backTo, backCtx, onComplete, isCompleted }) {
  const { techCards } = useContent();
  const card = techCards[techCardId];
  const [tab, setTab] = useState('ingredients');

  const goBack = () => navigate(backTo, backCtx || {});

  if (!card) {
    return (
      <div className="max-w-2xl mx-auto p-5 fade-in">
        <button onClick={goBack} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-4 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НАЗАД
        </button>
        <div className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[#E83A2C] mx-auto mb-3" />
          <div className="font-display text-xl tracking-wider text-white mb-2">ТЕХКАРТА НЕДОСТУПНА</div>
          <div className="text-sm text-gray-400">Она была удалена или ещё не создана. Сообщи админу.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-32 fade-in">
      {/* Hero */}
      <div className="relative h-48 bg-gradient-to-br overflow-hidden" style={{
        backgroundImage: `linear-gradient(135deg, ${card.color}40, ${card.color}10)`
      }}>
        <div className="absolute inset-0 grain"></div>
        <div className="absolute inset-0 diagonal-stripes opacity-30"></div>
        <div className="absolute top-5 left-5 right-20 flex items-start justify-between pt-safe">
          <button onClick={goBack} className="text-white/70 hover:text-white text-sm flex items-center gap-1 font-mono tracking-wider">
            <ChevronLeft className="w-4 h-4" /> НАЗАД
          </button>
          <div className="flex items-center gap-1">
            {Array.from({length: card.level}).map((_,i) => (
              <Flame key={i} className="w-3 h-3 fill-[#FFB800] text-[#FFB800]" />
            ))}
          </div>
        </div>
        <div className="absolute bottom-5 left-5 right-5">
          <div className="font-mono text-[10px] text-white/60 tracking-[0.2em] mb-1">ТЕХНОЛОГИЧЕСКАЯ КАРТА</div>
          <h1 className="font-display text-4xl tracking-wider text-white">{card.name.toUpperCase()}</h1>
          <div className="flex items-center gap-4 mt-2 font-mono text-xs text-white/70">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{card.time}</span>
            <span>•</span>
            <span>{card.weight}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 pt-4">
        <div className="flex gap-1 bg-[#15171B] p-1 rounded-lg border border-[#2A2E36] mb-4 overflow-x-auto">
          {[
            { id: 'ingredients', label: 'СОСТАВ' },
            { id: 'steps', label: 'ШАГИ' },
            ...(card.videoUrl ? [{ id: 'video', label: '▶ ВИДЕО' }] : []),
            { id: 'mistakes', label: 'ОШИБКИ' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 min-w-fit py-2 px-2 rounded-md font-mono text-[10px] tracking-wider transition active:scale-95 ${
                tab === t.id ? 'bg-[#E83A2C] text-white' : 'text-gray-500 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'video' && card.videoUrl && (
          <div className="fade-in space-y-3">
            <VideoPlayer url={card.videoUrl} />
            <div className="bg-[#15171B] border border-[#E83A2C]/30 rounded-xl p-3 text-xs text-gray-400 leading-relaxed">
              Смотри внимательно. Запомни: <span className="text-white">скорость, последовательность, аккуратность</span>. Сначала посмотри целиком, потом повтори под админом.
            </div>
          </div>
        )}

        {tab === 'ingredients' && (
          <div className="space-y-2 fade-in">
            {card.ingredients.map((ing, i) => (
              <div key={i} className={`bg-[#15171B] border rounded-xl p-3 flex items-center justify-between pop-in hover-lift ${
                ing.critical ? 'border-[#E83A2C]/40' : 'border-[#2A2E36]'
              }`} style={{ animationDelay: `${i * 0.04}s` }}>
                <div className="flex items-center gap-3">
                  {ing.critical
                    ? <AlertCircle className="w-4 h-4 text-[#E83A2C] flex-shrink-0 icon-bounce" />
                    : <div className="w-1.5 h-1.5 rounded-full bg-gray-600 flex-shrink-0" />
                  }
                  <div>
                    <div className="text-sm text-white">{ing.name}</div>
                    {ing.critical && <div className="font-mono text-[9px] text-[#E83A2C] tracking-wider mt-0.5">КРИТИЧЕСКИЙ</div>}
                  </div>
                </div>
                <div className="font-mono text-sm text-[#FFB800] tracking-wider">{ing.grams}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'steps' && (
          <StepsTimeline steps={card.steps} cardId={card.id} />
        )}

        {tab === 'mistakes' && (
          <div className="space-y-2 fade-in">
            {card.mistakes.map((m, i) => (
              <div key={i} className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-[#E83A2C] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-[#E83A2C] text-sm mb-1">{m.title}</div>
                    <div className="text-xs text-gray-400 leading-relaxed">{m.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom action */}
      {onComplete && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D] to-transparent pt-8 px-5 safe-bottom">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => {
                if (!isCompleted) onComplete();
                goBack();
              }}
              className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono tracking-wider py-3.5 rounded-lg glow-red flex items-center justify-center gap-2"
            >
              {isCompleted ? 'НАЗАД К МОДУЛЮ' : 'ИЗУЧЕНО'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: СПИСОК ТЕХКАРТ
// ═══════════════════════════════════════════════════════════════

function TechCardsList({ navigate }) {
  const { techCards, kitchenCategories } = useContent();
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState(0);  // 0 = все, 1-5 = по уровню сложности
  const cards = Object.values(techCards);
  const filtered = cards.filter(c => {
    if (levelFilter > 0 && c.level !== levelFilter) return false;
    return c.name.toLowerCase().includes(search.toLowerCase());
  });

  // Стат-блок: сколько лёгких/средних/сложных блюд
  const easyCount = cards.filter(c => c.level <= 2).length;
  const medCount = cards.filter(c => c.level === 3).length;
  const hardCount = cards.filter(c => c.level >= 4).length;

  return (
    <div className="max-w-2xl mx-auto pb-24 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <h1 className="font-display text-3xl tracking-wider text-white flex items-center gap-2">
          <ChefHat className="w-7 h-7 text-[#E83A2C]" /> КАРТЫ КУХНИ
        </h1>
        <div className="font-mono text-[10px] text-gray-600 tracking-wider mt-1">{cards.length} БЛЮД В МЕНЮ</div>

        <div className="relative mt-4">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск блюда..."
            className="w-full bg-[#15171B] border border-[#2A2E36] rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:border-[#E83A2C] focus:outline-none transition"
          />
        </div>
      </div>

      <div className="py-5 space-y-3">
        {/* ─── Hero-блок с мини-статистикой по сложности ─── */}
        {cards.length > 0 && (
          <div className="relative overflow-hidden rounded-2xl p-4 pop-in"
            style={{
              background: 'linear-gradient(135deg, rgba(232,58,44,0.06) 0%, rgba(255,184,0,0.05) 100%)',
              border: '1px solid rgba(232,58,44,0.15)',
            }}>
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(232,58,44,0.18) 0%, transparent 70%)', filter: 'blur(20px)' }} />
            <div className="relative">
              <div className="font-mono text-[10px] text-[#E83A2C] tracking-[0.25em] mb-3 flex items-center gap-2">
                <Flame className="w-3 h-3" /> ОБЗОР МЕНЮ
              </div>
              <div className="grid grid-cols-3 gap-2">
                <LevelFilterPill
                  level={0}
                  active={levelFilter === 0}
                  onClick={() => setLevelFilter(0)}
                  count={cards.length}
                  label="всё"
                  color="#FFB800"
                  flames={null}
                />
                <LevelFilterPill
                  level={1}
                  active={levelFilter > 0 && levelFilter <= 2}
                  onClick={() => setLevelFilter(levelFilter === 1 || levelFilter === 2 ? 0 : 2)}
                  count={easyCount}
                  label="лёгкие"
                  color="#00C781"
                  flames={1}
                />
                <LevelFilterPill
                  level={4}
                  active={levelFilter === 4 || levelFilter === 5}
                  onClick={() => setLevelFilter(levelFilter >= 4 ? 0 : 4)}
                  count={hardCount}
                  label="сложные"
                  color="#E83A2C"
                  flames={3}
                />
              </div>
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-12 text-center pop-in">
            <ChefHat className="w-12 h-12 text-gray-700 mx-auto mb-3 float-anim" />
            <div className="text-gray-500 text-sm">{search ? `По запросу «${search}» ничего не найдено` : 'Меню пока пустое'}</div>
          </div>
        ) : filtered.map((card, idx) => (
          <div
            key={card.id}
            onClick={() => navigate('techcard', { techCardId: card.id, backTo: 'techcards' })}
            className="group relative bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#2A2E36] rounded-xl p-4 cursor-pointer hover:border-[color:var(--cardc)] transition hover-lift overflow-hidden pop-in"
            style={{ ['--cardc']: card.color || '#E83A2C', animationDelay: `${Math.min(idx, 10) * 0.04}s` }}
          >
            {/* Цветная полоса бренда слева */}
            <div className="absolute left-0 top-0 bottom-0 w-1 transition-opacity opacity-50 group-hover:opacity-100"
              style={{ background: card.color || '#E83A2C' }} />

            {/* Декоративный glow в углу при hover */}
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: `radial-gradient(circle, ${card.color || '#E83A2C'}25 0%, transparent 70%)`, filter: 'blur(15px)' }} />

            <div className="relative flex items-center gap-3 pl-2">
              {/* Иконка блюда с цветным фоном */}
              <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3"
                style={{ backgroundColor: (card.color || '#E83A2C') + '20', transitionDuration: '0.25s' }}>
                <ChefHat className="w-6 h-6" style={{ color: card.color || '#E83A2C' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white">{card.name}</div>
                <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {card.time}</span>
                  <span>·</span>
                  <span>{card.weight}</span>
                  <span>·</span>
                  <span className="flex items-center gap-0.5">
                    {Array.from({length: card.level}).map((_,i) => <Flame key={i} className="w-2.5 h-2.5 fill-[#FFB800] text-[#FFB800]" />)}
                    {Array.from({length: 5 - card.level}).map((_,i) => <Flame key={`g${i}`} className="w-2.5 h-2.5 text-gray-700" />)}
                  </span>
                  {Array.isArray(card.steps) && card.steps.length > 0 && (
                    <>
                      <span>·</span>
                      <span>{card.steps.length} ШАГОВ</span>
                    </>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600 group-hover:translate-x-1 group-hover:text-[color:var(--cardc)] transition"
                style={{ transitionDuration: '0.2s' }} />
            </div>
          </div>
        ))}
      </div>

      <BottomNav active="cards" navigate={navigate} />
    </div>
  );
}

// Маленькая пилюля для фильтра уровней в TechCardsList
function LevelFilterPill({ level, active, onClick, count, label, color, flames }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg p-2.5 transition border ${
        active
          ? 'border-[color:var(--c)] bg-[color:var(--c)]/15 shadow-lg shadow-[color:var(--c)]/20'
          : 'border-[#2A2E36] bg-[#0A0B0D]/60 hover:border-[color:var(--c)]/50'
      }`}
      style={{ ['--c']: color }}
    >
      <div className="font-display text-xl text-white leading-none mb-1">
        <AnimatedCounter value={count} duration={600} />
      </div>
      <div className="font-mono text-[8px] text-gray-500 tracking-wider uppercase flex items-center justify-center gap-0.5">
        {flames !== null && Array.from({ length: flames }).map((_, i) => (
          <Flame key={i} className="w-2 h-2" style={{ color, fill: color }} />
        ))}
        <span style={{ color: active ? color : undefined }}>{label}</span>
      </div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: ТЕСТ
// ═══════════════════════════════════════════════════════════════

function TestScreen({ user, moduleId, navigate, onComplete, progress: userProgress }) {
  const { getCourseFor } = useContent();
  const course = getCourseFor(user);
  const mod = course?.modules.find(m => m.id === moduleId);
  const test = mod?.test;
  // Хуки — строго до любых return (иначе React падает при смене курса)
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  // Перемешиваем вопросы один раз; для single/multi/situation также мешаем опции,
  // сохраняя маппинг shuffled→original index, чтобы корректно проверять правильность
  const [questions] = useState(() => {
    return [...(test?.questions || [])]
      .sort(() => Math.random() - 0.5)
      .map(q => {
        if ((q.type === 'single' || q.type === 'multi' || q.type === 'situation') && Array.isArray(q.options)) {
          // создаём индексы и перемешиваем
          const indices = q.options.map((_, i) => i).sort(() => Math.random() - 0.5);
          const shuffledOptions = indices.map(i => q.options[i]);
          // переиндексируем correct
          let newCorrect;
          if (q.type === 'multi') {
            newCorrect = q.correct.map(c => indices.indexOf(c));
          } else {
            newCorrect = indices.indexOf(q.correct);
          }
          return { ...q, options: shuffledOptions, correct: newCorrect };
        }
        return q;
      });
  });

  if (!course) return <NoCourseAssigned user={user} navigate={navigate} />;
  if (!mod) return null;

  if (questions.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-5 fade-in">
        <button onClick={() => navigate('module', { moduleId })} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-4 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К МОДУЛЮ
        </button>
        <div className="bg-[#15171B] border border-[#FFB800]/40 rounded-2xl p-8 text-center">
          <Trophy className="w-12 h-12 text-[#FFB800] mx-auto mb-3" />
          <div className="font-display text-xl tracking-wider text-white mb-2">ТЕСТ ПУСТОЙ</div>
          <div className="text-sm text-gray-400">Админ ещё не добавил вопросы. Возвращайся позже.</div>
        </div>
      </div>
    );
  }

  const q = questions[idx];
  const progress = ((idx + 1) / questions.length) * 100;

  const checkAnswer = (q, ans) => {
    if (ans === undefined) return false;
    if (q.type === 'single' || q.type === 'situation') return ans === q.correct;
    if (q.type === 'multi') {
      if (!Array.isArray(ans) || ans.length !== q.correct.length) return false;
      return q.correct.every(c => ans.includes(c));
    }
    if (q.type === 'truefalse') return ans === q.correct;
    if (q.type === 'order') {
      return JSON.stringify(ans) === JSON.stringify(q.correct);
    }
    if (q.type === 'match') {
      return q.pairs.every((pair, i) => ans?.[i] === pair[1]);
    }
    return false;
  };

  const submitAnswer = (val) => {
    setAnswers(prev => ({ ...prev, [idx]: val }));
  };

  const finish = () => {
    let correct = 0;
    questions.forEach((q, i) => { if (checkAnswer(q, answers[i])) correct++; });
    const score = Math.round((correct / questions.length) * 100);
    const perfect = score === 100;
    const passed = score >= test.passing;
    const prev = userProgress?.tests?.[moduleId];
    const courseCompleted = passed && course.modules.every(m => m.id === moduleId || userProgress?.tests?.[m.id]?.passed)
      && !prev?.passed;                           // празднуем закрытие курса один раз
    onComplete(moduleId, score, test.passing, perfect, course);
    setShowResult({
      score, correct, total: questions.length, perfect,
      scenario: pickCelebration({ passed, perfect, firstAttempt: !prev?.attempts, courseCompleted }),
    });
    window.scrollTo(0, 0);
  };

  if (showResult) {
    const modIdx = course.modules.findIndex(m => m.id === moduleId);
    const nextMod = course.modules[modIdx + 1] || null;
    return <TestResult result={showResult} questions={questions} answers={answers} checkAnswer={checkAnswer} mod={mod} navigate={navigate} nextMod={nextMod} />;
  }

  return (
    <div className="max-w-2xl mx-auto min-h-screen flex flex-col fade-in">
      {/* Top bar */}
      <div className="p-5 border-b border-[#15171B]">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate('module', { moduleId })} className="text-gray-500 hover:text-white text-sm font-mono tracking-wider flex items-center gap-1">
            <X className="w-4 h-4" /> ВЫЙТИ
          </button>
          <div className="font-mono text-xs text-gray-500 tracking-wider">
            <span className="text-white">{idx + 1}</span> / {questions.length}
          </div>
        </div>
        <ProgressBar value={progress} max={100} color="#E83A2C" height={4} shimmer />
      </div>

      {/* Question — key={idx} ре-монтирует контейнер при смене вопроса → срабатывает slide-in */}
      <div key={idx} className="flex-1 p-5 overflow-y-auto slide-in-right">
        <div className="font-mono text-[10px] text-[#E83A2C] tracking-[0.2em] mb-3">
          {q.type === 'single' && 'ОДИН ОТВЕТ'}
          {q.type === 'multi' && 'НЕСКОЛЬКО ОТВЕТОВ'}
          {q.type === 'truefalse' && 'ПРАВДА / ЛОЖЬ'}
          {q.type === 'order' && 'ПОРЯДОК ШАГОВ'}
          {q.type === 'match' && 'СОПОСТАВЛЕНИЕ'}
          {q.type === 'situation' && 'СИТУАЦИЯ'}
        </div>
        <h2 className="font-display text-2xl tracking-wide text-white leading-tight mb-6">{q.q}</h2>

        {(q.type === 'single' || q.type === 'situation') && (
          <div className="space-y-2">
            {q.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => submitAnswer(i)}
                className={`w-full text-left p-4 rounded-xl border transition ${
                  answers[idx] === i
                    ? 'bg-[#E83A2C]/20 border-[#E83A2C] text-white'
                    : 'bg-[#15171B] border-[#2A2E36] hover:border-gray-600 text-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center font-mono text-xs flex-shrink-0 ${
                    answers[idx] === i ? 'bg-[#E83A2C] text-white' : 'bg-[#0A0B0D] text-gray-500'
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </div>
                  <span className="text-sm">{opt}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {q.type === 'multi' && (
          <div className="space-y-2">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ВЫБЕРИ ВСЕ ВЕРНЫЕ</div>
            {q.options.map((opt, i) => {
              const arr = answers[idx] || [];
              const checked = arr.includes(i);
              return (
                <button
                  key={i}
                  onClick={() => {
                    submitAnswer(checked ? arr.filter(x => x !== i) : [...arr, i]);
                  }}
                  className={`w-full text-left p-4 rounded-xl border transition ${
                    checked
                      ? 'bg-[#E83A2C]/20 border-[#E83A2C] text-white'
                      : 'bg-[#15171B] border-[#2A2E36] hover:border-gray-600 text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      checked ? 'bg-[#E83A2C] border-[#E83A2C]' : 'border-gray-600'
                    }`}>
                      {checked && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm">{opt}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {q.type === 'truefalse' && (
          <div className="grid grid-cols-2 gap-3">
            {[true, false].map(v => (
              <button
                key={v.toString()}
                onClick={() => submitAnswer(v)}
                className={`p-6 rounded-xl border-2 transition ${
                  answers[idx] === v
                    ? v ? 'bg-[#00C781]/20 border-[#00C781]' : 'bg-[#E83A2C]/20 border-[#E83A2C]'
                    : 'bg-[#15171B] border-[#2A2E36] hover:border-gray-600'
                }`}
              >
                <div className={`text-4xl mb-2 ${
                  answers[idx] === v ? (v ? 'text-[#00C781]' : 'text-[#E83A2C]') : 'text-gray-600'
                }`}>
                  {v ? <Check className="w-12 h-12 mx-auto" /> : <X className="w-12 h-12 mx-auto" />}
                </div>
                <div className="font-display text-lg tracking-wider text-white">
                  {v ? 'ПРАВДА' : 'ЛОЖЬ'}
                </div>
              </button>
            ))}
          </div>
        )}

        {q.type === 'order' && (
          <OrderQuestion key={idx} items={q.items} value={answers[idx]} onChange={submitAnswer} />
        )}

        {q.type === 'match' && (
          <MatchQuestion pairs={q.pairs} value={answers[idx] || []} onChange={submitAnswer} />
        )}
      </div>

      {/* Footer */}
      <div className="p-5 border-t border-[#15171B] bg-[#0A0B0D]">
        <div className="flex gap-3">
          {idx > 0 && (
            <button
              onClick={() => setIdx(idx - 1)}
              className="px-6 bg-[#15171B] border border-[#2A2E36] hover:border-gray-600 text-gray-300 font-mono tracking-wider rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          {idx < questions.length - 1 ? (
            <button
              onClick={() => setIdx(idx + 1)}
              disabled={answers[idx] === undefined}
              className="flex-1 bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:bg-gray-800 disabled:text-gray-600 text-white font-mono tracking-wider py-3.5 rounded-lg disabled:glow-red disabled:shadow-none flex items-center justify-center gap-2"
            >
              ДАЛЬШЕ <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={answers[idx] === undefined}
              className="flex-1 bg-[#FFB800] hover:bg-[#FFC820] disabled:bg-gray-800 disabled:text-gray-600 text-black font-mono tracking-wider py-3.5 rounded-lg flex items-center justify-center gap-2 glow-gold disabled:shadow-none"
            >
              <Trophy className="w-4 h-4" /> СДАТЬ ЭКЗАМЕН
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ПОЗДРАВЛЕНИЯ ПОСЛЕ ТЕСТА — сценарии с частицами, звуком и вибрацией
// ═══════════════════════════════════════════════════════════════
const SFX_KEY = 'ma_sfx_enabled';
const sfxEnabled = () => { try { return localStorage.getItem(SFX_KEY) !== '0'; } catch { return true; } };
const setSfxEnabled = (on) => { try { localStorage.setItem(SFX_KEY, on ? '1' : '0'); } catch {} };

// Синтез коротких мелодий на WebAudio — никаких файлов
function playMelody(kind) {
  if (!sfxEnabled()) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0.18;
    master.connect(ctx.destination);
    const note = (freq, start, dur, type = 'triangle', vol = 1) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = freq;
      o.connect(g); g.connect(master);
      const t = ctx.currentTime + start;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(vol, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.start(t); o.stop(t + dur + 0.05);
    };
    const pop = (start) => {                      // «хлопок» салюта — короткий шум
      const len = Math.floor(ctx.sampleRate * 0.25);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
      const src = ctx.createBufferSource();
      const g = ctx.createGain();
      g.gain.value = 0.6;
      src.buffer = buf; src.connect(g); g.connect(master);
      src.start(ctx.currentTime + start);
    };
    const N = { C4: 261.6, E4: 329.6, G4: 392, A4: 440, C5: 523.3, D5: 587.3, E5: 659.3, G5: 784, A5: 880, C6: 1046.5, Eb4: 311.1, Bb3: 233.1, G3: 196 };
    let total = 1.5;
    if (kind === 'godfather') {
      // Фанфары + салют
      [[N.G4, 0], [N.C5, 0.12], [N.E5, 0.24], [N.G5, 0.36]].forEach(([f, t]) => note(f, t, 0.2, 'square', 0.5));
      note(N.C6, 0.5, 0.9, 'sawtooth', 0.35); note(N.G5, 0.5, 0.9, 'triangle', 0.6); note(N.E5, 0.5, 0.9, 'triangle', 0.6);
      [0.9, 1.3, 1.6, 2.1].forEach(pop);
      total = 2.8;
    } else if (kind === 'perfect') {
      [N.C5, N.E5, N.G5, N.C6, N.G5, N.C6].forEach((f, i) => note(f, i * 0.08, 0.25, 'triangle', 0.8));
      note(N.C6, 0.5, 0.8, 'sine', 0.5);
      total = 1.6;
    } else if (kind === 'firstTry') {
      note(N.C5, 0, 0.5); note(N.E5, 0, 0.5); note(N.G5, 0, 0.5);
      note(N.D5, 0.22, 0.2); note(N.G5, 0.3, 0.7); note(N.C6, 0.3, 0.7, 'sine', 0.6);
      pop(0.05);
    } else if (kind === 'passed') {
      [N.G4, N.A4, N.C5, N.E5].forEach((f, i) => note(f, i * 0.1, 0.3));
      note(N.C5, 0.45, 0.6, 'sine', 0.7);
    } else if (kind === 'failed') {
      note(N.Eb4, 0, 0.35, 'sine', 0.6); note(N.Bb3, 0.28, 0.6, 'sine', 0.5);
      total = 1.1;
    }
    setTimeout(() => ctx.close().catch(() => {}), total * 1000 + 400);
  } catch {}
}

// Canvas-частицы: конфетти, салют, «пушки» с боков, дождь из эмодзи и монет
function ParticleCanvas({ scenario, runKey }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    const W = () => window.innerWidth;
    const H = () => window.innerHeight;
    const GOLD = ['#FFB800', '#FFD24D', '#FFE9A8', '#E0A100'];
    const MIX = ['#E83A2C', '#FFB800', '#00C781', '#3B82F6', '#A855F7', '#FFFFFF'];
    const parts = [];
    const rnd = (a, b) => a + Math.random() * (b - a);

    const confetti = (x, y, n, colors, spread = Math.PI, dir = -Math.PI / 2, speed = [6, 14]) => {
      for (let i = 0; i < n; i++) {
        const a = dir + rnd(-spread / 2, spread / 2);
        const v = rnd(speed[0], speed[1]);
        parts.push({ kind: 'paper', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: 0.25, drag: 0.985,
          w: rnd(6, 11), h: rnd(3, 6), rot: rnd(0, 6.28), vr: rnd(-0.3, 0.3), color: colors[i % colors.length], life: rnd(160, 240) });
      }
    };
    const firework = (x, y) => {
      const colors = [MIX[Math.floor(rnd(0, MIX.length))], '#FFB800', '#FFFFFF'];
      const n = 70;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const v = rnd(2.5, 6.5);
        parts.push({ kind: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: 0.06, drag: 0.975,
          r: rnd(1.5, 2.8), color: colors[i % colors.length], life: rnd(60, 95), trail: [] });
      }
    };
    const rain = (glyphs, n, spanFrames = 90) => {
      for (let i = 0; i < n; i++) {
        parts.push({ kind: 'glyph', x: rnd(0, W()), y: rnd(-H() * 0.6, -30), vx: rnd(-0.6, 0.6), vy: rnd(2, 4.5), g: 0.03, drag: 1,
          size: rnd(20, 34), rot: rnd(-0.5, 0.5), vr: rnd(-0.03, 0.03), glyph: glyphs[i % glyphs.length], life: 260 + rnd(0, spanFrames), delay: Math.floor(rnd(0, spanFrames)) });
      }
    };
    const coins = (n) => {
      for (let i = 0; i < n; i++) {
        parts.push({ kind: 'coin', x: rnd(0, W()), y: rnd(-H(), -20), vx: rnd(-0.5, 0.5), vy: rnd(3, 6), g: 0.05, drag: 1,
          r: rnd(7, 11), spin: rnd(0, 6.28), vs: rnd(0.1, 0.25), life: 260, delay: Math.floor(rnd(0, 60)) });
      }
    };

    const timers = [];
    const at = (ms, fn) => timers.push(setTimeout(fn, ms));
    if (scenario === 'godfather') {
      [0, 350, 700, 1100, 1500, 1900].forEach((ms, i) => at(ms, () => firework(rnd(W() * 0.15, W() * 0.85), rnd(H() * 0.15, H() * 0.45))));
      at(200, () => { confetti(0, H() * 0.8, 70, GOLD, 0.9, -Math.PI / 3); confetti(W(), H() * 0.8, 70, GOLD, 0.9, -Math.PI * 2 / 3); });
      at(900, () => rain(['🎩', '💰', '🌹', '🥃'], 26));
    } else if (scenario === 'perfect') {
      coins(60);
      at(150, () => confetti(W() / 2, H() * 0.35, 120, GOLD, Math.PI * 2, 0, [3, 11]));
      at(700, () => firework(W() / 2, H() * 0.28));
    } else if (scenario === 'firstTry') {
      confetti(0, H() * 0.85, 90, MIX, 0.8, -Math.PI / 3, [10, 18]);
      confetti(W(), H() * 0.85, 90, MIX, 0.8, -Math.PI * 2 / 3, [10, 18]);
      at(500, () => confetti(W() / 2, H() * 0.3, 60, MIX, Math.PI * 2, 0, [2, 8]));
    } else if (scenario === 'passed') {
      rain(['🍕', '🌯', '🍣', '🎩', '🔥', '⭐'], 40);
      at(100, () => confetti(W() / 2, H() * 0.4, 70, MIX, Math.PI * 2, 0, [3, 10]));
    }

    let raf;
    let frame = 0;
    const tick = () => {
      frame++;
      ctx.clearRect(0, 0, W(), H());
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        if (p.delay && p.delay > 0) { p.delay--; continue; }
        p.vx *= p.drag; p.vy = p.vy * p.drag + p.g;
        p.x += p.vx; p.y += p.vy;
        p.life--;
        const alpha = Math.max(0, Math.min(1, p.life / 40));
        ctx.globalAlpha = alpha;
        if (p.kind === 'paper') {
          p.rot += p.vr;
          ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2 * Math.abs(Math.cos(p.rot * 2)), p.w, p.h * Math.abs(Math.cos(p.rot * 2)) + 0.5);
          ctx.restore();
        } else if (p.kind === 'spark') {
          p.trail.push([p.x, p.y]); if (p.trail.length > 6) p.trail.shift();
          ctx.strokeStyle = p.color; ctx.lineWidth = p.r; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(p.trail[0][0], p.trail[0][1]);
          for (const [tx, ty] of p.trail) ctx.lineTo(tx, ty);
          ctx.stroke();
        } else if (p.kind === 'glyph') {
          p.rot += p.vr;
          ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.font = `${p.size}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(p.glyph, 0, 0);
          ctx.restore();
        } else if (p.kind === 'coin') {
          p.spin += p.vs;
          const sx = Math.abs(Math.cos(p.spin));
          ctx.save(); ctx.translate(p.x, p.y); ctx.scale(Math.max(0.15, sx), 1);
          const grd = ctx.createRadialGradient(-p.r / 3, -p.r / 3, 1, 0, 0, p.r);
          grd.addColorStop(0, '#FFF2B8'); grd.addColorStop(0.5, '#FFB800'); grd.addColorStop(1, '#A87800');
          ctx.fillStyle = grd;
          ctx.beginPath(); ctx.arc(0, 0, p.r, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        }
        if (p.life <= 0 || p.y > H() + 60) parts.splice(i, 1);
      }
      ctx.globalAlpha = 1;
      if (parts.length || frame < 150) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, W(), H());
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', resize);
    };
  }, [scenario, runKey]);
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none z-[10000]" style={{ width: '100vw', height: '100vh' }} aria-hidden="true" />;
}

// Выбор сценария по результату
function pickCelebration({ passed, perfect, firstAttempt, courseCompleted }) {
  if (!passed) return 'failed';
  if (courseCompleted) return 'godfather';
  if (perfect) return 'perfect';
  if (firstAttempt) return 'firstTry';
  return 'passed';
}

const CELEBRATION_COPY = {
  godfather: { stamp: 'Добро пожаловать в семью', line: 'Все операции закрыты. Теперь вы свой человек на смене.', color: '#FFB800' },
  perfect:   { stamp: 'Чистая работа', line: 'Ни одной ошибки. Дон бы одобрил.', color: '#FFB800' },
  firstTry:  { stamp: 'С первого захода', line: 'Сдано с первой попытки — так держать.', color: '#00C781' },
  passed:    { stamp: 'Операция выполнена', line: 'Экзамен сдан. Идём дальше.', color: '#00C781' },
  failed:    { stamp: 'Не в этот раз', line: 'Разберите ошибки ниже и попробуйте снова — семья подождёт.', color: '#E83A2C' },
};

function Celebration({ scenario }) {
  const [runKey, setRunKey] = useState(0);
  const [sound, setSound] = useState(sfxEnabled());
  useEffect(() => {
    const t = setTimeout(() => {
      playMelody(scenario);
      try {
        if (navigator.vibrate) navigator.vibrate(scenario === 'failed' ? [60, 60, 60] : scenario === 'godfather' ? [40, 40, 40, 40, 120] : [30, 40, 60]);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [scenario, runKey]);

  const replay = () => setRunKey(k => k + 1);
  const toggleSound = () => { const next = !sound; setSound(next); setSfxEnabled(next); };

  return (
    <>
      {scenario !== 'failed' && <ParticleCanvas scenario={scenario} runKey={runKey} />}
      <div className="flex items-center justify-center gap-2 mt-4">
        {scenario !== 'failed' && (
          <button type="button" onClick={replay} className="celebrate-chip">
            <Sparkles className="w-3.5 h-3.5" /> Ещё раз
          </button>
        )}
        <button type="button" onClick={toggleSound} className="celebrate-chip" aria-pressed={sound}>
          {sound ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} {sound ? 'Звук включён' : 'Без звука'}
        </button>
      </div>
    </>
  );
}

function OrderQuestion({ items, value, onChange }) {
  // Начальный порядок перемешиваем один раз; при возврате к вопросу берём сохранённый
  const [order, setOrder] = useState(() => (Array.isArray(value) && value.length === items.length)
    ? value
    : items.map((_, i) => i).sort(() => Math.random() - 0.5));

  useEffect(() => { onChange(order); /* eslint-disable-next-line */ }, [order]);

  const move = (i, dir) => {
    const ni = i + dir;
    if (ni < 0 || ni >= order.length) return;
    const next = [...order];
    [next[i], next[ni]] = [next[ni], next[i]];
    setOrder(next);
  };

  return (
    <div className="space-y-2">
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">РАССТАВЬ В ПРАВИЛЬНОМ ПОРЯДКЕ</div>
      {order.map((origIdx, i) => (
        <div key={origIdx} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E83A2C] flex items-center justify-center font-display text-sm text-white flex-shrink-0">
            {i + 1}
          </div>
          <div className="flex-1 text-sm text-white">{items[origIdx]}</div>
          <div className="flex flex-col gap-1">
            <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Выше" className="w-10 h-8 flex items-center justify-center bg-[#0A0B0D] rounded-lg hover:bg-[#2A2E36] disabled:opacity-30">
              <ArrowLeft className="w-3 h-3 text-gray-400 rotate-90" />
            </button>
            <button onClick={() => move(i, 1)} disabled={i === order.length - 1} aria-label="Ниже" className="w-10 h-8 flex items-center justify-center bg-[#0A0B0D] rounded-lg hover:bg-[#2A2E36] disabled:opacity-30">
              <ArrowLeft className="w-3 h-3 text-gray-400 -rotate-90" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchQuestion({ pairs, value, onChange }) {
  const allValues = pairs.map(p => p[1]);
  const [matches, setMatches] = useState(value.length === pairs.length ? value : Array(pairs.length).fill(null));

  const setMatch = (i, val) => {
    const next = [...matches];
    next[i] = val;
    setMatches(next);
    onChange(next);
  };

  return (
    <div>
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ВЫБЕРИ ПОДХОДЯЩЕЕ ЗНАЧЕНИЕ</div>
      <div className="space-y-2">
        {pairs.map((pair, i) => (
          <div key={i} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-3 flex items-center gap-2">
            <div className="flex-1 text-sm text-white">{pair[0]}</div>
            <ArrowRight className="w-4 h-4 text-gray-600" />
            <select
              value={matches[i] || ''}
              onChange={e => setMatch(i, e.target.value)}
              className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg px-3 py-2 text-sm text-[#FFB800] font-mono focus:border-[#E83A2C] focus:outline-none"
            >
              <option value="">— выбери —</option>
              {allValues.map((v, j) => (
                <option key={j} value={v}>{v}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

// CircularScore — анимированный круговой прогресс для финального экрана теста.
// SVG с двумя кругами: фоновый серый + цветной с анимацией stroke-dashoffset.
// Цвет зависит от passed/perfect. Внутри — большое число с AnimatedCounter.
function CircularScore({ score, passing, passed, perfect, size = 180 }) {
  const radius = (size - 24) / 2;
  const circumference = 2 * Math.PI * radius;
  const [displayScore, setDisplayScore] = useState(0);
  const color = perfect ? '#FFB800' : passed ? '#00C781' : '#E83A2C';

  // Анимация заполнения через CSS — управляем stroke-dashoffset.
  // При первом монтировании плавно заполняется от 0 до score.
  const [strokeOffset, setStrokeOffset] = useState(circumference);
  useEffect(() => {
    const t = setTimeout(() => {
      setStrokeOffset(circumference * (1 - score / 100));
      setDisplayScore(score);
    }, 100);
    return () => clearTimeout(t);
  }, [score, circumference]);

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Фоновый круг */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#2A2E36"
          strokeWidth="10"
        />
        {/* Прогресс */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeOffset}
          style={{
            transition: 'stroke-dashoffset 1.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            filter: `drop-shadow(0 0 6px ${color}80)`,
          }}
        />
        {/* Метка проходного балла — маленькая точка */}
        {passing > 0 && passing < 100 && (
          <circle
            cx={size / 2 + Math.cos((passing / 100) * 2 * Math.PI - Math.PI / 2) * radius}
            cy={size / 2 + Math.sin((passing / 100) * 2 * Math.PI - Math.PI / 2) * radius}
            r="3"
            fill="white"
            opacity="0.5"
          />
        )}
      </svg>
      {/* Центральное число */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-6xl text-white leading-none">
          <AnimatedCounter value={displayScore} duration={1300} />
        </div>
        <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-1">%</div>
      </div>
    </div>
  );
}

function TestResult({ result, questions, answers, checkAnswer, mod, navigate, nextMod }) {
  const passed = result.score >= mod.test.passing;
  const scenario = result.scenario || (passed ? (result.perfect ? 'perfect' : 'passed') : 'failed');
  const copy = CELEBRATION_COPY[scenario];

  return (
    <div className="max-w-2xl mx-auto pb-32 fade-in">
      <div className="p-5 pt-safe-5">
        {/* Hero */}
        <div className={`rounded-2xl p-6 text-center border-2 mb-5 relative overflow-hidden ${
          passed
            ? result.perfect
              ? 'border-[#FFB800] bg-gradient-to-br from-[#FFB800]/20 to-[#FFB800]/5 glow-gold'
              : 'border-[#00C781] bg-gradient-to-br from-[#00C781]/20 to-[#00C781]/5 glow-green'
            : 'border-[#E83A2C] bg-gradient-to-br from-[#E83A2C]/20 to-[#E83A2C]/5 shake'
        }`}>
          <div className="absolute inset-0 grain"></div>
          <div className="relative">
            {scenario === 'godfather' && (
              <div className="hat-drop mx-auto mb-2"><FedoraHat className="w-16 h-16" color="#FFB800" /></div>
            )}
            <div className="celebrate-stamp mb-4" style={{ '--stamp': copy.color }}>
              {copy.stamp}
            </div>

            {/* ─── Круговой анимированный прогресс ─── */}
            <CircularScore
              score={result.score}
              passing={mod.test.passing}
              passed={passed}
              perfect={result.perfect}
              animationKey="testresult"
            />

            <div className="font-mono text-xs text-gray-400 tracking-wider mt-3">
              ВЕРНО {result.correct} ИЗ {result.total} · ПРОХОДНОЙ {mod.test.passing}%
            </div>
            <p className="text-sm text-gray-300 mt-3 max-w-xs mx-auto leading-relaxed">{copy.line}</p>
            {result.perfect && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFB800]/20 border border-[#FFB800] rounded-lg pop-in">
                <Sparkles className="w-3 h-3 text-[#FFB800] icon-bounce" />
                <span className="font-mono text-[10px] text-[#FFB800] tracking-wider">+200 XP БОНУС</span>
              </div>
            )}
            <Celebration scenario={scenario} />
          </div>
        </div>

        {/* Разбор ошибок */}
        <div className="text-xs text-gray-600 font-mono tracking-wider mb-3">РАЗБОР ОТВЕТОВ</div>
        <div className="space-y-2">
          {questions.map((q, i) => {
            const isCorrect = checkAnswer(q, answers[i]);
            return (
              <div key={i} className={`bg-[#15171B] border rounded-xl p-4 ${
                isCorrect ? 'border-[#00C781]/30' : 'border-[#E83A2C]/30'
              }`}>
                <div className="flex items-start gap-2 mb-2">
                  {isCorrect ? <CheckCircle2 className="w-4 h-4 text-[#00C781] flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-[#E83A2C] flex-shrink-0 mt-0.5" />}
                  <div className="text-sm text-white flex-1">{q.q}</div>
                </div>
                {!isCorrect && (
                  <div className="text-xs text-gray-400 ml-6 mt-2">
                    {q.type === 'single' || q.type === 'situation' ? (
                      <span>Правильный ответ: <span className="text-[#00C781]">{q.options[q.correct]}</span></span>
                    ) : q.type === 'truefalse' ? (
                      <span>Правильный ответ: <span className="text-[#00C781]">{q.correct ? 'ПРАВДА' : 'ЛОЖЬ'}</span></span>
                    ) : q.type === 'multi' ? (
                      <span>Верные варианты: <span className="text-[#00C781]">{q.correct.map(c => q.options[c]).join(', ')}</span></span>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D] to-transparent pt-8 px-5 safe-bottom z-20">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => {
              if (!passed) navigate('test', { moduleId: mod.id, retry: Date.now() });
              else if (nextMod) navigate('module', { moduleId: nextMod.id });
              else navigate('dashboard');
            }}
            className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono tracking-wider py-3.5 rounded-lg glow-red flex items-center justify-center gap-2"
          >
            {!passed ? 'ПЕРЕСДАТЬ' : nextMod ? 'К СЛЕДУЮЩЕЙ ОПЕРАЦИИ' : 'НА ГЛАВНУЮ'}
            <ArrowRight className="w-4 h-4" />
          </button>
          {!passed && (
            <button onClick={() => navigate('module', { moduleId: mod.id })} className="w-full mt-2 py-2.5 text-sm text-gray-400 hover:text-white">
              Повторить уроки модуля
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ЭКРАН: ПРОФИЛЬ
// ═══════════════════════════════════════════════════════════════

function ProfileScreen({ user, progress, navigate, onLogout, users, updateUsers, showToast }) {
  const { getCourseFor, modules } = useContent();
  const course = getCourseFor(user);
  const [messages, setMessages] = useState([]);
  const [showInbox, setShowInbox] = useState(false);

  // Загружаем сообщения от админа
  useEffect(() => {
    (async () => {
      const msgs = (await safeGet(STORAGE_KEYS.MESSAGES(user.id))) || [];
      setMessages(msgs);
    })();
  }, [user.id]);

  const unreadCount = messages.filter(m => !m.read).length;

  const markAllRead = async () => {
    const updated = messages.map(m => ({ ...m, read: true }));
    setMessages(updated);
    await safeSet(STORAGE_KEYS.MESSAGES(user.id), updated);
  };

  const updateMe = async (patch) => {
    if (!updateUsers || !users) return;
    const next = users.map(u => u.id === user.id ? { ...u, ...patch } : u);
    await updateUsers(next);
  };

  if (!course) {
    return (
      <div className="max-w-2xl mx-auto pb-24 fade-in">
        <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
          <h1 className="font-display text-3xl tracking-wider text-white">ДОСЬЕ</h1>
        </div>
        <div className="p-5">
          <PersonalDataCard user={user} updateMe={updateMe} />
        </div>
        <BottomNav active="profile" navigate={navigate} />
      </div>
    );
  }
  const rank = getRank(progress.xp);
  const status = getReadinessStatus(progress, course);
  const overall = calcOverallProgress(progress, course);
  const testsP = course.modules.filter(m => progress.tests?.[m.id]?.passed).length;
  const avgScore = (() => {
    const scores = course.modules.map(m => progress.tests?.[m.id]?.score).filter(Boolean);
    if (!scores.length) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  })();
  const daysIn = Math.floor((Date.now() - progress.startedAt) / (1000 * 60 * 60 * 24));

  return (
    <div className="max-w-2xl mx-auto pb-24 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl tracking-wider text-white">ДОСЬЕ</h1>
          {messages.length > 0 && (
            <button
              onClick={() => { setShowInbox(s => !s); if (unreadCount > 0) markAllRead(); }}
              className="relative bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] px-3 py-1.5 rounded-lg flex items-center gap-2 transition font-mono text-xs tracking-wider"
            >
              <Bell className="w-3.5 h-3.5" /> СООБЩЕНИЯ
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#E83A2C] text-white text-[9px] font-mono rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="py-5 space-y-5">
        {/* Inbox от админа */}
        {showInbox && messages.length > 0 && (
          <div className="bg-[#15171B] border border-[#FFB800]/40 rounded-2xl p-4 space-y-2 fade-in">
            <div className="font-mono text-[10px] text-[#FFB800] tracking-wider">СООБЩЕНИЯ ОТ АДМИНА</div>
            {[...messages].reverse().map(m => (
              <div key={m.id} className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs text-gray-400 font-mono">{m.fromName || 'админ'}</div>
                  <div className="text-[10px] text-gray-600 font-mono">{new Date(m.at).toLocaleString('ru')}</div>
                </div>
                <div className="text-sm text-white whitespace-pre-line leading-relaxed">{m.text}</div>
              </div>
            ))}
          </div>
        )}

        {/* Аватар + основные */}
        <div className="bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#2A2E36] rounded-2xl p-6 relative dossier-corner overflow-hidden">
          <div className="absolute inset-0 diagonal-stripes opacity-50 pointer-events-none"></div>
          <div className="relative">
            <div className="flex items-start gap-4">
              <AvatarUpload
                user={user}
                size={80}
                onChange={async (dataUrl) => { await updateMe({ avatar: dataUrl }); showToast && showToast(dataUrl ? 'Аватар обновлён' : 'Аватар удалён'); }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-gray-500 tracking-[0.2em] mb-1">ФИО</div>
                <InlineText
                  value={user.name}
                  onChange={v => updateMe({ name: v })}
                  placeholder="Иванов Иван Иванович"
                  size="2xl"
                  className="font-display text-white tracking-wider block"
                />
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="font-mono text-xs px-2 py-1 rounded border" style={{ color: rank.color, borderColor: rank.color + '60' }}>
                    {rank.icon} {rank.title}
                  </span>
                  <StatusBadge status={status} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-[#2A2E36]">
              <Stat label="XP" value={progress.xp} accent="#FFB800" />
              <Stat label="ПРОГРЕСС" value={`${overall}%`} accent="#E83A2C" />
              <Stat label="ДНЕЙ В БАЗЕ" value={Math.max(1, daysIn)} accent="#00E5FF" />
            </div>
          </div>
        </div>

        {/* Личные данные — inline-редактирование */}
        <PersonalDataCard user={user} updateMe={updateMe} />

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-1">СДАНО ТЕСТОВ</div>
            <div className="font-display text-3xl text-white">{testsP}<span className="text-lg text-gray-600">/{course.modules.length}</span></div>
          </div>
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-1">СРЕДНИЙ БАЛЛ</div>
            <div className="font-display text-3xl text-[#FFB800]">{avgScore}<span className="text-lg text-gray-600">%</span></div>
          </div>
        </div>

        {/* Бейджи */}
        <div>
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-3">КОЛЛЕКЦИЯ ДОСТИЖЕНИЙ</div>
          <div className="grid grid-cols-3 gap-3">
            {Object.values(BADGES).map(b => {
              const earned = progress.badges?.includes(b.id);
              return (
                <div key={b.id} className={`bg-[#15171B] border rounded-xl p-3 text-center transition ${
                  earned ? 'border-[#FFB800]/40' : 'border-[#15171B] opacity-30'
                }`}>
                  <div className={`text-3xl mb-1 ${earned ? 'glow-pulse' : 'grayscale'}`}>{b.icon}</div>
                  <div className="font-mono text-[9px] text-white tracking-wider">{b.title.toUpperCase()}</div>
                  <div className="text-[9px] text-gray-500 mt-0.5">{b.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Exit-интервью — только если модуль включён */}
        {modules?.exit?.enabled && (
          <button
            onClick={() => navigate('exitInterview')}
            className="w-full bg-[#15171B] hover:bg-[#1F0A0A] border border-[#2A2E36] hover:border-[#E83A2C] text-gray-400 hover:text-white py-3 rounded-lg font-mono text-xs tracking-wider transition flex items-center justify-center gap-2"
          >
            <FileText className="w-3.5 h-3.5" /> ЗАПОЛНИТЬ EXIT-ИНТЕРВЬЮ
          </button>
        )}

        {/* ─── Настройки уведомлений ─── */}
        <NotificationSettingsBlock />

        {/* Logout */}
        <button onClick={onLogout} className="w-full bg-[#15171B] hover:bg-[#1F0A0A] border border-[#2A2E36] hover:border-[#E83A2C] text-gray-400 hover:text-[#E83A2C] py-3 rounded-lg font-mono text-xs tracking-wider transition flex items-center justify-center gap-2">
          <LogOut className="w-3.5 h-3.5" /> ВЫЙТИ ИЗ СИСТЕМЫ
        </button>
      </div>

      <BottomNav active="profile" navigate={navigate} />
    </div>
  );
}

// Личные данные стажёра — inline-редактирование своих полей
function PersonalDataCard({ user, updateMe }) {
  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">МОИ ДАННЫЕ</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">EMAIL</label>
          <div className="text-sm text-gray-400 font-mono">{user.email || '—'}</div>
        </div>
        <div>
          <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ТЕЛЕФОН</label>
          <InlineText
            value={user.phone}
            onChange={v => updateMe({ phone: v })}
            placeholder="+7 (___) ___-__-__"
            className="text-white block"
          />
        </div>
        <div>
          <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ПОЛ</label>
          <InlineSelect
            value={user.gender}
            onChange={v => updateMe({ gender: v })}
            options={[
              { value: 'male', label: '♂ Мужской' },
              { value: 'female', label: '♀ Женский' },
            ]}
            placeholder="—"
          />
        </div>
        <div>
          <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ДАТА РОЖДЕНИЯ</label>
          <input
            type="date"
            value={user.birthDate || ''}
            onChange={e => updateMe({ birthDate: e.target.value })}
            max={new Date().toISOString().slice(0,10)}
            className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#FFB800] rounded px-2 py-1 text-sm text-white outline-none"
          />
          {calcAge(user.birthDate) != null && (
            <div className="text-[10px] text-gray-500 font-mono mt-1">{calcAge(user.birthDate)} лет</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="font-mono text-[9px] text-gray-500 tracking-wider mb-1">{label}</div>
      <div className="font-display text-2xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИНКА: DASHBOARD
// ═══════════════════════════════════════════════════════════════

function AdminDashboard({ users, navigate, onLogout, currentUser, notifications }) {
  const { getCourseFor, tasks } = useContent();
  const [traineeProgress, setTraineeProgress] = useState({});
  // ─── Локальное UI-состояние: поиск, фильтр статуса, сортировка ───
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');  // all | ready | training | risk | notstarted
  const [sortBy, setSortBy] = useState('progress-desc');    // progress-desc | xp-desc | name | activity
  const trainees = users.filter(u => !u.isAdmin);
  // подсчёт задач для значков
  const myActiveTasks = (tasks || []).filter(t => t.assigneeId === currentUser?.id && t.status === 'active').length;
  const allActiveTasks = (tasks || []).filter(t => t.status === 'active').length;
  const reviewTasks = (tasks || []).filter(t => t.status === 'review').length;

  useEffect(() => {
    const loadProgress = async () => {
      const map = {};
      for (const t of trainees) {
        if (!t.lastLoginAt) continue;
        try {
          const key = STORAGE_KEYS.PROGRESS(t.id);
          const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${t.id}`, { credentials: 'include' });
          if (resp.ok) {
            const json = await resp.json();
            if (json?.value) map[t.id] = json.value;
          }
        } catch {}
      }
      setTraineeProgress(map);
    };
    
    loadProgress();
    const interval = setInterval(loadProgress, 30000);
    return () => clearInterval(interval);
  }, [users]);

  const getStatus = (uid) => {
    const p = traineeProgress[uid];
    if (!p) return { color: 'gray', label: 'НЕ НАЧАЛ' };
    const t = trainees.find(x => x.id === uid);
    const course = getCourseFor(t);
    if (!course) return { color: 'gray', label: 'НЕТ КУРСА' };
    return getReadinessStatus(p, course);
  };

  // Универсальный «бакет» для фильтрации/статистики
  // ready | training | risk | notstarted
  const bucketOf = (t) => {
    const p = traineeProgress[t.id];
    const course = getCourseFor(t);
    if (!p || !course) return 'notstarted';
    const s = getReadinessStatus(p, course).status;
    if (s === 'shift_soldier') return 'ready';
    if (s === 'training' || s === 'on_exam') {
      // Дополнительно проверяем риск: давно не было активности
      const lastActivity = Math.max(
        ...Object.values(p.lessons || {}).map(l => l.completedAt || 0),
        ...Object.values(p.tests || {}).map(tt => tt.lastAt || 0),
        p.startedAt || 0
      );
      const daysSince = (Date.now() - lastActivity) / (1000 * 60 * 60 * 24);
      if (daysSince > 2) return 'risk';
      return 'training';
    }
    return 'notstarted';
  };

  const stats = {
    total: trainees.length,
    ready: trainees.filter(t => bucketOf(t) === 'ready').length,
    onTrack: trainees.filter(t => bucketOf(t) === 'training').length,
    risk: trainees.filter(t => bucketOf(t) === 'risk').length,
    notStarted: trainees.filter(t => bucketOf(t) === 'notstarted').length,
  };

  // Фильтрация + сортировка списка стажёров
  const visibleTrainees = trainees.filter(t => {
    if (statusFilter !== 'all' && bucketOf(t) !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = ((t.name || '') + ' ' + (t.email || '') + ' ' + (t.role || '')).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '', 'ru');
    if (sortBy === 'xp-desc') {
      const ax = traineeProgress[a.id]?.xp || 0;
      const bx = traineeProgress[b.id]?.xp || 0;
      return bx - ax;
    }
    if (sortBy === 'activity') {
      return (b.lastActiveAt || b.lastLoginAt || 0) - (a.lastActiveAt || a.lastLoginAt || 0);
    }
    // По умолчанию — по проценту прогресса (desc)
    const pa = traineeProgress[a.id];
    const pb = traineeProgress[b.id];
    const courseA = getCourseFor(a);
    const courseB = getCourseFor(b);
    const oa = pa && courseA ? calcOverallProgress(pa, courseA) : 0;
    const ob = pb && courseB ? calcOverallProgress(pb, courseB) : 0;
    return ob - oa;
  });

  // Приветствие по времени суток
  const hour = new Date().getHours();
  const greeting = hour < 6 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер';
  const firstName = (currentUser.name || '').split(' ')[0] || 'Командир';

  // Краткая «тревога»: что требует внимания админу прямо сейчас
  // Используется в hero для одной осмысленной строки.
  const summary = (() => {
    const items = [];
    if (stats.ready > 0) items.push(`${stats.ready} ${stats.ready === 1 ? 'готов' : 'готовы'} к смене`);
    if (stats.risk > 0) items.push(`${stats.risk} в зоне риска`);
    if (reviewTasks > 0) items.push(`${reviewTasks} задач на ревью`);
    if (items.length === 0 && stats.total > 0) items.push('всё спокойно, держим темп');
    if (stats.total === 0) items.push('пока никого нет в системе');
    return items.join(' · ');
  })();

  return (
    <div className="max-w-5xl mx-auto pb-10 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <div className="flex items-center justify-between">
          <div>
            {currentUser.isDirector ? (
              <div className="font-mono text-[10px] text-[#FF7849] tracking-[0.2em] flex items-center gap-2">
                <Shield className="w-3 h-3" /> РУКОВОДИТЕЛЬ · {currentUser.name.toUpperCase()}
              </div>
            ) : (
              <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.2em] flex items-center gap-2">
                <Crown className="w-3 h-3" /> АДМИН · {currentUser.name.toUpperCase()}
              </div>
            )}
            <h1 className="font-display text-3xl tracking-wider text-white mt-1">КОНТРОЛЬ ОПЕРАЦИИ</h1>
          </div>
          <div className="flex items-center gap-1">
            <NotificationCenter navigate={navigate} currentUserId={currentUser.id} users={users} />
            <button onClick={onLogout} className="text-gray-600 hover:text-white p-2 min-touch flex items-center justify-center" aria-label="Выйти">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <div className="px-3 py-1.5 rounded-md bg-[#FFB800] text-black font-mono text-xs tracking-wider flex items-center gap-1.5">
            <Users className="w-3 h-3" /> СТАЖЁРЫ
          </div>
          <button
            onClick={() => navigate('adminContent')}
            className="px-3 py-1.5 rounded-md bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] font-mono text-xs tracking-wider flex items-center gap-1.5 transition"
          >
            <Database className="w-3 h-3" /> РЕДАКТОР КОНТЕНТА
          </button>
        </div>
      </div>

      <div className="py-5 space-y-5">
        {/* ─── HERO: приветствие + сводка ─── */}
	    <AdminDashboardHero 
          currentUser={currentUser} 
          trainees={trainees} 
          notifications={notifications} 
          navigate={navigate} 
        />

        {/* ─── Stats — 5 интерактивных карточек-фильтров ─── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <AdminStatCard
            label="ВСЕГО"
            value={stats.total}
            icon={Users}
            color="#FFFFFF"
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <AdminStatCard
            label="ГОТОВЫ"
            value={stats.ready}
            icon={CheckCircle2}
            color="#00C781"
            active={statusFilter === 'ready'}
            onClick={() => setStatusFilter(statusFilter === 'ready' ? 'all' : 'ready')}
          />
          <AdminStatCard
            label="НА ОБУЧЕНИИ"
            value={stats.onTrack}
            icon={Clock}
            color="#00E5FF"
            active={statusFilter === 'training'}
            onClick={() => setStatusFilter(statusFilter === 'training' ? 'all' : 'training')}
          />
          <AdminStatCard
            label="ЗОНА РИСКА"
            value={stats.risk}
            icon={AlertTriangle}
            color="#E83A2C"
            active={statusFilter === 'risk'}
            onClick={() => setStatusFilter(statusFilter === 'risk' ? 'all' : 'risk')}
            pulse={stats.risk > 0}
          />
          <AdminStatCard
            label="НЕ НАЧАЛИ"
            value={stats.notStarted}
            icon={MinusCircle}
            color="#6B7280"
            active={statusFilter === 'notstarted'}
            onClick={() => setStatusFilter(statusFilter === 'notstarted' ? 'all' : 'notstarted')}
          />
        </div>

        {/* Trainees list */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="font-display text-xl tracking-wider text-white">СТАЖЁРЫ</div>
              <div className="font-mono text-[10px] text-gray-500 tracking-wider px-2 py-0.5 rounded bg-[#15171B] border border-[#2A2E36]">
                {visibleTrainees.length}{visibleTrainees.length !== trainees.length && ` / ${trainees.length}`}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Поиск */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Поиск..."
                  className="w-36 sm:w-44 bg-[#15171B] border border-[#2A2E36] rounded-md pl-8 pr-2 py-1.5 text-xs text-white placeholder-gray-600 focus:border-[#FFB800] focus:outline-none transition"
                />
              </div>
              {/* Сортировка */}
              <div className="flex bg-[#15171B] border border-[#2A2E36] rounded-md p-0.5">
                {[
                  { id: 'progress-desc', label: '%', tip: 'По прогрессу' },
                  { id: 'xp-desc', label: 'XP', tip: 'По опыту' },
                  { id: 'activity', label: '⏱', tip: 'По активности' },
                  { id: 'name', label: 'А-Я', tip: 'По алфавиту' },
                ].map(o => (
                  <button
                    key={o.id}
                    onClick={() => setSortBy(o.id)}
                    title={o.tip}
                    className={`px-2 py-1 rounded font-mono text-[10px] tracking-wider transition ${
                      sortBy === o.id ? 'bg-[#FFB800] text-black' : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {trainees.length === 0 ? (
            <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-12 text-center pop-in">
              <Users className="w-12 h-12 text-gray-700 mx-auto mb-3 float-anim" />
              <div className="text-gray-500 text-sm mb-1">Стажёров пока нет</div>
              <div className="text-xs text-gray-600 font-mono tracking-wider">ОНИ ПОЯВЯТСЯ ПОСЛЕ ПЕРВОГО ЛОГИНА</div>
            </div>
          ) : visibleTrainees.length === 0 ? (
            <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-10 text-center pop-in">
              <Search className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <div className="text-gray-500 text-sm mb-1">Никого не найдено по фильтру</div>
              <button
                onClick={() => { setSearch(''); setStatusFilter('all'); }}
                className="mt-3 font-mono text-[10px] text-[#FFB800] hover:text-[#FFC820] tracking-wider transition"
              >
                ↻ СБРОСИТЬ ФИЛЬТРЫ
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {visibleTrainees.map((t, idx) => {
                const p = traineeProgress[t.id];
                const traineeCourse = getCourseFor(t);
                const overall = p && traineeCourse ? calcOverallProgress(p, traineeCourse) : 0;
                const status = getStatus(t.id);
                const xp = p?.xp || 0;
                const rank = getRank(xp);
                const testsP = p && traineeCourse ? traineeCourse.modules.filter(m => p.tests?.[m.id]?.passed).length : 0;
                const totalModules = traineeCourse?.modules?.length || 0;
                const allTestsPassed = traineeCourse && testsP === totalModules && totalModules > 0;
                const bucket = bucketOf(t);

                // Цвет статусной полосы слева
                const statusColor =
                  bucket === 'ready' ? '#00C781'
                  : bucket === 'risk' ? '#E83A2C'
                  : bucket === 'training' ? '#00E5FF'
                  : '#374151';

                // Дни в команде — для бейджа «новичок»
                const daysOnTeam = t.joinedAt ? Math.floor((Date.now() - t.joinedAt) / (24 * 3600 * 1000)) : null;
                const isNewbie = daysOnTeam !== null && daysOnTeam <= 14;

                // Активность
                const lastActiveAt = t.lastActiveAt || t.lastLoginAt;
                const hoursSinceActive = lastActiveAt ? Math.floor((Date.now() - lastActiveAt) / (1000 * 3600)) : null;
                const isOnlineNow = hoursSinceActive !== null && hoursSinceActive < 1;

                return (
                  <div
                    key={t.id}
                    onClick={() => navigate('adminTrainee', { userId: t.id })}
                    className="group bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#2A2E36] hover:border-[#FFB800]/50 rounded-xl p-4 cursor-pointer transition hover-lift pop-in relative overflow-hidden"
                    style={{ animationDelay: `${Math.min(idx, 10) * 0.04}s` }}
                  >
                    {/* Цветная полоса слева в цвет статуса */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 transition-opacity opacity-70 group-hover:opacity-100"
                      style={{ background: statusColor }} />

                    {/* Glow в углу при hover */}
                    <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: `radial-gradient(circle, ${statusColor}25 0%, transparent 70%)`, filter: 'blur(15px)' }} />

                    <div className="relative pl-2">
                      <div className="flex items-center gap-4">
                        {/* Avatar с индикатором онлайна */}
                        <div className="relative flex-shrink-0">
                          <Avatar user={t} size={44} />
                          {isOnlineNow && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#00C781] border-2 border-[#15171B] glow-pulse" title="Сейчас онлайн" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <div className="font-bold text-white">{t.name}</div>
                            <span className="font-mono text-[10px]" style={{ color: rank.color }}>{rank.title}</span>
                            {isNewbie && (
                              <span className="font-mono text-[8px] text-[#3B82F6] bg-[#3B82F6]/15 border border-[#3B82F6]/30 px-1.5 py-0.5 rounded tracking-wider flex items-center gap-0.5">
                                <Sparkles className="w-2.5 h-2.5" /> НОВИЧОК
                              </span>
                            )}
                            {allTestsPassed && !p?.checklistApproved && (
                              <span className="font-mono text-[8px] text-[#FFB800] bg-[#FFB800]/15 border border-[#FFB800]/40 px-1.5 py-0.5 rounded tracking-wider flex items-center gap-0.5 glow-pulse">
                                <AlertCircle className="w-2.5 h-2.5" /> ЖДЁТ ЭКЗАМЕНА
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-[10px] font-mono text-gray-500 tracking-wider flex-wrap">
                            <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-[#FFB800]" />{xp} XP</span>
                            <span>·</span>
                            <span>ТЕСТОВ {testsP}/{totalModules}</span>
                            {hoursSinceActive !== null && (
                              <>
                                <span>·</span>
                                <span className={isOnlineNow ? 'text-[#00C781]' : ''}>
                                  {isOnlineNow ? 'СЕЙЧАС' : hoursSinceActive < 24 ? `${hoursSinceActive}Ч НАЗАД` : `${Math.floor(hoursSinceActive / 24)}Д НАЗАД`}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Правая часть: % прогресса крупно + статус */}
                        <div className="text-right flex-shrink-0">
                          <div className="font-display text-2xl text-white leading-none">
                            <AnimatedCounter value={overall} duration={700} />
                            <span className="text-base text-gray-500">%</span>
                          </div>
                          <StatusBadge status={status} />
                        </div>

                        <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0 transition-transform group-hover:translate-x-1 group-hover:text-[#FFB800]"
                          style={{ transitionDuration: '0.2s' }} />
                      </div>

                      {/* Прогресс-бар обучения внизу карточки */}
                      <div className="mt-3">
                        <div className="h-1 rounded-full bg-[#2A2E36] overflow-hidden relative">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${overall}%`,
                              background: `linear-gradient(90deg, ${statusColor} 0%, ${statusColor}AA 100%)`,
                              boxShadow: overall > 0 ? `0 0 6px ${statusColor}80` : 'none',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// AdminStatCard — карточка-метрика на главном экране админа.
// Кликабельная: при клике становится фильтром для списка стажёров.
function AdminStatCard({ label, value, icon: Icon, color, active, onClick, pulse }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl p-4 transition relative overflow-hidden hover-lift group ${
        active
          ? 'border bg-[color:var(--c)]/10 shadow-lg shadow-[color:var(--c)]/15'
          : 'bg-[#15171B] border border-[#2A2E36] hover:border-[color:var(--c)]/50'
      }`}
      style={{ ['--c']: color, borderColor: active ? color : undefined }}
    >
      {/* Glow в углу */}
      <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full pointer-events-none transition-opacity"
        style={{
          background: `radial-gradient(circle, ${color}25 0%, transparent 70%)`,
          filter: 'blur(15px)',
          opacity: active ? 0.8 : 0,
        }} />
      <div className="relative">
        <div className="flex items-start justify-between mb-2">
          <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${pulse && value > 0 ? 'icon-bounce' : ''}`}
            style={{ color, transitionDuration: '0.25s' }} />
          {active && (
            <span className="font-mono text-[8px] tracking-wider px-1.5 py-0.5 rounded" style={{ background: color + '30', color }}>
              ФИЛЬТР
            </span>
          )}
        </div>
        <div className="font-display text-3xl leading-none" style={{ color: active ? color : 'white' }}>
          <AnimatedCounter value={value} duration={700} />
        </div>
        <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-1">{label}</div>
      </div>
    </button>
  );
}

function AdminStat({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-display text-3xl text-white leading-none">{value}</div>
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-1">{label}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИНКА: КАРТОЧКА СТАЖЁРА
// ═══════════════════════════════════════════════════════════════

function AdminTrainee({ userId, users, navigate }) {
  const { getCourseFor } = useContent();
  const [progress, setProgress] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loaded, setLoaded] = useState(false);     // флаг — попытка загрузки сделана
  const user = users.find(u => u.id === userId);
  const course = getCourseFor(user);

  useEffect(() => {
    (async () => {
      try {
        const p = await safeGet(STORAGE_KEYS.PROGRESS(userId));
        // КРИТИЧНО: пустой прогресс — это валидное состояние (например, для админа).
        // Если оставить null, спиннер крутится бесконечно.
        setProgress(p || { lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        const c = await safeGet(STORAGE_KEYS.CHECKLIST(userId));
        setChecklist(c || {});
      } catch (e) {
        // даже при ошибке БД — даём пустые данные, чтобы UI не завис
        setProgress({ lessons: {}, tests: {}, badges: [], xp: 0, startedAt: Date.now() });
        setChecklist({});
      } finally {
        setLoaded(true);
      }
    })();
  }, [userId]);

  // Показываем спиннер только пока действительно идёт загрузка.
  // Если данные подгрузились (loaded=true), а user/course всё ещё нет — это «не найдено»
  if (!loaded) {
    return (
      <div className="max-w-2xl mx-auto p-5">
        <button onClick={() => navigate('admin')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-5 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К СПИСКУ
        </button>
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500 mx-auto" />
        </div>
      </div>
    );
  }
  if (!user || !course) {
    return (
      <div className="max-w-2xl mx-auto p-5">
        <button onClick={() => navigate('admin')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-5 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К СПИСКУ
        </button>
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center">
          <div className="text-gray-400 mb-2">
            {!user ? 'Пользователь не найден' : 'Курс для этой роли не задан'}
          </div>
          <div className="text-xs text-gray-600 font-mono">
            {!user
              ? 'Возможно, пользователь был удалён'
              : 'У этого пользователя нет прикреплённого курса — это нормально для администраторов.'}
          </div>
        </div>
      </div>
    );
  }

  const overall = calcOverallProgress(progress, course);
  const status = getReadinessStatus(progress, course);
  const rank = getRank(progress.xp);
  const allTestsPassed = course.modules.every(m => progress.tests?.[m.id]?.passed);
  const checklistDone = progress.checklistApproved;
  const finalApproved = progress.finalApproved;

  const approveChecklist = async () => {
    const newP = { ...progress, checklistApproved: true, checklistApprovedAt: Date.now() };
    setProgress(newP);
    await safeSet(STORAGE_KEYS.PROGRESS(userId), newP);
  };

  const giveAdmittance = async () => {
    const newP = {
      ...progress,
      finalApproved: true,
      finalApprovedAt: Date.now(),
      badges: progress.badges?.includes('godfather') ? progress.badges : [...(progress.badges || []), 'godfather']
    };
    setProgress(newP);
    await safeSet(STORAGE_KEYS.PROGRESS(userId), newP);
  };

  const revokeAdmittance = async () => {
    const newP = { ...progress, finalApproved: false };
    setProgress(newP);
    await safeSet(STORAGE_KEYS.PROGRESS(userId), newP);
  };

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('admin')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К СПИСКУ
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#E83A2C] to-[#8B0F08] flex items-center justify-center font-display text-xl text-white">
            {user.name[0]}
          </div>
          <div>
            <h1 className="font-display text-2xl text-white tracking-wider">{user.name.toUpperCase()}</h1>
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-0.5">
              {user.role === 'cashier' ? 'ПОВАР-КАССИР · ШАУРМАФИЯ' : user.role.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      <div className="py-5 space-y-5">
        {/* Summary */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="ПРОГРЕСС" value={`${overall}%`} accent="#E83A2C" />
            <Stat label="XP" value={progress.xp} accent="#FFB800" />
            <Stat label="РАНГ" value={rank.title} accent={rank.color} />
            <div>
              <div className="font-mono text-[9px] text-gray-500 tracking-wider mb-1">СТАТУС</div>
              <StatusBadge status={status} />
            </div>
          </div>
        </div>

        {/* Modules detail */}
        <div>
          <div className="font-display text-lg tracking-wider text-white mb-3">ОПЕРАЦИИ</div>
          <div className="space-y-2">
            {course.modules.map(m => {
              const t = progress.tests?.[m.id];
              const lessonsDone = m.lessons.filter(l => progress.lessons?.[l.id]).length;
              return (
                <div key={m.id} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-white text-sm">{m.title}</div>
                      <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-1">
                        УРОКИ {lessonsDone}/{m.lessons.length} · ТЕСТ {t?.passed ? `${t.score}% ✓` : t ? `${t.score}% ✗ (${t.attempts} попыток)` : 'НЕ СДАН'}
                      </div>
                    </div>
                    {t?.passed ? (
                      <div className="px-2 py-1 bg-[#00C781]/10 border border-[#00C781] rounded-md font-mono text-[10px] text-[#00C781] tracking-wider">СДАН</div>
                    ) : (
                      <div className="px-2 py-1 bg-[#2A2E36] rounded-md font-mono text-[10px] text-gray-500 tracking-wider">В РАБОТЕ</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Practical checklist action */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-lg tracking-wider text-white">ПРАКТИЧЕСКИЙ ЧЕК-ЛИСТ</div>
              <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-0.5">
                {checklistDone ? 'ПОДТВЕРЖДЁН' : 'ТРЕБУЕТ ЗАПОЛНЕНИЯ'}
              </div>
            </div>
            <ClipboardCheck className={`w-6 h-6 ${checklistDone ? 'text-[#00C781]' : 'text-gray-600'}`} />
          </div>
          <button
            onClick={() => navigate('adminChecklist', { userId })}
            className="w-full bg-[#FFB800] hover:bg-[#FFC820] text-black font-mono tracking-wider py-3 rounded-lg flex items-center justify-center gap-2"
          >
            ОТКРЫТЬ ЧЕК-ЛИСТ <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Final admission */}
        <div className={`rounded-2xl p-5 border-2 ${
          finalApproved ? 'border-[#00C781] bg-[#00C781]/5' :
          allTestsPassed && checklistDone ? 'border-[#FFB800] bg-[#FFB800]/5' :
          'border-[#2A2E36] bg-[#15171B]'
        }`}>
          <div className="font-display text-xl tracking-wider text-white mb-2">ДОПУСК К САМОСТОЯТЕЛЬНОЙ СМЕНЕ</div>

          <div className="space-y-2 mb-4">
            <Requirement done={allTestsPassed} text={`Все ${course.modules.length} модулей сданы`} />
            <Requirement done={checklistDone} text="Практический чек-лист подтверждён" />
          </div>

          {finalApproved ? (
            <>
              <div className="bg-[#00C781]/10 border border-[#00C781] rounded-lg p-3 mb-3 text-center">
                <div className="font-display text-2xl text-[#00C781] tracking-wider">СПЕЦИАЛИСТ</div>
                <div className="font-mono text-[10px] text-gray-400 tracking-wider mt-1">
                  {progress.finalApproved && (<div>ПОДПИСАН {new Date(progress.finalApprovedAt || progress.checklistApprovedAt).toLocaleDateString('ru')}</div>)}
                </div>
              </div>
              <button
                onClick={revokeAdmittance}
                className="w-full bg-[#15171B] hover:bg-[#1F0A0A] border border-[#E83A2C]/40 hover:border-[#E83A2C] text-[#E83A2C] font-mono text-xs tracking-wider py-2.5 rounded-lg"
              >
                СНЯТЬ ДОПУСК (ВОЗВРАТ В ОБУЧЕНИЕ)
              </button>
            </>
          ) : (
            <button
              onClick={giveAdmittance}
              disabled={!(allTestsPassed && checklistDone)}
              className="w-full bg-[#00C781] hover:bg-[#00D78F] disabled:bg-gray-800 disabled:text-gray-600 text-black font-mono tracking-wider py-3 rounded-lg flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              <Crown className="w-4 h-4" /> ВЫДАТЬ ДОПУСК
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Requirement({ done, text }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${done ? 'text-[#00C781]' : 'text-gray-500'}`}>
      {done ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-700 flex-shrink-0" />}
      <span>{text}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИНКА: ПРАКТИЧЕСКИЙ ЧЕК-ЛИСТ
// ═══════════════════════════════════════════════════════════════

function AdminChecklist({ userId, users, navigate, adminName }) {
  const { checklistTemplate } = useContent();
  const [progress, setProgress] = useState(null);
  const [checklist, setChecklist] = useState({});
  const user = users.find(u => u.id === userId);

  useEffect(() => {
    (async () => {
      const p = await safeGet(STORAGE_KEYS.PROGRESS(userId));
      setProgress(p);
      const c = await safeGet(STORAGE_KEYS.CHECKLIST(userId));
      setChecklist(c || {});
    })();
  }, [userId]);

  const total = checklistTemplate.reduce((s, sec) => s + sec.items.length, 0);
  const checked = Object.values(checklist).filter(v => v?.checked).length;
  const allChecked = checked === total;

  const toggle = async (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    const next = {
      ...checklist,
      [key]: {
        ...(checklist[key] || {}),
        checked: !checklist[key]?.checked,
        by: adminName,
        at: Date.now()
      }
    };
    setChecklist(next);
    await safeSet(STORAGE_KEYS.CHECKLIST(userId), next);
  };

  const setComment = async (sectionIdx, itemIdx, comment) => {
    const key = `${sectionIdx}-${itemIdx}`;
    const next = {
      ...checklist,
      [key]: { ...(checklist[key] || {}), comment }
    };
    setChecklist(next);
    await safeSet(STORAGE_KEYS.CHECKLIST(userId), next);
  };

  const approveAll = async () => {
    if (!progress) return;
    const newP = { ...progress, checklistApproved: true, checklistApprovedAt: Date.now(), checklistApprovedBy: adminName };
    setProgress(newP);
    await safeSet(STORAGE_KEYS.PROGRESS(userId), newP);
    navigate('adminTrainee', { userId });
  };

  if (!user || !progress) return null;

  return (
    <div className="max-w-3xl mx-auto pb-32 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('adminTrainee', { userId })} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К ДОСЬЕ
        </button>
        <h1 className="font-display text-2xl text-white tracking-wider">ПРАКТИКА · {user.name.toUpperCase()}</h1>
        <div className="flex items-center gap-3 mt-3">
          <div className="flex-1 h-1.5 bg-[#15171B] rounded-full overflow-hidden">
            <div className="h-full bg-[#FFB800] transition-all" style={{ width: `${(checked / total) * 100}%` }} />
          </div>
          <div className="font-mono text-xs text-[#FFB800] tracking-wider">{checked}/{total}</div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {checklistTemplate.map((section, si) => {
          const sectionDone = section.items.every((_, ii) => checklist[`${si}-${ii}`]?.checked);
          return (
            <div key={si} className={`bg-[#15171B] border rounded-2xl p-5 ${
              sectionDone ? 'border-[#00C781]/30' : 'border-[#2A2E36]'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-lg tracking-wider text-white">{section.section.toUpperCase()}</div>
                {sectionDone && <CheckCircle2 className="w-5 h-5 text-[#00C781]" />}
              </div>
              <div className="space-y-2">
                {section.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  const data = checklist[key];
                  const isChecked = data?.checked;
                  return (
                    <div key={ii} className={`bg-[#0A0B0D] rounded-lg p-3 transition ${
                      isChecked ? 'border border-[#00C781]/20' : 'border border-[#2A2E36]'
                    }`}>
                      <button onClick={() => toggle(si, ii)} className="w-full flex items-start gap-3 text-left">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition ${
                          isChecked ? 'bg-[#00C781] border-[#00C781]' : 'border-gray-600'
                        }`}>
                          {isChecked && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1 text-sm text-white leading-tight pt-0.5">{item}</div>
                      </button>
                      {isChecked && (
                        <div className="mt-2 ml-9">
                          <input
                            type="text"
                            placeholder="Комментарий (опционально)..."
                            value={data?.comment || ''}
                            onChange={e => setComment(si, ii, e.target.value)}
                            className="w-full bg-[#15171B] border border-[#2A2E36] rounded px-2 py-1.5 text-xs text-gray-300 placeholder-gray-700 focus:border-[#FFB800] focus:outline-none"
                          />
                          {data?.by && (
                            <div className="font-mono text-[9px] text-gray-600 tracking-wider mt-1">
                              ✓ {data.by} · {new Date(data.at).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Approve action */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D] to-transparent pt-8 px-5 safe-bottom">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={approveAll}
            disabled={!allChecked || progress.checklistApproved}
            className="w-full bg-[#00C781] hover:bg-[#00D78F] disabled:bg-gray-800 disabled:text-gray-600 text-black font-mono tracking-wider py-3.5 rounded-lg flex items-center justify-center gap-2 disabled:cursor-not-allowed glow-green disabled:shadow-none"
          >
            {progress.checklistApproved ? (
              <>УЖЕ ПОДТВЕРЖДЁН</>
            ) : (
              <><ThumbsUp className="w-4 h-4" /> ПОДТВЕРДИТЬ ПРАКТИКУ</>
            )}
          </button>
          {!allChecked && !progress.checklistApproved && (
            <div className="text-center font-mono text-[10px] text-gray-600 tracking-wider mt-2">
              ОСТАЛОСЬ {total - checked} ПУНКТОВ
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН-ХАБ КОНТЕНТА
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// ПАНЕЛЬ РАЗРАБОТЧИКА — управление правами, модулями, настройками, логами
// ═══════════════════════════════════════════════════════════════
// Доступна ТОЛЬКО для аккаунта info@tarasovweb.ru (isDevAccount(currentUser) === true).
// Защита на 3 уровнях:
//   1. Плитка «Панель разработчика» в AdminContentHub видна только dev-аккаунту
//   2. Экран DevPanel при попытке открыть проверяет isDevAccount, иначе редиректит
//   3. Изменения uvedомлений модулей/прав/конфига уходят на бэк, где валидируется по правилам

function DevPanel({ navigate, currentUser, users, updateUsers, showToast, ctx }) {
  const {
    modules, updateModules,
    systemConfig, updateSystemConfig,
    auditLog,
    appendAuditLog,
    roles, updateRoles,
  } = useContent();
  const [tab, setTab] = useState(ctx?.tab || 'permissions');

  // Защита: только dev-аккаунт
  if (!isDevAccount(currentUser)) {
    return (
      <div className="max-w-3xl mx-auto p-5 fade-in">
        <AdminHeader back={() => navigate('adminContent')} title="ДОСТУП ЗАПРЕЩЁН" subtitle="Только разработчик" />
        <div className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
          <Shield className="w-12 h-12 text-[#E83A2C] mx-auto mb-3" />
          <div className="text-white font-bold mb-2">Эта панель доступна только разработчику</div>
          <div className="text-sm text-gray-400 mb-6">Войдите как info@tarasovweb.ru</div>
          <button
            onClick={() => navigate('adminContent')}
            className="px-5 py-2.5 bg-[#0A0B0D] hover:bg-[#2A2E36] border border-[#2A2E36] rounded-lg text-sm text-white font-mono tracking-wider"
          >
            ← НАЗАД
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-32 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="ПАНЕЛЬ РАЗРАБОТЧИКА" subtitle="Управление системой" />

      {/* Табы */}
      <div className="px-5 sticky top-[80px] z-[5] bg-[#0A0B0D]/95 backdrop-blur">
        <div className="flex gap-2 overflow-x-auto py-3 -mx-1 px-1">
          {[
            { id: 'permissions', label: 'ПРАВА', icon: Shield },
            { id: 'roles',       label: `РОЛИ (${roles.length})`, icon: Crown },
            { id: 'modules',     label: 'МОДУЛИ', icon: Settings },
            { id: 'system',      label: 'СИСТЕМА', icon: Palette },
            { id: 'logs',        label: `ЛОГИ (${auditLog.length})`, icon: FileText },
          ].map(t => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap text-xs font-mono tracking-wider transition border ${
                  isActive
                    ? 'bg-[#FFB800] border-[#FFB800] text-black shadow-lg shadow-[#FFB800]/30'
                    : 'bg-[#15171B] border-[#2A2E36] text-gray-400 hover:border-[#FFB800]/50 hover:text-white'
                }`}
                style={{ transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
                <t.icon className={`w-3.5 h-3.5 ${isActive ? '' : 'transition-transform group-hover:scale-110'}`} />
                {t.label}
                {isActive && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#FFB800] glow-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* ─── Monitoring блок ─── */}
        <DevMonitor users={users} auditLog={auditLog} roles={roles} systemConfig={systemConfig} />

        {/* Контент таба — оборачиваем в div с key={tab} для re-mount + slide анимации */}
        <div key={tab} className="slide-in-right">
        {tab === 'permissions' && (
          <DevPanelPermissions
            users={users}
            updateUsers={updateUsers}
            currentUser={currentUser}
            showToast={showToast}
            appendAuditLog={appendAuditLog}
            roles={roles}
          />
        )}
        {tab === 'roles' && (
          <DevPanelRoles
            roles={roles}
            updateRoles={updateRoles}
            users={users}
            currentUser={currentUser}
            showToast={showToast}
            appendAuditLog={appendAuditLog}
          />
        )}
        {tab === 'modules' && (
          <DevPanelModules
            modules={modules}
            updateModules={updateModules}
            showToast={showToast}
            currentUser={currentUser}
            appendAuditLog={appendAuditLog}
          />
        )}
        {tab === 'system' && (
          <DevPanelSystem
            systemConfig={systemConfig}
            updateSystemConfig={updateSystemConfig}
            showToast={showToast}
            currentUser={currentUser}
            appendAuditLog={appendAuditLog}
          />
        )}
        {tab === 'logs' && (
          <DevPanelLogs
            auditLog={auditLog}
            users={users}
          />
        )}
        </div>
      </div>
    </div>
  );
}

// DevMonitor — компактный блок с метриками системы в начале DevPanel.
// Показывает: пользователей, ролей, размер audit log, статус rate-limited features.
function DevMonitor({ users, auditLog, roles, systemConfig }) {
  const totalUsers = (users || []).length;
  const adminCount = (users || []).filter(u => u.isAdmin).length;
  const traineeCount = totalUsers - adminCount;
  const logCount = (auditLog || []).length;
  const rolesCount = (roles || []).length;
  // Последняя запись в логе — индикатор «жив ли поток событий»
  const lastLog = (auditLog || [])[0];
  const lastLogAgo = lastLog && lastLog.at
    ? Math.floor((Date.now() - lastLog.at) / 1000)
    : null;
  const lastLogLabel = lastLogAgo === null
    ? '—'
    : lastLogAgo < 60 ? `${lastLogAgo}s назад`
    : lastLogAgo < 3600 ? `${Math.floor(lastLogAgo / 60)}m назад`
    : lastLogAgo < 86400 ? `${Math.floor(lastLogAgo / 3600)}h назад`
    : `${Math.floor(lastLogAgo / 86400)}d назад`;

  return (
    <div className="bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#FFB800]/20 rounded-2xl p-4 pop-in">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-[#00C781] icon-bounce" />
        <div className="font-mono text-[10px] text-[#00C781] tracking-[0.2em]">MONITORING · LIVE</div>
        <div className="ml-auto flex items-center gap-1.5 font-mono text-[10px] text-gray-500">
          <div className="w-1.5 h-1.5 rounded-full bg-[#00C781] glow-pulse" />
          ONLINE
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <DevMetric icon={Users} label="Пользователи" value={totalUsers} sub={`${adminCount} админ · ${traineeCount} стаж`} color="#3B82F6" />
        <DevMetric icon={Crown} label="Роли" value={rolesCount} sub="кастомные" color="#FFB800" />
        <DevMetric icon={FileText} label="Audit log" value={logCount} sub={`посл: ${lastLogLabel}`} color="#10B981" />
        <DevMetric icon={Shield} label="Maint mode" value={systemConfig?.maintenanceMode ? 'ON' : 'OFF'}
          sub={systemConfig?.maintenanceMode ? '⚠ только dev' : 'все имеют доступ'}
          color={systemConfig?.maintenanceMode ? '#E83A2C' : '#6B7280'} />
        <DevMetric icon={Database} label="Storage" value="OK" sub="kv_store · mysql" color="#A855F7" />
      </div>
    </div>
  );
}

function DevMetric({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[#0A0B0D]/70 border border-[#2A2E36] rounded-lg p-2.5 transition hover:border-[color:var(--c)]/40"
      style={{ ['--c']: color }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" style={{ color }} />
        <div className="font-mono text-[9px] text-gray-500 tracking-wider uppercase truncate">{label}</div>
      </div>
      <div className="font-display text-lg text-white leading-none mb-1">{value}</div>
      <div className="font-mono text-[8px] text-gray-600 tracking-wider truncate">{sub}</div>
    </div>
  );
}

// ─── ВКЛАДКА «ПРАВА» — таблица пользователей с гранулярными тоглами ───
// ─── Сгруппированный по категориям список прав с тогглами ───
// Используется в карточке пользователя (DevPanelPermissions) и в редакторе роли (DevPanelRoles).
// Категории сворачиваются — открыто только то что нужно.
function PermissionsByCategory({ perms, onToggle, readOnly = false, accent = '#00C781' }) {
  const [openCats, setOpenCats] = useState(() => {
    // По умолчанию открываем «КОНТЕНТ» — самая частая категория
    return { content: true };
  });

  return (
    <div className="space-y-2">
      {PERMISSION_CATEGORIES.map(cat => {
        const permsInCat = PERMISSIONS_CATALOG.filter(p => p.category === cat.id);
        if (permsInCat.length === 0) return null;
        const isOpen = !!openCats[cat.id];
        const activeCount = permsInCat.filter(p => perms[p.id]).length;
        return (
          <div key={cat.id} className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenCats(o => ({ ...o, [cat.id]: !o[cat.id] }))}
              className="w-full p-3 flex items-center justify-between gap-3 hover:bg-[#15171B] transition text-left"
            >
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-6 rounded-full" style={{ background: cat.color }} />
                <span className="font-mono text-[11px] tracking-wider" style={{ color: cat.color }}>{cat.label}</span>
                <span className="text-[10px] text-gray-500">
                  {activeCount}/{permsInCat.length}
                </span>
              </div>
              <ChevronRight className={`w-4 h-4 text-gray-500 transition ${isOpen ? 'rotate-90' : ''}`} />
            </button>
            {isOpen && (
              <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-[#2A2E36]">
                {permsInCat.map(p => {
                  const has = !!perms[p.id];
                  return (
                    <div key={p.id} className="flex items-center justify-between gap-3 py-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white">{p.label}</div>
                        <div className="text-[10px] text-gray-500 leading-tight">{p.desc}</div>
                      </div>
                      {readOnly
                        ? <span className={`text-xs font-mono ${has ? 'text-[#00C781]' : 'text-gray-600'}`}>{has ? '✓ ВКЛ' : '✗ ВЫКЛ'}</span>
                        : <InlineToggle value={has} onChange={(v) => onToggle(p.id, v)} accent={accent} />
                      }
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DevPanelPermissions({ users, updateUsers, currentUser, showToast, appendAuditLog, roles }) {
  const [filter, setFilter] = useState('all');         // all | admins | trainees — ПО УМОЛЧАНИЮ ВСЕ
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // ─── Авто-подгрузка свежих users из БД ───
  // Когда разработчик открыл вкладку «Права», важно показать актуальный список —
  // иначе только что зарегистрированные юзеры не появятся.
  const refreshUsers = useCallback(async (showFeedback = false) => {
    if (showFeedback) setRefreshing(true);
    try {
      const fresh = await safeGet(STORAGE_KEYS.USERS);
      if (Array.isArray(fresh)) {
        await updateUsers(fresh);
        if (showFeedback) showToast?.(`Список обновлён: ${fresh.length} пользователей`);
      }
    } catch (e) {
      console.warn('[devpanel-refresh]', e.message);
    } finally {
      if (showFeedback) setRefreshing(false);
    }
  }, [users, updateUsers, showToast]);

  useEffect(() => {
    refreshUsers(false);
    const interval = setInterval(() => refreshUsers(false), 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleUsers = users
    .filter(u => {
      if (isDevAccount(u)) return false;   // dev-аккаунт не показываем (его права константны)
      if (filter === 'admins') return u.isAdmin;
      if (filter === 'trainees') return !u.isAdmin;
      return true;   // 'all'
    })
    .filter(u => {
      if (!search) return true;
      const q = search.toLowerCase();
      const hay = ((u.name || '') + ' ' + (u.fullName || '') + ' ' + (u.email || '') + ' ' + (u.phone || '') + ' ' + (u.role || '')).toLowerCase();
      return hay.includes(q);
    })
    .sort((a, b) => {
      // Сортировка: руководители → админы → стажёры (внутри группы — по дате создания, новые сверху)
      const rank = (u) => u.isDirector ? 0 : (u.isAdmin ? 1 : 2);
      const rA = rank(a), rB = rank(b);
      if (rA !== rB) return rA - rB;
      return (b.joinedAt || 0) - (a.joinedAt || 0);
    });

  const togglePerm = async (userId, permId, value) => {
    const u = users.find(x => x.id === userId);
    if (!u) return;
    const currentPerms = u.permissions || {};
    const newPerms = { ...currentPerms, [permId]: value };
    const updatedList = users.map(x => x.id === userId ? { ...x, permissions: newPerms } : x);
    await updateUsers(updatedList);
    appendAuditLog?.({
      action: 'permission_changed',
      actorName: currentUser.name,
      target: u.name,
      details: `${permId} = ${value}`,
    });
    showToast?.(value ? 'Право выдано' : 'Право отозвано');
  };

  const toggleAdmin = async (userId, value) => {
    const u = users.find(x => x.id === userId);
    if (!u) return;
    const patch = { isAdmin: value };
    if (!value) patch.isDirector = false;   // снимаем админа — снимаем и руководителя
    const updatedList = users.map(x => x.id === userId ? { ...x, ...patch } : x);
    await updateUsers(updatedList);
    appendAuditLog?.({
      action: value ? 'admin_granted' : 'admin_revoked',
      actorName: currentUser.name,
      target: u.name,
      details: value ? 'Назначен администратором' : 'Снят с админа',
    });
    showToast?.(value ? 'Назначен админом' : 'Снят с админа');
  };

  const toggleDirector = async (userId, value) => {
    const u = users.find(x => x.id === userId);
    if (!u) return;
    const updatedList = users.map(x => x.id === userId ? { ...x, isDirector: value, isAdmin: value || x.isAdmin } : x);
    await updateUsers(updatedList);
    appendAuditLog?.({
      action: value ? 'director_granted' : 'director_revoked',
      actorName: currentUser.name,
      target: u.name,
      details: value ? 'Назначен руководителем' : 'Снят с руководителя',
    });
    showToast?.(value ? 'Назначен руководителем' : 'Снят с руководителя');
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#15171B] border border-[#FFB800]/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Crown className="w-4 h-4 text-[#FFB800]" />
          <div className="font-mono text-[10px] text-[#FFB800] tracking-wider">УПРАВЛЕНИЕ ПРАВАМИ</div>
        </div>
        <div className="text-sm text-gray-400 leading-relaxed">
          Видны ВСЕ пользователи системы. Для назначения админ-прав откройте карточку (клик),
          включите тогл «Администратор» — тогда станут доступны гранулярные права.
          Руководители получают все права автоматически.
        </div>
      </div>

      {/* Поиск + кнопка обновления */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск по имени, email, телефону..."
            className="w-full bg-[#15171B] border border-[#2A2E36] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:border-[#FFB800] focus:outline-none transition"
          />
        </div>
        <button
          onClick={() => refreshUsers(true)}
          disabled={refreshing}
          title="Обновить список из БД"
          className="bg-[#15171B] hover:bg-[#00C781]/10 border border-[#2A2E36] hover:border-[#00C781] text-gray-300 hover:text-[#00C781] px-3 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
        >
          {refreshing
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {/* Фильтры */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-2 rounded-md text-xs font-mono tracking-wider transition border ${
            filter === 'all' ? 'bg-[#FFB800] border-[#FFB800] text-black' : 'bg-[#15171B] border-[#2A2E36] text-gray-400'
          }`}
        >
          ВСЕ ({users.filter(u => !isDevAccount(u)).length})
        </button>
        <button
          onClick={() => setFilter('admins')}
          className={`px-3 py-2 rounded-md text-xs font-mono tracking-wider transition border ${
            filter === 'admins' ? 'bg-[#FFB800] border-[#FFB800] text-black' : 'bg-[#15171B] border-[#2A2E36] text-gray-400'
          }`}
        >
          АДМИНЫ ({users.filter(u => u.isAdmin && !isDevAccount(u)).length})
        </button>
        <button
          onClick={() => setFilter('trainees')}
          className={`px-3 py-2 rounded-md text-xs font-mono tracking-wider transition border ${
            filter === 'trainees' ? 'bg-[#FFB800] border-[#FFB800] text-black' : 'bg-[#15171B] border-[#2A2E36] text-gray-400'
          }`}
        >
          СТАЖЁРЫ ({users.filter(u => !u.isAdmin && !isDevAccount(u)).length})
        </button>
      </div>

      {visibleUsers.length === 0 ? (
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center text-gray-500 text-sm">
          {search ? `По запросу «${search}» никого не найдено` : 'Пользователей пока нет.'}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleUsers.map(u => {
            const isExpanded = expandedId === u.id;
            const perms = getEffectivePermissions(u, roles);
            return (
              <div key={u.id} className="bg-[#15171B] border border-[#2A2E36] rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : u.id)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-[#1A1C20] transition text-left"
                >
                  {u.avatar
                    ? <img src={u.avatar} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                    : <div className="w-10 h-10 rounded-full bg-[#2A2E36] flex items-center justify-center text-xs text-gray-400 font-bold">{(u.name || '?').slice(0, 1).toUpperCase()}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold truncate">{u.name || u.fullName || '(без имени)'}</span>
                      {u.isDirector && <span className="text-[9px] font-mono text-[#FF7849] bg-[#FF7849]/15 px-1.5 py-0.5 rounded tracking-wider">РУКОВОДИТЕЛЬ</span>}
                      {u.isAdmin && !u.isDirector && <span className="text-[9px] font-mono text-[#FFB800] bg-[#FFB800]/15 px-1.5 py-0.5 rounded tracking-wider">АДМИН</span>}
                      {!u.isAdmin && <span className="text-[9px] font-mono text-gray-500 bg-[#2A2E36] px-1.5 py-0.5 rounded tracking-wider">СТАЖЁР</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">{u.email}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-500 transition ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 space-y-3 border-t border-[#2A2E36]">
                    {/* Уровни доступа */}
                    <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 space-y-2">
                      <div className="text-[10px] font-mono text-gray-500 tracking-wider mb-1">УРОВЕНЬ ДОСТУПА</div>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm text-white">Администратор</div>
                          <div className="text-[10px] text-gray-500">Доступ к админ-панели</div>
                        </div>
                        <InlineToggle value={!!u.isAdmin} onChange={(v) => toggleAdmin(u.id, v)} accent="#FFB800" />
                      </div>
                      {u.isAdmin && (
                        <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#2A2E36]">
                          <div>
                            <div className="text-sm text-white flex items-center gap-2">
                              Руководитель
                              <span className="text-[8px] font-mono text-[#FF7849]">SUPER</span>
                            </div>
                            <div className="text-[10px] text-gray-500">Все права автоматически + управление задачами</div>
                          </div>
                          <InlineToggle value={!!u.isDirector} onChange={(v) => toggleDirector(u.id, v)} accent="#FF7849" />
                        </div>
                      )}
                    </div>

                    {/* Кастомная роль — селектор из созданных в табе РОЛИ */}
                    {roles && roles.length > 0 && (
                      <div className="bg-[#0A0B0D] border border-[#A855F7]/30 rounded-lg p-3 space-y-2">
                        <div className="text-[10px] font-mono text-[#A855F7] tracking-wider mb-1">КАСТОМНАЯ РОЛЬ</div>
                        <div className="text-[10px] text-gray-500 mb-2 leading-tight">
                          Присвойте готовую роль — она автоматически даст набор прав.
                          {u.customRoleId && roles.find(r => r.id === u.customRoleId) && (
                            <span> Сейчас: <span className="text-white">«{roles.find(r => r.id === u.customRoleId).name}»</span></span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={async () => {
                              const updatedList = users.map(x => x.id === u.id ? { ...x, customRoleId: null } : x);
                              await updateUsers(updatedList);
                              appendAuditLog?.({ action: 'role_unassigned', actorName: currentUser.name, target: u.name, details: 'Роль снята' });
                              showToast?.('Роль снята');
                            }}
                            className={`px-3 py-1.5 rounded text-xs font-mono tracking-wider transition border ${
                              !u.customRoleId
                                ? 'bg-[#2A2E36] border-[#2A2E36] text-white'
                                : 'bg-[#0A0B0D] border-[#2A2E36] text-gray-500 hover:border-gray-400'
                            }`}
                          >
                            БЕЗ РОЛИ
                          </button>
                          {roles.map(r => {
                            const active = u.customRoleId === r.id;
                            return (
                              <button
                                key={r.id}
                                onClick={async () => {
                                  // Если роль baseAdmin — автоматически делаем юзера админом
                                  const updatedList = users.map(x => x.id === u.id ? {
                                    ...x,
                                    customRoleId: r.id,
                                    isAdmin: r.baseAdmin ? true : x.isAdmin,
                                  } : x);
                                  await updateUsers(updatedList);
                                  appendAuditLog?.({
                                    action: 'role_assigned',
                                    actorName: currentUser.name,
                                    target: u.name,
                                    details: `Назначена роль: ${r.name}`,
                                  });
                                  showToast?.(`Роль «${r.name}» назначена`);
                                }}
                                className="px-3 py-1.5 rounded text-xs font-mono tracking-wider transition border"
                                style={active
                                  ? { background: r.color, borderColor: r.color, color: '#fff' }
                                  : { background: '#0A0B0D', borderColor: r.color + '40', color: r.color }}
                              >
                                {r.name.toUpperCase()}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Гранулярные права — для админов которые НЕ руководители и без кастомной роли */}
                    {u.isAdmin && !u.isDirector && !u.customRoleId && (
                      <div className="bg-[#15171B] border border-[#2A2E36] rounded-lg p-3 space-y-2">
                        <div className="text-[10px] font-mono text-gray-500 tracking-wider mb-2">ГРАНУЛЯРНЫЕ ПРАВА</div>
                        <PermissionsByCategory
                          perms={perms}
                          onToggle={(permId, v) => togglePerm(u.id, permId, v)}
                          accent="#00C781"
                        />
                      </div>
                    )}

                    {/* Если у юзера кастомная роль — показываем её права read-only */}
                    {u.customRoleId && roles.find(r => r.id === u.customRoleId) && (
                      <div className="bg-[#15171B] border border-[#A855F7]/30 rounded-lg p-3 space-y-2">
                        <div className="text-[10px] font-mono text-[#A855F7] tracking-wider mb-1">ПРАВА ОТ КАСТОМНОЙ РОЛИ (READ-ONLY)</div>
                        <div className="text-[10px] text-gray-500 mb-2">Чтобы изменить — отредактируйте роль во вкладке «РОЛИ» или снимите роль с этого юзера.</div>
                        <PermissionsByCategory perms={perms} readOnly={true} />
                      </div>
                    )}

                    {!u.isAdmin && !u.customRoleId && (
                      <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-lg p-3 text-xs text-gray-400">
                        Этот пользователь — обычный стажёр без админ-прав. Включите «Администратор» для гранулярных прав, или присвойте готовую роль (если они созданы).
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── ВКЛАДКА «МОДУЛИ» — включить/выключить целые разделы ───
function DevPanelModules({ modules, updateModules, showToast, currentUser, appendAuditLog }) {
  const toggle = async (key, value) => {
    const next = { ...modules, [key]: { ...modules[key], enabled: value } };
    await updateModules(next);
    appendAuditLog?.({
      action: 'module_toggled',
      actorName: currentUser.name,
      target: modules[key].label,
      details: value ? 'Включен' : 'Выключен',
    });
    showToast?.(value ? `Модуль «${modules[key].label}» включён` : `Модуль «${modules[key].label}» выключён`);
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#15171B] border border-[#FFB800]/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="w-4 h-4 text-[#FFB800]" />
          <div className="font-mono text-[10px] text-[#FFB800] tracking-wider">МОДУЛИ СИСТЕМЫ</div>
        </div>
        <div className="text-sm text-gray-400 leading-relaxed">
          Включайте и выключайте целые разделы. Если модуль выключен — соответствующие пункты меню,
          экраны и кнопки скрываются у ВСЕХ пользователей. Данные модуля при этом остаются в БД и не удаляются.
        </div>
      </div>

      <div className="space-y-2">
        {Object.entries(modules).map(([key, mod]) => (
          <div key={key} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold">{mod.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{mod.description}</div>
              {!mod.enabled && (
                <div className="text-[10px] font-mono text-[#E83A2C] tracking-wider mt-1">⚠ ВЫКЛЮЧЕН — раздел недоступен для всех пользователей</div>
              )}
            </div>
            <InlineToggle value={mod.enabled} onChange={(v) => toggle(key, v)} accent="#00C781" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ВКЛАДКА «СИСТЕМА» — общие настройки ───
function DevPanelSystem({ systemConfig, updateSystemConfig, showToast, currentUser, appendAuditLog }) {
  const setVal = async (key, value) => {
    await updateSystemConfig({ [key]: value });
    appendAuditLog?.({
      action: 'system_config_changed',
      actorName: currentUser.name,
      target: key,
      details: `${key} = ${typeof value === 'boolean' ? (value ? 'ON' : 'OFF') : value}`,
    });
    showToast?.('Сохранено');
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#15171B] border border-[#FFB800]/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Palette className="w-4 h-4 text-[#FFB800]" />
          <div className="font-mono text-[10px] text-[#FFB800] tracking-wider">СИСТЕМНЫЕ НАСТРОЙКИ</div>
        </div>
        <div className="text-sm text-gray-400 leading-relaxed">
          Общие параметры системы. Изменения применяются мгновенно ко всем пользователям.
        </div>
      </div>

      {/* Регистрация */}
      <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
        <div className="text-[10px] font-mono text-gray-500 tracking-wider mb-3">РЕГИСТРАЦИЯ</div>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-white">Открытая регистрация</div>
              <div className="text-[10px] text-gray-500">Если выключено — на форме входа нет кнопки «Регистрация»</div>
            </div>
            <InlineToggle value={!!systemConfig.allowRegistration} onChange={v => setVal('allowRegistration', v)} accent="#00C781" />
          </div>
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#2A2E36]">
            <div>
              <div className="text-sm text-white">Вход через Яндекс</div>
              <div className="text-[10px] text-gray-500">Кнопка OAuth Яндекс на форме входа</div>
            </div>
            <InlineToggle value={!!systemConfig.allowOAuthYandex} onChange={v => setVal('allowOAuthYandex', v)} accent="#FC3F1D" />
          </div>
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#2A2E36]">
            <div>
              <div className="text-sm text-white">Вход через ВКонтакте</div>
              <div className="text-[10px] text-gray-500">Кнопка OAuth ВК на форме входа</div>
            </div>
            <InlineToggle value={!!systemConfig.allowOAuthVk} onChange={v => setVal('allowOAuthVk', v)} accent="#0077FF" />
          </div>
        </div>
      </div>

      {/* Цвет бренда */}
      <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
        <div className="text-[10px] font-mono text-gray-500 tracking-wider mb-3">ЦВЕТ БРЕНДА (АКЦЕНТ)</div>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={systemConfig.brandColor || '#E83A2C'}
            onChange={(e) => setVal('brandColor', e.target.value)}
            className="w-12 h-12 rounded cursor-pointer bg-transparent border border-[#2A2E36]"
          />
          <input
            type="text"
            value={systemConfig.brandColor || '#E83A2C'}
            onChange={(e) => setVal('brandColor', e.target.value)}
            className="flex-1 bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-3 py-2 text-white text-sm font-mono outline-none"
          />
          <div className="w-12 h-12 rounded" style={{ backgroundColor: systemConfig.brandColor || '#E83A2C' }} />
        </div>
        <div className="text-[10px] text-gray-600 mt-2">
          Этот цвет используется в фирменных кнопках, акцентах и логотипе. Применяется не везде — глобальный rebrand сделаем в Итерации 3C.
        </div>
      </div>

      {/* Email лимит */}
      <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
        <div className="text-[10px] font-mono text-gray-500 tracking-wider mb-3">ЛИМИТ EMAIL</div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="0"
            max="10000"
            value={systemConfig.smtpDailyLimit || 150}
            onChange={(e) => setVal('smtpDailyLimit', parseInt(e.target.value || '0', 10))}
            className="flex-1 bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-3 py-2 text-white text-sm font-mono outline-none"
          />
          <span className="text-xs text-gray-500 font-mono">писем/сутки</span>
        </div>
        <div className="text-[10px] text-gray-600 mt-2">
          ⚠ Это визуальный лимит для отслеживания. Реальный лимит SMTP-провайдера контролируется переменной SMTP_DAILY_LIMIT в .env.
          Яндекс на бесплатном тарифе: ~150 писем/сутки.
        </div>
      </div>

      {/* Maintenance mode */}
      <div className="bg-[#15171B] border border-[#E83A2C]/30 rounded-xl p-4">
        <div className="text-[10px] font-mono text-[#E83A2C] tracking-wider mb-3">⚠ ОПАСНАЯ ЗОНА</div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-sm text-white">Режим технических работ</div>
            <div className="text-[10px] text-gray-500">
              Все пользователи кроме разработчика увидят заглушку с сообщением.<br/>
              Используйте, когда нужно изменить много контента и не показывать промежуточное состояние.
            </div>
          </div>
          <InlineToggle value={!!systemConfig.maintenanceMode} onChange={v => setVal('maintenanceMode', v)} accent="#E83A2C" />
        </div>
        {systemConfig.maintenanceMode && (
          <input
            type="text"
            value={systemConfig.maintenanceMessage || ''}
            onChange={(e) => setVal('maintenanceMessage', e.target.value)}
            placeholder="Сообщение для пользователей"
            className="w-full bg-[#0A0B0D] border border-[#E83A2C]/40 focus:border-[#E83A2C] rounded-lg px-3 py-2 text-white text-sm outline-none"
          />
        )}
      </div>
    </div>
  );
}

// ─── ВКЛАДКА «ЛОГИ» — журнал значимых действий ───
function DevPanelLogs({ auditLog, users }) {
  const [actionFilter, setActionFilter] = useState('all');

  const visibleLog = auditLog.filter(e => actionFilter === 'all' || e.action === actionFilter);

  const actionLabels = {
    permission_changed:    { label: 'Изменение прав',      color: '#FFB800' },
    admin_granted:         { label: 'Назначен админом',    color: '#00C781' },
    admin_revoked:         { label: 'Снят с админа',       color: '#E83A2C' },
    director_granted:      { label: 'Назначен руководителем', color: '#00C781' },
    director_revoked:      { label: 'Снят с руководителя', color: '#E83A2C' },
    module_toggled:        { label: 'Модуль',              color: '#A855F7' },
    system_config_changed: { label: 'Системная настройка', color: '#3B82F6' },
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayDiff = Math.floor((today.getTime() - d.getTime()) / 86400000);
    if (dayDiff < 0) return d.toLocaleString('ru');
    if (dayDiff === 0) return 'сегодня ' + d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
    if (dayDiff === 1) return 'вчера ' + d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('ru');
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#15171B] border border-[#FFB800]/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-[#FFB800]" />
          <div className="font-mono text-[10px] text-[#FFB800] tracking-wider">ЖУРНАЛ ДЕЙСТВИЙ</div>
        </div>
        <div className="text-sm text-gray-400 leading-relaxed">
          Последние 500 значимых действий: изменение прав, переключение модулей, системные настройки.
          Журнал хранится в БД.
        </div>
      </div>

      {/* Фильтр */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setActionFilter('all')}
          className={`px-3 py-1.5 rounded-md text-xs font-mono tracking-wider transition border whitespace-nowrap ${
            actionFilter === 'all' ? 'bg-[#FFB800] border-[#FFB800] text-black' : 'bg-[#15171B] border-[#2A2E36] text-gray-400'
          }`}
        >
          ВСЕ ({auditLog.length})
        </button>
        {Object.entries(actionLabels).map(([action, meta]) => {
          const count = auditLog.filter(e => e.action === action).length;
          if (count === 0) return null;
          return (
            <button
              key={action}
              onClick={() => setActionFilter(action)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono tracking-wider transition border whitespace-nowrap ${
                actionFilter === action ? 'border-2 text-white' : 'bg-[#15171B] border-[#2A2E36] text-gray-400'
              }`}
              style={actionFilter === action
                ? { background: meta.color + '20', borderColor: meta.color, color: meta.color }
                : {}}
            >
              {meta.label.toUpperCase()} ({count})
            </button>
          );
        })}
      </div>

      {visibleLog.length === 0 ? (
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center text-gray-500 text-sm">
          Журнал пуст.
        </div>
      ) : (
        <div className="space-y-1.5">
          {visibleLog.slice(0, 200).map(entry => {
            const meta = actionLabels[entry.action] || { label: entry.action, color: '#5F5F68' };
            return (
              <div key={entry.id} className="bg-[#15171B] border border-[#2A2E36] rounded-lg p-3 flex items-start gap-3">
                <div
                  className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: meta.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono tracking-wider" style={{ color: meta.color }}>
                      {meta.label.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-gray-600 font-mono">{formatTime(entry.at)}</span>
                  </div>
                  <div className="text-sm text-white mt-0.5">
                    {entry.actorName ? <span className="font-bold">{entry.actorName}</span> : 'Кто-то'}
                    {entry.target && <span className="text-gray-400"> → {entry.target}</span>}
                  </div>
                  {entry.details && (
                    <div className="text-xs text-gray-500 mt-0.5 font-mono">{entry.details}</div>
                  )}
                </div>
              </div>
            );
          })}
          {visibleLog.length > 200 && (
            <div className="text-center text-xs text-gray-600 font-mono py-3">
              + ещё {visibleLog.length - 200} записей (показаны последние 200)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ВКЛАДКА «РОЛИ» — конструктор кастомных ролей ───
// Кастомная роль — это шаблон прав, который можно присвоить пользователю.
// Каждая роль определяет:
//   - набор прав (permissions)
//   - baseAdmin — даёт ли роль автоматически админ-доступ (можно создать «менеджера» без базового админ-флага)
//   - цвет и имя для удобства в UI
function DevPanelRoles({ roles, updateRoles, users, currentUser, showToast, appendAuditLog }) {
  const confirm = useConfirm();
  const [expandedId, setExpandedId] = useState(null);

  const createRole = async () => {
    // Дефолт: все права OFF, только базовое «отправлять сообщения» включено
    // (это типичный минимальный набор для любого админа)
    const defaultPerms = PERMISSIONS_CATALOG.reduce((acc, p) => {
      acc[p.id] = p.id === 'canSendMessages';
      return acc;
    }, {});
    const newRole = {
      id: 'role_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      name: 'Новая роль',
      color: '#A855F7',
      baseAdmin: true,              // по умолчанию роль даёт админ-доступ
      permissions: defaultPerms,
    };
    const newRoles = [...roles, newRole];
    await updateRoles(newRoles);
    setExpandedId(newRole.id);
    appendAuditLog?.({
      action: 'role_created',
      actorName: currentUser.name,
      target: newRole.name,
      details: 'Создана новая кастомная роль',
    });
    showToast?.('Роль создана');
  };

  const updateRole = async (roleId, patch) => {
    const newRoles = roles.map(r => r.id === roleId ? { ...r, ...patch } : r);
    await updateRoles(newRoles);
  };

  const updateRolePermission = async (roleId, permId, value) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;
    await updateRole(roleId, {
      permissions: { ...role.permissions, [permId]: value },
    });
    appendAuditLog?.({
      action: 'role_changed',
      actorName: currentUser.name,
      target: role.name,
      details: `${permId} = ${value}`,
    });
  };

  const deleteRole = async (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;
    const usersWithThisRole = users.filter(u => u.customRoleId === roleId);
    const msg = usersWithThisRole.length > 0
      ? `Удалить роль «${role.name}»? У ${usersWithThisRole.length} пользователей она присвоена — они потеряют права этой роли.`
      : `Удалить роль «${role.name}»?`;
    if (!(await confirm(msg, { danger: true }))) return;
    const newRoles = roles.filter(r => r.id !== roleId);
    await updateRoles(newRoles);
    appendAuditLog?.({
      action: 'role_deleted',
      actorName: currentUser.name,
      target: role.name,
      details: `Удалена роль с ${usersWithThisRole.length} назначениями`,
    });
    showToast?.('Роль удалена');
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#15171B] border border-[#A855F7]/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Crown className="w-4 h-4 text-[#A855F7]" />
          <div className="font-mono text-[10px] text-[#A855F7] tracking-wider">КАСТОМНЫЕ РОЛИ</div>
        </div>
        <div className="text-sm text-gray-400 leading-relaxed">
          Создайте свои роли — например «Менеджер смены», «Контент-редактор», «Старший повар».
          Каждой роли можно настроить точный набор прав, и затем присвоить эту роль пользователю одним кликом.
          Это удобнее, чем выдавать гранулярные права каждому юзеру вручную.
        </div>
      </div>

      <button
        onClick={createRole}
        className="w-full bg-[#A855F7] hover:bg-[#B567FF] text-white font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition"
      >
        <Plus className="w-4 h-4" /> СОЗДАТЬ РОЛЬ
      </button>

      {roles.length === 0 ? (
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center text-gray-500 text-sm">
          Ролей пока нет. Создайте первую — например «Менеджер смены» с правом создавать задачи и видеть статистику.
        </div>
      ) : (
        <div className="space-y-2">
          {roles.map(role => {
            const isExpanded = expandedId === role.id;
            const usersWithRole = users.filter(u => u.customRoleId === role.id);
            return (
              <div key={role.id} className="bg-[#15171B] border border-[#2A2E36] rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : role.id)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-[#1A1C20] transition text-left"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white"
                    style={{ backgroundColor: role.color }}
                  >
                    {(role.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold truncate">{role.name}</span>
                      {role.baseAdmin && <span className="text-[9px] font-mono text-[#FFB800] bg-[#FFB800]/15 px-1.5 py-0.5 rounded tracking-wider">АДМИН-ДОСТУП</span>}
                      <span className="text-[9px] font-mono text-gray-500 bg-[#2A2E36] px-1.5 py-0.5 rounded tracking-wider">
                        {Object.values(role.permissions || {}).filter(Boolean).length} ПРАВ
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {usersWithRole.length > 0
                        ? `Присвоена ${usersWithRole.length} ${usersWithRole.length === 1 ? 'пользователю' : 'пользователям'}`
                        : 'Никому не присвоена'}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-500 transition ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 space-y-3 border-t border-[#2A2E36]">
                    {/* Имя и цвет */}
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-[10px] font-mono text-gray-500 tracking-wider mb-1">НАЗВАНИЕ</label>
                        <input
                          type="text"
                          value={role.name}
                          onChange={(e) => updateRole(role.id, { name: e.target.value })}
                          className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#A855F7] rounded-lg px-3 py-2 text-white text-sm outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono text-gray-500 tracking-wider mb-1">ЦВЕТ</label>
                        <input
                          type="color"
                          value={role.color}
                          onChange={(e) => updateRole(role.id, { color: e.target.value })}
                          className="w-12 h-[38px] rounded cursor-pointer bg-transparent border border-[#2A2E36]"
                        />
                      </div>
                    </div>

                    {/* baseAdmin тогл */}
                    <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm text-white">Даёт доступ в админ-панель</div>
                        <div className="text-[10px] text-gray-500">
                          {role.baseAdmin
                            ? 'Юзер с этой ролью попадает в админ-интерфейс при логине'
                            : 'Юзер с этой ролью остаётся стажёром, но может пользоваться отмеченными ниже правами'}
                        </div>
                      </div>
                      <InlineToggle
                        value={!!role.baseAdmin}
                        onChange={(v) => updateRole(role.id, { baseAdmin: v })}
                        accent="#FFB800"
                      />
                    </div>

                    {/* Гранулярные права */}
                    <div className="bg-[#15171B] border border-[#2A2E36] rounded-lg p-3 space-y-2">
                      <div className="text-[10px] font-mono text-gray-500 tracking-wider mb-2">ПРАВА ЭТОЙ РОЛИ</div>
                      <PermissionsByCategory
                        perms={role.permissions || {}}
                        onToggle={(permId, v) => updateRolePermission(role.id, permId, v)}
                        accent={role.color}
                      />
                    </div>

                    {/* Удаление */}
                    <div className="pt-2 border-t border-[#2A2E36]">
                      <button
                        onClick={() => deleteRole(role.id)}
                        className="vibrate w-full bg-[#E83A2C]/10 hover:bg-[#E83A2C]/20 border border-[#E83A2C]/40 hover:border-[#E83A2C] text-[#E83A2C] font-mono text-xs tracking-wider py-2 rounded-lg flex items-center justify-center gap-2 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> УДАЛИТЬ РОЛЬ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminContentHub({ navigate, onLogout, currentUser, users, auditLog }) {
  const { courses, techCards, checklistTemplate, regulations, contacts, exitQuestions, exitResponses, tasks, exportContent, importContent, modules, hasPerm } = useContent();
  const [busyBackup, setBusyBackup] = useState(false);

  const totalModules = courses.reduce((s, c) => s + c.modules.length, 0);
  const totalLessons = courses.reduce((s, c) => s + c.modules.reduce((sm, m) => sm + m.lessons.length, 0), 0);
  const totalTC = Object.keys(techCards).length;
  const totalChecklistItems = checklistTemplate.reduce((s, sec) => s + sec.items.length, 0);
  const trainees = users?.filter(u => !u.isAdmin) || [];
  const admins = users?.filter(u => u.isAdmin) || [];

  // Подсчёт задач — для плитки «Управление задачами / Мои задачи»
  const myActiveTasks = (tasks || []).filter(t => t.assigneeId === currentUser?.id && t.status === 'active').length;
  const allActiveTasks = (tasks || []).filter(t => t.status === 'active').length;
  const reviewTasks = (tasks || []).filter(t => t.status === 'review').length;

  // ДР: сегодня + завтра
  const bdReminders = (users || [])
    .filter(u => u.birthDate)
    .map(u => ({ user: u, days: daysUntilBirthday(u.birthDate), age: calcAge(u.birthDate) }))
    .filter(b => b.days !== null && (b.days === 0 || b.days === 1));

  return (
    <div className="max-w-5xl mx-auto pb-10 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.2em] flex items-center gap-2">
              <Crown className="w-3 h-3" /> АДМИН · {currentUser.name.toUpperCase()}
            </div>
            <h1 className="font-display text-3xl tracking-wider text-white mt-1">РЕДАКТОР КОНТЕНТА</h1>
          </div>
          <div className="flex items-center gap-1">
            <NotificationCenter navigate={navigate} currentUserId={currentUser.id} users={users} />
            <button onClick={onLogout} className="text-gray-600 hover:text-white p-2 min-touch flex items-center justify-center" aria-label="Выйти">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => navigate('admin')}
            className="px-3 py-1.5 rounded-md bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] font-mono text-xs tracking-wider flex items-center gap-1.5 transition"
          >
            <Users className="w-3 h-3" /> СТАЖЁРЫ
          </button>
          <div className="px-3 py-1.5 rounded-md bg-[#FFB800] text-black font-mono text-xs tracking-wider flex items-center gap-1.5">
            <Database className="w-3 h-3" /> РЕДАКТОР КОНТЕНТА
          </div>
        </div>
      </div>

      <div className="py-5 space-y-3">
        {/* ─── ХИРО-БЛОК «КОМАНДНЫЙ ЦЕНТР» ─── */}
        <div className="relative overflow-hidden rounded-2xl border border-[#FFB800]/30 p-5 pop-in"
          style={{
            background: 'linear-gradient(135deg, rgba(255,184,0,0.08) 0%, rgba(232,58,44,0.06) 50%, rgba(20,20,25,0.0) 100%)',
          }}>
          {/* Декоративный шум-градиент */}
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.25) 0%, transparent 70%)', filter: 'blur(20px)' }} />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(232,58,44,0.18) 0%, transparent 70%)', filter: 'blur(30px)' }} />

          <div className="relative">
            <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.25em] mb-2 flex items-center gap-2">
              <Activity className="w-3 h-3" /> КОМАНДНЫЙ ЦЕНТР
            </div>
            <h2 className="font-display text-2xl text-white tracking-wider mb-1">MAFIA ACADEMY</h2>
            <div className="text-sm text-gray-400 mb-4">
              {trainees.length === 0 ? 'Ни одного стажёра не зарегистрировано'
                : trainees.length === 1 ? '1 стажёр в системе'
                : `${trainees.length} стажёров в системе`}
              {admins.length > 0 && ` · ${admins.length} ${admins.length === 1 ? 'админ' : 'админов'}`}
            </div>

            {/* Мини-сводка по контенту: 4 ключевые метрики */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <HubMetric icon={BookOpen} value={<AnimatedCounter value={courses.length} duration={600} />} label="курсов" color="#FFB800" />
              <HubMetric icon={Layers} value={<AnimatedCounter value={totalModules} duration={700} />} label="модулей" color="#E83A2C" />
              <HubMetric icon={FileText} value={<AnimatedCounter value={totalLessons} duration={800} />} label="уроков" color="#3B82F6" />
              <HubMetric icon={ChefHat} value={<AnimatedCounter value={totalTC} duration={900} />} label="блюд" color="#10B981" />
            </div>
          </div>
        </div>

        {bdReminders.length > 0 && (
          <div className="bg-gradient-to-br from-[#FFB800]/15 to-[#E83A2C]/10 border border-[#FFB800]/40 rounded-2xl p-4 pop-in">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-[#FFB800] icon-wiggle" />
              <div className="font-display text-base tracking-wider text-white">НАПОМИНАНИЕ О ДНЯХ РОЖДЕНИЯ</div>
            </div>
            <div className="space-y-2">
              {bdReminders.map(b => (
                <div key={b.user.id} className="flex items-center gap-3 bg-[#0A0B0D]/50 rounded-lg p-2">
                  <Avatar user={b.user} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate flex items-center gap-2">
                      <Cake className="w-3.5 h-3.5 text-[#FFB800]" />
                      {b.user.name}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono tracking-wider">
                      {b.days === 0 ? <span className="text-[#FFB800] font-bold">СЕГОДНЯ</span> : <span className="text-[#00E5FF]">ЗАВТРА</span>}
                      {b.age != null && ` · исполняется ${b.days === 0 ? b.age : b.age + 1}`}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('adminMessages', { userId: b.user.id })}
                    className="text-[10px] bg-[#FFB800] hover:bg-[#FFC820] text-black px-2.5 py-1 rounded font-mono tracking-wider transition"
                  >
                    ПОЗДРАВИТЬ
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#E83A2C] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300 leading-relaxed">
            Все изменения <span className="text-white font-bold">мгновенно видны стажёрам</span>. Будьте аккуратны при правке курса в продакшене — стажёры на середине обучения увидят новый контент.
          </div>
        </div>

        {/* ─── ОБУЧЕНИЕ ─── */}
        <div className="text-[10px] font-mono text-gray-500 tracking-wider mt-4 mb-2 pl-1">ОБУЧЕНИЕ</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {hasPerm(currentUser, 'canEditCourses') && (
            <ContentTile
              icon={BookOpen}
              label={`КУРСЫ (${courses.length})`}
              title="Программы обучения"
              stats={`${totalModules} модулей · ${totalLessons} уроков`}
              onClick={() => navigate('adminCourses')}
            />
          )}
          {modules.techCards.enabled && hasPerm(currentUser, 'canEditTechCards') && (
            <ContentTile
              icon={ChefHat}
              label="ТЕХКАРТЫ"
              title="Меню кухни"
              stats={`${totalTC} ${totalTC === 1 ? 'блюдо' : 'блюд'}`}
              onClick={() => navigate('adminTechCards')}
            />
          )}
          {modules.checklist.enabled && hasPerm(currentUser, 'canEditChecklists') && (
            <ContentTile
              icon={ClipboardCheck}
              label="ЧЕК-ЛИСТ"
              title="Практика"
              stats={`${checklistTemplate.length} разделов · ${totalChecklistItems} пунктов`}
              onClick={() => navigate('adminChecklistTpl')}
            />
          )}
          {modules.regulations.enabled && hasPerm(currentUser, 'canEditRegulations') && (
            <ContentTile
              icon={Shield}
              label="РЕГЛАМЕНТЫ"
              title="Должностные инструкции"
              stats={`${regulations.length} ${regulations.length === 1 ? 'регламент' : 'регламентов'}`}
              onClick={() => navigate('adminRegulations')}
            />
          )}
        </div>

        {/* ─── УПРАВЛЕНИЕ ─── */}
        <div className="text-[10px] font-mono text-gray-500 tracking-wider mt-4 mb-2 pl-1">УПРАВЛЕНИЕ</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(hasPerm(currentUser, 'canEditUsers') || hasPerm(currentUser, 'canCreateUsers')) && (
            <ContentTile
              icon={Users}
              label="ПОЛЬЗОВАТЕЛИ"
              title="Команда и стажёры"
              stats={`${admins.length} админов · ${trainees.length} стажёров`}
              onClick={() => navigate('adminUsers')}
            />
          )}
          {modules.tasks.enabled && (hasPerm(currentUser, 'canCreateTasks') || hasPerm(currentUser, 'canEditAllTasks') || (currentUser?.isAdmin)) && (
            <ContentTile
              icon={CheckSquare}
              label={hasPerm(currentUser, 'canEditAllTasks') ? "УПРАВЛЕНИЕ ЗАДАЧАМИ" : "МОИ ЗАДАЧИ"}
              title={hasPerm(currentUser, 'canEditAllTasks') ? "Поручения команде" : "Поручения от руководителя"}
              stats={hasPerm(currentUser, 'canEditAllTasks')
                ? `${allActiveTasks} активных · ${reviewTasks} на проверке`
                : `${myActiveTasks} активных задач`}
              onClick={() => navigate(hasPerm(currentUser, 'canEditAllTasks') ? 'directorTasks' : 'adminMyTasks')}
            />
          )}
          {hasPerm(currentUser, 'canSeeStats') && (
            <ContentTile
              icon={BarChart3}
              label="СТАТИСТИКА"
              title="Дашборд обучения"
              stats="ТОПы, ошибки, прогресс"
              onClick={() => navigate('adminStats')}
            />
          )}
        </div>

        {/* ─── СИСТЕМА ─── */}
        <div className="text-[10px] font-mono text-gray-500 tracking-wider mt-4 mb-2 pl-1">СИСТЕМА</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {hasPerm(currentUser, 'canEditSystemSettings') && (
            <ContentTile
              icon={Settings}
              label="НАСТРОЙКИ"
              title="Регистрация и доступ"
              stats="поля, ФИО, дата рождения"
              onClick={() => navigate('adminSettings')}
            />
          )}
          {hasPerm(currentUser, 'canEditSystemSettings') && (
            <ContentTile
              icon={Palette}
              label="ТЕМА И СТАРТ"
              title="Оформление и тексты"
              stats="цвета, темы, лого"
              onClick={() => navigate('adminStartScreen')}
            />
          )}
          {modules.contacts.enabled && hasPerm(currentUser, 'canEditContacts') && (
            <ContentTile
              icon={Phone}
              label="КОНТАКТЫ"
              title="Справочник телефонов"
              stats={`${contacts.sections.length} разделов`}
              onClick={() => navigate('adminContacts')}
            />
          )}
          {modules.exit.enabled && hasPerm(currentUser, 'canEditExitQuestions') && (
            <ContentTile
              icon={FileText}
              label="EXIT-АНКЕТА"
              title="Вопросы при уходе"
              stats={`${exitQuestions.questions.length} вопросов · ${exitResponses.length} ответов`}
              onClick={() => navigate('adminExitQuestions')}
            />
          )}
          {modules.exit.enabled && hasPerm(currentUser, 'canSeeExitResponses') && (
            <ContentTile
              icon={Archive}
              label="ОТВЕТЫ EXIT"
              title="База анкет ушедших"
              stats={`${exitResponses.length} ${exitResponses.length === 1 ? 'анкета' : 'анкет'}`}
              onClick={() => navigate('adminExitResponses')}
            />
          )}
        </div>

        {/* ─── ПАНЕЛЬ РАЗРАБОТЧИКА ─── */}
        {isDevAccount(currentUser) && (
          <>
            <div className="text-[10px] font-mono text-gray-500 tracking-wider mt-4 mb-2 pl-1">РАЗРАБОТЧИК</div>
            <button
              onClick={() => navigate('devPanel')}
              className="w-full bg-[#15171B] border rounded-xl p-5 text-left group hover-lift relative overflow-hidden glow-border"
              style={{ borderColor: '#FFB80060' }}
            >
              <div className="absolute inset-0 gradient-dev opacity-10 pointer-events-none" />
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.25) 0%, transparent 70%)' }} />
              <div className="relative">
                <Crown className="w-7 h-7 mb-3 text-[#FFB800] glow-pulse" />
                <div className="font-mono text-[10px] text-[#FFB800] tracking-wider mb-1 flex items-center gap-1.5">
                  ПАНЕЛЬ РАЗРАБОТЧИКА
                  <span className="px-1 py-0.5 bg-[#FFB800] text-black rounded text-[9px]">DEV</span>
                </div>
                <div className="font-display text-xl text-white tracking-wider mb-1">УПРАВЛЕНИЕ СИСТЕМОЙ</div>
                <div className="text-xs text-gray-400">права · роли · модули · логи</div>
                <div className="mt-3 font-mono text-[10px] text-[#FFB800] tracking-wider flex items-center gap-1 transition group-hover:translate-x-1">
                  ОТКРЫТЬ <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </button>
          </>
        )}

        {/* ───────────── ПОСЛЕДНИЕ ДЕЙСТВИЯ ───────────── */}
        {isDevAccount(currentUser) && auditLog.length > 0 && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#FFB800]" />
                <div className="font-mono text-[10px] text-gray-500 tracking-wider">ПОСЛЕДНИЕ ДЕЙСТВИЯ</div>
              </div>
              <button
                onClick={() => navigate('devPanel', { tab: 'logs' })}
                className="text-[10px] font-mono text-gray-500 hover:text-[#FFB800] transition"
              >
                ВСЕ ЛОГИ →
              </button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {auditLog.slice(-10).map((entry, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-gray-600 font-mono flex-shrink-0 w-18 text-right">
                    {new Date(entry.at).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-gray-400 flex-1">
                    <span className="text-white">{entry.actorName || 'Система'}</span>
                    {entry.target && <span className="text-gray-500"> → {entry.target}</span>}
                    <span className="text-gray-600 ml-1">— {entry.details}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ───────────── РЕЗЕРВНОЕ КОПИРОВАНИЕ ───────────── */}
        {hasPerm(currentUser, 'canBackupContent') && (
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-[#FFB800]" />
            <div className="font-mono text-[10px] text-gray-500 tracking-wider">РЕЗЕРВНОЕ КОПИРОВАНИЕ</div>
          </div>
          <div className="text-sm text-gray-400 mb-3 leading-relaxed">
            Сохраните весь контент в&nbsp;один JSON-файл. Файл можно загрузить обратно в&nbsp;любой момент.
          </div>
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-3">
            ⚠ ПРОГРЕСС СТАЖЁРОВ, ЗАДАЧИ И ПОЛЬЗОВАТЕЛИ В БЭКАП НЕ ВКЛЮЧАЮТСЯ
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={async () => {
                setBusyBackup(true);
                try {
                  const data = exportContent();
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const dateStr = new Date().toISOString().slice(0, 10);
                  a.href = url;
                  a.download = `mafia-academy-backup-${dateStr}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } finally {
                  setBusyBackup(false);
                }
              }}
              disabled={busyBackup}
              className="flex-1 bg-[#15171B] border border-[#00C781]/40 hover:border-[#00C781] text-[#00C781] font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition"
            >
              <Download className="w-3.5 h-3.5" /> СКАЧАТЬ БЭКАП
            </button>

            <label className="flex-1 bg-[#15171B] border border-[#FFB800]/40 hover:border-[#FFB800] text-[#FFB800] font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center justify-center gap-2 cursor-pointer transition">
              <Upload className="w-3.5 h-3.5" /> ЗАГРУЗИТЬ БЭКАП
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  try {
                    const text = await file.text();
                    const backup = JSON.parse(text);
                    const ts = backup.exportedAt ? new Date(backup.exportedAt).toLocaleString('ru') : 'неизвестно';
                    const ok = window.confirm(
                      `Загрузить бэкап от ${ts}?\n\n` +
                      `ВНИМАНИЕ: текущий контент будет ЗАМЕНЁН данными из файла.\n` +
                      `Прогресс стажёров не пострадает.`
                    );
                    if (!ok) return;
                    setBusyBackup(true);
                    const result = await importContent(backup);
                    setBusyBackup(false);
                    if (result.ok) {
                      window.alert(
                        `✓ Бэкап восстановлен.\n\nЗагружено: ${result.applied.join(', ')}.\n\n` +
                        `Страница будет перезагружена для применения.`
                      );
                      window.location.reload();
                    } else {
                      window.alert('Ошибка восстановления: ' + result.error);
                    }
                  } catch (err) {
                    setBusyBackup(false);
                    window.alert('Не удалось прочитать файл: ' + err.message);
                  }
                }}
              />
            </label>
          </div>
        </div>
        )}
		
        {/* Блок «Сброс к заводским настройкам» удалён — слишком опасная операция.
            Вместо него — резервное копирование контента (см. выше). */}
      </div>
    </div>
  );
}

// ─── Защита экрана правом ───
// Используется в начале админ-экранов:
//   const guard = <PermissionGate user={currentUser} perm="canEditCourses" navigate={navigate} />;
//   if (guard) return guard;
// Возвращает null если право есть, или экран «Доступ закрыт» если нет.
function PermissionGate({ user, perm, navigate, label }) {
  const { hasPerm } = useContent();
  if (hasPerm(user, perm)) return null;
  const permMeta = PERMISSIONS_CATALOG.find(p => p.id === perm);
  return (
    <div className="max-w-3xl mx-auto p-5 fade-in">
      <div className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
        <Lock className="w-12 h-12 text-[#E83A2C] mx-auto mb-4" />
        <div className="font-display text-2xl tracking-wider text-white mb-2">ДОСТУП ЗАКРЫТ</div>
        <div className="text-sm text-gray-400 mb-2">
          Для {label || 'этого раздела'} нужно право <span className="text-white font-mono">«{permMeta?.label || perm}»</span>
        </div>
        <div className="text-xs text-gray-600 mb-6 font-mono">
          Попроси разработчика выдать тебе это право в Панели разработчика.
        </div>
        <button
          onClick={() => navigate('adminContent')}
          className="px-5 py-2.5 bg-[#0A0B0D] hover:bg-[#2A2E36] border border-[#2A2E36] rounded-lg text-sm text-white font-mono tracking-wider"
        >
          ← НАЗАД
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// UI-КОМПОНЕНТЫ: АНИМАЦИИ И ИНТЕРАКТИВ
// ═══════════════════════════════════════════════════════════════

// ─── AnimatedCounter ───
// Плавно анимирует число от старого к новому. Используется для XP, прогресса, счётчиков.
// При изменении value запускает easing-анимацию через requestAnimationFrame.
function AnimatedCounter({ value, duration = 700, format = (v) => Math.round(v), className = '', accent }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    const animate = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return (
    <span className={className} style={accent ? { color: accent } : undefined}>
      {format(display)}
    </span>
  );
}

// ─── ProgressBar ───
// Полоса прогресса с заливкой и опциональным shimmer-блеском.
// При первом рендере проигрывает анимацию заполнения от 0 до value.
function ProgressBar({ value, max = 100, color = '#FFB800', height = 8, shimmer = true, className = '', label }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className={className}>
      {label && (
        <div className="flex items-center justify-between mb-1.5 text-[10px] font-mono tracking-wider">
          <span className="text-gray-500">{label}</span>
          <span style={{ color }}>{Math.round(pct)}%</span>
        </div>
      )}
      <div
        className="bg-[#15171B] rounded-full overflow-hidden"
        style={{ height }}
      >
        <div
          className={`h-full rounded-full progress-fill ${shimmer && pct > 0 ? 'progress-shine' : ''}`}
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${color}DD 0%, ${color} 100%)`,
            boxShadow: pct > 0 ? `0 0 8px ${color}80` : 'none',
          }}
        />
      </div>
    </div>
  );
}

// ─── Skeleton ───
// Заменитель спиннеров — рендерит «скелет» интерфейса пока данные грузятся.
function Skeleton({ width = '100%', height = 16, className = '', rounded = true, count = 1, gap = 8 }) {
  if (count === 1) {
    return (
      <div
        className={`skeleton-block ${className}`}
        style={{ width, height, borderRadius: rounded ? 6 : 0 }}
      />
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }} className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="skeleton-block"
          style={{ width: i === count - 1 ? '70%' : width, height, borderRadius: rounded ? 6 : 0 }}
        />
      ))}
    </div>
  );
}

// ─── Confetti ───
// Эффект конфетти при ачивке. Рендерит N частиц с разным цветом / задержкой / горизонтальным смещением.
// Использовать так: const [boom, setBoom] = useState(false); ... <Confetti active={boom} onDone={() => setBoom(false)} />
// При active=true проигрывает анимацию 2.5 секунды и затем onDone.
function Confetti({ active, onDone, count = 60, colors = ['#E83A2C', '#FFB800', '#00C781', '#3B82F6', '#A855F7'] }) {
  const [pieces, setPieces] = useState([]);

  useEffect(() => {
    if (!active) return;
    const arr = Array.from({ length: count }).map((_, i) => ({
      id: i + '_' + Date.now(),
      left: 50 + (Math.random() - 0.5) * 80,           // % от ширины
      dx: (Math.random() - 0.5) * 600,                  // px горизонтального смещения
      delay: Math.random() * 400,                       // мс
      duration: 1800 + Math.random() * 800,             // мс
      color: colors[i % colors.length],
      size: 6 + Math.random() * 6,
      rotateStart: Math.random() * 360,
      isCircle: Math.random() > 0.5,
    }));
    setPieces(arr);
    const t = setTimeout(() => {
      setPieces([]);
      onDone?.();
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active || pieces.length === 0) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden">
      {pieces.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: p.isCircle ? '50%' : '2px',
            transform: `rotate(${p.rotateStart}deg)`,
            animation: `confetti-fall ${p.duration}ms ${p.delay}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
            ['--dx']: `${p.dx}px`,
          }}
        />
      ))}
    </div>
  );
}

// ─── MagneticButton ───
// Кнопка, которая «притягивается» к курсору при наведении (на десктопе).
// На мобилках работает как обычная кнопка — magnetic-эффект отключается.
function MagneticButton({ children, className = '', style = {}, strength = 0.25, onClick, ...rest }) {
  const ref = useRef(null);
  const handleMove = (e) => {
    if (window.matchMedia('(hover: none)').matches) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) * strength;
    const y = (e.clientY - rect.top - rect.height / 2) * strength;
    el.style.transform = `translate(${x}px, ${y}px)`;
  };
  const handleLeave = () => {
    const el = ref.current;
    if (el) el.style.transform = '';
  };
  return (
    <button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={onClick}
      className={`magnetic-btn ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </button>
  );
}

// ─── TiltCard ───
// 3D-tilt при наведении мыши. На мобилках работает как обычный блок.
function TiltCard({ children, className = '', style = {}, maxTilt = 8, ...rest }) {
  const ref = useRef(null);
  const handleMove = (e) => {
    if (window.matchMedia('(hover: none)').matches) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rotateY = (x - 0.5) * maxTilt * 2;
    const rotateX = -(y - 0.5) * maxTilt * 2;
    el.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(8px)`;
  };
  const handleLeave = () => {
    const el = ref.current;
    if (el) el.style.transform = '';
  };
  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ transition: 'transform 0.15s ease-out', transformStyle: 'preserve-3d', ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── ScreenTransition ───
// Обёртка для плавных переходов между экранами. По умолчанию — fade-in + лёгкий слайд снизу.
// Используется в роутере для перехода между screen-значениями.
function ScreenTransition({ screenKey, children, direction = 'right' }) {
  const animClass = direction === 'left' ? 'slide-in-left' : 'slide-in-right';
  return (
    <div key={screenKey} className={animClass}>
      {children}
    </div>
  );
}

// HubMetric — компактный счётчик для hero-блока AdminContentHub.
// Показывает иконку, анимированное число и подпись. Использует AnimatedCounter.
function HubMetric({ icon: Icon, value, label, color = '#FFB800' }) {
  return (
    <div className="bg-[#0A0B0D]/60 border border-[#2A2E36] rounded-xl px-3 py-2.5 flex items-center gap-3 transition hover:border-[color:var(--c)]/60"
      style={{ ['--c']: color }}>
      <Icon className="w-5 h-5 flex-shrink-0" style={{ color }} />
      <div className="min-w-0">
        <div className="font-display text-xl text-white leading-none">{value}</div>
        <div className="font-mono text-[9px] text-gray-500 tracking-wider mt-0.5">{label.toUpperCase()}</div>
      </div>
    </div>
  );
}

function ContentTile({ icon: Icon, label, title, stats, onClick, accent }) {
  const color = accent || '#FFB800';
  const ref = useRef(null);

  const handleMove = (e) => {
    if (window.matchMedia('(hover: none)').matches) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rotateY = (x - 0.5) * 8;
    const rotateX = -(y - 0.5) * 8;
    el.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(4px)`;
    // Glow follows cursor — мягкое свечение в направлении курсора
    el.style.background = `radial-gradient(circle at ${x * 100}% ${y * 100}%, ${color}22 0%, #15171B 60%)`;
  };
  const handleLeave = () => {
    const el = ref.current;
    if (el) {
      el.style.transform = '';
      el.style.background = '#15171B';
    }
  };

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="bg-[#15171B] border rounded-xl p-5 text-left group hover-lift relative overflow-hidden"
      style={{
        borderColor: accent ? color + '60' : '#2A2E36',
        transition: 'transform 0.15s ease-out, border-color 0.18s ease-out',
        transformStyle: 'preserve-3d',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; }}
    >
      {/* Decorative glow орб в углу */}
      <div
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: `radial-gradient(circle, ${color}40 0%, transparent 70%)` }}
      />
      <Icon className="w-7 h-7 mb-3 transition-transform group-hover:scale-110" style={{ color, transitionDuration: '0.25s' }} />
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-1">{label}</div>
      <div className="font-display text-xl text-white tracking-wider mb-1">{title.toUpperCase()}</div>
      <div className="text-xs text-gray-500">{stats}</div>
      <div className="mt-3 font-mono text-[10px] text-gray-600 tracking-wider flex items-center gap-1 transition group-hover:translate-x-1" style={{ color: 'inherit', transitionDuration: '0.2s' }}>
        ОТКРЫТЬ <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: КУРС (список модулей)
// ═══════════════════════════════════════════════════════════════

function AdminCourseEditor({ courseId, navigate, showToast, currentUser }) {
  const confirm = useConfirm();
  const { getCourseById, updateCourse, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditCourses')) {
    return <PermissionGate user={currentUser} perm="canEditCourses" navigate={navigate} label="редактирования курса" />;
  }
  const course = getCourseById(courseId);

  if (!course) return null;

  const addModuleAt = async (insertIdx = course.modules.length) => {
    const newMod = {
      //id: genId('m'),
      id: 'm_' + Math.random().toString(36).slice(2, 10),
      title: 'Новый модуль',
      subtitle: '',
      duration: 15,
      icon: 'BookOpen',
      xp: 200,
      critical: false,
      lessons: [],
      test: { passing: 75, questions: [] }
    };
    const next = [...course.modules];
    next.splice(insertIdx, 0, newMod);
    await updateCourse(courseId, { modules: next });
  };

  const reorderModules = async (from, to) => {
    const next = [...course.modules];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    await updateCourse(courseId, { modules: next });
  };

  const deleteModule = async (idx) => {
    if (!(await confirm('Удалить модуль безвозвратно? Прогресс стажёров по нему обнулится.', { danger: true }))) return;
    const next = course.modules.filter((_, i) => i !== idx);
    await updateCourse(courseId, { modules: next });
    showToast('Модуль удалён', 'error');
  };

  const patchModule = (modId, patch) => {
    const next = course.modules.map(m => m.id === modId ? { ...m, ...patch } : m);
    return updateCourse(courseId, { modules: next });
  };

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader
        back={() => navigate('adminContent')}
        title="КУРС"
        subtitle={`${course.brand || 'Без бренда'} · ${course.role || 'Без роли'}`}
      />

      <div className="py-5 space-y-5">
        {/* Метаданные курса — всё inline */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">МЕТАДАННЫЕ КУРСА · НАЖМИ НА ТЕКСТ ДЛЯ ИЗМЕНЕНИЯ</div>

          <div className="space-y-3">
            <InlineText
              value={course.title}
              onChange={v => updateCourse(courseId, { title: v })}
              placeholder="Название курса"
              size="2xl"
              className="text-white font-bold block"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-mono tracking-wider">БРЕНД:</span>
              <InlineText
                value={course.brand}
                onChange={v => updateCourse(courseId, { brand: v })}
                placeholder="Шаурмафия"
                className="text-white"
              />
              <span className="text-gray-700">·</span>
              <span className="text-xs text-gray-500 font-mono tracking-wider">РОЛЬ:</span>
              <InlineText
                value={course.role}
                onChange={v => updateCourse(courseId, { role: v })}
                placeholder="Повар-кассир"
                className="text-white"
              />
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-[#2A2E36]">
              <span className="text-xs text-gray-500 font-mono tracking-wider">ПРОХОДНОЙ БАЛЛ ФИНАЛА:</span>
              <InlineNumber
                value={course.passing}
                onChange={v => updateCourse(courseId, { passing: v })}
                suffix="%"
                min={0} max={100}
                className="text-[#FFB800]"
              />
            </div>
          </div>
        </div>

        {/* Модули */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg tracking-wider text-white">МОДУЛИ · {course.modules.length}</div>
          </div>
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-2 px-1">
            ПОТЯНИ ЗА ПОЛОСКИ · НАЖМИ НА ТЕКСТ · ДОБАВЛЯЙ МЕЖДУ БЛОКАМИ
          </div>

          {course.modules.length === 0 ? (
            <button
              onClick={() => addModuleAt(0)}
              className="w-full bg-[#15171B] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] rounded-xl p-12 transition group"
            >
              <Plus className="w-8 h-8 text-gray-600 group-hover:text-[#FFB800] mx-auto mb-2 transition" />
              <div className="text-gray-500 group-hover:text-white text-sm transition">Добавить первый модуль</div>
            </button>
          ) : (
            <>
              <AddBlockButton onAdd={() => addModuleAt(0)} label="модуль" />
              <DraggableList
                items={course.modules}
                onReorder={reorderModules}
                renderItem={(mod, idx, dragProps) => (
                  <>
                    <div className={`bg-[#15171B] border rounded-xl p-4 transition ${mod.critical ? 'border-l-2 border-l-[#E83A2C] border-[#2A2E36]' : 'border-[#2A2E36]'} hover:border-[#FFB800]/40`}>
                      <div className="flex items-start gap-3">
                        <div
                          {...dragProps}
                          className={`${dragProps.className} w-6 h-11 flex items-center justify-center text-gray-600 hover:text-[#FFB800] flex-shrink-0`}
                          title="Перетащи для смены порядка"
                        >
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-[#0A0B0D] flex items-center justify-center font-mono text-xs text-gray-500 flex-shrink-0">
                          {(idx + 1).toString().padStart(2, '0')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <InlineText
                              value={mod.title}
                              onChange={v => patchModule(mod.id, { title: v })}
                              placeholder="Название модуля"
                              className="font-bold text-white"
                            />
                            <InlineToggle
                              value={mod.critical}
                              onChange={v => patchModule(mod.id, { critical: v })}
                              label={mod.critical ? 'КРИТ' : 'обычн.'}
                              accent="#E83A2C"
                            />
                          </div>
                          <div className="mt-1">
                            <InlineText
                              value={mod.subtitle}
                              onChange={v => patchModule(mod.id, { subtitle: v })}
                              placeholder="Подзаголовок (короткое описание)"
                              className="text-gray-400"
                              size="sm"
                            />
                          </div>
                          <div className="font-mono text-[10px] text-gray-600 tracking-wider mt-2 flex items-center gap-2 flex-wrap">
                            <span>УРОКОВ: {mod.lessons.length}</span>
                            <span>·</span>
                            <span>ВОПРОСОВ: {mod.test?.questions?.length || 0}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <InlineNumber
                                value={mod.duration}
                                onChange={v => patchModule(mod.id, { duration: v })}
                                suffix="м"
                                min={1} max={999}
                              />
                            </span>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <Zap className="w-3 h-3 text-[#FFB800]" />
                              <InlineNumber
                                value={mod.xp}
                                onChange={v => patchModule(mod.id, { xp: v })}
                                suffix=" XP"
                                min={0} max={9999}
                              />
                            </span>
                          </div>
                          <button
                            onClick={() => navigate('adminModule', { courseId, moduleId: mod.id })}
                            className="mt-2 text-xs text-[#FFB800] hover:text-[#FFC820] flex items-center gap-1 transition"
                          >
                            Открыть уроки и тест <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <IconBtn icon={Trash2} onClick={() => deleteModule(idx)} danger />
                        </div>
                      </div>
                    </div>
                    <AddBlockButton onAdd={() => addModuleAt(idx + 1)} label="модуль" />
                  </>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: МОДУЛЬ (метаданные + список уроков + тест)
// ═══════════════════════════════════════════════════════════════

function AdminModuleEditor({ courseId, moduleId, navigate, showToast, currentUser }) {
  const confirm = useConfirm();
  const { getCourseById, techCards, updateCourse, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditCourses')) {
    return <PermissionGate user={currentUser} perm="canEditCourses" navigate={navigate} label="редактирования модуля" />;
  }
  const course = getCourseById(courseId);
  const mod = course?.modules.find(m => m.id === moduleId);

  if (!mod) return null;

  const patchModule = (patch) => {
    const newMod = { ...mod, ...patch };
    const next = course.modules.map(m => m.id === moduleId ? newMod : m);
    return updateCourse(courseId, { modules: next });
  };

  const updateModule = async (newMod) => {
    const next = course.modules.map(m => m.id === moduleId ? newMod : m);
    await updateCourse(courseId, { modules: next });
  };

  const addLessonAt = async (type = 'text', insertIdx = mod.lessons.length) => {
    const newLesson = type === 'techcard'
      ? { id: genId('l'), title: 'Новая техкарта', type: 'techcard', techCardId: Object.keys(techCards)[0] || '', duration: 5 }
      : { id: genId('l'), title: 'Новый урок', type: 'text', duration: 5, content: '' };
    const nextLessons = [...mod.lessons];
    nextLessons.splice(insertIdx, 0, newLesson);
    await updateModule({ ...mod, lessons: nextLessons });
  };

  const reorderLessons = async (from, to) => {
    const next = [...mod.lessons];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    await updateModule({ ...mod, lessons: next });
  };

  const deleteLesson = async (idx) => {
    if (!(await confirm('Удалить урок?', { danger: true }))) return;
    const next = mod.lessons.filter((_, i) => i !== idx);
    await updateModule({ ...mod, lessons: next });
    showToast('Урок удалён', 'error');
  };

  const patchLesson = (lessonId, patch) => {
    const nextLessons = mod.lessons.map(l => l.id === lessonId ? { ...l, ...patch } : l);
    return updateModule({ ...mod, lessons: nextLessons });
  };

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminCourse', { courseId })} title="МОДУЛЬ" subtitle={mod.title} />

      <div className="py-5 space-y-5">
        {/* Метаданные модуля — всё inline */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">МЕТАДАННЫЕ МОДУЛЯ</div>

          <div className="space-y-3">
            <InlineText
              value={mod.title}
              onChange={v => patchModule({ title: v })}
              placeholder="Название модуля"
              size="2xl"
              className="text-white font-bold block"
            />
            <InlineText
              value={mod.subtitle}
              onChange={v => patchModule({ subtitle: v })}
              placeholder="Короткое описание / подзаголовок"
              className="text-gray-400 block"
            />

            <div className="flex items-center gap-4 pt-2 border-t border-[#2A2E36] flex-wrap">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-500 font-mono tracking-wider">МИНУТ:</span>
                <InlineNumber
                  value={mod.duration}
                  onChange={v => patchModule({ duration: v })}
                  min={1} max={999}
                  className="text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-[#FFB800]" />
                <span className="text-xs text-gray-500 font-mono tracking-wider">XP:</span>
                <InlineNumber
                  value={mod.xp}
                  onChange={v => patchModule({ xp: v })}
                  min={0} max={9999}
                  className="text-[#FFB800]"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-mono tracking-wider">ПРОХОДНОЙ ТЕСТА:</span>
                <InlineNumber
                  value={mod.test?.passing || 75}
                  onChange={v => patchModule({ test: { ...mod.test, passing: v } })}
                  suffix="%"
                  min={0} max={100}
                  className="text-[#FFB800]"
                />
              </div>
              <InlineToggle
                value={mod.critical}
                onChange={v => patchModule({ critical: v })}
                label={mod.critical ? 'КРИТИЧЕСКИЙ' : 'обычный'}
                accent="#E83A2C"
              />
            </div>
          </div>
        </div>

        {/* Уроки */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="font-display text-lg tracking-wider text-white">УРОКИ · {mod.lessons.length}</div>
            <div className="flex gap-2">
              <button onClick={() => addLessonAt('text')} className="bg-[#FFB800] hover:bg-[#FFC820] text-black font-mono text-xs tracking-wider px-3 py-2 rounded-lg flex items-center gap-1.5 hover-lift">
                <Plus className="w-3.5 h-3.5" /> ТЕКСТ
              </button>
              <button onClick={() => addLessonAt('techcard')} className="bg-[#15171B] border border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 font-mono text-xs tracking-wider px-3 py-2 rounded-lg flex items-center gap-1.5 hover-lift">
                <Plus className="w-3.5 h-3.5" /> ТЕХКАРТА
              </button>
            </div>
          </div>

          {mod.lessons.length === 0 ? (
            <div className="bg-[#15171B] border border-dashed border-[#2A2E36] rounded-xl p-8 text-center">
              <div className="text-gray-500 text-sm mb-3">Уроков пока нет</div>
              <div className="text-xs text-gray-600 font-mono tracking-wider">ДОБАВЬ ПЕРВЫЙ КНОПКАМИ ВЫШЕ</div>
            </div>
          ) : (
            <>
              <AddBlockButton onAdd={() => addLessonAt('text', 0)} label="урок" />
              <DraggableList
                items={mod.lessons}
                onReorder={reorderLessons}
                renderItem={(les, idx, dragProps) => {
                  const cardOptions = Object.values(techCards).map(c => ({ value: c.id, label: c.name }));
                  return (
                    <>
                      <div className="bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800]/40 rounded-xl p-3 transition">
                        <div className="flex items-start gap-2">
                          <div
                            {...dragProps}
                            className={`${dragProps.className} w-5 h-8 flex items-center justify-center text-gray-600 hover:text-[#FFB800] flex-shrink-0`}
                          >
                            <GripVertical className="w-3.5 h-3.5" />
                          </div>
                          <div className="w-8 h-8 rounded-md bg-[#0A0B0D] flex items-center justify-center font-mono text-xs text-gray-500 flex-shrink-0">
                            {(idx + 1).toString().padStart(2, '0')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {les.type === 'techcard'
                                ? <ChefHat className="w-3.5 h-3.5 text-[#E83A2C] flex-shrink-0" />
                                : <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                              <InlineText
                                value={les.title}
                                onChange={v => patchLesson(les.id, { title: v })}
                                placeholder="Название урока"
                                className="font-bold text-white"
                              />
                            </div>
                            <div className="mt-1.5 flex items-center gap-2 flex-wrap font-mono text-[10px] text-gray-600 tracking-wider">
                              {les.type === 'techcard' ? (
                                <>
                                  <span>ПРИВЯЗАНА К:</span>
                                  <InlineSelect
                                    value={les.techCardId}
                                    onChange={v => patchLesson(les.id, { techCardId: v })}
                                    options={cardOptions}
                                    placeholder="— выбери блюдо —"
                                  />
                                </>
                              ) : (
                                <span>ТЕКСТОВЫЙ УРОК</span>
                              )}
                              <span>·</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <InlineNumber
                                  value={les.duration}
                                  onChange={v => patchLesson(les.id, { duration: v })}
                                  suffix=" мин"
                                  min={1} max={999}
                                />
                              </span>
                            </div>
                            <button
                              onClick={() => navigate('adminLesson', { courseId, moduleId, lessonId: les.id })}
                              className="mt-1.5 text-[10px] text-[#FFB800] hover:text-[#FFC820] flex items-center gap-1 font-mono tracking-wider transition"
                            >
                              {les.type === 'techcard' ? 'РЕДАКТИРОВАТЬ ВИДЕО' : 'РЕДАКТИРОВАТЬ КОНТЕНТ И ВИДЕО'}
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <IconBtn icon={Trash2} onClick={() => deleteLesson(idx)} danger />
                          </div>
                        </div>
                      </div>
                      <AddBlockButton onAdd={() => addLessonAt('text', idx + 1)} label="урок" />
                    </>
                  );
                }}
              />
            </>
          )}
        </div>

        {/* Тест */}
        <div>
          <div className="font-display text-lg tracking-wider text-white mb-3">ТЕСТ МОДУЛЯ</div>
          <button
            onClick={() => navigate('adminTest', { courseId, moduleId })}
            className="w-full bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] rounded-xl p-4 text-left transition group flex items-center gap-3 hover-lift"
          >
            <Trophy className="w-6 h-6 text-[#FFB800]" />
            <div className="flex-1">
              <div className="font-bold text-white text-sm">Редактировать тест</div>
              <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-0.5">
                {mod.test?.questions?.length || 0} ВОПРОСОВ · ПРОХОДНОЙ {mod.test?.passing || 75}%
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#FFB800]" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: УРОК
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// ФОТОГРАФИИ К УРОКАМ — редактор (админ) + просмотрщик (стажёр)
// ═══════════════════════════════════════════════════════════════

// Утилита: сжимаем изображение до max 1200px, JPEG q=0.8.
// Фото от телефона 4-8 MB → 200-500 KB. Это и для лимита kv_store 5MB,
// и для скорости загрузки на медленных сетях стажёров.
async function compressLessonPhoto(file) {
  if (!file.type.startsWith('image/')) {
    return Promise.reject(new Error('Файл не является изображением'));
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const r = Math.min(MAX / width, MAX / height);
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('compress failed'));
        const fr2 = new FileReader();
        fr2.onload = () => resolve(fr2.result);
        fr2.onerror = () => reject(fr2.error);
        fr2.readAsDataURL(blob);
      }, 'image/jpeg', 0.82);
    };
    img.onerror = () => reject(new Error('Не удалось загрузить изображение'));
    img.src = dataUrl;
  });
}

function LessonPhotosEditor({ photos, onChange, showToast }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setBusy(true);
    const added = [];
    for (const f of arr) {
      try {
        const data = await compressLessonPhoto(f);
        added.push({
          id: 'p_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
          name: f.name,
          caption: '',
          data,
          at: Date.now(),
        });
      } catch (e) {
        showToast?.('Не удалось добавить фото: ' + f.name);
      }
    }
    if (added.length > 0) {
      onChange([...(photos || []), ...added]);
      showToast?.(`Добавлено фото: ${added.length}`);
    }
    setBusy(false);
  };

  const move = (idx, dir) => {
    const next = [...photos];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const updateCaption = (id, caption) => {
    onChange(photos.map(p => p.id === id ? { ...p, caption } : p));
  };

  const removePhoto = (id) => {
    onChange(photos.filter(p => p.id !== id));
  };

  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <ImageIcon className="w-5 h-5 text-[#E83A2C]" />
        <div className="font-display text-lg tracking-wider text-white">ФОТО К УРОКУ</div>
        {photos.length > 0 && (
          <div className="ml-auto font-mono text-[10px] text-gray-500 tracking-wider">{photos.length} ШТ</div>
        )}
      </div>

      <div className="text-xs text-gray-500 mb-4 leading-relaxed">
        Прикрепите фото для ознакомления стажёров: реальная подача блюда, правильная сборка, ошибки на смене.
        Файлы автоматически сжимаются до 1200px.
      </div>

      <input
        ref={ref}
        type="file"
        multiple
        accept="image/*"
        capture="environment"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        className="hidden"
      />

      {photos.length > 0 && (
        <div className="space-y-3 mb-4">
          {photos.map((p, idx) => (
            <div key={p.id} className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 flex gap-3">
              <a href={p.data} target="_blank" rel="noopener" className="flex-shrink-0">
                <img src={p.data} alt={p.name} className="w-24 h-24 object-cover rounded-md border border-[#2A2E36]" />
              </a>
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={p.caption || ''}
                  onChange={e => updateCaption(p.id, e.target.value)}
                  placeholder="Подпись (опционально, например: «Правильная сборка»)"
                  className="w-full bg-transparent border-b border-[#2A2E36] focus:border-[#E83A2C] outline-none text-sm text-white placeholder-gray-600 py-1.5 mb-2 transition"
                />
                <div className="text-[10px] text-gray-600 font-mono tracking-wider truncate mb-2">{p.name}</div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="p-1.5 bg-[#15171B] border border-[#2A2E36] rounded text-gray-400 hover:text-white disabled:opacity-30 transition" title="Выше">
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === photos.length - 1}
                    className="p-1.5 bg-[#15171B] border border-[#2A2E36] rounded text-gray-400 hover:text-white disabled:opacity-30 transition" title="Ниже">
                    <ArrowDown className="w-3 h-3" />
                  </button>
                  <button onClick={() => removePhoto(p.id)}
                    className="p-1.5 bg-[#15171B] border border-[#E83A2C]/30 rounded text-[#E83A2C] hover:bg-[#E83A2C]/10 transition ml-auto" title="Удалить">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="w-full py-3 border border-dashed border-[#2A2E36] hover:border-[#E83A2C] text-gray-500 hover:text-[#E83A2C] rounded-lg transition flex items-center justify-center gap-2 text-xs font-mono tracking-wider disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {busy ? 'ОБРАБОТКА...' : 'ДОБАВИТЬ ФОТО'}
      </button>
    </div>
  );
}

// Просмотрщик для стажёра: галерея, открытие в полный размер, навигация
function LessonPhotosViewer({ photos }) {
  const [openIdx, setOpenIdx] = useState(null);

  // Закрытие по ESC, навигация стрелками
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenIdx(null);
      else if (e.key === 'ArrowLeft') setOpenIdx(i => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setOpenIdx(i => Math.min(photos.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIdx, photos.length]);

  return (
    <div className="fade-in">
      <div className="flex items-center gap-2 mb-2 px-1">
        <ImageIcon className="w-4 h-4 text-[#FFB800]" />
        <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.2em]">ФОТО · {photos.length} ШТ</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {photos.map((p, idx) => (
          <button
            key={p.id}
            onClick={() => setOpenIdx(idx)}
            className="aspect-square bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] rounded-lg overflow-hidden transition relative group"
          >
            <img src={p.data} alt={p.caption || p.name} className="w-full h-full object-cover transition group-hover:scale-105" />
            {p.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-2 pt-6">
                <div className="text-[10px] text-white font-mono tracking-wider text-left line-clamp-2">{p.caption}</div>
              </div>
            )}
          </button>
        ))}
      </div>

      {openIdx !== null && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-4 fade-in"
          onClick={() => setOpenIdx(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setOpenIdx(null); }}
            className="absolute top-4 right-4 min-touch flex items-center justify-center bg-[#15171B] border border-[#2A2E36] rounded-full text-white hover:bg-[#E83A2C] hover:border-[#E83A2C] transition"
            aria-label="Закрыть"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="max-w-full max-h-[80vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img src={photos[openIdx].data} alt={photos[openIdx].caption || ''} className="max-w-full max-h-[80vh] object-contain" />
          </div>

          {photos[openIdx].caption && (
            <div className="mt-4 max-w-2xl text-center text-white text-sm font-mono tracking-wide">
              {photos[openIdx].caption}
            </div>
          )}

          {photos.length > 1 && (
            <div className="mt-4 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setOpenIdx(i => Math.max(0, i - 1))}
                disabled={openIdx === 0}
                className="min-touch flex items-center justify-center bg-[#15171B] border border-[#2A2E36] rounded-full text-white hover:bg-[#E83A2C] disabled:opacity-30 transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="font-mono text-xs text-gray-400 tracking-wider min-w-[60px] text-center">
                {openIdx + 1} / {photos.length}
              </div>
              <button
                onClick={() => setOpenIdx(i => Math.min(photos.length - 1, i + 1))}
                disabled={openIdx === photos.length - 1}
                className="min-touch flex items-center justify-center bg-[#15171B] border border-[#2A2E36] rounded-full text-white hover:bg-[#E83A2C] disabled:opacity-30 transition"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminLessonEditor({ courseId, moduleId, lessonId, navigate, showToast, currentUser }) {
  const { getCourseById, techCards, updateCourse, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditCourses')) {
    return <PermissionGate user={currentUser} perm="canEditCourses" navigate={navigate} label="редактирования урока" />;
  }
  const course = getCourseById(courseId);
  const mod = course?.modules.find(m => m.id === moduleId);
  const lesson = mod?.lessons.find(l => l.id === lessonId);

  if (!mod || !lesson) return null;

  // Автосохранение
  const patch = (patchObj) => {
    const newLesson = { ...lesson, ...patchObj };
    const newMod = { ...mod, lessons: mod.lessons.map(l => l.id === lessonId ? newLesson : l) };
    const next = course.modules.map(m => m.id === moduleId ? newMod : m);
    return updateCourse(courseId, { modules: next });
  };

  const cardOptions = Object.values(techCards).map(c => ({ value: c.id, label: c.name }));

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminModule', { courseId, moduleId })} title="УРОК" subtitle={lesson.title} />

      <div className="py-5 space-y-5">
        {/* Метаданные */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">МЕТАДАННЫЕ · ИЗМЕНЕНИЯ СОХРАНЯЮТСЯ АВТОМАТИЧЕСКИ</div>

          <InlineText
            value={lesson.title}
            onChange={v => patch({ title: v })}
            placeholder="Название урока"
            size="2xl"
            className="text-white font-bold block mb-3"
          />

          <div className="flex items-center gap-3 flex-wrap mb-3">
            <span className="text-xs text-gray-500 font-mono tracking-wider">ДЛИТЕЛЬНОСТЬ:</span>
            <InlineNumber
              value={lesson.duration}
              onChange={v => patch({ duration: v })}
              suffix=" мин"
              min={1} max={999}
              className="text-white"
            />
            <span className="text-gray-700">·</span>
            <span className="text-xs text-gray-500 font-mono tracking-wider">ТИП:</span>
            <button
              onClick={() => patch({ type: 'text', content: lesson.content || '' })}
              className={`px-3 py-1 rounded-md text-xs font-mono tracking-wider transition flex items-center gap-1.5 ${
                lesson.type === 'text' ? 'bg-[#FFB800] text-black' : 'bg-[#0A0B0D] border border-[#2A2E36] text-gray-400 hover:border-[#FFB800]'
              }`}
            >
              <FileText className="w-3 h-3" /> ТЕКСТ
            </button>
            <button
              onClick={() => patch({ type: 'techcard', techCardId: lesson.techCardId || Object.keys(techCards)[0] || '' })}
              className={`px-3 py-1 rounded-md text-xs font-mono tracking-wider transition flex items-center gap-1.5 ${
                lesson.type === 'techcard' ? 'bg-[#E83A2C] text-white' : 'bg-[#0A0B0D] border border-[#2A2E36] text-gray-400 hover:border-[#E83A2C]'
              }`}
            >
              <ChefHat className="w-3 h-3" /> ТЕХКАРТА
            </button>
          </div>
        </div>

        {/* Контент или привязка к техкарте */}
        {lesson.type === 'text' ? (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">СОДЕРЖИМОЕ УРОКА · КЛИКНИ ЧТОБЫ ПРАВИТЬ</div>
            <InlineTextarea
              value={lesson.content || ''}
              onChange={v => patch({ content: v })}
              placeholder="Напиши текст урока…&#10;&#10;Используй пустую строку между абзацами."
              rows={14}
            />
          </div>
        ) : (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <ChefHat className="w-5 h-5 text-[#E83A2C]" />
              <div className="font-display text-lg tracking-wider text-white">ПРИВЯЗАННАЯ ТЕХКАРТА</div>
            </div>
            <InlineSelect
              value={lesson.techCardId}
              onChange={v => patch({ techCardId: v })}
              options={cardOptions}
              placeholder="— выбери блюдо —"
              className="w-full"
            />
            {lesson.techCardId && (
              <button
                onClick={() => navigate('adminTechCard', { cardId: lesson.techCardId })}
                className="mt-3 text-xs text-[#FFB800] hover:text-[#FFC820] flex items-center gap-1 transition"
              >
                Открыть и отредактировать карту блюда <ChevronRight className="w-3 h-3" />
              </button>
            )}
            <div className="text-xs text-gray-600 mt-3">
              Не нашли нужное блюдо?{' '}
              <button onClick={() => navigate('adminTechCards')} className="text-[#FFB800] underline hover:text-[#FFC820]">
                Создайте техкарту
              </button>
            </div>
          </div>
        )}

        {/* Видео — для любого типа урока (тильда-стиль: одно поле) */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <PlayCircle className="w-5 h-5 text-[#E83A2C]" />
            <div className="font-display text-lg tracking-wider text-white">ВИДЕО К УРОКУ</div>
          </div>
          <InlineText
            value={lesson.videoUrl}
            onChange={v => patch({ videoUrl: v })}
            placeholder="Вставь ссылку YouTube / Vimeo / VK / mp4"
            className="text-white block"
          />
          <div className="text-xs text-gray-600 mt-2">
            Например, демонстрация процесса приготовления, разбор ошибок на смене, видео-инструктаж.
          </div>
          {lesson.videoUrl && (
            <div className="mt-3">
              <VideoPlayer url={lesson.videoUrl} />
            </div>
          )}
        </div>

        {/* ───────────── ФОТОГРАФИИ К УРОКУ ───────────── */}
        <LessonPhotosEditor
          photos={lesson.photos || []}
          onChange={(photos) => patch({ photos })}
          showToast={showToast}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: ТЕСТ И ВОПРОСЫ
// ═══════════════════════════════════════════════════════════════

const QUESTION_TYPES = [
  { id: 'single', label: 'Один ответ' },
  { id: 'multi', label: 'Несколько ответов' },
  { id: 'truefalse', label: 'Правда / Ложь' },
  { id: 'order', label: 'Порядок шагов' },
  { id: 'match', label: 'Сопоставление' },
  { id: 'situation', label: 'Ситуация (один ответ)' },
];

function AdminTestEditor({ courseId, moduleId, navigate, showToast, currentUser }) {
  const confirm = useConfirm();
  const { getCourseById, updateCourse, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditCourses')) {
    return <PermissionGate user={currentUser} perm="canEditCourses" navigate={navigate} label="редактирования теста" />;
  }
  const course = getCourseById(courseId);
  const mod = course?.modules.find(m => m.id === moduleId);
  if (!mod) return null;
  const test = mod.test || { passing: 75, questions: [] };

  const updateTest = async (newQuestions) => {
    const newMod = { ...mod, test: { ...test, questions: newQuestions } };
    const next = course.modules.map(m => m.id === moduleId ? newMod : m);
    await updateCourse(courseId, { modules: next });
  };

  const addQuestion = async (type) => {
    let q = { type, q: 'Новый вопрос' };
    if (type === 'single' || type === 'situation') q = { ...q, options: ['Вариант 1', 'Вариант 2'], correct: 0 };
    if (type === 'multi') q = { ...q, options: ['Вариант 1', 'Вариант 2'], correct: [0] };
    if (type === 'truefalse') q = { ...q, correct: true };
    if (type === 'order') q = { ...q, items: ['Шаг 1', 'Шаг 2', 'Шаг 3'], correct: [0, 1, 2] };
    if (type === 'match') q = { ...q, pairs: [['Левая 1', 'Правая 1'], ['Левая 2', 'Правая 2']] };
    await updateTest([...test.questions, q]);
    showToast('Вопрос добавлен');
  };

  const moveQ = async (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= test.questions.length) return;
    const next = [...test.questions];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    await updateTest(next);
  };

  const deleteQ = async (idx) => {
    if (!(await confirm('Удалить вопрос?', { danger: true }))) return;
    await updateTest(test.questions.filter((_, i) => i !== idx));
  };

  const updateQ = async (idx, newQ) => {
    const next = [...test.questions];
    next[idx] = newQ;
    await updateTest(next);
  };

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminModule', { courseId, moduleId })} title="ТЕСТ" subtitle={mod.title} />

      <div className="p-5 space-y-4">
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-1">ПРОХОДНОЙ БАЛЛ</div>
          <div className="font-display text-3xl text-[#FFB800]">{test.passing}%</div>
          <div className="text-xs text-gray-500 mt-1">Меняется в метаданных модуля</div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg tracking-wider text-white">ВОПРОСЫ ({test.questions.length})</div>
          </div>

          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-3 mb-3">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-2">ДОБАВИТЬ ВОПРОС</div>
            <div className="flex flex-wrap gap-1.5">
              {QUESTION_TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => addQuestion(t.id)}
                  className="bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] font-mono text-[10px] tracking-wider px-2.5 py-1.5 rounded transition flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> {t.label.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {test.questions.length === 0 ? (
            <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center text-gray-500 text-sm">
              Вопросов пока нет.
            </div>
          ) : (
            <div className="space-y-2">
              {test.questions.map((q, idx) => (
                <QuestionEditor
                  key={idx}
                  q={q}
                  idx={idx}
                  total={test.questions.length}
                  onUpdate={newQ => updateQ(idx, newQ)}
                  onMove={dir => moveQ(idx, dir)}
                  onDelete={() => deleteQ(idx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionEditor({ q, idx, total, onUpdate, onMove, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const typeLabel = QUESTION_TYPES.find(t => t.id === q.type)?.label || q.type;

  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl overflow-hidden">
      <div className="p-3 flex items-start gap-2">
        <div className="w-7 h-7 rounded bg-[#0A0B0D] flex items-center justify-center font-mono text-[10px] text-gray-500 flex-shrink-0">
          {(idx + 1).toString().padStart(2, '0')}
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="font-mono text-[9px] text-[#E83A2C] tracking-wider mb-0.5">{typeLabel.toUpperCase()}</div>
          <div className="text-sm text-white leading-snug">{q.q}</div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <IconBtn icon={ArrowUp} onClick={() => onMove(-1)} disabled={idx === 0} />
          <IconBtn icon={ArrowDown} onClick={() => onMove(1)} disabled={idx === total - 1} />
          <IconBtn icon={expanded ? X : Edit3} onClick={() => setExpanded(!expanded)} accent />
          <IconBtn icon={Trash2} onClick={onDelete} danger />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#2A2E36] p-4 bg-[#0A0B0D] space-y-3">
          <FormTextarea label="Текст вопроса" value={q.q} onChange={v => onUpdate({ ...q, q: v })} rows={2} />

          {(q.type === 'single' || q.type === 'situation') && (
            <OptionsEditor
              options={q.options}
              correct={q.correct}
              type="single"
              onChange={(options, correct) => onUpdate({ ...q, options, correct })}
            />
          )}

          {q.type === 'multi' && (
            <OptionsEditor
              options={q.options}
              correct={q.correct}
              type="multi"
              onChange={(options, correct) => onUpdate({ ...q, options, correct })}
            />
          )}

          {q.type === 'truefalse' && (
            <div>
              <label className="block text-xs font-mono text-gray-500 tracking-wider mb-2">ПРАВИЛЬНЫЙ ОТВЕТ</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onUpdate({ ...q, correct: true })}
                  className={`p-3 rounded-lg border text-center transition ${
                    q.correct === true ? 'border-[#00C781] bg-[#00C781]/10 text-[#00C781]' : 'border-[#2A2E36] text-gray-500'
                  }`}
                >
                  <Check className="w-4 h-4 mx-auto mb-1" /> ПРАВДА
                </button>
                <button
                  onClick={() => onUpdate({ ...q, correct: false })}
                  className={`p-3 rounded-lg border text-center transition ${
                    q.correct === false ? 'border-[#E83A2C] bg-[#E83A2C]/10 text-[#E83A2C]' : 'border-[#2A2E36] text-gray-500'
                  }`}
                >
                  <X className="w-4 h-4 mx-auto mb-1" /> ЛОЖЬ
                </button>
              </div>
            </div>
          )}

          {q.type === 'order' && (
            <ItemsEditor
              label="ШАГИ В ПРАВИЛЬНОМ ПОРЯДКЕ"
              items={q.items}
              onChange={items => onUpdate({ ...q, items, correct: items.map((_, i) => i) })}
              hint="Пользователь будет видеть их в случайном порядке и должен расставить как у вас."
            />
          )}

          {q.type === 'match' && (
            <PairsEditor
              pairs={q.pairs}
              onChange={pairs => onUpdate({ ...q, pairs })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function OptionsEditor({ options, correct, type, onChange }) {
  const isCorrect = (idx) => type === 'single' ? correct === idx : (correct || []).includes(idx);

  const toggle = (idx) => {
    if (type === 'single') onChange(options, idx);
    else {
      const arr = correct || [];
      const next = arr.includes(idx) ? arr.filter(x => x !== idx) : [...arr, idx];
      onChange(options, next);
    }
  };

  const updateOpt = (idx, val) => {
    const next = [...options];
    next[idx] = val;
    onChange(next, correct);
  };

  const addOpt = () => onChange([...options, 'Новый вариант'], correct);

  const removeOpt = (idx) => {
    const next = options.filter((_, i) => i !== idx);
    let newCorrect = correct;
    if (type === 'single') {
      newCorrect = correct === idx ? 0 : correct > idx ? correct - 1 : correct;
    } else {
      newCorrect = (correct || []).filter(x => x !== idx).map(x => x > idx ? x - 1 : x);
    }
    onChange(next, newCorrect);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-mono text-gray-500 tracking-wider">
          ВАРИАНТЫ {type === 'single' ? '(отметь правильный)' : '(отметь все правильные)'}
        </label>
        <button onClick={addOpt} className="text-[#FFB800] text-xs flex items-center gap-1 hover:text-[#FFC820]">
          <Plus className="w-3 h-3" /> добавить
        </button>
      </div>
      <div className="space-y-1.5">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              onClick={() => toggle(i)}
              className={`w-6 h-6 rounded ${type === 'single' ? 'rounded-full' : ''} border-2 flex items-center justify-center flex-shrink-0 transition ${
                isCorrect(i) ? 'bg-[#00C781] border-[#00C781]' : 'border-gray-600 hover:border-[#00C781]'
              }`}
            >
              {isCorrect(i) && <Check className="w-3.5 h-3.5 text-white" />}
            </button>
            <input
              value={opt}
              onChange={e => updateOpt(i, e.target.value)}
              className="flex-1 bg-[#15171B] border border-[#2A2E36] rounded-md px-3 py-2 text-sm text-white focus:border-[#FFB800] focus:outline-none"
            />
            <IconBtn icon={Trash2} onClick={() => removeOpt(i)} danger disabled={options.length <= 2} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemsEditor({ label, items, onChange, hint }) {
  const update = (idx, val) => {
    const next = [...items]; next[idx] = val; onChange(next);
  };
  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const next = [...items]; [next[idx], next[ni]] = [next[ni], next[idx]]; onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-mono text-gray-500 tracking-wider">{label}</label>
        <button onClick={() => onChange([...items, 'Новый шаг'])} className="text-[#FFB800] text-xs flex items-center gap-1 hover:text-[#FFC820]">
          <Plus className="w-3 h-3" /> добавить
        </button>
      </div>
      {hint && <div className="text-xs text-gray-600 mb-2">{hint}</div>}
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#15171B] border border-[#2A2E36] flex items-center justify-center font-mono text-xs text-[#FFB800] flex-shrink-0">
              {i + 1}
            </div>
            <input
              value={it}
              onChange={e => update(i, e.target.value)}
              className="flex-1 bg-[#15171B] border border-[#2A2E36] rounded-md px-3 py-2 text-sm text-white focus:border-[#FFB800] focus:outline-none"
            />
            <IconBtn icon={ArrowUp} onClick={() => move(i, -1)} disabled={i === 0} />
            <IconBtn icon={ArrowDown} onClick={() => move(i, 1)} disabled={i === items.length - 1} />
            <IconBtn icon={Trash2} onClick={() => onChange(items.filter((_, x) => x !== i))} danger disabled={items.length <= 2} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PairsEditor({ pairs, onChange }) {
  const update = (i, side, val) => {
    const next = pairs.map((p, idx) => idx === i ? (side === 'L' ? [val, p[1]] : [p[0], val]) : p);
    onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-mono text-gray-500 tracking-wider">ПАРЫ ДЛЯ СОПОСТАВЛЕНИЯ</label>
        <button onClick={() => onChange([...pairs, ['Левая', 'Правая']])} className="text-[#FFB800] text-xs flex items-center gap-1 hover:text-[#FFC820]">
          <Plus className="w-3 h-3" /> добавить
        </button>
      </div>
      <div className="space-y-1.5">
        {pairs.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={p[0]}
              onChange={e => update(i, 'L', e.target.value)}
              className="flex-1 bg-[#15171B] border border-[#2A2E36] rounded-md px-3 py-2 text-sm text-white focus:border-[#FFB800] focus:outline-none"
            />
            <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <input
              value={p[1]}
              onChange={e => update(i, 'R', e.target.value)}
              className="flex-1 bg-[#15171B] border border-[#2A2E36] rounded-md px-3 py-2 text-sm text-[#FFB800] focus:border-[#FFB800] focus:outline-none"
            />
            <IconBtn icon={Trash2} onClick={() => onChange(pairs.filter((_, x) => x !== i))} danger disabled={pairs.length <= 2} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: СПИСОК ТЕХКАРТ
// ═══════════════════════════════════════════════════════════════

function AdminTechCardsList({ navigate, showToast, currentUser }) {
  const confirm = useConfirm();
  const { techCards, updateTechCards, kitchenCategories, updateKitchenCategories, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditTechCards')) {
    return <PermissionGate user={currentUser} perm="canEditTechCards" navigate={navigate} label="редактирования тех-карт" />;
  }
  const cards = Object.values(techCards);

  const [brandFilter, setBrandFilter] = useState('all');        // all | shawarmafia | rollomafia
  const [categoryFilter, setCategoryFilter] = useState('all');  // all | <categoryId>
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Фильтрация
  const visibleCards = cards.filter(card => {
    const cardBrands = Array.isArray(card.brands) && card.brands.length > 0 ? card.brands : ['shawarmafia'];
    if (brandFilter !== 'all' && !cardBrands.includes(brandFilter)) return false;
    if (categoryFilter !== 'all' && card.categoryId !== categoryFilter) return false;
    return true;
  });

  // Группировка визуальная — по категориям внутри текущего фильтра бренда
  const visibleCategories = (kitchenCategories || [])
    .filter(c => !c.hidden)
    .filter(c => brandFilter === 'all' || (c.brands || []).includes(brandFilter))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const addCard = async () => {
    const id = genId('card');
    // По умолчанию ставим бренд из текущего фильтра, либо оба
    const defaultBrands = brandFilter !== 'all' ? [brandFilter] : ['shawarmafia'];
    const defaultCategory = categoryFilter !== 'all' ? categoryFilter : (visibleCategories[0]?.id || '');
    const newCard = {
      id, name: 'Новое блюдо',
      brands: defaultBrands,
      categoryId: defaultCategory,
      weight: '300 г', time: '5 мин', level: 1, color: '#E83A2C',
      ingredients: [], steps: [], mistakes: [],
    };
    await updateTechCards({ ...techCards, [id]: newCard });
    showToast('Карта создана');
    navigate('adminTechCard', { cardId: id });
  };

  // Дублирование карточки блюда.
  // ВАЖНО: НЕ перегенерируем id у вложенных полей — в этом проекте:
  //   • ingredients — массив объектов БЕЗ id (поля name/amount/grams/critical)
  //   • steps — массив СТРОК (не объектов)
  //   • mistakes — массив объектов БЕЗ id (поля title/desc)
  // deepClone через JSON делает полную независимую копию — этого достаточно,
  // чтобы изменения копии не влияли на оригинал.
  const duplicateCard = async (originalId) => {
    const orig = techCards[originalId];
    if (!orig) return;
    try {
      const copy = deepClone(orig);
      copy.id = genId('card');
      copy.name = (orig.name || 'Блюдо') + ' (копия)';
      await updateTechCards({ ...techCards, [copy.id]: copy });
      showToast(`Дублировано: «${copy.name}»`);
      navigate('adminTechCard', { cardId: copy.id });
    } catch (e) {
      console.error('[duplicateCard]', e);
      showToast('Не удалось дублировать карточку', 'error');
    }
  };

  const deleteCard = async (id) => {
    if (!(await confirm('Удалить позицию из меню? Все уроки, ссылающиеся на неё, перестанут работать.', { danger: true }))) return;
    const next = { ...techCards };
    delete next[id];
    await updateTechCards(next);
    showToast('Позиция удалена', 'error');
  };

  return (
    <div className="max-w-4xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="МЕНЮ КУХНИ" subtitle="Шаурмафия + Ролломафия" />

      <div className="p-5 space-y-4">
        {/* Табы по брендам */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setBrandFilter('all')}
            className={`px-4 py-2 rounded-lg text-xs font-mono tracking-wider whitespace-nowrap transition border ${
              brandFilter === 'all' ? 'bg-[#FFB800] border-[#FFB800] text-black' : 'bg-[#15171B] border-[#2A2E36] text-gray-400 hover:border-[#FFB800]/50'
            }`}
          >
            ВСЕ КУХНИ · {cards.length}
          </button>
          {BRAND_LIST.map(b => {
            const count = cards.filter(c => (c.brands || ['shawarmafia']).includes(b.id)).length;
            return (
              <button
                key={b.id}
                onClick={() => setBrandFilter(b.id)}
                className={`px-4 py-2 rounded-lg text-xs font-mono tracking-wider whitespace-nowrap transition border flex items-center gap-2 ${
                  brandFilter === b.id ? 'border-2' : 'bg-[#15171B] border-[#2A2E36] text-gray-400 hover:border-[#2A2E36]'
                }`}
                style={brandFilter === b.id ? { background: b.color + '20', borderColor: b.color, color: b.color } : {}}
              >
                <span>{b.icon}</span> {b.name.toUpperCase()} · {count}
              </button>
            );
          })}
        </div>

        {/* Фильтр категорий + кнопка управления */}
        <div className="flex gap-2 overflow-x-auto pb-2 items-center">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 rounded-md text-xs font-mono tracking-wider whitespace-nowrap transition border ${
              categoryFilter === 'all' ? 'bg-[#E83A2C] border-[#E83A2C] text-white' : 'bg-[#0A0B0D] border-[#2A2E36] text-gray-400 hover:border-[#E83A2C]/50'
            }`}
          >
            ВСЕ
          </button>
          {visibleCategories.map(cat => {
            const count = visibleCards.filter(c => c.categoryId === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => setCategoryFilter(cat.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono tracking-wider whitespace-nowrap transition border flex items-center gap-1.5 ${
                  categoryFilter === cat.id ? 'bg-[#E83A2C] border-[#E83A2C] text-white' : 'bg-[#0A0B0D] border-[#2A2E36] text-gray-400 hover:border-[#E83A2C]/50'
                }`}
              >
                <span>{cat.icon}</span> {cat.name.toUpperCase()}
                <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
          <button
            onClick={() => setShowCategoryManager(true)}
            className="ml-auto px-3 py-1.5 rounded-md text-xs font-mono tracking-wider whitespace-nowrap bg-[#0A0B0D] border border-[#2A2E36] text-gray-500 hover:border-[#FFB800] hover:text-[#FFB800] transition flex items-center gap-1.5 flex-shrink-0"
          >
            <Settings className="w-3 h-3" /> КАТЕГОРИИ
          </button>
        </div>

        <button
          onClick={addCard}
          className="w-full bg-[#FFB800] hover:bg-[#FFC820] text-black font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> ДОБАВИТЬ ПОЗИЦИЮ В МЕНЮ
        </button>

        {visibleCards.length === 0 ? (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center text-gray-500 text-sm">
            {cards.length === 0 ? 'Меню пока пустое.' : 'В этой категории/кухне позиций нет.'}
          </div>
        ) : (
          <div className="space-y-2">
            {visibleCards.map(card => {
              const cardBrands = Array.isArray(card.brands) && card.brands.length > 0 ? card.brands : ['shawarmafia'];
              const cat = (kitchenCategories || []).find(c => c.id === card.categoryId);
              return (
                <div key={card.id} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 hover:border-[#FFB800]/40 transition">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: (card.color || '#E83A2C') + '20' }}>
                      <ChefHat className="w-6 h-6" style={{ color: card.color || '#E83A2C' }} />
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('adminTechCard', { cardId: card.id })}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-bold text-white">{card.name}</div>
                        {cardBrands.map(bId => {
                          const b = getBrandById(bId);
                          if (!b) return null;
                          return (
                            <span key={bId} className="text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: b.color + '20', color: b.color }}>
                              {b.icon} {b.short.toUpperCase()}
                            </span>
                          );
                        })}
                        {cat && (
                          <span className="text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded bg-[#0A0B0D] border border-[#2A2E36] text-gray-400">
                            {cat.icon} {cat.name}
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-1">
                        {card.ingredients?.length || 0} ИНГР · {card.steps?.length || 0} ШАГОВ · {card.mistakes?.length || 0} ОШИБОК
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <IconBtn icon={Edit3} onClick={() => navigate('adminTechCard', { cardId: card.id })} accent />
                      <IconBtn icon={Copy} onClick={() => duplicateCard(card.id)} title="Дублировать карточку" />
                      <IconBtn icon={Trash2} onClick={() => deleteCard(card.id)} danger />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Менеджер категорий — модалка */}
      {showCategoryManager && (
        <KitchenCategoryManager
          categories={kitchenCategories}
          onChange={updateKitchenCategories}
          onClose={() => setShowCategoryManager(false)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function KitchenCategoryManager({ categories, onChange, onClose, showToast }) {
  const confirm = useConfirm();
  const [list, setList] = useState([...(categories || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  useEffect(() => {
    setList([...(categories || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
  }, [categories]);

  const saveList = async (newList) => {
    const reordered = newList.map((c, i) => ({ ...c, order: i + 1 }));
    setList(reordered);
    await onChange(reordered);
    setHasChanges(false);
  };

  const markChanged = () => setHasChanges(true);

  const handleClose = async () => {
    if (hasChanges) {
      const reordered = list.map((c, i) => ({ ...c, order: i + 1 }));
      await onChange(reordered);
      showToast?.('Категории сохранены', 'success');
    }
    onClose();
  };

  const addCategory = async () => {
    const newCat = {
      id: 'cat_' + Date.now(),
      name: 'Новая категория',
      icon: '🍽️',
      brands: ['shawarmafia', 'rollomafia'],
      order: (list[list.length - 1]?.order || 0) + 1,
      hidden: false,
    };
    const newList = [...list, newCat];
    setList(newList);
    setHasChanges(true);
    await onChange(newList.map((c, i) => ({ ...c, order: i + 1 })));
  };

  const updateCategory = async (id, patch) => {
    const newList = list.map(c => c.id === id ? { ...c, ...patch } : c);
    setList(newList);
    setHasChanges(true);
    await onChange(newList.map((c, i) => ({ ...c, order: i + 1 })));
  };

  const removeCategory = async (id) => {
    const cat = list.find(c => c.id === id);
    if (!(await confirm(`Удалить категорию «${cat?.name}»?`, { danger: true }))) return;
    const newList = list.filter(c => c.id !== id);
    setList(newList);
    setHasChanges(true);
    await onChange(newList.map((c, i) => ({ ...c, order: i + 1 })));
    showToast?.('Категория удалена', 'error');
  };

  const toggleBrand = async (catId, brandId) => {
    const cat = list.find(c => c.id === catId);
    if (!cat) return;
    const brands = (cat.brands || []);
    const next = brands.includes(brandId)
      ? brands.filter(b => b !== brandId)
      : [...brands, brandId];
    if (next.length === 0) {
      showToast?.('Категория должна быть привязана хотя бы к одной кухне', 'error');
      return;
    }
    await updateCategory(catId, { brands: next });
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const newList = [...list];
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    const reordered = newList.map((c, i) => ({ ...c, order: i + 1 }));
    setList(reordered);
    setHasChanges(true);
    onChange(reordered);
  };

  // Drag & Drop handlers (desktop only)
  const handleDragStart = (e, idx) => {
    if (isMobile) return;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    if (isMobile) return;
    e.preventDefault();
    setDropIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (isMobile || dragIdx === null || dragIdx === idx) return;
    const newList = [...list];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(idx, 0, moved);
    const reordered = newList.map((c, i) => ({ ...c, order: i + 1 }));
    setList(reordered);
    setHasChanges(true);
    onChange(reordered);
    setDragIdx(null);
    setDropIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDropIdx(null);
  };
  
  const handleSave = async () => {
    const reordered = list.map((c, i) => ({ ...c, order: i + 1 }));
    await onChange(reordered);
    setHasChanges(false);
    showToast?.('Категории сохранены', 'success');
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 fade-in" onClick={handleClose}>
      <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0B0D] border-b border-[#2A2E36] p-5 flex items-center justify-between z-10">
		  <div>
			<div className="font-display text-xl tracking-wider text-white">КАТЕГОРИИ МЕНЮ</div>
			<div className="text-xs text-gray-500 mt-0.5">
			  {isMobile ? 'Кнопки ▲▼ для сортировки' : 'Перетаскивайте для сортировки'}
			</div>
		  </div>
		  <div className="flex items-center gap-2">
			<button
			  onClick={handleSave}
			  className="bg-[#00C781] hover:bg-[#00D78F] text-black font-mono text-xs tracking-wider px-3 py-1.5 rounded-lg transition flex items-center gap-1.5"
			>
			  <Save className="w-3.5 h-3.5" /> СОХРАНИТЬ
			</button>
			<button onClick={handleClose} className="min-touch flex items-center justify-center text-gray-400 hover:text-white transition">
			  <X className="w-5 h-5" />
			</button>
		  </div>
		</div>
        <div className="p-5 space-y-2">
          {list.map((cat, idx) => (
            <div
              key={cat.id}
              draggable={!isMobile}
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              className={`bg-[#15171B] border rounded-xl p-3 transition ${
                !isMobile ? 'cursor-grab active:cursor-grabbing' : ''
              } ${
                dropIdx === idx && dragIdx !== idx ? 'border-[#FFB800] border-dashed' : 'border-[#2A2E36]'
              } ${dragIdx === idx ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {/* Drag handle (desktop) or move buttons (mobile) */}
                {!isMobile ? (
                  <div className="text-gray-600 hover:text-white cursor-grab active:cursor-grabbing p-1">
                    <GripVertical className="w-4 h-4" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    <button
                      className="p-0.5 bg-[#0A0B0D] border border-[#2A2E36] rounded text-gray-400 hover:text-white disabled:opacity-30 transition"
                      disabled={idx === 0}
                      onClick={() => move(idx, -1)}
                    >
                      <ArrowUp className="w-2.5 h-2.5" />
                    </button>
                    <button
                      className="p-0.5 bg-[#0A0B0D] border border-[#2A2E36] rounded text-gray-400 hover:text-white disabled:opacity-30 transition"
                      disabled={idx === list.length - 1}
                      onClick={() => move(idx, 1)}
                    >
                      <ArrowDown className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}

                <input
                  type="text"
                  className="w-10 bg-transparent text-xl text-center outline-none"
                  maxLength={2}
                  value={cat.icon}
                  onChange={e => updateCategory(cat.id, { icon: e.target.value })}
                />
                <input
                  type="text"
                  className="flex-1 bg-transparent border-b border-[#2A2E36] focus:border-[#E83A2C] outline-none text-sm text-white py-1 transition"
                  placeholder="Название категории"
                  value={cat.name}
                  onChange={e => updateCategory(cat.id, { name: e.target.value })}
                />
                <button
                  className="p-1.5 bg-[#0A0B0D] border border-[#2A2E36] rounded text-gray-400 hover:text-white transition"
                  title="Скрыть"
                  onClick={() => updateCategory(cat.id, { hidden: !cat.hidden })}
                >
                  {cat.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
                <button
                  className="p-1.5 bg-[#0A0B0D] border border-[#E83A2C]/30 rounded text-[#E83A2C] hover:bg-[#E83A2C]/10 transition"
                  title="Удалить"
                  onClick={() => removeCategory(cat.id)}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {['shawarmafia', 'rollomafia'].map(brandId => {
                  const brand = BRANDS[brandId];
                  const active = (cat.brands || []).includes(brandId);
                  return (
                    <button
                      key={brandId}
                      className="text-[10px] font-mono tracking-wider px-2 py-1 rounded transition"
                      style={{
                        background: active ? brand.color + '25' : '#0A0B0D',
                        color: active ? brand.color : '#5F5F68',
                        border: `1px solid ${active ? brand.color + '60' : '#2A2E36'}`,
                        opacity: active ? 1 : 0.4,
                      }}
                      onClick={() => toggleBrand(cat.id, brandId)}
                    >
                      {brand.icon} {brand.name.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <button
            className="w-full py-3 border border-dashed border-[#2A2E36] hover:border-[#FFB800] text-gray-500 hover:text-[#FFB800] rounded-lg transition flex items-center justify-center gap-2 text-xs font-mono tracking-wider"
            onClick={addCategory}
          >
            <Plus className="w-4 h-4" /> ДОБАВИТЬ КАТЕГОРИЮ
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: ТЕХКАРТА
// ═══════════════════════════════════════════════════════════════

// ─── Блок выбора кухонь и категории в редакторе позиции меню ───
function TechCardBrandsCategoryEditor({ card, patch }) {
  const { kitchenCategories } = useContent();
  const cardBrands = Array.isArray(card.brands) && card.brands.length > 0 ? card.brands : ['shawarmafia'];

  // Категории — отфильтрованы под выбранные бренды позиции (показываем только релевантные)
  const availableCategories = (kitchenCategories || [])
    .filter(c => !c.hidden)
    .filter(c => (c.brands || []).some(b => cardBrands.includes(b)))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const toggleBrand = async (brandId) => {
    const next = cardBrands.includes(brandId)
      ? cardBrands.filter(b => b !== brandId)
      : [...cardBrands, brandId];
    if (next.length === 0) return;  // должен остаться хотя бы один бренд
    await patch({ brands: next });
  };

  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">КУХНИ И КАТЕГОРИЯ</div>

      <div className="mb-4">
        <div className="text-xs text-gray-500 font-mono tracking-wider mb-2">КУХНИ (МОЖНО НЕСКОЛЬКО):</div>
        <div className="flex flex-wrap gap-2">
          {BRAND_LIST.map(b => {
            const active = cardBrands.includes(b.id);
            return (
              <button
                key={b.id}
                onClick={() => toggleBrand(b.id)}
                className={`px-3 py-2 rounded-lg text-xs font-mono tracking-wider transition flex items-center gap-2 border-2 ${
                  active ? '' : 'opacity-50 hover:opacity-80'
                }`}
                style={active
                  ? { background: b.color + '20', color: b.color, borderColor: b.color }
                  : { background: '#0A0B0D', borderColor: '#2A2E36', color: '#5F5F68' }}
              >
                {active && <Check className="w-3.5 h-3.5" />}
                <span>{b.icon}</span>
                {b.name.toUpperCase()}
              </button>
            );
          })}
        </div>
        {cardBrands.length > 1 && (
          <div className="text-[10px] text-gray-600 font-mono mt-2">
            ↳ Позиция будет видна в меню обеих кухонь
          </div>
        )}
      </div>

      <div>
        <div className="text-xs text-gray-500 font-mono tracking-wider mb-2">КАТЕГОРИЯ:</div>
        {availableCategories.length === 0 ? (
          <div className="text-xs text-gray-600 italic">
            Нет доступных категорий для выбранных кухонь. Создайте категорию в управлении меню.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableCategories.map(cat => {
              const active = card.categoryId === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => patch({ categoryId: cat.id })}
                  className={`px-3 py-2 rounded-lg text-xs font-mono tracking-wider transition flex items-center gap-1.5 border ${
                    active ? 'bg-[#E83A2C] border-[#E83A2C] text-white' : 'bg-[#0A0B0D] border-[#2A2E36] text-gray-400 hover:border-[#E83A2C]/50'
                  }`}
                >
                  <span>{cat.icon}</span>
                  {cat.name.toUpperCase()}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminTechCardEditor({ cardId, navigate, showToast, currentUser }) {
  const { techCards, updateTechCards, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditTechCards')) {
    return <PermissionGate user={currentUser} perm="canEditTechCards" navigate={navigate} label="редактирования тех-карт" />;
  }
  const card = techCards[cardId];

  if (!card) {
    return (
      <div className="max-w-3xl mx-auto p-5">
        <button onClick={() => navigate('adminTechCards')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-4 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К ТЕХКАРТАМ
        </button>
        <div className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[#E83A2C] mx-auto mb-3" />
          <div className="text-white">Карта не найдена</div>
        </div>
      </div>
    );
  }

  // Автосохранение — все изменения уходят в shared storage немедленно
  const patch = (patchObj) => {
    return updateTechCards({ ...techCards, [cardId]: { ...card, ...patchObj } });
  };

  // ─── Ингредиенты ───
  const addIngredientAt = (insertIdx = (card.ingredients?.length || 0)) => {
    const next = [...(card.ingredients || [])];
    next.splice(insertIdx, 0, { name: '', grams: '', critical: false });
    return patch({ ingredients: next });
  };
  const updIngredient = (i, field, v) => {
    const next = [...card.ingredients];
    next[i] = { ...next[i], [field]: v };
    return patch({ ingredients: next });
  };
  const reorderIng = (from, to) => {
    const next = [...card.ingredients];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ ingredients: next });
  };
  const removeIng = (i) => patch({ ingredients: card.ingredients.filter((_, x) => x !== i) });

  // ─── Шаги ───
  const addStepAt = (insertIdx = (card.steps?.length || 0)) => {
    const next = [...(card.steps || [])];
    next.splice(insertIdx, 0, '');
    return patch({ steps: next });
  };
  const updStep = (i, v) => {
    const next = [...card.steps]; next[i] = v;
    return patch({ steps: next });
  };
  const reorderSteps = (from, to) => {
    const next = [...card.steps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ steps: next });
  };
  const removeStep = (i) => patch({ steps: card.steps.filter((_, x) => x !== i) });

  // ─── Ошибки ───
  const addMistakeAt = (insertIdx = (card.mistakes?.length || 0)) => {
    const next = [...(card.mistakes || [])];
    next.splice(insertIdx, 0, { title: '', desc: '' });
    return patch({ mistakes: next });
  };
  const updMistake = (i, field, v) => {
    const next = [...card.mistakes];
    next[i] = { ...next[i], [field]: v };
    return patch({ mistakes: next });
  };
  const reorderMistakes = (from, to) => {
    const next = [...card.mistakes];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ mistakes: next });
  };
  const removeMistake = (i) => patch({ mistakes: card.mistakes.filter((_, x) => x !== i) });

  // Прикрепляем id-функцию к спискам для DraggableList (использует item.id или idx)
  const ingredients = (card.ingredients || []).map((it, i) => ({ ...it, _id: `ing-${i}-${it.name}` }));
  const steps = (card.steps || []).map((s, i) => ({ value: s, _id: `step-${i}` }));
  const mistakes = (card.mistakes || []).map((m, i) => ({ ...m, _id: `mis-${i}-${m.title}` }));

  // Inline-предпросмотр: как карточка видна стажёру
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminTechCards')} title="ТЕХКАРТА" subtitle={card.name} />

      {/* Toggle: режим редактирования / предпросмотр */}
      <div className="px-5 pt-2 flex justify-end">
        <button
          onClick={() => setShowPreview(p => !p)}
          className={`font-mono text-xs tracking-wider px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition border ${
            showPreview
              ? 'bg-[#FFB800] border-[#FFB800] text-black'
              : 'bg-[#15171B] border-[#2A2E36] text-gray-400 hover:border-[#FFB800] hover:text-[#FFB800]'
          }`}
        >
          {showPreview ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {showPreview ? 'РЕДАКТИРОВАТЬ' : 'ПРЕДПРОСМОТР'}
        </button>
      </div>

      {/* Режим предпросмотра — рендерим тот же компонент TechCardScreen, но без onComplete */}
      {showPreview && (
        <div className="mt-2 pop-in">
          <div className="px-5 mb-2 font-mono text-[10px] text-[#FFB800] tracking-[0.25em] flex items-center gap-2">
            <Eye className="w-3 h-3" /> ВИД ДЛЯ СТАЖЁРА · СОХРАНЕНИЕ ОТКЛЮЧЕНО
          </div>
          <div className="border-2 border-dashed border-[#FFB800]/30 rounded-2xl mx-3 overflow-hidden">
            <TechCardScreen
              techCardId={cardId}
              navigate={() => {}}
              backTo="adminTechCards"
              backCtx={{}}
            />
          </div>
        </div>
      )}

      {!showPreview && (
      <div className="py-5 space-y-5">
        {/* Метаданные блюда */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">МЕТАДАННЫЕ БЛЮДА · ИЗМЕНЕНИЯ СОХРАНЯЮТСЯ АВТОМАТИЧЕСКИ</div>

          <InlineText
            value={card.name}
            onChange={v => patch({ name: v })}
            placeholder="Название блюда"
            size="2xl"
            className="text-white font-bold block mb-3"
          />

          <div className="flex items-center gap-3 flex-wrap mb-3">
            <span className="text-xs text-gray-500 font-mono tracking-wider">ВЕС/ОБЪЁМ:</span>
            <InlineText
              value={card.weight}
              onChange={v => patch({ weight: v })}
              placeholder="380 г"
              className="text-white"
            />
            <span className="text-gray-700">·</span>
            <span className="text-xs text-gray-500 font-mono tracking-wider">ВРЕМЯ:</span>
            <InlineText
              value={card.time}
              onChange={v => patch({ time: v })}
              placeholder="4 мин"
              className="text-white"
            />
            <span className="text-gray-700">·</span>
            <span className="text-xs text-gray-500 font-mono tracking-wider">УРОВЕНЬ:</span>
            <InlineNumber
              value={card.level}
              onChange={v => patch({ level: v })}
              min={1} max={5}
              className="text-[#FFB800]"
            />
          </div>

          <div className="flex items-center gap-2 pt-3 border-t border-[#2A2E36]">
            <span className="text-xs text-gray-500 font-mono tracking-wider">ЦВЕТ:</span>
            {['#E83A2C', '#FFB800', '#00C781', '#00E5FF', '#A855F7'].map(c => (
              <button
                key={c}
                onClick={() => patch({ color: c })}
                className={`w-7 h-7 rounded-lg border-2 transition active:scale-90 ${card.color === c ? 'border-white' : 'border-transparent hover:border-gray-500'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* ───── Кухни (бренды) и категория ───── */}
        <TechCardBrandsCategoryEditor card={card} patch={patch} />

        {/* Видео */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <PlayCircle className="w-5 h-5 text-[#E83A2C]" />
            <div className="font-display text-lg tracking-wider text-white">ВИДЕО ПРИГОТОВЛЕНИЯ</div>
          </div>
          <InlineText
            value={card.videoUrl}
            onChange={v => patch({ videoUrl: v })}
            placeholder="Вставь ссылку YouTube / Vimeo / VK / mp4"
            className="text-white block"
          />
          {card.videoUrl && (
            <div className="mt-3">
              <VideoPlayer url={card.videoUrl} />
            </div>
          )}
        </div>

        {/* Ингредиенты */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-lg tracking-wider text-white">ИНГРЕДИЕНТЫ · {ingredients.length}</div>
          </div>
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-2">НАЖМИ НА ИМЯ ИЛИ ГРАММОВКУ · ТЯНИ ЗА GRIP</div>

          {ingredients.length === 0 ? (
            <button onClick={() => addIngredientAt(0)} className="w-full bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] rounded-lg py-4 transition text-gray-500 hover:text-[#FFB800] text-sm flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Добавить первый ингредиент
            </button>
          ) : (
            <>
              <AddBlockButton onAdd={() => addIngredientAt(0)} label="ингр." />
              <DraggableList
                items={ingredients}
                idKey="_id"
                onReorder={reorderIng}
                renderItem={(ing, i, dragProps) => (
                  <>
                    <div className={`bg-[#0A0B0D] border rounded-lg p-2.5 transition ${ing.critical ? 'border-[#E83A2C]/40' : 'border-[#2A2E36]'}`}>
                      <div className="flex items-center gap-2">
                        <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] p-1`}>
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <InlineText
                            value={ing.name}
                            onChange={v => updIngredient(i, 'name', v)}
                            placeholder="Название ингредиента"
                            className="text-white block"
                          />
                        </div>
                        <InlineText
                          value={ing.grams}
                          onChange={v => updIngredient(i, 'grams', v)}
                          placeholder="120 г"
                          className="text-[#FFB800] font-mono"
                        />
                        <InlineToggle
                          value={ing.critical || false}
                          onChange={v => updIngredient(i, 'critical', v)}
                          accent="#E83A2C"
                        />
                        <IconBtn icon={Trash2} onClick={() => removeIng(i)} danger />
                      </div>
                      {ing.critical && (
                        <div className="text-[9px] text-[#E83A2C] font-mono tracking-wider mt-1 ml-7">КРИТИЧЕСКИЙ — ОБЯЗАТЕЛЬНО ПО РЕЦЕПТУ</div>
                      )}
                    </div>
                    <AddBlockButton onAdd={() => addIngredientAt(i + 1)} label="ингр." />
                  </>
                )}
              />
            </>
          )}
        </div>

        {/* Шаги */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-lg tracking-wider text-white">ШАГИ ПРИГОТОВЛЕНИЯ · {steps.length}</div>
          </div>
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-2">НАЖМИ НА ШАГ · ТЯНИ ЗА GRIP</div>

          {steps.length === 0 ? (
            <button onClick={() => addStepAt(0)} className="w-full bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] rounded-lg py-4 transition text-gray-500 hover:text-[#FFB800] text-sm flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Добавить первый шаг
            </button>
          ) : (
            <>
              <AddBlockButton onAdd={() => addStepAt(0)} label="шаг" />
              <DraggableList
                items={steps}
                idKey="_id"
                onReorder={reorderSteps}
                renderItem={(s, i, dragProps) => (
                  <>
                    <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 transition">
                      <div className="flex items-start gap-2">
                        <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] p-1 mt-1`}>
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>
                        <div className="w-7 h-7 rounded bg-[#E83A2C] flex items-center justify-center font-display text-xs text-white flex-shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <InlineTextarea
                            value={s.value}
                            onChange={v => updStep(i, v)}
                            placeholder="Опиши шаг приготовления"
                            rows={2}
                          />
                        </div>
                        <IconBtn icon={Trash2} onClick={() => removeStep(i)} danger />
                      </div>
                    </div>
                    <AddBlockButton onAdd={() => addStepAt(i + 1)} label="шаг" />
                  </>
                )}
              />
            </>
          )}
        </div>

        {/* Ошибки */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-lg tracking-wider text-white">ЧАСТЫЕ ОШИБКИ · {mistakes.length}</div>
          </div>
          <div className="text-xs text-gray-600 font-mono tracking-wider mb-2">КЛИКНИ НА ЗАГОЛОВОК ИЛИ ОПИСАНИЕ</div>

          {mistakes.length === 0 ? (
            <button onClick={() => addMistakeAt(0)} className="w-full bg-[#1F0A0A] hover:bg-[#E83A2C]/10 border border-dashed border-[#E83A2C]/30 hover:border-[#E83A2C] rounded-lg py-4 transition text-gray-500 hover:text-[#E83A2C] text-sm flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Добавить первую ошибку
            </button>
          ) : (
            <>
              <AddBlockButton onAdd={() => addMistakeAt(0)} label="ошибка" />
              <DraggableList
                items={mistakes}
                idKey="_id"
                onReorder={reorderMistakes}
                renderItem={(m, i, dragProps) => (
                  <>
                    <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-lg p-3 transition">
                      <div className="flex items-start gap-2">
                        <div {...dragProps} className={`${dragProps.className} text-[#E83A2C]/60 hover:text-[#E83A2C] p-1 mt-1`}>
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>
                        <X className="w-4 h-4 text-[#E83A2C] flex-shrink-0 mt-1.5" />
                        <div className="flex-1 min-w-0 space-y-1">
                          <InlineText
                            value={m.title}
                            onChange={v => updMistake(i, 'title', v)}
                            placeholder="Что часто делают не так"
                            className="text-[#E83A2C] font-bold block"
                          />
                          <InlineTextarea
                            value={m.desc}
                            onChange={v => updMistake(i, 'desc', v)}
                            placeholder="Что произойдёт и как правильно"
                            rows={2}
                            className="text-xs"
                          />
                        </div>
                        <IconBtn icon={Trash2} onClick={() => removeMistake(i)} danger />
                      </div>
                    </div>
                    <AddBlockButton onAdd={() => addMistakeAt(i + 1)} label="ошибка" />
                  </>
                )}
              />
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: ШАБЛОН ПРАКТИЧЕСКОГО ЧЕК-ЛИСТА
// ═══════════════════════════════════════════════════════════════

function AdminChecklistTemplate({ navigate, showToast, currentUser }) {
  const confirm = useConfirm();
  const { checklistTemplate, updateChecklistTemplate, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditChecklists')) {
    return <PermissionGate user={currentUser} perm="canEditChecklists" navigate={navigate} label="редактирования чек-листа" />;
  }

  // Автосохранение: меняем сразу
  const patchAll = (next) => updateChecklistTemplate(next);

  const addSectionAt = (insertIdx = checklistTemplate.length) => {
    const next = [...checklistTemplate];
    next.splice(insertIdx, 0, { section: 'Новый раздел', items: ['Новый пункт'] });
    return patchAll(next);
  };
  const updSection = (i, field, v) => {
    const next = checklistTemplate.map((s, idx) => idx === i ? { ...s, [field]: v } : s);
    return patchAll(next);
  };
  const reorderSections = (from, to) => {
    const next = [...checklistTemplate];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patchAll(next);
  };
  const removeSection = async (i) => {
    if (!(await confirm('Удалить раздел?', { danger: true }))) return;
    return patchAll(checklistTemplate.filter((_, x) => x !== i));
  };

  const addItemAt = (si, insertIdx) => {
    const newItems = [...checklistTemplate[si].items];
    if (insertIdx === undefined) insertIdx = newItems.length;
    newItems.splice(insertIdx, 0, 'Новый пункт');
    return updSection(si, 'items', newItems);
  };
  const updItem = (si, ii, v) => {
    const newItems = checklistTemplate[si].items.map((it, idx) => idx === ii ? v : it);
    return updSection(si, 'items', newItems);
  };
  const reorderItems = (si) => (from, to) => {
    const next = [...checklistTemplate[si].items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return updSection(si, 'items', next);
  };
  const removeItem = (si, ii) => updSection(si, 'items', checklistTemplate[si].items.filter((_, x) => x !== ii));

  const sections = checklistTemplate.map((s, i) => ({ ...s, _id: `sec-${i}-${s.section}` }));

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="ЧЕК-ЛИСТ" subtitle="Шаблон практической части" />

      <div className="py-5 space-y-3">
        <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-xl p-3 text-xs text-gray-400 leading-relaxed">
          Это <strong className="text-white">шаблон</strong>. Каждый стажёр получает свою копию при оценке. Редактирование шаблона не затронет уже подписанные чек-листы. Все изменения сохраняются автоматически.
        </div>

        {sections.length === 0 ? (
          <button
            onClick={() => addSectionAt(0)}
            className="w-full bg-[#15171B] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] py-12 rounded-xl flex flex-col items-center gap-2 transition"
          >
            <Plus className="w-8 h-8" />
            <span className="text-sm">Добавить первый раздел</span>
          </button>
        ) : (
          <>
            <AddBlockButton onAdd={() => addSectionAt(0)} label="раздел" />
            <DraggableList
              items={sections}
              idKey="_id"
              onReorder={reorderSections}
              renderItem={(section, si, dragProps) => {
                const items = section.items.map((it, ii) => ({ value: it, _id: `it-${si}-${ii}-${it.slice(0, 10)}` }));
                return (
                  <>
                    <div className="bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800]/40 rounded-2xl p-4 space-y-2 transition">
                      <div className="flex items-center gap-2">
                        <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] p-1`}>
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <InlineText
                            value={section.section}
                            onChange={v => updSection(si, 'section', v)}
                            placeholder="Название раздела"
                            size="lg"
                            className="font-bold text-white block"
                          />
                        </div>
                        <IconBtn icon={Trash2} onClick={() => removeSection(si)} danger />
                      </div>

                      <div className="pl-8 space-y-1">
                        {items.length === 0 ? (
                          <button
                            onClick={() => addItemAt(si, 0)}
                            className="text-xs text-gray-500 hover:text-[#FFB800] flex items-center gap-1 py-1 transition"
                          >
                            <Plus className="w-3 h-3" /> Добавить пункт
                          </button>
                        ) : (
                          <>
                            <AddBlockButton onAdd={() => addItemAt(si, 0)} label="пункт" />
                            <DraggableList
                              items={items}
                              idKey="_id"
                              onReorder={reorderItems(si)}
                              renderItem={(it, ii, idp) => (
                                <>
                                  <div className="flex items-center gap-2 group bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-2 hover:border-[#FFB800]/30 transition">
                                    <div {...idp} className={`${idp.className} text-gray-700 hover:text-[#FFB800] p-1`}>
                                      <GripVertical className="w-3 h-3" />
                                    </div>
                                    <div className="w-4 h-4 rounded border border-gray-600 flex-shrink-0"></div>
                                    <div className="flex-1 min-w-0">
                                      <InlineText
                                        value={it.value}
                                        onChange={v => updItem(si, ii, v)}
                                        placeholder="Текст пункта чек-листа"
                                        size="sm"
                                        className="text-gray-300 block"
                                      />
                                    </div>
                                    <button
                                      onClick={() => removeItem(si, ii)}
                                      disabled={section.items.length <= 1}
                                      className="text-gray-600 hover:text-[#E83A2C] disabled:opacity-30 disabled:cursor-not-allowed p-1 opacity-0 group-hover:opacity-100 transition"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <AddBlockButton onAdd={() => addItemAt(si, ii + 1)} label="пункт" />
                                </>
                              )}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <AddBlockButton onAdd={() => addSectionAt(si + 1)} label="раздел" />
                  </>
                );
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИНКА: ОБЩИЕ КОМПОНЕНТЫ ФОРМ
// ═══════════════════════════════════════════════════════════════

function AdminHeader({ back, title, subtitle, children }) {
  const { openMenu } = useNavUi();
  return (
    <div className="app-header pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 z-10">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <button onClick={back} className="-ml-2 min-touch px-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 text-sm flex items-center gap-1 transition">
          <ChevronLeft className="w-4 h-4" /> Назад
        </button>
        <button onClick={openMenu} className="min-touch rounded-xl text-gray-400 hover:text-white hover:bg-white/5 flex items-center justify-center transition" aria-label="Меню и поиск">
          <Search className="w-5 h-5" />
        </button>
      </div>
      <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.2em]">{title}</div>
      <h1 className="font-display text-2xl sm:text-3xl tracking-wider text-white mt-0.5 break-words">{subtitle?.toUpperCase()}</h1>
      {children}
    </div>
  );
}

function AdminActionBar({ onSave, onCancel }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D] to-transparent pt-8 px-5 safe-bottom z-20">
      <div className="max-w-3xl mx-auto flex gap-3">
        <button
          onClick={onCancel}
          className="px-5 bg-[#15171B] border border-[#2A2E36] hover:border-gray-600 text-gray-300 font-mono tracking-wider py-3 rounded-lg"
        >
          ОТМЕНА
        </button>
        <button
          onClick={onSave}
          className="flex-1 bg-[#00C781] hover:bg-[#00D78F] text-black font-mono tracking-wider py-3 rounded-lg flex items-center justify-center gap-2 glow-green"
        >
          <Save className="w-4 h-4" /> СОХРАНИТЬ
        </button>
      </div>
    </div>
  );
}

function FormInput({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">{label.toUpperCase()}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0A0B0D] border border-[#2A2E36] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FFB800] focus:outline-none transition"
      />
    </div>
  );
}

function FormTextarea({ label, value, onChange, rows = 4, hint }) {
  return (
    <div>
      <label className="block text-xs font-mono text-gray-500 tracking-wider mb-1.5">{label.toUpperCase()}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        className="w-full bg-[#0A0B0D] border border-[#2A2E36] rounded-lg px-3 py-2.5 text-white text-sm resize-y focus:border-[#FFB800] focus:outline-none transition font-body leading-relaxed"
      />
      {hint && <div className="text-xs text-gray-600 mt-1">{hint}</div>}
    </div>
  );
}

function FormCheckbox({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
          value ? 'bg-[#FFB800] border-[#FFB800]' : 'border-gray-600'
        }`}
      >
        {value && <Check className="w-4 h-4 text-black" />}
      </button>
      <span className="text-sm text-gray-300">{label}</span>
    </label>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[#2A2E36] pb-2 last:border-b-0 last:pb-0">
      <div className="font-mono text-[10px] text-gray-500 tracking-wider">{label.toUpperCase()}</div>
      <div className="text-white text-sm text-right max-w-[60%] truncate">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// INLINE-РЕДАКТИРОВАНИЕ (Тильда-стиль)
// Кликни по тексту → правишь прямо в потоке → Enter/blur сохраняет
// ═══════════════════════════════════════════════════════════════

/**
 * Однострочное inline-поле. Выглядит как обычный текст, при клике
 * превращается в input. Enter — сохранить, Esc — отменить, blur — сохранить.
 */
function InlineText({
  value, onChange, placeholder = 'Нажми, чтобы изменить',
  className = '', size = 'md', readOnly = false
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value || '');
  };

  const sizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'font-display text-3xl tracking-wider',
  };
  const sizeClass = sizes[size] || sizes.md;

  if (readOnly) {
    return <span className={`${sizeClass} ${className}`}>{value || placeholder}</span>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') cancel();
        }}
        className={`${sizeClass} ${className} bg-[#0A0B0D] border border-[#FFB800] rounded px-2 py-0.5 outline-none w-full max-w-full`}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className={`${sizeClass} ${className} cursor-text rounded px-2 py-0.5 -mx-2 -my-0.5 hover:bg-[#FFB800]/10 hover:ring-1 hover:ring-[#FFB800]/30 transition inline-block ${!value ? 'text-gray-600 italic' : ''}`}
      title="Нажми, чтобы изменить"
    >
      {value || placeholder}
    </span>
  );
}

/**
 * Многострочное inline-поле. Esc — отменить, blur — сохранить.
 */
function InlineTextarea({ value, onChange, placeholder = 'Текст…', rows = 4, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };
  const cancel = () => { setEditing(false); setDraft(value || ''); };

  if (editing) {
    return (
      <textarea
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') cancel(); }}
        rows={rows}
        className={`${className} w-full bg-[#0A0B0D] border border-[#FFB800] rounded px-3 py-2 outline-none resize-y text-sm text-gray-200 leading-relaxed`}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      className={`${className} cursor-text rounded p-3 -m-1 hover:bg-[#FFB800]/5 hover:ring-1 hover:ring-[#FFB800]/30 transition whitespace-pre-line leading-relaxed text-sm ${!value ? 'text-gray-600 italic' : 'text-gray-300'}`}
      title="Нажми, чтобы изменить"
    >
      {value || placeholder}
    </div>
  );
}

/**
 * Inline-число. Сразу клик и правишь.
 */
function InlineNumber({ value, onChange, suffix = '', placeholder = '0', min, max, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  const ref = useRef(null);

  useEffect(() => { setDraft(String(value ?? '')); }, [value]);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);

  const commit = () => {
    setEditing(false);
    let n = parseInt(draft, 10);
    if (isNaN(n)) n = 0;
    if (typeof min === 'number') n = Math.max(min, n);
    if (typeof max === 'number') n = Math.min(max, n);
    if (n !== value) onChange(n);
    setDraft(String(n));
  };

  if (editing) {
    return (
      <input
        ref={ref}
        type="number"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setEditing(false); setDraft(String(value ?? '')); }
        }}
        className={`${className} bg-[#0A0B0D] border border-[#FFB800] rounded px-2 py-0.5 outline-none w-20 text-sm`}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className={`${className} cursor-text rounded px-2 py-0.5 -mx-2 -my-0.5 hover:bg-[#FFB800]/10 hover:ring-1 hover:ring-[#FFB800]/30 transition inline-block font-mono text-sm`}
      title="Нажми, чтобы изменить"
    >
      {(value ?? '') !== '' ? `${value}${suffix}` : <span className="text-gray-600 italic">{placeholder}</span>}
    </span>
  );
}

/**
 * Inline-select — выпадающий список без открытия отдельного экрана.
 */
function InlineSelect({ value, onChange, options, placeholder = 'Не выбрано', className = '' }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className={`${className} bg-[#0A0B0D] border border-[#2A2E36] hover:border-[#FFB800] focus:border-[#FFB800] rounded px-2 py-1 outline-none text-sm text-white transition cursor-pointer`}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * Inline-checkbox (toggle).
 */
function InlineToggle({ value, onChange, label, accent = '#FFB800' }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className="flex items-center gap-2 group cursor-pointer hover-lift"
    >
      <span
        className="relative w-10 h-5 rounded-full transition"
        style={{ background: value ? accent : '#2A2E36' }}
      >
        <span
          className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow"
          style={{ left: value ? '22px' : '2px' }}
        />
      </span>
      {label && <span className="text-xs text-gray-400 group-hover:text-white transition">{label}</span>}
    </button>
  );
}

/**
 * Кнопка-разделитель «+» между блоками. По умолчанию невидима, появляется при hover.
 * onAdd — функция, которая вставит новый блок на эту позицию.
 */
function AddBlockButton({ onAdd, label = 'Добавить', className = '' }) {
  return (
    <div className={`relative h-2 group ${className}`}>
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-[#2A2E36] opacity-0 group-hover:opacity-100 transition" />
      <button
        onClick={onAdd}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition px-3 py-1 bg-[#FFB800] text-black font-mono text-[10px] tracking-wider rounded flex items-center gap-1 z-10 shadow-lg hover:bg-[#FFC820]"
      >
        <Plus className="w-3 h-3" /> {label.toUpperCase()}
      </button>
    </div>
  );
}

/**
 * Drag-and-drop обёртка списка через HTML5 API. Без внешних библиотек.
 * `items` — массив, `onReorder(from, to)` — колбэк.
 * `renderItem(item, idx, dragHandleProps)` — функция рендера элемента.
 */
function DraggableList({ items, onReorder, renderItem, idKey = 'id' }) {
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const handleDragStart = (e, idx) => {
    setDraggingIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // нужно установить data чтобы Firefox не отменил
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIdx !== idx) setOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (draggingIdx !== null && draggingIdx !== idx) {
      onReorder(draggingIdx, idx);
    }
    setDraggingIdx(null);
    setOverIdx(null);
  };

  const handleDragEnd = () => {
    setDraggingIdx(null);
    setOverIdx(null);
  };

  return (
    <div>
      {items.map((item, idx) => (
        <div
          key={item[idKey] || idx}
          draggable={false}
          onDragOver={e => handleDragOver(e, idx)}
          onDrop={e => handleDrop(e, idx)}
          onDragEnd={handleDragEnd}
          className={`transition ${
            draggingIdx === idx ? 'opacity-40' : ''
          } ${
            overIdx === idx && draggingIdx !== null && draggingIdx !== idx ? 'border-t-2 border-[#FFB800]' : ''
          }`}
        >
          {renderItem(item, idx, {
            draggable: true,
            onDragStart: (e) => handleDragStart(e, idx),
            onDragEnd: handleDragEnd,
            'aria-label': 'Перетащить для изменения порядка',
            className: 'cursor-grab active:cursor-grabbing',
          })}
        </div>
      ))}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, disabled, danger, accent, title }) {
  let classes = 'w-8 h-8 rounded flex items-center justify-center transition flex-shrink-0 touch-target';
  if (disabled) classes += ' opacity-30 cursor-not-allowed';
  else if (danger) classes += ' bg-[#15171B] border border-[#2A2E36] hover:border-[#E83A2C] text-gray-400 hover:text-[#E83A2C]';
  else if (accent) classes += ' bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800]';
  else classes += ' bg-[#15171B] border border-[#2A2E36] hover:border-gray-500 text-gray-400 hover:text-white';

  return (
    <button onClick={onClick} disabled={disabled} className={classes} title={title}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: СПИСОК КУРСОВ (новый)
// ═══════════════════════════════════════════════════════════════

function AdminCoursesList({ navigate, showToast, currentUser }) {
  const { courses, updateCourses, updateCourse, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditCourses')) {
    return <PermissionGate user={currentUser} perm="canEditCourses" navigate={navigate} label="редактирования курсов" />;
  }

  const addCourseAt = async (insertIdx = courses.length) => {
    const newCourse = {
      id: genId('course'),
      brand: '',
      role: '',
      title: 'Новый курс',
      passing: 85,
      modules: []
    };
    const next = [...courses];
    next.splice(insertIdx, 0, newCourse);
    await updateCourses(next);
  };

  const reorder = async (from, to) => {
    const next = [...courses];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    await updateCourses(next);
  };

  const deleteCourse = async (id) => {
    if (!(await confirm('Удалить курс? Прогресс стажёров по нему не будет работать.', { danger: true }))) return;
    await updateCourses(courses.filter(c => c.id !== id));
    showToast('Курс удалён', 'error');
  };

  const duplicateCourse = async (id) => {
    const orig = courses.find(c => c.id === id);
    if (!orig) return;
    const copy = deepClone(orig);
    copy.id = genId('course');
    copy.title = orig.title + ' (копия)';
    copy.modules.forEach(m => {
      m.id = genId('m');
      m.lessons.forEach(l => { l.id = genId('l'); });
    });
    await updateCourses([...courses, copy]);
    showToast('Курс продублирован');
  };

  return (
    <div className="max-w-3xl mx-auto pb-32 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="КУРСЫ" subtitle="Все программы обучения" />

      <div className="p-5 space-y-1">
        {courses.length === 0 ? (
          <div className="bg-[#15171B] border border-dashed border-[#2A2E36] rounded-xl p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <div className="text-gray-500 text-sm mb-3">Курсов пока нет</div>
            <button
              onClick={() => addCourseAt(0)}
              className="bg-[#FFB800] hover:bg-[#FFC820] text-black font-mono text-xs tracking-wider px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" /> СОЗДАТЬ ПЕРВЫЙ КУРС
            </button>
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-600 font-mono tracking-wider mb-2 px-1">
              ПОТЯНИ ЗА ПОЛОСКИ СЛЕВА, ЧТОБЫ ПОМЕНЯТЬ ПОРЯДОК · НАЖМИ НА ТЕКСТ, ЧТОБЫ ИЗМЕНИТЬ
            </div>
            <AddBlockButton onAdd={() => addCourseAt(0)} label="курс" />
            <DraggableList
              items={courses}
              onReorder={reorder}
              renderItem={(c, idx, dragProps) => {
                const totalLessons = c.modules.reduce((s, m) => s + m.lessons.length, 0);
                const totalQ = c.modules.reduce((s, m) => s + (m.test?.questions?.length || 0), 0);
                return (
                  <>
                    <div className="bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800]/40 rounded-xl p-4 transition">
                      <div className="flex items-start gap-3">
                        <div
                          {...dragProps}
                          className={`${dragProps.className} w-6 h-11 flex items-center justify-center text-gray-600 hover:text-[#FFB800] flex-shrink-0`}
                          title="Перетащи для смены порядка"
                        >
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#FFB800]/30 to-[#FFB800]/10 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-5 h-5 text-[#FFB800]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <InlineText
                            value={c.title}
                            onChange={v => updateCourse(c.id, { title: v })}
                            placeholder="Название курса"
                            className="font-bold text-white block"
                          />
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <InlineText
                              value={c.brand}
                              onChange={v => updateCourse(c.id, { brand: v })}
                              placeholder="Бренд"
                              className="font-mono text-[10px] text-gray-400 tracking-wider uppercase"
                              size="sm"
                            />
                            <span className="text-gray-700 text-[10px]">·</span>
                            <InlineText
                              value={c.role}
                              onChange={v => updateCourse(c.id, { role: v })}
                              placeholder="Роль"
                              className="font-mono text-[10px] text-gray-400 tracking-wider uppercase"
                              size="sm"
                            />
                          </div>
                          <div
                            className="font-mono text-[9px] text-gray-600 tracking-wider mt-2 cursor-pointer hover:text-[#FFB800] transition"
                            onClick={() => navigate('adminCourse', { courseId: c.id })}
                            title="Открыть модули"
                          >
                            {c.modules.length} МОДУЛЕЙ · {totalLessons} УРОКОВ · {totalQ} ВОПРОСОВ → ОТКРЫТЬ
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <IconBtn icon={Copy} onClick={() => duplicateCourse(c.id)} />
                          <IconBtn icon={Edit3} onClick={() => navigate('adminCourse', { courseId: c.id })} accent />
                          <IconBtn icon={Trash2} onClick={() => deleteCourse(c.id)} danger />
                        </div>
                      </div>
                    </div>
                    <AddBlockButton onAdd={() => addCourseAt(idx + 1)} label="курс" />
                  </>
                );
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// РЕДАКТОР: СПИСОК ПОЛЬЗОВАТЕЛЕЙ
// ═══════════════════════════════════════════════════════════════

// ─── UsersStatsHero ───
// Hero-блок над списком сотрудников. Показывает 4 ключевые метрики с анимированными цифрами:
// всего · активны сегодня · новые за неделю · скоро ДР.
function UsersStatsHero({ users }) {
  const total = users.length;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const weekAgo = today.getTime() - 7 * 24 * 3600 * 1000;

  let activeToday = 0;
  let newThisWeek = 0;
  let bdSoon = 0;
  for (const u of users) {
    if (u.lastActiveDate) {
      const d = new Date(u.lastActiveDate);
      if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        if (d.getTime() === today.getTime()) activeToday++;
      }
    }
    if ((u.joinedAt || 0) >= weekAgo) newThisWeek++;
    const dToBd = daysUntilBirthday(u.birthDate);
    if (dToBd !== null && dToBd >= 0 && dToBd <= 14) bdSoon++;
  }
  // Процент активных сегодня от общего числа
  const activePct = total > 0 ? Math.round((activeToday / total) * 100) : 0;

  return (
    <div className="relative overflow-hidden rounded-2xl p-4 pop-in"
      style={{
        background: 'linear-gradient(135deg, rgba(255,184,0,0.06) 0%, rgba(0,199,129,0.05) 50%, rgba(0,0,0,0) 100%)',
        border: '1px solid rgba(255,184,0,0.12)',
      }}>
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.18) 0%, transparent 70%)', filter: 'blur(25px)' }} />
      <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,199,129,0.15) 0%, transparent 70%)', filter: 'blur(25px)' }} />

      <div className="relative">
        <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.25em] mb-2 flex items-center gap-2">
          <Users className="w-3 h-3" /> КОМАНДА
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatPill
            icon={Users}
            value={<AnimatedCounter value={total} duration={700} />}
            label="всего"
            color="#FFB800"
          />
          <StatPill
            icon={Activity}
            value={<><AnimatedCounter value={activeToday} duration={800} /><span className="text-gray-600 text-xs ml-1">/{total}</span></>}
            label={`активны (${activePct}%)`}
            color="#00C781"
          />
          <StatPill
            icon={Sparkles}
            value={<AnimatedCounter value={newThisWeek} duration={900} />}
            label="новых за неделю"
            color="#3B82F6"
          />
          <StatPill
            icon={Calendar}
            value={<AnimatedCounter value={bdSoon} duration={1000} />}
            label="ДР в 2 недели"
            color="#E83A2C"
          />
        </div>
      </div>
    </div>
  );
}

// Маленький переиспользуемый блок-плашка с метрикой
function StatPill({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-[#0A0B0D]/60 border border-[#2A2E36] rounded-xl px-3 py-2.5 flex items-center gap-2.5 transition hover:border-[color:var(--c)]/50 hover-lift"
      style={{ ['--c']: color }}>
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <div className="min-w-0 flex-1">
        <div className="font-display text-base text-white leading-none flex items-baseline">{value}</div>
        <div className="font-mono text-[8px] text-gray-500 tracking-wider mt-0.5 uppercase truncate">{label}</div>
      </div>
    </div>
  );
}

function AdminUsersList({ users, updateUsers, navigate, showToast, currentUser }) {
  const confirm = useConfirm();
  const { hasPerm } = useContent();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('joined-desc');   // joined-desc | name | bd-soon
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Гейт — без права редактировать пользователей экран недоступен
  if (!hasPerm(currentUser, 'canEditUsers') && !hasPerm(currentUser, 'canCreateUsers')) {
    return <PermissionGate user={currentUser} perm="canEditUsers" navigate={navigate} label="базы сотрудников" />;
  }

  const canCreate = hasPerm(currentUser, 'canCreateUsers');
  const canDelete = hasPerm(currentUser, 'canDeleteUsers');
  const canMessage = hasPerm(currentUser, 'canSendMessages');

  // ─── Авто-подгрузка свежих users из БД ───
  // При открытии экрана и каждые 15 секунд проверяем, не появились ли новые юзеры в БД.
  // Это решает проблему: админ открыл список, а в другой вкладке кто-то зарегистрировался —
  // без авто-обновления нового юзера в списке не будет до перезагрузки страницы.
  const refreshUsers = useCallback(async (showFeedback = false) => {
    if (showFeedback) setRefreshing(true);
    try {
      const fresh = await safeGet(STORAGE_KEYS.USERS);
      if (Array.isArray(fresh)) {
        // Сравниваем по числу юзеров и по сумме id — если что-то изменилось, обновим стейт
        const localIds = users.map(u => u.id).sort().join('|');
        const freshIds = fresh.map(u => u.id).sort().join('|');
        if (localIds !== freshIds || fresh.length !== users.length) {
          await updateUsers(fresh);
          if (showFeedback) showToast(`Список обновлён: ${fresh.length} пользователей`);
        } else if (showFeedback) {
          showToast('Список актуален');
        }
      }
      setLastRefresh(Date.now());
    } catch (e) {
      console.warn('[users-refresh]', e.message);
      if (showFeedback) showToast('Не удалось обновить список', 'error');
    } finally {
      if (showFeedback) setRefreshing(false);
    }
  }, [users, updateUsers, showToast]);

  useEffect(() => {
    // Первая подгрузка при открытии экрана
    refreshUsers(false);
    // Polling каждые 15 секунд
    const interval = setInterval(() => refreshUsers(false), 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // зависимостей нет — иначе будет запускаться при каждом изменении users (бесконечный цикл)

  const filtered = users.filter(u => {
    if (filter === 'admin' && !u.isAdmin) return false;
    if (filter === 'trainee' && u.isAdmin) return false;
    const q = search.toLowerCase();
    if (q) {
      const hay = ((u.name || '') + ' ' + (u.email || '') + ' ' + (u.phone || '') + ' ' + (u.role || '')).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
	if (sort === 'online') {
      const aActive = a.lastActiveAt || a.lastLoginAt || 0;
      const bActive = b.lastActiveAt || b.lastLoginAt || 0;
      return bActive - aActive;
    }
    if (sort === 'name') return (a.name || '').localeCompare(b.name || '', 'ru');
    if (sort === 'bd-soon') {
      const da = daysUntilBirthday(a.birthDate);
      const db = daysUntilBirthday(b.birthDate);
      // у кого нет даты — в конец
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    }
    return (b.joinedAt || 0) - (a.joinedAt || 0);
  });

  const addUser = async () => {
    const newUser = {
      id: 'u_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      email: '',
      name: 'Новый сотрудник',
      passwordHash: '',
      avatar: '',
      brands: ['shawarmafia'],
      role: '',
      isAdmin: false,
      joinedAt: Date.now(),
      level: 1,
      streakDays: 0,
    };
    await updateUsers([...users, newUser]);
    showToast('Сотрудник добавлен');
    navigate('adminUser', { userId: newUser.id });
  };

  const canEditUser = (target) => {
    // Кто может что делать с пользователем — проверка прав
    if (!currentUser?.isAdmin) return false;
    // АБСОЛЮТНАЯ ЗАЩИТА: dev-аккаунт нельзя редактировать или удалять никому, даже себе.
    // Это защита от случайного удаления единственного «root»-аккаунта системы.
    if (isDevAccount(target)) return false;
    if (target.id === currentUser.id) return false; // никто не может удалять/менять себя через эту панель
    if (target.isDirector && !currentUser.isDirector) return false; // на руководителя — только руководитель
    if (target.isAdmin && !target.isDirector && !currentUser.isDirector) return false; // админа — только руководитель
    return true;
  };

  const deleteUser = async (id) => {
    const u = users.find(x => x.id === id);
    if (!u) return;
    if (!canDelete) {
      showToast('Нет права удалять пользователей', 'error');
      return;
    }
    if (!canEditUser(u)) {
      showToast('У вас нет прав на удаление этого пользователя', 'error');
      return;
    }
    if (!(await confirm(`Удалить пользователя «${u?.name}»? Его прогресс будет утерян.`, { danger: true }))) return;
    await updateUsers(users.filter(x => x.id !== id));
    try {
      await window.storage.delete(STORAGE_KEYS.PROGRESS(id));
      await window.storage.delete(STORAGE_KEYS.CHECKLIST(id));
      await window.storage.delete(STORAGE_KEYS.MESSAGES(id));
    } catch (e) { /* noop */ }
    showToast('Пользователь удалён', 'error');
  };

  return (
    <div className="max-w-3xl mx-auto pb-32 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="ПОЛЬЗОВАТЕЛИ" subtitle="База Сотрудников" />

      <div className="py-5 space-y-3">
        {/* ─── Хиро-блок со статистикой ─── */}
        <UsersStatsHero users={users} />

        <div className="flex gap-2">
          {canCreate && (
            <button
              onClick={addUser}
              className="flex-1 bg-gradient-to-br from-[#FFB800] to-[#FF9500] hover:from-[#FFC820] hover:to-[#FFAA00] text-black font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center justify-center gap-2 hover-lift transition shadow-lg shadow-[#FFB800]/20"
            >
              <Plus className="w-4 h-4" /> ДОБАВИТЬ
            </button>
          )}
          <button
            onClick={() => refreshUsers(true)}
            disabled={refreshing}
            title="Обновить список из БД"
            className={`bg-[#15171B] hover:bg-[#00C781]/10 border border-[#2A2E36] hover:border-[#00C781] text-gray-300 hover:text-[#00C781] font-mono text-xs tracking-wider px-3 py-3 rounded-lg flex items-center gap-2 transition disabled:opacity-50 ${!canCreate ? 'flex-1 justify-center' : ''}`}
          >
            {refreshing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
          </button>
          {canMessage && (
            <button
              onClick={() => navigate('adminMessages')}
              className="bg-[#15171B] hover:bg-[#FFB800]/10 border border-[#2A2E36] hover:border-[#FFB800] text-gray-300 hover:text-[#FFB800] font-mono text-xs tracking-wider px-4 py-3 rounded-lg flex items-center gap-2 transition"
            >
              <Send className="w-4 h-4" /> СООБЩЕНИЯ
            </button>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по имени, email, телефону..."
              className="w-full bg-[#15171B] border border-[#2A2E36] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:border-[#FFB800] focus:outline-none transition"
            />
          </div>
          <div className="flex bg-[#15171B] border border-[#2A2E36] rounded-lg p-1">
            {[{ id: 'all', label: 'Все' }, { id: 'trainee', label: 'Стажёры' }, { id: 'admin', label: 'Админы' }].map(o => (
              <button
                key={o.id}
                onClick={() => setFilter(o.id)}
                className={`px-3 py-1 rounded font-mono text-[10px] tracking-wider transition ${
                  filter === o.id ? 'bg-[#FFB800] text-black' : 'text-gray-500 hover:text-white'
                }`}
              >
                {o.label.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="flex bg-[#15171B] border border-[#2A2E36] rounded-lg p-1">
            {[
			  { id: 'online', label: 'Онлайн' },
              { id: 'joined-desc', label: 'Новые' },
              { id: 'name', label: 'А-Я' },
              { id: 'bd-soon', label: 'Скоро ДР' }
            ].map(o => (
              <button
                key={o.id}
                onClick={() => setSort(o.id)}
                className={`px-3 py-1 rounded font-mono text-[10px] tracking-wider transition ${
                  sort === o.id ? 'bg-[#FFB800] text-black' : 'text-gray-500 hover:text-white'
                }`}
                title="Сортировка"
              >
                {o.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-12 text-center">
            <Users className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <div className="text-gray-500 text-sm">Никого не найдено</div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u, idx) => {
              const brands = u.brands || (u.brand ? [u.brand] : []);
              const age = calcAge(u.birthDate);
              const dToBd = daysUntilBirthday(u.birthDate);
              const bdToday = dToBd === 0;
              const bdSoon = dToBd !== null && dToBd > 0 && dToBd <= 30;
              // ─── Индикатор активности за последние 7 дней ───
              // Хранится только lastActiveDate (один последний день) — точную историю не построим.
              // Показываем индикатор: «был сегодня», «был вчера», «N дней назад», «не заходил».
              // 7 точек-полосок показывают: горит та, которая соответствует lastActiveDate.
              const today = new Date(); today.setHours(0,0,0,0);
              let lastActiveDate = null;
              if (u.lastActiveDate) {
                const d = new Date(u.lastActiveDate);
                if (!isNaN(d.getTime())) { d.setHours(0,0,0,0); lastActiveDate = d; }
              }
              const daysSinceActive = lastActiveDate
                ? Math.floor((today - lastActiveDate) / (24 * 3600 * 1000))
                : null;
              const activeLabel = daysSinceActive === null
                ? 'не заходил'
                : daysSinceActive === 0 ? 'сегодня'
                : daysSinceActive === 1 ? 'вчера'
                : daysSinceActive < 7 ? `${daysSinceActive} ${pluralDays(daysSinceActive)} назад`
                : daysSinceActive < 30 ? `${Math.floor(daysSinceActive / 7)} нед назад`
                : `${Math.floor(daysSinceActive / 30)} мес назад`;
              const activeColor = daysSinceActive === null ? '#374151'
                : daysSinceActive === 0 ? '#00C781'
                : daysSinceActive < 3 ? '#FFB800'
                : daysSinceActive < 14 ? '#F59E0B'
                : '#6B7280';

              // ─── Бейджи стажа ───
              // «Новичок» — присоединился ≤ 14 дней назад
              // «Опытный» — присоединился ≥ 180 дней назад (полгода)
              // НЕ показываем для админов чтобы не загромождать
              const daysOnTeam = u.joinedAt ? Math.floor((Date.now() - u.joinedAt) / (24 * 3600 * 1000)) : null;
              const isNewbie = daysOnTeam !== null && daysOnTeam <= 14 && !u.isAdmin;
              const isVeteran = daysOnTeam !== null && daysOnTeam >= 180 && !u.isAdmin;

              return (
                <div
                  key={u.id}
                  className={`bg-gradient-to-br from-[#15171B] to-[#0F1116] border rounded-xl p-3.5 hover:border-[#FFB800]/50 hover-lift transition relative overflow-hidden pop-in group ${bdToday ? 'border-[#FFB800] ring-1 ring-[#FFB800]/40' : 'border-[#2A2E36]'}`}
                  style={{ animationDelay: `${Math.min(idx, 10) * 0.04}s` }}
                >
                  {/* Тонкая полоса-индикатор активности слева */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 transition-opacity group-hover:opacity-100" style={{ background: activeColor, opacity: 0.7 }} />

                  {/* Декоративный glow при hover */}
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: `radial-gradient(circle, ${activeColor}25 0%, transparent 70%)`, filter: 'blur(15px)' }} />

                  <div className="relative flex items-start gap-3 pl-2">
                    {/* Avatar с ring-индикатором активности */}
                    <div className="relative flex-shrink-0">
                      <Avatar user={u} size={48} />
                      {/* Точка активности на аватаре */}
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#15171B]"
                        style={{
                          background: activeColor,
                          boxShadow: daysSinceActive === 0 ? `0 0 6px ${activeColor}` : 'none',
                        }}
                        title={`Активность: ${activeLabel}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('adminUser', { userId: u.id })}>
                      {/* ФИО + бэйджи */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-bold text-white text-sm">{u.name || '— без ФИО —'}</div>
                        {isNewbie && (
                          <span className="font-mono text-[8px] text-[#3B82F6] bg-[#3B82F6]/15 border border-[#3B82F6]/30 px-1.5 py-0.5 rounded tracking-wider flex items-center gap-0.5" title={`В команде ${daysOnTeam} ${pluralDays(daysOnTeam)}`}>
                            <Sparkles className="w-2.5 h-2.5" /> НОВИЧОК
                          </span>
                        )}
                        {isVeteran && (
                          <span className="font-mono text-[8px] text-[#A855F7] bg-[#A855F7]/15 border border-[#A855F7]/30 px-1.5 py-0.5 rounded tracking-wider flex items-center gap-0.5" title={`В команде ${Math.floor(daysOnTeam / 30)} мес`}>
                            <Award className="w-2.5 h-2.5" /> ОПЫТНЫЙ
                          </span>
                        )}
                        {u.isDirector && (
                          <span className="font-mono text-[8px] text-white bg-gradient-to-r from-[#FF7849] to-[#E83A2C] px-1.5 py-0.5 rounded tracking-wider flex items-center gap-1 glow-red" title="Полные права разработчика">
                            <Shield className="w-2 h-2" /> РУКОВОДИТЕЛЬ
                          </span>
                        )}
                        {u.isAdmin && !u.isDirector && (
                          <span className="font-mono text-[8px] text-[#FFB800] border border-[#FFB800] px-1.5 py-0.5 rounded tracking-wider flex items-center gap-1">
                            <Crown className="w-2 h-2" /> АДМИН
                          </span>
                        )}
                        {u.emailVerified && (
                          <span className="font-mono text-[8px] text-[#00C781] border border-[#00C781]/40 px-1.5 py-0.5 rounded tracking-wider flex items-center gap-1" title="Email подтверждён">
                            <Check className="w-2 h-2" /> EMAIL ОК
                          </span>
                        )}
                        {u.gender && (
                          <span className="font-mono text-[10px] text-gray-500" title={u.gender === 'male' ? 'Мужской' : 'Женский'}>
                            {u.gender === 'male' ? '♂' : '♀'}
                          </span>
                        )}
                        {bdToday && (
                          <span className="font-mono text-[9px] text-black bg-[#FFB800] px-1.5 py-0.5 rounded tracking-wider flex items-center gap-1 pop-in">
                            <Cake className="w-2.5 h-2.5" /> ДР СЕГОДНЯ!
                          </span>
                        )}
                      </div>

                      {/* Контактные данные */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 mt-2">
                        {u.email && (
                          <a
                            href={`mailto:${u.email}`}
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-[#FFB800] font-mono truncate transition"
                            title="Написать письмо"
                          >
                            <Send className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{u.email}</span>
                          </a>
                        )}
                        {u.phone && (
                          <a
                            href={`tel:${u.phone.replace(/[^\d+]/g, '')}`}
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-[#FFB800] font-mono truncate transition"
                            title="Позвонить"
                          >
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{u.phone}</span>
                          </a>
                        )}
                      </div>

                      {/* Возраст + ДР + дни до ДР */}
                      {u.birthDate && (
                        <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] font-mono">
                          <span className="flex items-center gap-1 text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {formatBirthDay(u.birthDate)}
                            {age != null && <span className="text-white ml-1">· {age} {pluralYears(age)}</span>}
                          </span>
                          {dToBd !== null && !bdToday && (
                            <span className={`flex items-center gap-1 ${bdSoon ? 'text-[#FFB800]' : 'text-gray-600'}`}>
                              <Cake className="w-3 h-3" />
                              {dToBd === 1 ? 'завтра' : `через ${dToBd} ${pluralDays(dToBd)}`}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Бренды и роль */}
                      <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-2 flex items-center gap-1 flex-wrap">
                        {brands.length === 0 ? (
                          <span className="text-gray-700">без направления</span>
                        ) : brands.map(bid => {
                          const b = BRANDS[bid];
                          return b ? (
                            <span key={bid} className="px-1.5 py-0.5 rounded text-[9px]" style={{ background: b.color + '20', color: b.color }}>
                              {b.icon} {b.name}
                            </span>
                          ) : null;
                        })}
                        {u.role && <span className="text-gray-500">· {u.role}</span>}
                      </div>

                      {/* Полоска активности — 7 точек, последняя горит */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex gap-1">
                          {Array.from({ length: 7 }).map((_, i) => {
                            // i=0 это 6 дней назад, i=6 это сегодня
                            const dayOffset = 6 - i;
                            const isActiveDay = daysSinceActive !== null && daysSinceActive === dayOffset;
                            return (
                              <div key={i}
                                className="w-2 h-2 rounded-full transition"
                                style={{
                                  background: isActiveDay ? activeColor : '#2A2E36',
                                  boxShadow: isActiveDay ? `0 0 6px ${activeColor}` : 'none',
                                }}
                              />
                            );
                          })}
                        </div>
                        
						{(() => {
                          const activeTime = u.lastActiveAt || u.lastLoginAt;
                          if (!activeTime) return null;
                          
                          const now = new Date();
                          const loginDate = new Date(activeTime);
                          
                          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                          const loginDay = new Date(loginDate.getFullYear(), loginDate.getMonth(), loginDate.getDate());
                          const diffDays = Math.floor((today - loginDay) / 86400000);
                          
                          const diffMs = now - loginDate;
                          const minutes = Math.floor(diffMs / 60000);
                          const timeStr = loginDate.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
                          
                          let text, color;
                          if (minutes < 5) {
                            text = 'онлайн';
                            color = '#00C781';
                          } else if (minutes < 60) {
                            text = `${minutes} мин назад`;
                            color = '#FFB800';
                          } else if (diffDays === 0) {
                            text = `сегодня в ${timeStr}`;
                            color = '#FFB800';
                          } else if (diffDays === 1) {
                            text = `вчера в ${timeStr}`;
                            color = '#6B7280';
                          } else {
                            text = loginDate.toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                            color = '#6B7280';
                          }
                          return (
                            <span className="font-mono text-[10px] tracking-wider ml-2" style={{ color }}>
                              {text}
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <IconBtn icon={Edit3} onClick={() => navigate('adminUser', { userId: u.id })} accent />
                      {canEditUser(u) && canDelete && <IconBtn icon={Trash2} onClick={() => deleteUser(u.id)} danger />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Plurals
function pluralYears(n) {
  const abs = Math.abs(n) % 100;
  const lastDigit = abs % 10;
  if (abs > 10 && abs < 20) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
}
function pluralDays(n) {
  const abs = Math.abs(n) % 100;
  const lastDigit = abs % 10;
  if (abs > 10 && abs < 20) return 'дней';
  if (lastDigit === 1) return 'день';
  if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
  return 'дней';
}

function AdminUserEditor({ userId, users, updateUsers, navigate, showToast, currentUser }) {
  const { courses, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditUsers')) {
    return <PermissionGate user={currentUser} perm="canEditUsers" navigate={navigate} label="редактирования пользователей" />;
  }
  const user = users.find(u => u.id === userId);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto p-5">
        <button onClick={() => navigate('adminUsers')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-4 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НАЗАД
        </button>
        <div className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[#E83A2C] mx-auto mb-3" />
          <div className="text-white">Пользователь не найден</div>
        </div>
      </div>
    );
  }

  // ─── ПРОВЕРКА ПРАВ ─────────────────────────────────────────────
  // Правила иерархии:
  //   • Руководитель (isDirector) — может всё, кроме изменения других руководителей и понижения себя
  //   • Админ (isAdmin && !isDirector) — может редактировать только обычных пользователей
  //   • Админ НЕ может: редактировать руководителей, других админов, давать админ-права, удалять админов
  //   • Никто не может изменить себя через эту панель (используется ProfileScreen)
  const isMe = currentUser?.id === userId;
  const targetIsDirector = !!user.isDirector;
  const targetIsAdmin = !!user.isAdmin;
  const meIsDirector = !!currentUser?.isDirector;
  const meIsAdmin = !!currentUser?.isAdmin;

  // Условия блокировки
  const blockReason = (() => {
    // АБСОЛЮТНАЯ защита dev-аккаунта — никто не может его редактировать через эту панель
    if (isDevAccount(user) && !isDevAccount(currentUser)) {
      return 'Профиль разработчика защищён системой. Редактирование возможно только из самого этого аккаунта.';
    }
    if (!meIsAdmin) return 'Нет прав для редактирования пользователей';
    if (targetIsDirector && !meIsDirector) return 'Только Руководитель может редактировать других руководителей';
    if (targetIsAdmin && !targetIsDirector && !meIsDirector && !isMe) return 'Админ не может редактировать других админов. Обратитесь к Руководителю.';
    if (isMe && !meIsDirector) return 'Изменения собственного профиля доступны через раздел «Профиль»';
    return null;
  })();

  if (blockReason) {
    return (
      <div className="max-w-3xl mx-auto p-5 fade-in">
        <AdminHeader back={() => navigate('adminUsers')} title="ДОСТУП ОГРАНИЧЕН" subtitle={user.name} />
        <div className="bg-[#15171B] border border-[#FFB800]/40 rounded-2xl p-8 text-center">
          <Shield className="w-12 h-12 text-[#FFB800] mx-auto mb-3" />
          <div className="text-white font-medium mb-2">Редактирование заблокировано</div>
          <div className="text-sm text-gray-400 mb-6 max-w-md mx-auto">{blockReason}</div>
          <button
            onClick={() => navigate('adminUsers')}
            className="px-5 py-2.5 bg-[#0A0B0D] hover:bg-[#2A2E36] border border-[#2A2E36] rounded-lg text-sm text-white font-mono tracking-wider transition"
          >
            ← К СПИСКУ
          </button>
        </div>
      </div>
    );
  }
  // ─── /ПРОВЕРКА ПРАВ ────────────────────────────────────────────

  // Автосохранение каждого поля
  const patch = (patchObj) => updateUsers(users.map(u => u.id === userId ? { ...u, ...patchObj } : u));

  const toggleBrand = (brandId) => {
    const current = user.brands || (user.brand ? [user.brand] : []);
    const next = current.includes(brandId)
      ? current.filter(b => b !== brandId)
      : [...current, brandId];
    return patch({ brands: next });
  };

  const setPassword = async () => {
    const pwd = prompt('Введите новый пароль (минимум 6 символов):');
    if (!pwd) return;
    if (pwd.length < 6) { showToast('Пароль слишком короткий', 'error'); return; }
    try {
      const apiUrl = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
      const resp = await fetch(`${apiUrl}/auth/admin-reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, newPassword: pwd }),
      });
      if (resp.ok) {
        showToast('Пароль обновлён');
      } else {
        const err = await resp.json().catch(() => ({}));
        showToast(err.error || 'Ошибка', 'error');
      }
    } catch (e) {
      showToast('Ошибка сети', 'error');
    }
  };

  const brandsForUser = user.brands || (user.brand ? [user.brand] : []);
  const allRoles = [...new Set([...courses.map(c => c.role), ...Object.values(CANONICAL_ROLES).flat()])].filter(Boolean);

  // Сколько курсов доступно пользователю
  const accessibleCourses = getCoursesForUser(user, courses);

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminUsers')} title="ПОЛЬЗОВАТЕЛЬ" subtitle={user.name} />

      <div className="py-5 space-y-5">
        {/* Аватар + основные данные */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-4">ИЗМЕНЕНИЯ СОХРАНЯЮТСЯ АВТОМАТИЧЕСКИ</div>

          <div className="flex items-start gap-4">
            <AvatarUpload
              user={user}
              size={80}
              onChange={async (dataUrl) => { await patch({ avatar: dataUrl }); showToast(dataUrl ? 'Аватар обновлён' : 'Аватар удалён'); }}
            />
            <div className="flex-1 min-w-0">
              <InlineText
                value={user.name}
                onChange={v => patch({ name: v })}
                placeholder="Имя"
                size="xl"
                className="text-white font-bold block"
              />
              <div className="mt-1">
                <InlineText
                  value={user.email}
                  onChange={v => patch({ email: (v || '').trim().toLowerCase() })}
                  placeholder="email@example.com"
                  className="text-gray-400 font-mono text-xs block"
                />
              </div>
              <div className="font-mono text-[10px] text-gray-600 tracking-wider mt-2">
                ID: {user.id} · СОЗДАН {new Date(user.joinedAt).toLocaleDateString('ru')}
              </div>
            </div>
          </div>

          {/* Личные данные */}
          <div className="mt-4 pt-4 border-t border-[#2A2E36]">
            <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ЛИЧНЫЕ ДАННЫЕ</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ТЕЛЕФОН</label>
                <InlineText
                  value={user.phone}
                  onChange={v => patch({ phone: v })}
                  placeholder="+7 (___) ___-__-__"
                  className="text-white block"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ПОЛ</label>
                <InlineSelect
                  value={user.gender}
                  onChange={v => patch({ gender: v })}
                  options={[
                    { value: 'male', label: '♂ Мужской' },
                    { value: 'female', label: '♀ Женский' },
                  ]}
                  placeholder="—"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ДАТА РОЖДЕНИЯ</label>
                <input
                  type="date"
                  value={user.birthDate || ''}
                  onChange={e => patch({ birthDate: e.target.value })}
                  max={new Date().toISOString().slice(0,10)}
                  className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#FFB800] rounded px-2 py-1 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">ВОЗРАСТ</label>
                <div className="px-2 py-1 text-sm text-gray-400 font-mono">
                  {calcAge(user.birthDate) != null ? `${calcAge(user.birthDate)} лет · ДР ${formatBirthDay(user.birthDate)}` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Тогл «Администратор» — выдаёт ТОЛЬКО разработчик (info@tarasovweb.ru) */}
          {isDevAccount(currentUser) && !isDevAccount(user) && (
            <div className="mt-4 pt-4 border-t border-[#2A2E36] flex items-center justify-between gap-3">
              <div>
                <div className="text-sm text-white font-bold flex items-center gap-2">
                  Статус администратора
                  <span className="text-[9px] font-mono text-[#FFB800] tracking-wider px-1.5 py-0.5 bg-[#FFB800]/10 rounded">DEV ONLY</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {user.isAdmin ? 'Полный доступ: контент, пользователи, статистика' : 'Стажёр: проходит обучение'}
                </div>
              </div>
              <InlineToggle
                value={user.isAdmin}
                onChange={async v => {
                  // если снимаем админа — снимаем и директора
                  const patchObj = { isAdmin: v };
                  if (!v) patchObj.isDirector = false;
                  await patch(patchObj);
                  showToast(v ? 'Назначен администратором' : 'Снят с админа');
                }}
                accent="#FFB800"
              />
            </div>
          )}

          {/* Информационный плашка о текущем статусе — для НЕ-разработчиков */}
          {!isDevAccount(currentUser) && user.isAdmin && (
            <div className="mt-4 pt-4 border-t border-[#2A2E36]">
              <div className="flex items-center gap-2 text-xs">
                <Shield className="w-3.5 h-3.5 text-[#FFB800]" />
                <span className="font-mono text-[#FFB800] tracking-wider">АДМИНИСТРАТОР</span>
                <span className="text-gray-500">— права назначает только разработчик</span>
              </div>
            </div>
          )}

          {/* Toggle «Руководитель» — назначает ТОЛЬКО разработчик */}
          {isDevAccount(currentUser) && user.isAdmin && !isDevAccount(user) && (
            <div className="mt-4 pt-4 border-t border-[#2A2E36] flex items-center justify-between gap-3">
              <div>
                <div className="text-sm text-white font-bold flex items-center gap-2">
                  Права руководителя
                  <span className="text-[9px] font-mono text-[#FFB800] tracking-wider px-1.5 py-0.5 bg-[#FFB800]/10 rounded">DEV ONLY</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {user.isDirector
                    ? 'Может ставить задачи администраторам и видеть весь модуль задач'
                    : 'Дать пользователю права руководителя и возможность управлять задачами'}
                </div>
              </div>
              <InlineToggle
                value={!!user.isDirector}
                onChange={async v => {
                  await patch({ isDirector: v });
                  showToast(v ? 'Назначен руководителем' : 'Права руководителя сняты');
                }}
                accent="#FF7849"
              />
            </div>
          )}

          {/* Информационный блок для НЕ-разработчиков */}
          {!isDevAccount(currentUser) && user.isDirector && (
            <div className="mt-4 pt-4 border-t border-[#2A2E36]">
              <div className="flex items-center gap-2 text-xs">
                <Shield className="w-3.5 h-3.5 text-[#FFB800]" />
                <span className="font-mono text-[#FFB800] tracking-wider">РУКОВОДИТЕЛЬ</span>
                <span className="text-gray-500">— права назначает только разработчик</span>
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-[#2A2E36]">
            <button onClick={setPassword} className="text-xs text-[#FFB800] hover:text-[#FFC820] flex items-center gap-1 transition">
              <Lock className="w-3 h-3" /> Сменить пароль
            </button>
          </div>
        </div>

        {/* Доступы к брендам */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ДОСТУП К НАПРАВЛЕНИЯМ</div>
          <div className="text-xs text-gray-500 mb-3">
            Отметь направления, к курсам которых пользователь имеет доступ. Можно открыть оба сразу.
          </div>
          <div className="grid grid-cols-2 gap-2">
            {BRAND_LIST.map(b => {
              const enabled = brandsForUser.includes(b.id);
              return (
                <button
                  key={b.id}
                  onClick={() => toggleBrand(b.id)}
                  className={`p-3 rounded-lg border text-left transition ${
                    enabled
                      ? 'border-[#FFB800] bg-[#FFB800]/10'
                      : 'border-[#2A2E36] bg-[#0A0B0D] hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="text-2xl">{b.icon}</div>
                    {enabled && <Check className="w-4 h-4 text-[#FFB800]" />}
                  </div>
                  <div className="font-bold text-sm text-white mt-1">{b.name}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{b.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Роль (должность) */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ДОЛЖНОСТЬ</div>
          <div className="flex items-center gap-2 flex-wrap">
            <InlineText
              value={user.role}
              onChange={v => patch({ role: v })}
              placeholder="Например: Повар-кассир"
              className="text-white"
            />
          </div>
          {allRoles.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-gray-500 mb-1">Шаблонные роли:</div>
              <div className="flex flex-wrap gap-1.5">
                {allRoles.map(r => (
                  <button
                    key={r}
                    onClick={() => patch({ role: r })}
                    className="px-2 py-1 bg-[#0A0B0D] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] text-[10px] rounded font-mono tracking-wider transition"
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Доступные курсы */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ДОСТУПНЫЕ КУРСЫ</div>
          {accessibleCourses.length === 0 ? (
            <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#E83A2C] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-400">
                Курсов нет. Создай курс под выбранную пару «направление + должность» или поправь данные пользователя.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {accessibleCourses.map(c => (
                <div key={c.id} className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-[#FFB800] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate">{c.title}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{c.modules.length} модулей</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Быстрые действия */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ДЕЙСТВИЯ</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => navigate('adminTrainee', { userId: user.id })}
              className="bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-[#2A2E36] hover:border-[#FFB800] text-white text-sm py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
            >
              <TrendingUp className="w-4 h-4" /> Прогресс обучения
            </button>
            <button
              onClick={() => navigate('adminMessages', { userId: user.id })}
              className="bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-[#2A2E36] hover:border-[#FFB800] text-white text-sm py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
            >
              <Send className="w-4 h-4" /> Написать сообщение
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: СООБЩЕНИЯ ПОЛЬЗОВАТЕЛЯМ
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// ЗАДАЧИ — модуль управления поручениями
// Руководитель (isDirector) ставит задачи Администраторам (isAdmin),
// Администратор отчитывается с фото/видео, ставит статус «на проверке»,
// Руководитель закрывает.
// ═══════════════════════════════════════════════════════════════

// ─── Хелперы ───
const TASK_PRIORITIES = [
  { id: 'low',      label: 'Низкий',       color: '#7BB661', icon: '○' },
  { id: 'medium',   label: 'Средний',      color: '#FFB800', icon: '◐' },
  { id: 'high',     label: 'Высокий',      color: '#FF7849', icon: '●' },
  { id: 'critical', label: 'Критический',  color: '#E83A2C', icon: '⬤' },
];
const TASK_STATUSES = [
  { id: 'active',    label: 'В работе',     color: '#3B82F6', icon: Hourglass },
  { id: 'review',    label: 'На проверке',  color: '#FFB800', icon: AlertOctagon },
  { id: 'done',      label: 'Завершена',    color: '#00C781', icon: CheckCircle2 },
  { id: 'cancelled', label: 'Отменена',     color: '#6B7280', icon: XCircle },
];
const getPriority = (id) => TASK_PRIORITIES.find(p => p.id === id) || TASK_PRIORITIES[1];
const getStatus = (id) => TASK_STATUSES.find(s => s.id === id) || TASK_STATUSES[0];
const formatDeadline = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  const now = new Date();
  const diffH = (ts - now.getTime()) / 3600_000;
  const dateStr = d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: '2-digit' });
  if (diffH < 0) return { text: `Просрочено · ${dateStr}`, expired: true };
  if (diffH < 24) return { text: `Сегодня · ${dateStr}`, urgent: true };
  if (diffH < 72) return { text: `Через ${Math.ceil(diffH/24)} дн · ${dateStr}`, urgent: false };
  return { text: dateStr, urgent: false };
};

// Сжатие изображений / приём файлов: data URL до ~600кб
async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}
async function compressImage(file, maxSize = 1200, quality = 0.78) {
  if (!file.type.startsWith('image/')) return await fileToDataUrl(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        const fr2 = new FileReader();
        fr2.onload = () => resolve(fr2.result);
        fr2.onerror = () => reject(fr2.error);
        fr2.readAsDataURL(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// ─── 1. DirectorTasksList: список всех задач (только для руководителя) ───
function DirectorTasksList({ users, navigate, currentUser, showToast }) {
  const { tasks, deleteTask, hasPerm } = useContent();
  const confirm = useConfirm();
  const [filter, setFilter] = useState('active'); // active | review | done | all
  const [search, setSearch] = useState('');

  // Доступ: руководитель ИЛИ админ с правом canEditAllTasks/canCreateTasks
  if (!hasPerm(currentUser, 'canEditAllTasks') && !hasPerm(currentUser, 'canCreateTasks')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Lock className="w-12 h-12 text-[#FF4A3C] mx-auto mb-4" />
          <h2 className="font-display text-xl text-white mb-2">ДОСТУП ЗАКРЫТ</h2>
          <p className="text-sm text-gray-400 mb-6">Этот раздел доступен пользователям с правом «Видеть все задачи» или «Создавать задачи».</p>
          <button onClick={() => navigate('admin')} className="px-4 py-2 bg-[#15171B] border border-[#2A2E36] rounded text-sm">Назад</button>
        </div>
      </div>
    );
  }

  const admins = users.filter(u => u.isAdmin && !u.isDirector);  // обычные админы (без директоров)
  const visibleTasks = (tasks || []).filter(t => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      const assignee = users.find(u => u.id === t.assigneeId);
      const hay = (t.title + ' ' + (t.description||'') + ' ' + (assignee?.name||'')).toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }).sort((a, b) => {
    // Сначала просроченные, потом по дедлайну
    const aExp = a.deadline && a.deadline < Date.now() && a.status === 'active';
    const bExp = b.deadline && b.deadline < Date.now() && b.status === 'active';
    if (aExp !== bExp) return aExp ? -1 : 1;
    if (a.deadline && b.deadline) return a.deadline - b.deadline;
    return b.createdAt - a.createdAt;
  });

  const counts = {
    active: (tasks || []).filter(t => t.status === 'active').length,
    review: (tasks || []).filter(t => t.status === 'review').length,
    done: (tasks || []).filter(t => t.status === 'done').length,
    all: (tasks || []).length,
  };

  const handleDelete = async (taskId, title) => {
    const ok = await confirm({ title: 'Удалить задачу?', message: `«${title}» будет удалена безвозвратно.`, confirmText: 'Удалить', danger: true });
    if (!ok) return;
    await deleteTask(taskId);
    showToast('Задача удалена');
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="border-b border-[#2A2E36]">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-3">
          <button onClick={() => navigate('admin')} className="p-2 -ml-2 hover:bg-[#15171B] rounded transition"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <div className="text-[10px] font-mono tracking-widest text-gray-500">УПРАВЛЕНИЕ ЗАДАЧАМИ</div>
            <h1 className="font-display text-2xl tracking-wider text-white">Поручения команде</h1>
          </div>
          <button
            onClick={() => navigate('directorTask', { taskId: 'new' })}
            className="px-4 py-2.5 bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono text-sm tracking-wider rounded transition flex items-center gap-2 glow-red"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">НОВАЯ ЗАДАЧА</span>
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-6">
        {/* Фильтры-табы */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
          {[
            { id: 'active', label: 'Активные', count: counts.active },
            { id: 'review', label: 'На проверке', count: counts.review },
            { id: 'done',   label: 'Завершённые', count: counts.done },
            { id: 'all',    label: 'Все', count: counts.all },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-mono tracking-wider whitespace-nowrap transition border ${
                filter === t.id
                  ? 'bg-[#E83A2C] border-[#E83A2C] text-white'
                  : 'bg-[#15171B] border-[#2A2E36] text-gray-400 hover:text-white'
              }`}
            >
              {t.label}
              <span className={`ml-2 text-[10px] ${filter === t.id ? 'text-white/70' : 'text-gray-600'}`}>{t.count}</span>
            </button>
          ))}
        </div>

        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск по названию, описанию, исполнителю..."
          className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white placeholder-gray-700 outline-none transition text-sm mb-5"
        />

        {visibleTasks.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#2A2E36] rounded-xl">
            <CheckSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <div className="text-gray-500 text-sm mb-4">
              {filter === 'active' ? 'Активных задач нет — отличная работа.' :
               filter === 'review' ? 'Нет задач на проверке.' :
               filter === 'done' ? 'Завершённых задач пока нет.' :
               'Задач ещё нет. Поставьте первую — администраторы получат её в своём кабинете.'}
            </div>
            <button
              onClick={() => navigate('directorTask', { taskId: 'new' })}
              className="px-5 py-2.5 bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono text-sm tracking-wider rounded transition inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> ПОСТАВИТЬ ЗАДАЧУ
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleTasks.map(task => {
              const assignee = users.find(u => u.id === task.assigneeId);
              const pri = getPriority(task.priority);
              const stat = getStatus(task.status);
              const StatIcon = stat.icon;
              const deadline = formatDeadline(task.deadline);
              const todoTotal = (task.todos || []).length;
              const todoDone = (task.todos || []).filter(t => t.done).length;
              const commentsCount = (task.comments || []).length;
              return (
                <div
                  key={task.id}
                  className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 hover:border-[#3A3E48] transition cursor-pointer"
                  onClick={() => navigate('directorTask', { taskId: task.id })}
                >
                  <div className="flex items-start gap-3">
                    {/* приоритет */}
                    <div
                      className="w-1 rounded-full self-stretch flex-shrink-0"
                      style={{ background: pri.color, minHeight: 48 }}
                      title={`Приоритет: ${pri.label}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <h3 className="font-medium text-white text-base leading-tight">{task.title}</h3>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(task.id, task.title); }}
                          className="text-gray-600 hover:text-[#FF4A3C] p-1 -m-1 transition"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {task.description && (
                        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{task.description}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-3 text-[11px] font-mono">
                        {/* статус */}
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded"
                          style={{ background: stat.color + '20', color: stat.color }}
                        >
                          <StatIcon className="w-3 h-3" />
                          {stat.label.toUpperCase()}
                        </span>
                        {/* исполнитель */}
                        <span className="text-gray-400">
                          <User className="w-3 h-3 inline mr-1" />
                          {assignee?.name || '—'}
                        </span>
                        {/* дедлайн */}
                        {deadline && (
                          <span className={deadline.expired ? 'text-[#FF4A3C]' : deadline.urgent ? 'text-[#FFB800]' : 'text-gray-500'}>
                            <Clock className="w-3 h-3 inline mr-1" />
                            {deadline.text}
                          </span>
                        )}
                        {/* приоритет */}
                        <span style={{ color: pri.color }}>
                          {pri.icon} {pri.label.toUpperCase()}
                        </span>
                        {/* todo */}
                        {todoTotal > 0 && (
                          <span className="text-gray-500">
                            <ListChecks className="w-3 h-3 inline mr-1" />
                            {todoDone}/{todoTotal}
                          </span>
                        )}
                        {/* комментарии */}
                        {commentsCount > 0 && (
                          <span className="text-gray-500">
                            <MessageSquare className="w-3 h-3 inline mr-1" />
                            {commentsCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 2. DirectorTaskEditor: создание / редактирование задачи ───
function DirectorTaskEditor({ taskId, users, navigate, currentUser, showToast }) {
  const { tasks, createTask, updateTask, deleteTask, addTaskComment, hasPerm } = useContent();
  const notif = useNotifications();
  const confirm = useConfirm();
  const isNew = taskId === 'new';
  const existing = isNew ? null : (tasks || []).find(t => t.id === taskId);

  const [title, setTitle] = useState(existing?.title || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [assigneeId, setAssigneeId] = useState(existing?.assigneeId || '');
  const [priority, setPriority] = useState(existing?.priority || 'medium');
  const [status, setStatus] = useState(existing?.status || 'active');
  const [deadlineStr, setDeadlineStr] = useState(existing?.deadline ? new Date(existing.deadline).toISOString().slice(0, 16) : '');
  const [todos, setTodos] = useState(existing?.todos || []);
  const [newTodoText, setNewTodoText] = useState('');
  const [attachments, setAttachments] = useState(existing?.attachments || []);
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);

  // Доступ к редактированию задачи: создание — нужно canCreateTasks, редактирование — canEditAllTasks или canCreateTasks
  if (!hasPerm(currentUser, 'canCreateTasks') && !hasPerm(currentUser, 'canEditAllTasks')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-[#FF4A3C] mx-auto mb-4" />
          <div className="text-sm text-gray-400">Нужно право «Создавать задачи» или «Видеть все задачи»</div>
        </div>
      </div>
    );
  }

  const admins = users.filter(u => u.isAdmin);

  const handleAddTodo = () => {
    if (!newTodoText.trim()) return;
    setTodos([...todos, { id: 'td_' + Date.now(), text: newTodoText.trim(), done: false }]);
    setNewTodoText('');
  };
  const toggleTodo = (id) => setTodos(todos.map(t => t.id === id ? { ...t, done: !t.done } : t));
  const removeTodo = (id) => setTodos(todos.filter(t => t.id !== id));

  const handleFiles = async (files, target = 'task') => {
    const arr = Array.from(files);
    const out = [];
    for (const f of arr) {
      try {
        const data = f.type.startsWith('image/') ? await compressImage(f) : await fileToDataUrl(f);
        out.push({ id: 'a_' + Date.now() + '_' + Math.random().toString(36).slice(2,5), name: f.name, type: f.type, size: f.size, data });
      } catch (e) {
        showToast('Не удалось добавить файл: ' + f.name);
      }
    }
    if (target === 'task') setAttachments([...attachments, ...out]);
    else setCommentAttachments([...commentAttachments, ...out]);
  };
  const removeAttachment = (id, target = 'task') => {
    if (target === 'task') setAttachments(attachments.filter(a => a.id !== id));
    else setCommentAttachments(commentAttachments.filter(a => a.id !== id));
  };

  const handleSave = async () => {
    if (!title.trim()) { showToast('Укажите название задачи'); return; }
    if (!assigneeId) { showToast('Выберите исполнителя'); return; }
    const deadline = deadlineStr ? new Date(deadlineStr).getTime() : null;

    if (isNew) {
      const created = await createTask({
        title: title.trim(), description: description.trim(),
        assigneeId, createdBy: currentUser.id,
        priority, deadline, todos, attachments,
      });
      // Уведомление исполнителю
      if (notif && assigneeId !== currentUser.id) {
        notif.addNotification(assigneeId, {
          type: 'task_assigned',
          title: 'Новая задача: ' + (title.trim() || 'Без названия'),
          text: description.trim().slice(0, 120),
          from: currentUser.id,
          link: { screen: 'adminMyTask', ctx: { taskId: created.id } },
        });
      }
      showToast('Задача создана');
      navigate('directorTask', { taskId: created.id });
    } else {
      const prevStatus = existing?.status;
      const prevAssignee = existing?.assigneeId;
      await updateTask(taskId, {
        title: title.trim(), description: description.trim(),
        assigneeId, priority, status, deadline, todos, attachments,
      });
      // Уведомление о смене статуса — обоим сторонам
      if (notif && prevStatus !== status) {
        const statusLabel = (TASK_STATUSES.find(s => s.id === status) || {}).label || status;
        const both = [...new Set([assigneeId, existing?.createdBy].filter(x => x && x !== currentUser.id))];
        for (const uid of both) {
          notif.addNotification(uid, {
            type: 'task_status',
            title: `Статус задачи изменён: «${statusLabel}»`,
            text: title.trim(),
            from: currentUser.id,
            link: { screen: uid === existing?.createdBy ? 'directorTask' : 'adminMyTask', ctx: { taskId } },
          });
        }
      }
      // Если переназначили исполнителя — уведомить нового
      if (notif && prevAssignee !== assigneeId && assigneeId !== currentUser.id) {
        notif.addNotification(assigneeId, {
          type: 'task_assigned',
          title: 'Вам назначена задача: ' + title.trim(),
          text: description.trim().slice(0, 120),
          from: currentUser.id,
          link: { screen: 'adminMyTask', ctx: { taskId } },
        });
      }
      showToast('Задача обновлена');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() && commentAttachments.length === 0) return;
    await addTaskComment(taskId, { authorId: currentUser.id, text: commentText.trim(), attachments: commentAttachments });
    // Уведомление исполнителю о новом комментарии (от руководителя)
    if (notif && existing?.assigneeId && existing.assigneeId !== currentUser.id) {
      notif.addNotification(existing.assigneeId, {
        type: 'task_comment',
        title: 'Новый комментарий в задаче',
        text: (commentText.trim() || '📎 Вложение').slice(0, 120) + ' · ' + (existing.title || '').slice(0, 60),
        from: currentUser.id,
        link: { screen: 'adminMyTask', ctx: { taskId } },
      });
    }
    setCommentText('');
    setCommentAttachments([]);
    showToast('Комментарий добавлен');
  };

  const handleDelete = async () => {
    if (isNew) { navigate('directorTasks'); return; }
    const ok = await confirm({ title: 'Удалить задачу?', message: 'Действие необратимо.', confirmText: 'Удалить', danger: true });
    if (!ok) return;
    await deleteTask(taskId);
    showToast('Задача удалена');
    navigate('directorTasks');
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="border-b border-[#2A2E36] sticky top-0 z-10 bg-[#0A0B0D]/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('directorTasks')} className="p-2 -ml-2 hover:bg-[#15171B] rounded transition"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <div className="text-[10px] font-mono tracking-widest text-gray-500">{isNew ? 'НОВАЯ ЗАДАЧА' : 'РЕДАКТИРОВАНИЕ'}</div>
            <div className="text-sm text-white font-medium truncate">{isNew ? 'Создание задачи' : (title || 'Без названия')}</div>
          </div>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#E83A2C] hover:bg-[#FF4A3C] text-white font-mono text-xs tracking-wider rounded transition flex items-center gap-2"
          >
            <Save className="w-3.5 h-3.5" />
            {isNew ? 'СОЗДАТЬ' : 'СОХРАНИТЬ'}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-6 space-y-5">
        {/* Название */}
        <div>
          <label className="block text-[10px] font-mono text-gray-500 tracking-widest mb-2">НАЗВАНИЕ</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Что нужно сделать?"
            className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white text-base outline-none transition"
            autoFocus={isNew}
          />
        </div>

        {/* Описание */}
        <div>
          <label className="block text-[10px] font-mono text-gray-500 tracking-widest mb-2">ОПИСАНИЕ</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Подробности, контекст, ссылки..."
            rows={4}
            className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-4 py-3 text-white text-sm outline-none transition resize-none"
          />
        </div>

        {/* Грид: исполнитель / приоритет / дедлайн / статус */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-mono text-gray-500 tracking-widest mb-2">ИСПОЛНИТЕЛЬ</label>
            <select
              value={assigneeId}
              onChange={e => setAssigneeId(e.target.value)}
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-3 py-2.5 text-white text-sm outline-none transition"
            >
              <option value="">— Выбрать администратора —</option>
              {admins.map(a => (
                <option key={a.id} value={a.id}>{a.name}{a.isDirector ? ' (Руководитель)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono text-gray-500 tracking-widest mb-2">ПРИОРИТЕТ</label>
            <div className="grid grid-cols-4 gap-1">
              {TASK_PRIORITIES.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPriority(p.id)}
                  className={`px-2 py-2.5 rounded-lg text-xs font-mono tracking-wide transition border ${
                    priority === p.id ? 'border-white/30' : 'border-[#2A2E36] opacity-60 hover:opacity-100'
                  }`}
                  style={priority === p.id ? { background: p.color + '20', color: p.color, borderColor: p.color + '60' } : {}}
                  title={p.label}
                >
                  {p.icon}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono text-gray-500 tracking-widest mb-2">ДЕДЛАЙН</label>
            <input
              type="datetime-local"
              value={deadlineStr}
              onChange={e => setDeadlineStr(e.target.value)}
              className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-3 py-2.5 text-white text-sm outline-none transition"
            />
          </div>
          {!isNew && (
            <div>
              <label className="block text-[10px] font-mono text-gray-500 tracking-widest mb-2">СТАТУС</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded-lg px-3 py-2.5 text-white text-sm outline-none transition"
              >
                {TASK_STATUSES.map(s => (<option key={s.id} value={s.id}>{s.label}</option>))}
              </select>
            </div>
          )}
        </div>

        {/* To-do list */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-[#E83A2C]" />
            <div className="text-sm font-medium text-white">Чек-лист задачи</div>
            <div className="text-[10px] font-mono text-gray-500 ml-auto">
              {todos.filter(t => t.done).length}/{todos.length}
            </div>
          </div>
          {todos.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {todos.map(t => (
                <div key={t.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#0A0B0D] transition group">
                  <button onClick={() => toggleTodo(t.id)} className="flex-shrink-0">
                    {t.done
                      ? <CheckCircle2 className="w-4 h-4 text-[#00C781]" />
                      : <div className="w-4 h-4 border border-[#3A3E48] rounded-full"></div>}
                  </button>
                  <span className={`flex-1 text-sm ${t.done ? 'text-gray-500 line-through' : 'text-gray-200'}`}>{t.text}</span>
                  <button onClick={() => removeTodo(t.id)} className="text-gray-600 hover:text-[#FF4A3C] opacity-0 group-hover:opacity-100 transition">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={newTodoText}
              onChange={e => setNewTodoText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddTodo()}
              placeholder="Добавить пункт..."
              className="flex-1 bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded px-3 py-2 text-white text-sm outline-none transition"
            />
            <button onClick={handleAddTodo} className="px-3 py-2 bg-[#0A0B0D] hover:bg-[#15171B] border border-[#2A2E36] rounded text-gray-400 hover:text-white transition">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Вложения */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Paperclip className="w-4 h-4 text-[#E83A2C]" />
            <div className="text-sm font-medium text-white">Вложения</div>
            <div className="text-[10px] font-mono text-gray-500 ml-auto">{attachments.length}</div>
          </div>
          {attachments.length > 0 && <AttachmentsGrid attachments={attachments} onRemove={(id) => removeAttachment(id, 'task')} />}
          <FileUploadButton onFiles={(f) => handleFiles(f, 'task')} />
        </div>

        {/* Комментарии (только для существующей задачи) */}
        {!isNew && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-[#E83A2C]" />
              <div className="text-sm font-medium text-white">Обсуждение</div>
              <div className="text-[10px] font-mono text-gray-500 ml-auto">{(existing?.comments || []).length}</div>
            </div>
            <CommentsThread comments={existing?.comments || []} users={users} />
            <div className="mt-4 pt-4 border-t border-[#2A2E36]">
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Написать комментарий..."
                rows={2}
                className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded px-3 py-2 text-white text-sm outline-none transition resize-none mb-2"
              />
              {commentAttachments.length > 0 && <AttachmentsGrid attachments={commentAttachments} onRemove={(id) => removeAttachment(id, 'comment')} />}
              <div className="flex gap-2 items-center mt-2">
                <FileUploadButton compact onFiles={(f) => handleFiles(f, 'comment')} />
                <button
                  onClick={handleAddComment}
                  disabled={!commentText.trim() && commentAttachments.length === 0}
                  className="ml-auto px-4 py-2 bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-xs tracking-wider rounded transition flex items-center gap-2"
                >
                  <Send className="w-3.5 h-3.5" /> ОТПРАВИТЬ
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Удалить */}
        {!isNew && (
          <button
            onClick={handleDelete}
            className="w-full py-2.5 border border-[#FF4A3C]/30 text-[#FF4A3C] hover:bg-[#FF4A3C]/10 rounded-lg text-xs font-mono tracking-wider transition flex items-center justify-center gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" /> УДАЛИТЬ ЗАДАЧУ
          </button>
        )}
      </div>
    </div>
  );
}

// ─── 3. AdminMyTasksList: задачи назначенные мне (для админа/исполнителя) ───
function AdminMyTasksList({ users, navigate, currentUser, showToast }) {
  const { tasks } = useContent();
  const [filter, setFilter] = useState('active');

  const myTasks = (tasks || []).filter(t => t.assigneeId === currentUser?.id);
  const visible = myTasks.filter(t => filter === 'all' || t.status === filter)
    .sort((a, b) => {
      const aExp = a.deadline && a.deadline < Date.now() && a.status === 'active';
      const bExp = b.deadline && b.deadline < Date.now() && b.status === 'active';
      if (aExp !== bExp) return aExp ? -1 : 1;
      if (a.deadline && b.deadline) return a.deadline - b.deadline;
      return b.createdAt - a.createdAt;
    });

  const counts = {
    active: myTasks.filter(t => t.status === 'active').length,
    review: myTasks.filter(t => t.status === 'review').length,
    done:   myTasks.filter(t => t.status === 'done').length,
    all:    myTasks.length,
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="border-b border-[#2A2E36]">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-3">
          <button onClick={() => navigate('admin')} className="p-2 -ml-2 hover:bg-[#15171B] rounded transition"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <div className="text-[10px] font-mono tracking-widest text-gray-500">МОИ ЗАДАЧИ</div>
            <h1 className="font-display text-2xl tracking-wider text-white">Поручения от руководителя</h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-6">
        <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
          {[
            { id: 'active', label: 'В работе', count: counts.active },
            { id: 'review', label: 'На проверке', count: counts.review },
            { id: 'done',   label: 'Завершённые', count: counts.done },
            { id: 'all',    label: 'Все', count: counts.all },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-mono tracking-wider whitespace-nowrap transition border ${
                filter === t.id ? 'bg-[#E83A2C] border-[#E83A2C] text-white' : 'bg-[#15171B] border-[#2A2E36] text-gray-400 hover:text-white'
              }`}
            >
              {t.label}
              <span className={`ml-2 text-[10px] ${filter === t.id ? 'text-white/70' : 'text-gray-600'}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#2A2E36] rounded-xl">
            <CheckSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <div className="text-gray-500 text-sm">
              {filter === 'active' ? 'У вас нет активных задач.' : `Нет задач в категории «${filter}»`}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map(task => {
              const author = users.find(u => u.id === task.createdBy);
              const pri = getPriority(task.priority);
              const stat = getStatus(task.status);
              const StatIcon = stat.icon;
              const deadline = formatDeadline(task.deadline);
              const todoTotal = (task.todos || []).length;
              const todoDone = (task.todos || []).filter(t => t.done).length;
              return (
                <div
                  key={task.id}
                  className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 hover:border-[#3A3E48] transition cursor-pointer"
                  onClick={() => navigate('adminMyTask', { taskId: task.id })}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-1 rounded-full self-stretch flex-shrink-0" style={{ background: pri.color, minHeight: 48 }} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white text-base leading-tight mb-1">{task.title}</h3>
                      {task.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{task.description}</p>}
                      <div className="flex items-center flex-wrap gap-3 text-[11px] font-mono">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: stat.color + '20', color: stat.color }}>
                          <StatIcon className="w-3 h-3" /> {stat.label.toUpperCase()}
                        </span>
                        <span className="text-gray-400">от {author?.name || 'Руководителя'}</span>
                        {deadline && (
                          <span className={deadline.expired ? 'text-[#FF4A3C]' : deadline.urgent ? 'text-[#FFB800]' : 'text-gray-500'}>
                            <Clock className="w-3 h-3 inline mr-1" />{deadline.text}
                          </span>
                        )}
                        <span style={{ color: pri.color }}>{pri.icon} {pri.label.toUpperCase()}</span>
                        {todoTotal > 0 && (
                          <span className="text-gray-500"><ListChecks className="w-3 h-3 inline mr-1" />{todoDone}/{todoTotal}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 4. AdminMyTaskView: просмотр своей задачи (админ — исполнитель) ───
function AdminMyTaskView({ taskId, users, navigate, currentUser, showToast }) {
  const { tasks, updateTask, addTaskComment, hasPerm } = useContent();
  const notif = useNotifications();
  const task = (tasks || []).find(t => t.id === taskId);
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);

  if (!task) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-sm text-gray-400 mb-4">Задача не найдена</div>
          <button onClick={() => navigate('adminMyTasks')} className="px-4 py-2 bg-[#15171B] border border-[#2A2E36] rounded text-sm">К списку задач</button>
        </div>
      </div>
    );
  }

  // Проверка: эта задача назначена мне? Либо у меня есть право canEditAllTasks?
  if (task.assigneeId !== currentUser?.id && !hasPerm(currentUser, 'canEditAllTasks')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-[#FF4A3C] mx-auto mb-4" />
          <div className="text-sm text-gray-400 mb-4">Эта задача назначена не вам</div>
          <button onClick={() => navigate('adminMyTasks')} className="px-4 py-2 bg-[#15171B] border border-[#2A2E36] rounded text-sm">К моим задачам</button>
        </div>
      </div>
    );
  }

  const author = users.find(u => u.id === task.createdBy);
  const pri = getPriority(task.priority);
  const stat = getStatus(task.status);
  const StatIcon = stat.icon;
  const deadline = formatDeadline(task.deadline);

  const toggleTodo = async (todoId) => {
    const newTodos = task.todos.map(t => t.id === todoId ? { ...t, done: !t.done } : t);
    await updateTask(task.id, { todos: newTodos });
  };

  const handleStatusChange = async (newStatus) => {
    await updateTask(task.id, { status: newStatus });
    // Уведомление руководителю о смене статуса
    if (notif && task.createdBy && task.createdBy !== currentUser.id) {
      const statusLabel = (TASK_STATUSES.find(s => s.id === newStatus) || {}).label || newStatus;
      notif.addNotification(task.createdBy, {
        type: 'task_status',
        title: `${currentUser.name}: «${statusLabel}»`,
        text: task.title,
        from: currentUser.id,
        link: { screen: 'directorTask', ctx: { taskId: task.id } },
      });
    }
    showToast(newStatus === 'review' ? 'Задача отправлена на проверку' : 'Статус обновлён');
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files);
    const out = [];
    for (const f of arr) {
      try {
        const data = f.type.startsWith('image/') ? await compressImage(f) : await fileToDataUrl(f);
        out.push({ id: 'a_' + Date.now() + '_' + Math.random().toString(36).slice(2,5), name: f.name, type: f.type, size: f.size, data });
      } catch (e) {}
    }
    setCommentAttachments([...commentAttachments, ...out]);
  };
  const removeCommentAtt = (id) => setCommentAttachments(commentAttachments.filter(a => a.id !== id));

  const handleAddComment = async () => {
    if (!commentText.trim() && commentAttachments.length === 0) return;
    await addTaskComment(task.id, { authorId: currentUser.id, text: commentText.trim(), attachments: commentAttachments });
    // Уведомление руководителю о новом комментарии исполнителя
    if (notif && task.createdBy && task.createdBy !== currentUser.id) {
      notif.addNotification(task.createdBy, {
        type: 'task_comment',
        title: `${currentUser.name} ответил в задаче`,
        text: (commentText.trim() || '📎 Вложение').slice(0, 120) + ' · ' + (task.title || '').slice(0, 60),
        from: currentUser.id,
        link: { screen: 'directorTask', ctx: { taskId: task.id } },
      });
    }
    setCommentText('');
    setCommentAttachments([]);
    showToast('Комментарий добавлен');
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="border-b border-[#2A2E36] sticky top-0 z-10 bg-[#0A0B0D]/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('adminMyTasks')} className="p-2 -ml-2 hover:bg-[#15171B] rounded transition"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono tracking-widest text-gray-500">ЗАДАЧА</div>
            <div className="text-sm text-white font-medium truncate">{task.title}</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-6 space-y-5">
        {/* Шапка задачи */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-1 rounded-full self-stretch flex-shrink-0" style={{ background: pri.color, minHeight: 40 }} />
            <div className="flex-1">
              <h1 className="font-display text-xl text-white mb-2">{task.title}</h1>
              <div className="flex items-center flex-wrap gap-3 text-[11px] font-mono">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: stat.color + '20', color: stat.color }}>
                  <StatIcon className="w-3 h-3" /> {stat.label.toUpperCase()}
                </span>
                <span style={{ color: pri.color }}>{pri.icon} ПРИОРИТЕТ: {pri.label.toUpperCase()}</span>
                {deadline && (
                  <span className={deadline.expired ? 'text-[#FF4A3C]' : deadline.urgent ? 'text-[#FFB800]' : 'text-gray-500'}>
                    <Clock className="w-3 h-3 inline mr-1" />{deadline.text}
                  </span>
                )}
                <span className="text-gray-500">от {author?.name || 'Руководителя'}</span>
              </div>
            </div>
          </div>
          {task.description && (
            <div className="text-sm text-gray-300 leading-relaxed mt-3 pt-3 border-t border-[#2A2E36] whitespace-pre-wrap">{task.description}</div>
          )}
        </div>

        {/* Управление статусом */}
        {task.status !== 'done' && task.status !== 'cancelled' && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
            <div className="text-[10px] font-mono text-gray-500 tracking-widest mb-3">ИЗМЕНИТЬ СТАТУС</div>
            <div className="flex flex-wrap gap-2">
              {task.status === 'active' && (
                <button
                  onClick={() => handleStatusChange('review')}
                  className="px-4 py-2.5 bg-[#FFB800]/15 hover:bg-[#FFB800]/25 border border-[#FFB800]/40 text-[#FFB800] font-mono text-xs tracking-wider rounded transition flex items-center gap-2"
                >
                  <AlertOctagon className="w-3.5 h-3.5" /> ОТПРАВИТЬ НА ПРОВЕРКУ
                </button>
              )}
              {task.status === 'review' && (
                <button
                  onClick={() => handleStatusChange('active')}
                  className="px-4 py-2.5 bg-[#3B82F6]/15 hover:bg-[#3B82F6]/25 border border-[#3B82F6]/40 text-[#3B82F6] font-mono text-xs tracking-wider rounded transition flex items-center gap-2"
                >
                  <Hourglass className="w-3.5 h-3.5" /> ВЕРНУТЬ В РАБОТУ
                </button>
              )}
            </div>
            <div className="text-[11px] text-gray-500 mt-3 leading-relaxed">
              {task.status === 'active'
                ? 'Когда работа выполнена — отметьте задачу на проверку. Руководитель её закроет.'
                : 'Задача ожидает закрытия руководителем. Если что-то нужно доделать — можно вернуть в работу.'}
            </div>
          </div>
        )}

        {/* To-do чек-лист */}
        {(task.todos || []).length > 0 && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks className="w-4 h-4 text-[#E83A2C]" />
              <div className="text-sm font-medium text-white">Чек-лист</div>
              <div className="text-[10px] font-mono text-gray-500 ml-auto">
                {task.todos.filter(t => t.done).length}/{task.todos.length}
              </div>
            </div>
            <div className="space-y-1.5">
              {task.todos.map(t => (
                <button
                  key={t.id}
                  onClick={() => toggleTodo(t.id)}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-[#0A0B0D] transition text-left"
                  disabled={task.status === 'done' || task.status === 'cancelled'}
                >
                  {t.done
                    ? <CheckCircle2 className="w-5 h-5 text-[#00C781] flex-shrink-0" />
                    : <div className="w-5 h-5 border-2 border-[#3A3E48] rounded-full flex-shrink-0"></div>}
                  <span className={`flex-1 text-sm ${t.done ? 'text-gray-500 line-through' : 'text-gray-200'}`}>{t.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Вложения задачи */}
        {(task.attachments || []).length > 0 && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Paperclip className="w-4 h-4 text-[#E83A2C]" />
              <div className="text-sm font-medium text-white">Вложения</div>
            </div>
            <AttachmentsGrid attachments={task.attachments} readonly />
          </div>
        )}

        {/* Комментарии */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-[#E83A2C]" />
            <div className="text-sm font-medium text-white">Обсуждение</div>
            <div className="text-[10px] font-mono text-gray-500 ml-auto">{(task.comments || []).length}</div>
          </div>
          <CommentsThread comments={task.comments || []} users={users} />
          {task.status !== 'done' && task.status !== 'cancelled' && (
            <div className="mt-4 pt-4 border-t border-[#2A2E36]">
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Комментарий с фото или видео отчётом..."
                rows={2}
                className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#E83A2C] rounded px-3 py-2 text-white text-sm outline-none transition resize-none mb-2"
              />
              {commentAttachments.length > 0 && <AttachmentsGrid attachments={commentAttachments} onRemove={removeCommentAtt} />}
              <div className="flex gap-2 items-center mt-2">
                <FileUploadButton compact onFiles={handleFiles} />
                <button
                  onClick={handleAddComment}
                  disabled={!commentText.trim() && commentAttachments.length === 0}
                  className="ml-auto px-4 py-2 bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-xs tracking-wider rounded transition flex items-center gap-2"
                >
                  <Send className="w-3.5 h-3.5" /> ОТПРАВИТЬ
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Вспомогательные компоненты ───
function FileUploadButton({ onFiles, compact = false }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
        className="hidden"
      />
      <button
        onClick={() => ref.current?.click()}
        className={compact
          ? "px-3 py-2 bg-[#0A0B0D] hover:bg-[#15171B] border border-[#2A2E36] rounded text-gray-400 hover:text-white transition flex items-center gap-2 text-xs"
          : "w-full py-3 border border-dashed border-[#2A2E36] hover:border-[#E83A2C] text-gray-500 hover:text-[#E83A2C] rounded-lg transition flex items-center justify-center gap-2 text-xs font-mono tracking-wider"
        }
      >
        <Camera className="w-3.5 h-3.5" />
        {compact ? 'ФАЙЛ' : 'ПРИКРЕПИТЬ ФОТО ИЛИ ВИДЕО'}
      </button>
    </>
  );
}

function AttachmentsGrid({ attachments, onRemove, readonly }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
      {attachments.map(a => (
        <div key={a.id} className="relative group aspect-square bg-[#0A0B0D] border border-[#2A2E36] rounded overflow-hidden">
          {a.type?.startsWith('image/') ? (
            <a href={a.data} target="_blank" rel="noopener" className="block w-full h-full">
              <img src={a.data} alt={a.name} className="w-full h-full object-cover" />
            </a>
          ) : a.type?.startsWith('video/') ? (
            <video src={a.data} controls className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 p-2">
              <FileText className="w-6 h-6 mb-1" />
              <div className="text-[9px] truncate w-full text-center">{a.name}</div>
            </div>
          )}
          {!readonly && onRemove && (
            <button
              onClick={() => onRemove(a.id)}
              className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-[#FF4A3C] text-white rounded flex items-center justify-center transition opacity-0 group-hover:opacity-100"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function CommentsThread({ comments, users }) {
  if (comments.length === 0) {
    return <div className="text-center py-6 text-xs text-gray-600">Комментариев пока нет</div>;
  }
  return (
    <div className="space-y-3">
      {comments.map(c => {
        const author = users.find(u => u.id === c.authorId);
        return (
          <div key={c.id} className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#E83A2C] to-[#8B0F08] flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                {(author?.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="text-xs font-medium text-white">{author?.name || 'Пользователь'}</div>
              {author?.isDirector && <span className="text-[9px] font-mono text-[#FFB800] tracking-wider">РУКОВ.</span>}
              <div className="text-[10px] text-gray-600 ml-auto font-mono">
                {new Date(c.at).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            {c.text && <div className="text-sm text-gray-200 whitespace-pre-wrap mb-2">{c.text}</div>}
            {(c.attachments || []).length > 0 && <AttachmentsGrid attachments={c.attachments} readonly />}
          </div>
        );
      })}
    </div>
  );
}


function AdminMessages({ users, navigate, currentUser, showToast, initialUserId }) {
  const notif = useNotifications();
  const { hasPerm } = useContent();
  const [selectedId, setSelectedId] = useState(initialUserId || null);
  useEffect(() => { if (initialUserId) setSelectedId(initialUserId); }, [initialUserId]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  if (!hasPerm(currentUser, 'canSendMessages')) {
    return <PermissionGate user={currentUser} perm="canSendMessages" navigate={navigate} label="отправки сообщений" />;
  }

  const trainees = users.filter(u => !u.isAdmin);
  const filtered = trainees.filter(u =>
    !search || (u.name + (u.email || '')).toLowerCase().includes(search.toLowerCase())
  );
  const selected = users.find(u => u.id === selectedId);

  const send = async () => {
    if (!selected || !text.trim()) return;
    setBusy(true);
    try {
      const key = STORAGE_KEYS.MESSAGES(selected.id);
      const existingResp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${selected.id}`, { credentials: 'include' });
      let existing = [];
      if (existingResp.ok) {
        const json = await existingResp.json();
        if (Array.isArray(json?.value)) existing = json.value;
      }
      const msg = {
        id: 'msg_' + Date.now(),
        from: currentUser.id,
        fromName: currentUser.name,
        text: text.trim(),
        at: Date.now(),
        read: false,
      };
      await fetch(`/api/kv/${encodeURIComponent(key)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: [...existing, msg], shared: false, owner_id: selected.id }),
      });
      if (notif && selected.id !== currentUser.id) {
        notif.addNotification(selected.id, {
          type: 'message',
          title: 'Сообщение от ' + currentUser.name,
          text: text.trim().slice(0, 140),
          from: currentUser.id,
          link: { screen: 'messages' },
        });
      }
      realtimeBus?.dispatchEvent(new CustomEvent('rt', { detail: { type: 'new_message', chatUserId: selected.id, from: currentUser.id } }));
      setText('');
    } catch (e) {
      showToast('Не удалось отправить', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="max-w-5xl mx-auto chat-screen flex flex-col fade-in">
      <AdminHeader back={() => (selected && window.matchMedia('(max-width: 767px)').matches) ? setSelectedId(null) : navigate('admin')} title="СООБЩЕНИЯ" subtitle="Чат со стажёрами" />

      <div className="flex-1 flex border-t border-[#2A2E36] overflow-hidden min-h-0">
        {/* Левая панель — список контактов (на телефоне скрывается, когда открыт чат) */}
        <div className={`md:w-80 w-full border-r border-[#2A2E36] flex-col bg-[#0A0B0D] flex-shrink-0 ${selected ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Поиск стажёра..."
                className="w-full bg-[#15171B] border border-[#2A2E36] rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-[#FFB800] focus:outline-none transition"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map(u => (
              <button
                key={u.id}
                onClick={() => setSelectedId(u.id)}
                className={`w-full p-3 flex items-center gap-3 transition border-b border-[#1A1C20] ${
                  selectedId === u.id ? 'bg-[#FFB800]/10 border-l-2 border-l-[#FFB800]' : 'hover:bg-[#15171B] border-l-2 border-l-transparent'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <Avatar user={u} size={44} />
                  {u.lastActiveAt && Date.now() - u.lastActiveAt < 5 * 60 * 1000 && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0A0B0D] bg-[#00C781]" title="В сети" />
                  )}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm text-white font-medium truncate">{u.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{u.role || u.email || 'Стажёр'}</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="p-6 text-center text-gray-600 text-sm">Никого не найдено</div>
            )}
          </div>
        </div>

        {/* Правая панель — чат */}
        <div className={`flex-1 flex-col bg-[#0A0B0D] min-w-0 ${selected ? 'flex' : 'hidden md:flex'}`}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#15171B] flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-gray-700" />
                </div>
                <div className="text-gray-500 text-sm">Выбери стажёра слева</div>
                <div className="text-xs text-gray-600 mt-1">чтобы открыть чат</div>
              </div>
            </div>
          ) : (
            <>
              {/* Шапка чата */}
              <div className="p-3 border-b border-[#2A2E36] flex items-center gap-3 bg-[#0A0B0D]/95 backdrop-blur sticky top-0 z-10">
                <button onClick={() => setSelectedId(null)} className="md:hidden min-touch -ml-2 flex items-center justify-center text-gray-400" aria-label="К списку">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <Avatar user={selected} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm">{selected.name}</div>
                  <div className="text-[11px] text-gray-500">{selected.role || selected.email || 'Стажёр'}</div>
                </div>
              </div>

              {/* История сообщений */}
              <ChatHistory userId={selected.id} />

              {/* Поле ввода */}
              <div className="p-3 border-t border-[#2A2E36] bg-[#0A0B0D] safe-bottom-sm">
                <div className="flex items-end gap-2">
                  <textarea
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Напиши сообщение..."
                    rows={1}
                    className="flex-1 bg-[#15171B] border border-[#2A2E36] focus:border-[#FFB800] rounded-xl px-4 py-3 text-white text-sm outline-none resize-none max-h-32"
                    style={{ minHeight: 42 }}
                  />
                  <button
                    onClick={send}
                    disabled={busy || !text.trim()}
                    className="w-10 h-10 rounded-xl bg-[#FFB800] hover:bg-[#FFC820] disabled:bg-gray-800 disabled:text-gray-600 text-black flex items-center justify-center flex-shrink-0 transition"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {['Не забывай проходить уроки!', 'Жду на смене сегодня.', 'Отличная работа!', 'Пройди тест до пятницы.'].map(t => (
                    <button
                      key={t}
                      onClick={() => setText(t)}
                      className="text-xs px-2.5 py-1.5 bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] rounded-full transition"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Компонент истории чата
function ChatHistory({ userId }) {
  const [history, setHistory] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMessages = useCallback(async () => {
    try {
      const key = STORAGE_KEYS.MESSAGES(userId);
      const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${userId}`, { credentials: 'include' });
      if (resp.ok) {
        const json = await resp.json();
        const all = Array.isArray(json?.value) ? json.value : [];
        if (all.length > 20) {
          setHistory(all.slice(-20));
          setHasMore(true);
        } else {
          setHistory(all);
          setHasMore(false);
        }
      } else {
        setHistory([]);
        setHasMore(false);
      }
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setHistory([]);
    setLoading(true);
    loadMessages();
    // Страховочный опрос на случай, если сокет недоступен
    const t = setInterval(loadMessages, 20000);
    return () => clearInterval(t);
  }, [userId, loadMessages]);

  // Мгновенное обновление через общий сокет приложения
  useRealtime((data) => {
    if ((data?.type === 'new_message' || data?.type === 'messages_updated') && data.chatUserId === userId) {
      loadMessages();
    }
  });

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const key = STORAGE_KEYS.MESSAGES(userId);
      const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${userId}`, { credentials: 'include' });
      if (resp.ok) {
        const json = await resp.json();
        const all = Array.isArray(json?.value) ? json.value : [];
        const currentLength = history.length;
        const start = Math.max(0, all.length - currentLength - 20);
        const older = all.slice(start, all.length - currentLength);
        if (older.length > 0) {
          setHistory([...older, ...history]);
        }
        if (start <= 0) setHasMore(false);
      }
    } catch {} finally {
      setLoadingMore(false);
    }
  }, [userId, history, hasMore, loadingMore]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [history.length]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    if (containerRef.current.scrollTop < 50 && hasMore && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loadingMore, loadMore]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col-reverse overflow-y-auto p-4" ref={containerRef} onScroll={handleScroll}>
      <div className="space-y-1">
        {loadingMore && (
          <div className="text-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-gray-600 mx-auto" />
          </div>
        )}
        {history.length === 0 ? (
          <div className="flex items-center justify-center text-center py-20">
            <div>
              <div className="text-gray-600 text-sm">Нет сообщений</div>
              <div className="text-xs text-gray-700 mt-1">Напишите первое сообщение</div>
            </div>
          </div>
        ) : (
          [...history].map((msg, i) => {
            const isAdmin = msg.from !== userId;
            const prevMsg = i > 0 ? history[i - 1] : null;
            const showDate = !prevMsg || new Date(msg.at).toDateString() !== new Date(prevMsg.at).toDateString();
            
            return (
              <div key={msg.id}>
                {showDate && (
                  <div className="text-center my-3">
                    <span className="text-[9px] font-mono text-gray-600 bg-[#15171B] px-3 py-1 rounded-full">
                      {new Date(msg.at).toLocaleDateString('ru', { day: 'numeric', month: 'long' })}
                    </span>
                  </div>
                )}
                <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-1`}>
                  <div className={`chat-bubble ${isAdmin ? 'is-mine' : 'is-theirs'}`}>
                    <div className="text-sm whitespace-pre-wrap break-words">{msg.text}</div>
                    <div className={`text-[9px] mt-1 ${isAdmin ? 'text-red-200' : 'text-gray-500'} flex items-center gap-1 justify-end`}>
                      {new Date(msg.at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                      {isAdmin && msg.read && <span>✓</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ВИДЕО HELPER: парсер ссылок
// ═══════════════════════════════════════════════════════════════

// Разбирает ссылку (или вставленный код <iframe>) и возвращает, как её показать.
// { kind: 'file' | 'iframe', src, provider }  или null
function parseVideoSource(raw) {
  let url = String(raw || '').trim();
  if (!url) return null;
  // Вставили код плеера целиком — берём src из iframe
  const iframeSrc = url.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  if (iframeSrc) url = iframeSrc[1].replace(/&amp;/g, '&');
  if (url.startsWith('//')) url = 'https:' + url;

  // Загруженный на наш сервер файл
  if (url.startsWith('/api/uploads/')) return { kind: 'file', src: url, provider: 'upload' };
  // Прямая ссылка на файл
  if (/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url)) return { kind: 'file', src: url, provider: 'file' };

  // YouTube
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}`, provider: 'youtube' };
  // Vimeo
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}`, provider: 'vimeo' };
  // Rutube
  const rt = url.match(/rutube\.ru\/(?:video|play\/embed|shorts)\/([a-f0-9]{32})/i);
  if (rt) return { kind: 'iframe', src: `https://rutube.ru/play/embed/${rt[1]}`, provider: 'rutube' };

  // ВКонтакте / VK Видео
  if (/(^|\.)(vk\.com|vk\.ru|vkvideo\.ru)\//i.test(url.replace(/^https?:\/\//, ''))) {
    // Уже embed-ссылка (из «Поделиться → Экспортировать»): оставляем как есть, в ней есть hash
    if (/video_ext\.php/i.test(url)) return { kind: 'iframe', src: url.replace(/^http:/, 'https:'), provider: 'vk' };
    // video-123_456, clip-123_456, ?z=video-123_456 — все варианты
    const m = url.match(/(?:video|clip)(-?\d+)_(\d+)/);
    if (m) {
      const hash = (url.match(/[?&]hash=([a-f0-9]+)/i) || [])[1];
      return {
        kind: 'iframe',
        src: `https://vk.com/video_ext.php?oid=${m[1]}&id=${m[2]}&hd=2${hash ? `&hash=${hash}` : ''}`,
        provider: 'vk',
      };
    }
    return null;
  }
  // Неизвестный хостинг: пробуем как iframe только если это https
  if (/^https:\/\//i.test(url)) return { kind: 'iframe', src: url, provider: 'other' };
  return null;
}

// Совместимость со старыми вызовами
function getEmbedUrl(url) { return parseVideoSource(url)?.src || null; }
function isDirectVideo(url) { return parseVideoSource(url)?.kind === 'file'; }

const VIDEO_PROVIDER_LABEL = { youtube: 'YouTube', vimeo: 'Vimeo', rutube: 'Rutube', vk: 'VK Видео', upload: 'Файл с устройства', file: 'Видеофайл', other: 'Видео' };

// Загрузка видеофайла на сервер кусками по ~768 КБ — проходит через любые лимиты прокси
async function uploadVideoFile(file, { onProgress, signal } = {}) {
  const api = (import.meta?.env?.VITE_API_URL || '/api').replace(/\/$/, '');
  const readErr = async (r, fallback) => {
    const b = await r.json().catch(() => ({}));
    return new Error(b.message || fallback);
  };
  const init = await fetch(`${api}/uploads/init`, {
    method: 'POST', credentials: 'include', signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: file.name, size: file.size, type: file.type }),
  });
  if (!init.ok) throw await readErr(init, 'Сервер не принял файл');
  const { uploadId, chunkSize } = await init.json();
  const total = Math.ceil(file.size / chunkSize);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize));
    let lastErr = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const r = await fetch(`${api}/uploads/${uploadId}/chunk?index=${i}`, {
          method: 'PUT', credentials: 'include', signal,
          headers: { 'Content-Type': 'application/octet-stream' },
          body: blob,
        });
        if (!r.ok) throw await readErr(r, `Ошибка на части ${i + 1}`);
        lastErr = null;
        break;
      } catch (e) {
        if (signal?.aborted) throw e;
        lastErr = e;
        await new Promise(res => setTimeout(res, 800 * (attempt + 1)));
      }
    }
    if (lastErr) throw lastErr;
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const done = await fetch(`${api}/uploads/${uploadId}/complete`, { method: 'POST', credentials: 'include', signal });
  if (!done.ok) throw await readErr(done, 'Не удалось сохранить файл');
  return done.json();
}

const formatBytes = (b) => b >= 1024 * 1024 * 1024 ? (b / 1024 / 1024 / 1024).toFixed(1) + ' ГБ' : (b / 1024 / 1024).toFixed(b > 100 * 1024 * 1024 ? 0 : 1) + ' МБ';

// Редактор видео: ссылка (ВК / YouTube / Rutube / код плеера) или файл с устройства
function VideoSourceEditor({ value, name, onChange, onRemove, showToast }) {
  const src = parseVideoSource(value);
  const [tab, setTab] = useState(value && src?.provider === 'upload' ? 'file' : 'link');
  const [draft, setDraft] = useState(value && src?.provider !== 'upload' ? value : '');
  const [upload, setUpload] = useState(null);   // { pct, name, size }
  const abortRef = useRef(null);
  const fileRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;          // после долгой загрузки вызываем свежий колбэк
  const draftParsed = parseVideoSource(draft);

  useEffect(() => () => abortRef.current?.abort(), []);

  const applyLink = () => {
    if (!draft.trim()) return;
    if (!draftParsed) { showToast?.('Не получилось распознать ссылку на видео', 'error'); return; }
    // Храним нормализованный embed — так плеер не зависит от формата исходной ссылки
    onChange(draftParsed.kind === 'iframe' ? draftParsed.src : draft.trim(), '');
    showToast?.('Видео добавлено');
  };

  const pickFile = async (file) => {
    if (!file) return;
    if (!/^video\//.test(file.type) && !/\.(mp4|webm|mov|m4v)$/i.test(file.name)) {
      showToast?.('Выберите видеофайл: MP4, WebM или MOV', 'error');
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setUpload({ pct: 0, name: file.name, size: file.size });
    try {
      const res = await uploadVideoFile(file, { signal: ctrl.signal, onProgress: (pct) => setUpload(u => u && { ...u, pct }) });
      onChangeRef.current(res.url, file.name);
      showToast?.('Видео загружено');
    } catch (e) {
      if (!ctrl.signal.aborted) showToast?.(e.message || 'Не удалось загрузить видео', 'error');
    } finally {
      setUpload(null);
      abortRef.current = null;
    }
  };

  return (
    <div className="video-editor">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="seg flex-1 min-w-0">
          <button type="button" className={tab === 'link' ? 'on' : ''} onClick={() => setTab('link')}>
            <PlayCircle className="w-3.5 h-3.5" /> Ссылка
          </button>
          <button type="button" className={tab === 'file' ? 'on' : ''} onClick={() => setTab('file')}>
            <Upload className="w-3.5 h-3.5" /> С устройства
          </button>
        </div>
        <button
          type="button"
          onClick={() => { abortRef.current?.abort(); onRemove(); }}
          className="w-9 h-9 flex-shrink-0 rounded-lg text-gray-500 hover:text-white hover:bg-[#E83A2C] flex items-center justify-center transition"
          aria-label="Убрать видео" title="Убрать видео"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {tab === 'link' && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } }}
              placeholder="vk.com/video-123_456, vkvideo.ru, YouTube, Rutube"
              className="flex-[1_1_220px] min-w-0 bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#FFB800] rounded-lg px-3 py-2.5 text-sm text-white outline-none"
            />
            <button
              type="button"
              onClick={applyLink}
              disabled={!draft.trim()}
              className="flex-auto min-h-[42px] px-4 rounded-lg bg-[#FFB800] text-black text-sm font-semibold disabled:opacity-40"
            >
              Вставить
            </button>
          </div>
          <div className="text-xs text-gray-500 leading-relaxed">
            {draft.trim()
              ? (draftParsed ? <span className="text-[#00C781]">Распознано: {VIDEO_PROVIDER_LABEL[draftParsed.provider]}</span> : <span className="text-[#FF6B5C]">Ссылка не похожа на видео</span>)
              : <>Для закрытых видео ВК вставьте код из «Поделиться → Экспортировать» — в нём есть ключ доступа.</>}
          </div>
        </div>
      )}

      {tab === 'file' && (
        <div>
          {upload ? (
            <div className="rounded-lg border border-[#2A2E36] bg-[#0A0B0D] p-3">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-white">{upload.name}</span>
                <span className="text-gray-400 tabular-nums flex-shrink-0">{upload.pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#15171B] mt-2 overflow-hidden">
                <div className="h-full bg-[#FFB800] transition-all" style={{ width: `${upload.pct}%` }} />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500">{formatBytes(upload.size)} · не закрывайте страницу</span>
                <button type="button" onClick={() => abortRef.current?.abort()} className="text-xs text-gray-400 hover:text-[#E83A2C]">Отменить</button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-lg border border-dashed border-[#2A2E36] hover:border-[#FFB800] bg-[#0A0B0D] p-4 text-center transition"
            >
              <Upload className="w-5 h-5 text-[#FFB800] mx-auto mb-1.5" />
              <div className="text-sm text-white">{src?.provider === 'upload' ? 'Заменить видео' : 'Выбрать видео'}</div>
              <div className="text-xs text-gray-500 mt-0.5">MP4, WebM или MOV · на телефоне можно снять сразу</div>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/*"
            className="hidden"
            onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>
      )}

      {value && src && !upload && (
        <div className="mt-3">
          <div className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            <Video className="w-3.5 h-3.5" /> {VIDEO_PROVIDER_LABEL[src.provider]}{name ? ` · ${name}` : ''}
          </div>
          <VideoPlayer url={value} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// УВЕДОМЛЕНИЯ — Toast и Center
// ═══════════════════════════════════════════════════════════════

// Всплывашка-уведомление в правом верхнем углу
function NotificationToast({ notif, onDismiss, onClick }) {
  const type = NOTIF_TYPES[notif.type] || NOTIF_TYPES.system;
  return (
    <div className="notif-toast pop-in" role="alert">
      <div
        onClick={onClick}
        className="cursor-pointer notif-toast-card"
        style={{ '--notif-color': type.color }}
      >
        <div className="text-2xl flex-shrink-0 mt-0.5">{type.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-wider mb-0.5" style={{ color: type.color }}>
            {type.label}
          </div>
          {notif.title && (
            <div className="font-medium text-white text-sm leading-tight mb-0.5 truncate">{notif.title}</div>
          )}
          {notif.text && (
            <div className="text-xs text-gray-400 leading-tight line-clamp-2">{notif.text}</div>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="text-gray-600 hover:text-white p-1 -m-1 flex-shrink-0 transition"
          aria-label="Закрыть"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Центр уведомлений — кнопка-колокольчик + панель (на телефоне — шторка снизу)
function NotificationCenter({ navigate, currentUserId, users, compact = false }) {
  const { notifications, unreadCount, markNotificationRead, markAllNotificationsRead, clearNotifications, setNotifications } = useNotifications();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const isAdmin = !!users?.find(u => u.id === currentUserId)?.isAdmin;
  // На телефоне панель — шторка через портал: липкая шапка с blur иначе «запирает» fixed-элементы внутри себя
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const fn = () => setIsPhone(mq.matches);
    mq.addEventListener?.('change', fn);
    return () => mq.removeEventListener?.('change', fn);
  }, []);

  // При открытии подтягиваем СВОЮ ленту из БД (у админа раньше грузились ленты всех
  // стажёров — он видел чужие «Сообщение от …», а клик по ним портил его собственную ленту).
  useEffect(() => {
    if (!open || !currentUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const key = STORAGE_KEYS.NOTIFICATIONS(currentUserId);
        const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${currentUserId}`, { credentials: 'include' });
        if (!cancelled && resp.ok) {
          const json = await resp.json();
          if (Array.isArray(json?.value)) setNotifications(json.value);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [open, currentUserId, setNotifications]);

  // Закрытие по клику вне и по Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClick = (notif) => {
    const target = resolveNotifLink(notif, isAdmin);
    markNotificationRead(notif.id);
    setOpen(false);
    if (target) navigate(target.screen, target.ctx);
  };

  const fmt = (ts) => {
    const diff = Date.now() - ts;
    const min = 60_000;
    if (diff < min) return 'только что';
    if (diff < 60 * min) return Math.floor(diff / min) + ' мин';
    if (diff < 24 * 60 * min) return Math.floor(diff / (60 * min)) + ' ч';
    if (diff < 7 * 24 * 60 * min) return Math.floor(diff / (24 * 60 * min)) + ' дн';
    return new Date(ts).toLocaleDateString('ru', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`relative min-touch flex items-center justify-center rounded-xl transition ${
          compact ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
        } ${open ? 'bg-white/5 text-white' : ''}`}
        aria-label={`Уведомления${unreadCount > 0 ? ` (${unreadCount} непрочитанных)` : ''}`}
        aria-expanded={open}
      >
        {unreadCount > 0 ? <BellRing className="w-5 h-5 text-[#FFB800] icon-wiggle" /> : <Bell className="w-5 h-5" /> }
        {unreadCount > 0 && (
          <span className="notif-dot">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (() => {
        const panel = (
        <>
          {isPhone && <div className="sheet-backdrop" onClick={() => setOpen(false)} />}
          <div className="notif-panel" role="dialog" aria-label="Уведомления" ref={panelRef}>
            <div className="sheet-grip sm:hidden" />
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <div className="flex items-baseline gap-2">
                <div className="font-display text-lg tracking-wider text-white">Уведомления</div>
                {unreadCount > 0 && (
                  <div className="text-xs text-[#FFB800]">{unreadCount} новых</div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllNotificationsRead()}
                    className="text-xs text-gray-400 hover:text-[#FFB800] px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition"
                  >
                    Прочитать все
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-500 hover:text-white p-2 rounded-lg hover:bg-white/5 transition"
                  aria-label="Закрыть"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="notif-panel-list">
              {notifications.length === 0 ? (
                <div className="p-10 text-center">
                  <FedoraHat className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <div className="text-sm text-gray-400">Тихо, как в хорошей семье</div>
                  <div className="text-xs text-gray-600 mt-1">Здесь появятся задачи, комментарии и сообщения</div>
                </div>
              ) : (
                <div>
                  {notifications.map(notif => {
                    const type = NOTIF_TYPES[notif.type] || NOTIF_TYPES.system;
                    const hasTarget = !!resolveNotifLink(notif, isAdmin);
                    return (
                      <button
                        key={notif.id}
                        onClick={() => handleClick(notif)}
                        className={`notif-row ${!notif.read ? 'is-unread' : ''}`}
                        style={{ '--notif-color': type.color }}
                      >
                        <div className="notif-row-icon">{type.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[11px] font-semibold" style={{ color: type.color }}>
                              {type.label.charAt(0) + type.label.slice(1).toLowerCase()}
                            </span>
                            <span className="text-[11px] text-gray-600">{fmt(notif.at)}</span>
                          </div>
                          {notif.title && (
                            <div className={`text-sm leading-snug ${notif.read ? 'text-gray-400' : 'text-white font-semibold'}`}>
                              {notif.title}
                            </div>
                          )}
                          {notif.text && (
                            <div className="text-xs text-gray-500 leading-snug line-clamp-2 mt-0.5">{notif.text}</div>
                          )}
                        </div>
                        {hasTarget && <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0 self-center" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="px-3 py-2 border-t border-white/5 flex justify-between items-center safe-bottom-sm">
                <div className="text-xs text-gray-600 px-1">Всего {notifications.length}</div>
                <button
                  onClick={async () => {
                    if (await confirm('Очистить всю историю уведомлений?', { danger: true })) {
                      await clearNotifications();
                    }
                  }}
                  className="text-xs text-gray-500 hover:text-[#E83A2C] px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition"
                >
                  Очистить
                </button>
              </div>
            )}
          </div>
        </>
        );
        return isPhone ? createPortal(panel, document.body) : panel;
      })()}
    </div>
  );
}

// Блок настроек уведомлений (в профиле)
function NotificationSettingsBlock() {
  const { notifSettings, updateNotifSettings } = useNotifications();
  const [expanded, setExpanded] = useState(false);

  const Toggle = ({ value, onChange, label, hint, accent = '#FFB800' }) => (
    <button
      onClick={() => onChange(!value)}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-[#0A0B0D] transition group text-left"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white group-hover:text-white">{label}</div>
        {hint && <div className="text-[10px] text-gray-600 mt-0.5">{hint}</div>}
      </div>
      <div
        className="w-10 h-6 rounded-full transition relative flex-shrink-0"
        style={{ background: value ? accent : '#2A2E36' }}
      >
        <div
          className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all"
          style={{ left: value ? '18px' : '2px' }}
        />
      </div>
    </button>
  );

  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full p-3 flex items-center gap-3 hover:bg-[#0A0B0D] transition"
      >
        <Bell className="w-4 h-4 text-[#FFB800] flex-shrink-0" />
        <div className="flex-1 text-left">
          <div className="text-sm text-white">Уведомления</div>
          <div className="text-[10px] text-gray-600 font-mono tracking-wider mt-0.5">
            {notifSettings.inAppEnabled ? 'ВКЛЮЧЕНЫ' : 'ВЫКЛЮЧЕНЫ'} ·
            {notifSettings.soundEnabled ? ' СО ЗВУКОМ' : ' БЕЗ ЗВУКА'} ·
            {notifSettings.emailEnabled ? ' EMAIL' : ' БЕЗ EMAIL'}
          </div>
        </div>
        {expanded ? <ChevronLeft className="w-4 h-4 text-gray-500 rotate-90" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {expanded && (
        <div className="border-t border-[#2A2E36] p-2 space-y-1 fade-in">
          <div className="px-3 py-1.5 font-mono text-[10px] text-gray-500 tracking-wider">ОСНОВНОЕ</div>
          <Toggle
            value={notifSettings.inAppEnabled}
            onChange={v => updateNotifSettings({ inAppEnabled: v })}
            label="Уведомления в приложении"
            hint="Всплывающие сообщения и значок-колокольчик"
          />
          <Toggle
            value={notifSettings.soundEnabled}
            onChange={v => updateNotifSettings({ soundEnabled: v })}
            label="Звуковой сигнал"
            hint="Короткий звук при новых уведомлениях"
          />
          <Toggle
            value={notifSettings.emailEnabled}
            onChange={v => updateNotifSettings({ emailEnabled: v })}
            label="Email-уведомления"
            hint="Дублировать на email (только важные события)"
            accent="#00C781"
          />

          <div className="px-3 py-1.5 mt-2 font-mono text-[10px] text-gray-500 tracking-wider">ПО ТИПАМ СОБЫТИЙ</div>
          <Toggle
            value={notifSettings.tasks}
            onChange={v => updateNotifSettings({ tasks: v })}
            label="📋 Новые задачи"
          />
          <Toggle
            value={notifSettings.taskComments}
            onChange={v => updateNotifSettings({ taskComments: v })}
            label="💬 Комментарии к задачам"
          />
          <Toggle
            value={notifSettings.taskStatus}
            onChange={v => updateNotifSettings({ taskStatus: v })}
            label="🔄 Смена статуса задач"
          />
          <Toggle
            value={notifSettings.messages}
            onChange={v => updateNotifSettings({ messages: v })}
            label="✉️ Личные сообщения"
          />
          <Toggle
            value={notifSettings.system}
            onChange={v => updateNotifSettings({ system: v })}
            label="ℹ️ Системные"
          />
        </div>
      )}
    </div>
  );
}

function VideoPlayer({ url }) {
  const [failed, setFailed] = useState(false);
  const src = parseVideoSource(url);
  if (!url) return null;
  if (!src) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-xl border border-[#2A2E36] bg-[#0A0B0D] p-3 text-sm text-[#FFB800]">
        <PlayCircle className="w-4 h-4" /> Открыть видео
      </a>
    );
  }
  if (src.kind === 'file') {
    return failed ? (
      <div className="rounded-xl border border-[#2A2E36] bg-[#0A0B0D] p-4 text-sm text-gray-400">
        Видео не воспроизводится в этом браузере.{' '}
        <a href={src.src} target="_blank" rel="noopener noreferrer" className="text-[#FFB800] underline">Открыть файл</a>
      </div>
    ) : (
      <video
        src={src.src}
        controls
        playsInline
        preload="metadata"
        className="w-full rounded-xl bg-black max-h-[70vh]"
        onError={() => setFailed(true)}
      >
        Ваш браузер не поддерживает видео.
      </video>
    );
  }
  return (
    <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
      <iframe
        src={src.src}
        className="absolute top-0 left-0 w-full h-full"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        title="Видео"
      />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// АДМИН: РЕГЛАМЕНТЫ — список
// ═══════════════════════════════════════════════════════════════

function AdminRegulationsList({ navigate, showToast, currentUser }) {
  const { regulations, updateRegulations, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditRegulations')) {
    return <PermissionGate user={currentUser} perm="canEditRegulations" navigate={navigate} label="редактирования регламентов" />;
  }

  const groups = {
    shawarmafia: regulations.filter(r => r.brand === 'shawarmafia'),
    rollomafia: regulations.filter(r => r.brand === 'rollomafia'),
    shared: regulations.filter(r => r.brand === 'shared'),
  };

  const addRegulation = async (brand) => {
    const newReg = {
      id: genId('reg'),
      brand,
      title: 'Новый регламент',
      subtitle: '',
      icon: '📋',
      instructions: [],       // ← новое поле: массив { id, text, photo? }
      rights: [],
      duties: [],
      privileges: [],
      penalties: [],
    };
    await updateRegulations([...regulations, newReg]);
    navigate('adminRegulation', { regId: newReg.id });
  };

  const deleteRegulation = async (id) => {
    if (!(await confirm('Удалить регламент?', { danger: true }))) return;
    await updateRegulations(regulations.filter(r => r.id !== id));
    showToast('Регламент удалён', 'error');
  };

  const duplicateRegulation = async (id) => {
    const orig = regulations.find(r => r.id === id);
    if (!orig) return;
    const copy = { ...deepClone(orig), id: genId('reg'), title: orig.title + ' (копия)' };
    await updateRegulations([...regulations, copy]);
    showToast('Регламент скопирован');
  };

  const renderGroup = (key, label, icon, color) => {
    const list = groups[key];
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{icon}</span>
            <h3 className="font-display text-lg tracking-wider text-white">{label}</h3>
            <span className="font-mono text-[10px] text-gray-600 tracking-wider">· {list.length}</span>
          </div>
          <button
            onClick={() => addRegulation(key)}
            className="text-[#FFB800] hover:text-[#FFC820] text-xs font-mono tracking-wider flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> ДОБАВИТЬ
          </button>
        </div>
        {list.length === 0 ? (
          <div className="bg-[#15171B] border border-dashed border-[#2A2E36] rounded-lg p-4 text-xs text-gray-500 text-center">
            Нет регламентов
          </div>
        ) : list.map(reg => (
          <div key={reg.id} className="bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800]/40 rounded-xl p-4 transition hover-lift">
            <div className="flex items-center gap-3">
              <div className="text-2xl flex-shrink-0">{reg.icon || '📋'}</div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('adminRegulation', { regId: reg.id })}>
                <div className="font-bold text-white">{reg.title}</div>
                {reg.subtitle && <div className="text-xs text-gray-500 mt-0.5 truncate">{reg.subtitle}</div>}
                <div className="font-mono text-[9px] text-gray-600 tracking-wider mt-1">
                  {(reg.instructions?.length || 0)} ИНСТР · {reg.rights.length} ПРАВ · {reg.duties.length} ОБЯЗАННОСТЕЙ · {reg.privileges.length} ПРИВИЛЕГИЙ · {reg.penalties.length} ШТРАФОВ
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <IconBtn icon={Copy} onClick={() => duplicateRegulation(reg.id)} />
                <IconBtn icon={Edit3} onClick={() => navigate('adminRegulation', { regId: reg.id })} accent />
                <IconBtn icon={Trash2} onClick={() => deleteRegulation(reg.id)} danger />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="РЕГЛАМЕНТЫ" subtitle="Должностные инструкции" />

      <div className="p-5 space-y-6">
        <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-xl p-3 text-xs text-gray-400 leading-relaxed">
          Регламенты разбиты на 3 группы. <span className="text-white">Шаурмафия</span> и <span className="text-white">Ролломафия</span> — специфичные для бренда; <span className="text-white">Общие</span> — для ролей, работающих с обоими брендами (курьеры, администраторы).
        </div>
        {renderGroup('shawarmafia', 'Шаурмафия', BRANDS.shawarmafia.icon)}
        {renderGroup('rollomafia', 'Ролломафия', BRANDS.rollomafia.icon)}
        {renderGroup('shared', 'Общие должности', '🤝')}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: РЕГЛАМЕНТ — редактор (Tilda-стиль, всё inline)
// ═══════════════════════════════════════════════════════════════

// ─── InstructionCard ───
// Редактор одной инструкции в регламенте: многострочный текст + опциональное фото.
// Используется в AdminRegulationEditor для каждого пункта в reg.instructions.
function InstructionCard({ instruction, index, onUpdate, onRemove, showToast }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);  // lightbox для предпросмотра
  // Локально: показывать ли поле для ввода ссылки на видео.
  // Открывается по клику «+ ДОБАВИТЬ ВИДЕО». Если у инструкции уже есть videoUrl — показываем поле сразу.
  const [showVideoInput, setShowVideoInput] = useState(!!instruction.videoUrl);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressLessonPhoto(file);
      onUpdate('photo', dataUrl);
      showToast?.('Фото добавлено');
    } catch (e) {
      console.error('[InstructionCard] photo upload:', e);
      showToast?.('Не удалось загрузить фото', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = () => onUpdate('photo', '');
  const removeVideo = () => {
    onUpdate({ videoUrl: '', videoName: '' });
    setShowVideoInput(false);
  };

  return (
    <div className="bg-[#0A0B0D] border border-[#FFB800]/20 rounded-xl overflow-hidden hover:border-[#FFB800]/40 transition group">
      <div className="p-3 flex items-start gap-2">
        {/* Номер шага */}
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 font-mono text-[11px] bg-[#FFB800]/15 text-[#FFB800] border border-[#FFB800]/30">
          {index + 1}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Текст инструкции — многострочный */}
          <InlineTextarea
            value={instruction.text}
            onChange={v => onUpdate('text', v)}
            placeholder="Подробный текст инструкции (можно разделить на абзацы)"
            rows={3}
            className="!p-1"
          />

          {/* Фото — превью или загрузчик */}
          {instruction.photo ? (
            <div className="relative inline-block max-w-full">
              <img
                src={instruction.photo}
                alt="Иллюстрация"
                onClick={() => setPhotoOpen(true)}
                className="max-h-32 rounded-lg border border-[#2A2E36] cursor-zoom-in hover:border-[#FFB800]/60 transition"
              />
              <button
                onClick={removePhoto}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-[#E83A2C] text-white flex items-center justify-center transition"
                title="Удалить фото"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : null}

          {/* Видео — ссылка (ВК/YouTube/Rutube) или файл с устройства */}
          {showVideoInput && (
            <VideoSourceEditor
              value={instruction.videoUrl || ''}
              name={instruction.videoName || ''}
              onChange={(url, fileName) => onUpdate({ videoUrl: url, videoName: fileName || '' })}
              onRemove={removeVideo}
              showToast={showToast}
            />
          )}

          {/* Кнопки добавления — только те, что ещё не использованы */}
          <div className="flex items-center gap-3 flex-wrap">
            {!instruction.photo && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="font-mono text-[10px] tracking-wider text-gray-500 hover:text-[#FFB800] flex items-center gap-1.5 transition disabled:opacity-50"
              >
                {busy
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> ЗАГРУЗКА...</>
                  : <><ImageIcon className="w-3 h-3" /> + ДОБАВИТЬ ФОТО</>
                }
              </button>
            )}
            {!showVideoInput && (
              <button
                onClick={() => setShowVideoInput(true)}
                className="font-mono text-[10px] tracking-wider text-gray-500 hover:text-[#E83A2C] flex items-center gap-1.5 transition"
              >
                <PlayCircle className="w-3 h-3" /> + ВИДЕО (ССЫЛКА ВК ИЛИ ФАЙЛ)
              </button>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>

        <IconBtn icon={Trash2} onClick={onRemove} danger />
      </div>

      {/* Lightbox для просмотра фото в полный размер */}
      {photoOpen && instruction.photo && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 fade-in"
          onClick={() => setPhotoOpen(false)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#15171B] hover:bg-[#E83A2C] text-white flex items-center justify-center transition"
            onClick={e => { e.stopPropagation(); setPhotoOpen(false); }}
          >
            <X className="w-5 h-5" />
          </button>
          <img src={instruction.photo} className="max-w-full max-h-full rounded-lg" alt="Иллюстрация" />
        </div>
      )}
    </div>
  );
}

function AdminRegulationEditor({ regId, navigate, showToast, currentUser }) {
  const { getRegulationById, updateRegulation, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditRegulations')) {
    return <PermissionGate user={currentUser} perm="canEditRegulations" navigate={navigate} label="редактирования регламента" />;
  }
  const reg = getRegulationById(regId);

  if (!reg) {
    return (
      <div className="max-w-3xl mx-auto p-5">
        <button onClick={() => navigate('adminRegulations')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-4 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НАЗАД
        </button>
        <div className="bg-[#15171B] border border-[#E83A2C]/40 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[#E83A2C] mx-auto mb-3" />
          <div className="text-white">Регламент не найден</div>
        </div>
      </div>
    );
  }

  const patch = (patchObj) => updateRegulation(regId, patchObj);

  // Простые списки строк (rights, duties, privileges)
  const addStringAt = (field, insertIdx = (reg[field] || []).length, defaultValue = 'Новый пункт') => {
    const next = [...(reg[field] || [])];
    next.splice(insertIdx, 0, defaultValue);
    return patch({ [field]: next });
  };
  const updString = (field, i, v) => {
    const next = [...reg[field]]; next[i] = v;
    return patch({ [field]: next });
  };
  const reorderString = (field) => (from, to) => {
    const next = [...reg[field]];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ [field]: next });
  };
  const removeString = (field, i) => patch({ [field]: reg[field].filter((_, x) => x !== i) });

  // ─── Инструкции — массив объектов { id, text, photo? } ───
  // text — markdown-подобный текст (line breaks сохраняются),
  // photo — base64 data-url одного изображения, опционально.
  // Загрузка/сжатие фото — через тот же compressLessonPhoto что и для уроков.
  const addInstructionAt = (insertIdx = (reg.instructions || []).length) => {
    const next = [...(reg.instructions || [])];
    next.splice(insertIdx, 0, { id: genId('ins'), text: '', photo: '' });
    return patch({ instructions: next });
  };
  // field может быть строкой (одно поле) или объектом (несколько полей сразу).
  // Ищем инструкцию по id: пока грузится видео, список могли переупорядочить.
  const updInstruction = (i, field, v, insId) => {
    const next = [...(reg.instructions || [])];
    const idx = insId ? next.findIndex(x => x.id === insId) : i;
    if (idx < 0 || !next[idx]) return;
    const change = (field && typeof field === 'object') ? field : { [field]: v };
    next[idx] = { ...next[idx], ...change };
    return patch({ instructions: next });
  };
  const reorderInstructions = (from, to) => {
    const next = [...(reg.instructions || [])];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ instructions: next });
  };
  const removeInstruction = (i) =>
    patch({ instructions: (reg.instructions || []).filter((_, x) => x !== i) });

  // Штрафы — массив объектов { title, amount }
  const addPenaltyAt = (insertIdx = (reg.penalties || []).length) => {
    const next = [...(reg.penalties || [])];
    next.splice(insertIdx, 0, { title: '', amount: '' });
    return patch({ penalties: next });
  };
  const updPenalty = (i, field, v) => {
    const next = [...reg.penalties]; next[i] = { ...next[i], [field]: v };
    return patch({ penalties: next });
  };
  const reorderPenalties = (from, to) => {
    const next = [...reg.penalties];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ penalties: next });
  };
  const removePenalty = (i) => patch({ penalties: reg.penalties.filter((_, x) => x !== i) });

  const brandOptions = [
    { value: 'shawarmafia', label: 'Шаурмафия' },
    { value: 'rollomafia', label: 'Ролломафия' },
    { value: 'shared', label: 'Общая должность' },
  ];

  // Универсальный рендер списка строк
  const renderStringList = (field, label, color, icon) => {
    const items = (reg[field] || []).map((s, i) => ({ value: s, _id: `${field}-${i}-${s.slice(0, 20)}` }));
    return (
      <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + '20', color }}>
            {icon}
          </div>
          <div className="font-display text-lg tracking-wider text-white">{label}</div>
          <span className="font-mono text-[10px] text-gray-600 tracking-wider">· {items.length}</span>
        </div>

        {items.length === 0 ? (
          <button
            onClick={() => addStringAt(field, 0)}
            className="w-full bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] rounded-lg py-3 text-sm text-gray-500 hover:text-[#FFB800] flex items-center justify-center gap-2 transition"
          >
            <Plus className="w-4 h-4" /> Добавить первый пункт
          </button>
        ) : (
          <>
            <AddBlockButton onAdd={() => addStringAt(field, 0)} label="пункт" />
            <DraggableList
              items={items}
              idKey="_id"
              onReorder={reorderString(field)}
              renderItem={(item, i, dragProps) => (
                <>
                  <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-2.5 flex items-start gap-2">
                    <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] p-1`}>
                      <GripVertical className="w-3.5 h-3.5" />
                    </div>
                    <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 font-mono text-[10px]" style={{ background: color + '20', color }}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <InlineTextarea
                        value={item.value}
                        onChange={v => updString(field, i, v)}
                        placeholder="Опиши пункт"
                        rows={1}
                        className="!p-1"
                      />
                    </div>
                    <IconBtn icon={Trash2} onClick={() => removeString(field, i)} danger />
                  </div>
                  <AddBlockButton onAdd={() => addStringAt(field, i + 1)} label="пункт" />
                </>
              )}
            />
          </>
        )}
      </div>
    );
  };

  const penalties = (reg.penalties || []).map((p, i) => ({ ...p, _id: `pen-${i}-${(p.title || '').slice(0, 20)}` }));

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminRegulations')} title="РЕГЛАМЕНТ" subtitle={reg.title} />

      <div className="py-5 space-y-5">
        {/* Метаданные */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ИЗМЕНЕНИЯ СОХРАНЯЮТСЯ АВТОМАТИЧЕСКИ</div>

          <div className="flex items-start gap-3">
            <InlineText
              value={reg.icon}
              onChange={v => patch({ icon: v })}
              placeholder="📋"
              size="3xl"
              className="text-4xl"
            />
            <div className="flex-1 min-w-0">
              <InlineText
                value={reg.title}
                onChange={v => patch({ title: v })}
                placeholder="Название должности"
                size="2xl"
                className="text-white font-bold block"
              />
              <div className="mt-1">
                <InlineText
                  value={reg.subtitle}
                  onChange={v => patch({ subtitle: v })}
                  placeholder="Короткое описание"
                  className="text-gray-400 block"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[#2A2E36] flex items-center gap-2">
            <span className="text-xs text-gray-500 font-mono tracking-wider">ГРУППА:</span>
            <InlineSelect
              value={reg.brand}
              onChange={v => patch({ brand: v })}
              options={brandOptions}
            />
          </div>
        </div>

        {/* ─── Инструкции — НОВЫЙ блок, идёт первым ─── */}
        <div className="bg-[#15171B] border border-[#FFB800]/20 rounded-2xl p-5 relative overflow-hidden">
          {/* Декоративный glow в углу */}
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.12) 0%, transparent 70%)', filter: 'blur(20px)' }} />

          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#FFB800]/20 text-[#FFB800]">
                <FileText className="w-4 h-4" />
              </div>
              <div className="font-display text-lg tracking-wider text-white">ИНСТРУКЦИИ</div>
              <span className="font-mono text-[10px] text-gray-600 tracking-wider">· {(reg.instructions || []).length}</span>
            </div>
            <div className="text-xs text-gray-500 mb-4 ml-10">
              Подробные шаги с возможностью добавить иллюстрации. Стажёр видит каждый шаг отдельной карточкой.
            </div>

            {(reg.instructions || []).length === 0 ? (
              <button
                onClick={() => addInstructionAt(0)}
                className="w-full bg-[#0A0B0D] hover:bg-[#FFB800]/10 border border-dashed border-[#FFB800]/30 hover:border-[#FFB800] rounded-lg py-4 text-sm text-gray-500 hover:text-[#FFB800] flex items-center justify-center gap-2 transition group"
              >
                <Plus className="w-4 h-4 transition-transform group-hover:rotate-90" style={{ transitionDuration: '0.25s' }} />
                Добавить первую инструкцию
              </button>
            ) : (
              <>
                <AddBlockButton onAdd={() => addInstructionAt(0)} label="инструкцию" />
                <DraggableList
                  items={(reg.instructions || []).map((ins, i) => ({ ...ins, _id: ins.id || `ins-${i}` }))}
                  idKey="_id"
                  onReorder={reorderInstructions}
                  renderItem={(ins, i, dragProps) => (
                    <>
                      <div className="flex items-stretch gap-2">
                        <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] py-3 flex items-center self-stretch`}>
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <InstructionCard
                            instruction={ins}
                            index={i}
                            onUpdate={(field, v) => updInstruction(i, field, v, ins.id)}
                            onRemove={() => removeInstruction(i)}
                            showToast={showToast}
                          />
                        </div>
                      </div>
                      <AddBlockButton onAdd={() => addInstructionAt(i + 1)} label="инструкцию" />
                    </>
                  )}
                />
              </>
            )}
          </div>
        </div>

        {renderStringList('rights', 'ПРАВА', '#00C781', '✓')}
        {renderStringList('duties', 'ОБЯЗАННОСТИ', '#00E5FF', '★')}
        {renderStringList('privileges', 'ПРИВИЛЕГИИ', '#FFB800', '◆')}

        {/* Штрафы — особый блок с двумя полями */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#E83A2C]/20 text-[#E83A2C]">
              ⚠
            </div>
            <div className="font-display text-lg tracking-wider text-white">ШТРАФНЫЕ САНКЦИИ</div>
            <span className="font-mono text-[10px] text-gray-600 tracking-wider">· {penalties.length}</span>
          </div>

          {penalties.length === 0 ? (
            <button
              onClick={() => addPenaltyAt(0)}
              className="w-full bg-[#1F0A0A] hover:bg-[#E83A2C]/10 border border-dashed border-[#E83A2C]/30 hover:border-[#E83A2C] rounded-lg py-3 text-sm text-gray-500 hover:text-[#E83A2C] flex items-center justify-center gap-2 transition"
            >
              <Plus className="w-4 h-4" /> Добавить первый штраф
            </button>
          ) : (
            <>
              <AddBlockButton onAdd={() => addPenaltyAt(0)} label="штраф" />
              <DraggableList
                items={penalties}
                idKey="_id"
                onReorder={reorderPenalties}
                renderItem={(p, i, dragProps) => (
                  <>
                    <div className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-lg p-3 flex items-start gap-2">
                      <div {...dragProps} className={`${dragProps.className} text-[#E83A2C]/60 hover:text-[#E83A2C] p-1`}>
                        <GripVertical className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <InlineText
                          value={p.title}
                          onChange={v => updPenalty(i, 'title', v)}
                          placeholder="Описание нарушения"
                          className="text-[#E83A2C] font-bold block"
                        />
                        <InlineText
                          value={p.amount}
                          onChange={v => updPenalty(i, 'amount', v)}
                          placeholder="Сумма штрафа / последствия"
                          className="text-gray-400 block font-mono text-xs"
                        />
                      </div>
                      <IconBtn icon={Trash2} onClick={() => removePenalty(i)} danger />
                    </div>
                    <AddBlockButton onAdd={() => addPenaltyAt(i + 1)} label="штраф" />
                  </>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// СТАЖЁР: ЭКРАН РЕГЛАМЕНТОВ
// ═══════════════════════════════════════════════════════════════

function RegulationsScreen({ user, navigate, initialRegId }) {
  const { getRegulationsFor } = useContent();
  const regs = getRegulationsFor(user);
  const [openId, setOpenId] = useState(initialRegId || null);
  const openReg = openId ? regs.find(r => r.id === openId) : null;
  useEffect(() => { if (openId && !openReg) setOpenId(null); }, [openId, openReg]);

  const groups = {
    shawarmafia: regs.filter(r => r.brand === 'shawarmafia'),
    rollomafia: regs.filter(r => r.brand === 'rollomafia'),
    shared: regs.filter(r => r.brand === 'shared'),
  };

  if (openReg) {
    return <RegulationDetail reg={openReg} onBack={() => { setOpenId(null); if (initialRegId) navigate('regulations'); }} />;
  }

  const renderGroup = (key, label, icon, brandColor = '#FFB800') => {
    const list = groups[key];
    if (list.length === 0) return null;
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-1 mb-1">
          <span className="text-xl">{icon}</span>
          <h3 className="font-display text-lg tracking-wider text-white">{label}</h3>
          <span className="font-mono text-[10px] text-gray-600 tracking-wider">· {list.length}</span>
          <div className="flex-1 h-px bg-gradient-to-r from-[#2A2E36] to-transparent ml-2" />
        </div>
        {list.map((reg, i) => {
          const instructionsCount = (reg.instructions || []).length;
          const hasVideo = (reg.instructions || []).some(ins => ins.videoUrl);
          const totalRules = (reg.rights?.length || 0) + (reg.duties?.length || 0) + (reg.privileges?.length || 0);
          return (
            <button
              key={reg.id}
              onClick={() => setOpenId(reg.id)}
              className="group w-full bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#2A2E36] hover:border-[#FFB800]/50 rounded-xl p-4 text-left transition hover-lift relative overflow-hidden pop-in"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              {/* Цветная полоса бренда слева */}
              <div className="absolute left-0 top-0 bottom-0 w-1 transition-opacity opacity-60 group-hover:opacity-100"
                style={{ background: brandColor }} />

              {/* Glow в углу при hover */}
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `radial-gradient(circle, ${brandColor}25 0%, transparent 70%)`, filter: 'blur(15px)' }} />

              <div className="relative flex items-center gap-3 pl-2">
                <div className="text-3xl flex-shrink-0 transition-transform group-hover:scale-110" style={{ transitionDuration: '0.25s' }}>
                  {reg.icon || '📋'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-base">{reg.title}</div>
                  {reg.subtitle && <div className="text-xs text-gray-500 mt-0.5 truncate">{reg.subtitle}</div>}
                  {/* Мини-статистика — что внутри регламента */}
                  <div className="flex items-center gap-3 mt-1.5 font-mono text-[9px] tracking-wider text-gray-600">
                    {instructionsCount > 0 && (
                      <span className="flex items-center gap-1 text-[#FFB800]">
                        <FileText className="w-2.5 h-2.5" /> {instructionsCount} ИНСТР
                      </span>
                    )}
                    {hasVideo && (
                      <span className="flex items-center gap-1 text-[#E83A2C]">
                        <PlayCircle className="w-2.5 h-2.5" /> ВИДЕО
                      </span>
                    )}
                    {totalRules > 0 && <span>{totalRules} ПРАВИЛ</span>}
                    {reg.penalties?.length > 0 && (
                      <span className="flex items-center gap-1 text-[#E83A2C]/70">
                        ⚠ {reg.penalties.length}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0 transition-transform group-hover:translate-x-1 group-hover:text-[#FFB800]" style={{ transitionDuration: '0.2s' }} />
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto pb-32 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('dashboard')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НА ГЛАВНУЮ
        </button>
        <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.2em]">РЕГЛАМЕНТЫ</div>
        <h1 className="font-display text-3xl tracking-wider text-white mt-0.5">ПРАВА И ОБЯЗАННОСТИ</h1>
      </div>

      <div className="p-5 space-y-6">
        {regs.length === 0 ? (
          <div className="bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#2A2E36] rounded-xl p-12 text-center pop-in">
            <Shield className="w-12 h-12 text-gray-700 mx-auto mb-3 float-anim" />
            <div className="text-gray-500 text-sm">Регламенты для тебя пока не настроены</div>
          </div>
        ) : (
          <>
            {renderGroup('shawarmafia', 'Шаурмафия', BRANDS.shawarmafia.icon, BRANDS.shawarmafia.color)}
            {renderGroup('rollomafia', 'Ролломафия', BRANDS.rollomafia.icon, BRANDS.rollomafia.color)}
            {renderGroup('shared', 'Общие', '🤝', '#A855F7')}
          </>
        )}
      </div>
      <BottomNav active="regs" navigate={navigate} />
    </div>
  );
}

// ─── InstructionViewer ───
// Карточка инструкции для стажёра: номер, текст с переносами строк, опциональное фото с lightbox.
function InstructionViewer({ instruction, index }) {
  const [photoOpen, setPhotoOpen] = useState(false);
  return (
    <>
      <div
        className="bg-gradient-to-br from-[#15171B] to-[#0F1116] border border-[#FFB800]/20 hover:border-[#FFB800]/40 rounded-xl p-4 hover-lift pop-in relative overflow-hidden transition"
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        {/* Декоративный glow в правом верхнем углу */}
        <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,184,0,0.1) 0%, transparent 70%)', filter: 'blur(15px)' }} />

        <div className="relative flex items-start gap-3">
          {/* Номер шага — кружок с золотой обводкой */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-sm flex-shrink-0 bg-gradient-to-br from-[#FFB800] to-[#FF9500] text-black shadow-lg shadow-[#FFB800]/30">
            {index + 1}
          </div>

          <div className="flex-1 min-w-0">
            {/* Текст с сохранением переносов */}
            {instruction.text && (
              <div className="text-[15px] text-gray-100 leading-relaxed whitespace-pre-line" style={{ lineHeight: 1.65 }}>
                {instruction.text}
              </div>
            )}

            {/* Фото — кликабельный preview */}
            {instruction.photo && (
              <button
                onClick={() => setPhotoOpen(true)}
                className={`${instruction.text ? 'mt-3' : ''} block max-w-full rounded-lg overflow-hidden border border-[#2A2E36] hover:border-[#FFB800] transition group/img cursor-zoom-in relative`}
              >
                <img
                  src={instruction.photo}
                  alt={`Иллюстрация к шагу ${index + 1}`}
                  className="max-h-64 w-auto transition-transform group-hover/img:scale-[1.02]"
                  style={{ transitionDuration: '0.3s' }}
                />
                {/* Hint zoom */}
                <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition">
                  <Maximize2 className="w-4 h-4 text-white" />
                </div>
              </button>
            )}

            {/* Видео — отдельный блок с плеером */}
            {instruction.videoUrl && (
              <div className={`${(instruction.text || instruction.photo) ? 'mt-3' : ''}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <PlayCircle className="w-3 h-3 text-[#E83A2C]" />
                  <span className="font-mono text-[9px] text-[#E83A2C] tracking-[0.2em]">ВИДЕО ИНСТРУКЦИИ</span>
                </div>
                <div className="rounded-lg overflow-hidden border border-[#2A2E36]">
                  <VideoPlayer url={instruction.videoUrl} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {photoOpen && instruction.photo && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 fade-in"
          onClick={() => setPhotoOpen(false)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#15171B]/90 hover:bg-[#E83A2C] text-white flex items-center justify-center transition backdrop-blur"
            onClick={e => { e.stopPropagation(); setPhotoOpen(false); }}
            aria-label="Закрыть"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute top-4 left-4 font-mono text-xs text-white/60 tracking-widest">
            ИНСТРУКЦИЯ · ШАГ {index + 1}
          </div>
          <img src={instruction.photo} className="max-w-full max-h-full rounded-lg" alt="Иллюстрация" />
        </div>
      )}
    </>
  );
}

function RegulationDetail({ reg, onBack }) {
  const instructions = reg.instructions || [];
  // Если есть инструкции — открываем именно их первыми. Иначе — права (как было).
  const [tab, setTab] = useState(instructions.length > 0 ? 'instructions' : 'rights');
  const tabs = [
    ...(instructions.length > 0
      ? [{ id: 'instructions', label: 'Инструкции', icon: '📋', color: '#FFB800', items: instructions }]
      : []
    ),
    { id: 'rights', label: 'Права', icon: '✓', color: '#00C781', items: reg.rights },
    { id: 'duties', label: 'Обязанности', icon: '★', color: '#00E5FF', items: reg.duties },
    { id: 'privileges', label: 'Привилегии', icon: '◆', color: '#FFB800', items: reg.privileges },
    { id: 'penalties', label: 'Штрафы', icon: '⚠', color: '#E83A2C', items: reg.penalties },
  ];
  const active = tabs.find(t => t.id === tab);

  return (
    <div className="max-w-2xl mx-auto pb-10 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={onBack} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К РЕГЛАМЕНТАМ
        </button>
        <div className="flex items-center gap-3">
          <span className="text-3xl">{reg.icon || '📋'}</span>
          <div>
            <h1 className="font-display text-2xl tracking-wider text-white">{reg.title.toUpperCase()}</h1>
            {reg.subtitle && <p className="text-xs text-gray-500 mt-0.5">{reg.subtitle}</p>}
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Табы — горизонтальный скролл, активный с цветной заливкой и pulse-точкой */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto -mx-1 px-1 pb-1">
          {tabs.map(t => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative flex-shrink-0 py-2 px-3 rounded-lg font-mono text-[10px] tracking-wider transition active:scale-95 flex items-center gap-1.5 border ${
                  isActive ? 'text-white shadow-lg' : 'text-gray-500 hover:text-white border-[#2A2E36] bg-[#15171B]'
                }`}
                style={isActive ? {
                  background: t.color + '20',
                  borderColor: t.color,
                  boxShadow: `0 4px 12px ${t.color}30`,
                } : {}}
              >
                <span style={{ color: isActive ? t.color : undefined }}>{t.icon}</span>
                <span>{t.label.toUpperCase()}</span>
                <span className={`opacity-70 ${isActive ? 'font-bold' : ''}`}>{t.items.length}</span>
                {isActive && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full glow-pulse" style={{ background: t.color }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Содержимое */}
        <div key={tab} className="space-y-2 slide-in-right">
          {active.items.length === 0 ? (
            <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-8 text-center">
              <div className="text-gray-500 text-sm">Раздел пуст</div>
            </div>
          ) : active.id === 'instructions' ? (
            // ─── Инструкции — карточки с текстом и опциональным фото ───
            active.items.map((ins, i) => (
              <InstructionViewer key={ins.id || i} instruction={ins} index={i} />
            ))
          ) : active.id === 'penalties' ? (
            active.items.map((p, i) => (
              <div key={i} className="bg-[#1F0A0A] border border-[#E83A2C]/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded bg-[#E83A2C] text-white flex items-center justify-center font-mono text-xs flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#E83A2C] mb-1">{p.title}</div>
                    <div className="text-sm text-white font-mono">{p.amount}</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            active.items.map((it, i) => (
              <div key={i} className="bg-[#15171B] border rounded-xl p-4 flex items-start gap-3" style={{ borderColor: active.color + '40' }}>
                <div className="w-7 h-7 rounded flex items-center justify-center font-mono text-xs flex-shrink-0" style={{ background: active.color + '20', color: active.color }}>
                  {i + 1}
                </div>
                <div className="flex-1 text-sm text-gray-200 leading-relaxed">{it}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: СТАТИСТИЧЕСКИЙ ДАШБОРД
// ═══════════════════════════════════════════════════════════════

function StatsDashboard({ users, navigate, onLogout, currentUser }) {
  const { courses, getCourseFor, hasPerm } = useContent();
  const [traineeProgress, setTraineeProgress] = useState({});
  if (!hasPerm(currentUser, 'canSeeStats')) {
    return <PermissionGate user={currentUser} perm="canSeeStats" navigate={navigate} label="просмотра статистики" />;
  }

  const trainees = users.filter(u => !u.isAdmin);

  // Загрузка прогресса всех стажёров
  useEffect(() => {
    const loadProgress = async () => {
      const map = {};
      for (const u of trainees) {
		//if (!u.lastLoginAt) continue;
        try {
          const key = STORAGE_KEYS.PROGRESS(u.id);
          const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${u.id}`, { credentials: 'include' });
          if (resp.ok) {
            const json = await resp.json();
            if (json?.value) map[u.id] = json.value;
          }
        } catch {}
      }
      setTraineeProgress(map);
    };
    
    loadProgress();
    const interval = setInterval(loadProgress, 30000);
    return () => clearInterval(interval);
  }, [users]);

  // Считаем метрики по каждому стажёру
  const stats = trainees.map(u => {
    const p = traineeProgress[u.id];
    const course = getCourseFor(u);
    if (!p || !course) return { user: u, course, hasData: false, xp: u.xp || 0, errors: 0, totalQuestions: 0, accuracy: 100, modulesPassed: 0, totalModules: course?.modules?.length || 0, completedAt: null, lastActive: u.lastLoginAt || u.joinedAt };

    let totalQ = 0;
    let errors = 0;
    let modulesPassed = 0;
    let earliestPass = Infinity;
    let latestPass = 0;
    for (const mod of course.modules) {
      const t = p.tests?.[mod.id];
      if (t?.passed) modulesPassed++;
      if (t) {
        const qCount = (t.totalQuestions || mod.test?.questions?.length || 0);
        totalQ += qCount;
        errors += Math.max(0, qCount - (t.correct || 0));
        if (t.lastAt) {
          earliestPass = Math.min(earliestPass, t.lastAt);
          latestPass = Math.max(latestPass, t.lastAt);
        }
      }
    }
    const accuracy = totalQ > 0 ? Math.round(((totalQ - errors) / totalQ) * 100) : 0;
    const completedAt = modulesPassed === course.modules.length && course.modules.length > 0 ? latestPass : null;
    const lastActive = Math.max(
      ...Object.values(p.lessons || {}).map(l => l.completedAt || 0),
      ...Object.values(p.tests || {}).map(t => t.lastAt || 0),
      p.startedAt || 0,
      u.lastLoginAt || u.joinedAt || 0
    );

    return {
      user: u,
      course,
      hasData: true,
      xp: p.xp || u.xp || 0,
      errors,
      totalQuestions: totalQ,
      accuracy,
      modulesPassed,
      totalModules: course.modules.length,
      completedAt,
      lastActive,
      streakDays: u.streakDays || 0,
    };
  });

  // ─── ТОПы ───
  const topByXP = [...stats].sort((a, b) => b.xp - a.xp).slice(0, 10);
  const topByAccuracy = [...stats].filter(s => s.totalQuestions > 0).sort((a, b) => b.accuracy - a.accuracy).slice(0, 10);
  const perfect = stats.filter(s => s.totalQuestions > 0 && s.errors === 0);
  const stuck = stats.filter(s => s.hasData && (Date.now() - s.lastActive) > 2 * 24 * 3600 * 1000 && s.completedAt === null);
  const completed = stats.filter(s => s.completedAt !== null);

  // Глобальные показатели
  const totalErrors = stats.reduce((s, x) => s + x.errors, 0);
  const totalQuestions = stats.reduce((s, x) => s + x.totalQuestions, 0);
  const globalAccuracy = totalQuestions > 0 ? Math.round(((totalQuestions - totalErrors) / totalQuestions) * 100) : 0;

  // Топ ошибочных модулей
  const moduleErrors = {};
  for (const s of stats) {
    if (!s.course) continue;
    for (const mod of s.course.modules) {
      const t = traineeProgress[s.user.id]?.tests?.[mod.id];
      if (!t) continue;
      const qCount = (t.totalQuestions || mod.test?.questions?.length || 0);
      const errs = Math.max(0, qCount - (t.correct || 0));
      if (errs > 0) {
        const key = `${s.course.title} · ${mod.title}`;
        if (!moduleErrors[key]) moduleErrors[key] = { count: 0, attempts: 0 };
        moduleErrors[key].count += errs;
        moduleErrors[key].attempts += 1;
      }
    }
  }
  const topErrorModules = Object.entries(moduleErrors)
    .map(([key, v]) => ({ key, ...v, avg: v.count / v.attempts }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return (
    <div className="max-w-5xl mx-auto pb-10 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('adminContent')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> К ХАБУ
        </button>
        <div className="font-mono text-[10px] text-[#FFB800] tracking-[0.2em] flex items-center gap-2">
          <BarChart3 className="w-3 h-3" /> СТАТИСТИКА · {currentUser.name.toUpperCase()}
        </div>
        <h1 className="font-display text-3xl tracking-wider text-white mt-1">ДАШБОРД ОБУЧЕНИЯ</h1>
      </div>

      <div className="py-5 space-y-5">
        {/* Глобальные показатели */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBlock icon={Users} label="Стажёров" value={trainees.length} accent="#00E5FF" />
          <StatBlock icon={CheckCircle2} label="Завершили курс" value={completed.length} accent="#00C781" />
          <StatBlock icon={Target} label="Средняя точность" value={`${globalAccuracy}%`} accent="#FFB800" />
          <StatBlock icon={AlertTriangle} label="Застряли (2+ дня)" value={stuck.length} accent="#E83A2C" />
        </div>

        {/* ТОП по XP */}
        <DashboardSection icon={Trophy} title="ТОП ПО XP" accent="#FFB800">
          {topByXP.length === 0 ? <EmptyMini>Пока никто не получил XP</EmptyMini> : topByXP.map((s, i) => (
            <LeaderRow key={s.user.id} rank={i + 1} s={s} valueLabel={`${s.xp.toLocaleString('ru')} XP`} accent="#FFB800" />
          ))}
        </DashboardSection>

        {/* ТОП без ошибок */}
        <DashboardSection icon={Award} title="БЕЗ ОШИБОК В ТЕСТАХ" accent="#00C781">
          {perfect.length === 0 ? <EmptyMini>Пока никто не прошёл тесты безошибочно</EmptyMini> : perfect.map((s, i) => (
            <LeaderRow key={s.user.id} rank={i + 1} s={s} valueLabel={`${s.totalQuestions} вопросов`} accent="#00C781" />
          ))}
        </DashboardSection>

        {/* ТОП по точности */}
        <DashboardSection icon={Target} title="ТОЧНОСТЬ ОТВЕТОВ" accent="#00E5FF">
          {topByAccuracy.length === 0 ? <EmptyMini>Никто ещё не отвечал на вопросы</EmptyMini> : topByAccuracy.map((s, i) => (
            <LeaderRow key={s.user.id} rank={i + 1} s={s} valueLabel={`${s.accuracy}% · ${s.errors} ошибок`} accent="#00E5FF" />
          ))}
        </DashboardSection>

        {/* Самые сложные модули */}
        <DashboardSection icon={AlertCircle} title="МОДУЛИ С НАИБОЛЬШИМ ЧИСЛОМ ОШИБОК" accent="#E83A2C">
          {topErrorModules.length === 0 ? <EmptyMini>Данных пока недостаточно</EmptyMini> : topErrorModules.map((m, i) => (
            <div key={m.key} className="bg-[#15171B] border border-[#2A2E36] rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded bg-[#E83A2C]/20 text-[#E83A2C] flex items-center justify-center font-mono text-xs">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm truncate">{m.key}</div>
                  <div className="font-mono text-[10px] text-gray-500 tracking-wider">{m.count} ОШИБОК · {m.attempts} ПОПЫТОК · ~{m.avg.toFixed(1)} ошибок/попытку</div>
                </div>
              </div>
              <div className="w-full h-1.5 bg-[#0A0B0D] rounded-full overflow-hidden">
                <div className="h-full bg-[#E83A2C]" style={{ width: `${Math.min(100, (m.count / Math.max(1, topErrorModules[0].count)) * 100)}%` }} />
              </div>
            </div>
          ))}
        </DashboardSection>

        {/* Застрявшие */}
        {stuck.length > 0 && (
          <DashboardSection icon={Clock} title="ЗАСТРЯЛИ — БЕЗ АКТИВНОСТИ 2+ ДНЯ" accent="#E83A2C">
            {stuck.map((s, i) => {
              const daysAgo = Math.floor((Date.now() - s.lastActive) / (24 * 3600 * 1000));
              return (
                <button
                  key={s.user.id}
                  onClick={() => navigate('adminTrainee', { userId: s.user.id })}
                  className="w-full bg-[#1F0A0A] border border-[#E83A2C]/30 hover:border-[#E83A2C] rounded-lg p-3 flex items-center gap-3 text-left transition"
                >
                  <Avatar user={s.user} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate">{s.user.name}</div>
                    <div className="font-mono text-[10px] text-[#E83A2C] tracking-wider">НЕ АКТИВЕН {daysAgo} ДН. · ПРОГРЕСС {s.modulesPassed}/{s.totalModules}</div>
                  </div>
                  <Send className="w-4 h-4 text-[#E83A2C]" />
                </button>
              );
            })}
          </DashboardSection>
        )}
      </div>
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
      <Icon className="w-5 h-5 mb-2" style={{ color: accent }} />
      <div className="font-display text-3xl tracking-wider text-white">{value}</div>
      <div className="font-mono text-[10px] text-gray-500 tracking-wider mt-1">{label.toUpperCase()}</div>
    </div>
  );
}

function DashboardSection({ icon: Icon, title, accent, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 px-1">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <h3 className="font-display text-base tracking-wider text-white">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function EmptyMini({ children }) {
  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-lg p-4 text-center text-xs text-gray-500">
      {children}
    </div>
  );
}

function LeaderRow({ rank, s, valueLabel, accent, highlight }) {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  // Когда accent — это CSS-переменная вида 'var(--accent)', нельзя конкатенировать с '20'
  const isCssVar = typeof accent === 'string' && accent.startsWith('var(');
  const bgTinted = isCssVar
    ? 'color-mix(in srgb, var(--accent) 20%, transparent)'
    : (accent + '20');
  return (
    <div className={`border rounded-lg p-3 flex items-center gap-3 transition ${
      highlight ? 'bg-[#FFB800]/10 themed-border-accent' : 'bg-[#15171B] border-[#2A2E36]'
    }`}>
      <div
        className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 font-mono text-sm"
        style={{ background: rank <= 3 ? bgTinted : '#0A0B0D', color: rank <= 3 ? accent : '#666' }}
      >
        {medal || rank}
      </div>
      <Avatar user={s.user} size={32} />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white text-sm truncate">
          {s.user.name}
          {highlight && <span className="text-[10px] themed-text-accent font-mono ml-2 tracking-wider">ЭТО ТЫ</span>}
        </div>
        <div className="font-mono text-[10px] text-gray-500 tracking-wider truncate">
          {s.course?.title || 'без курса'}
        </div>
      </div>
      <div className="font-mono text-xs tracking-wider flex-shrink-0" style={{ color: accent }}>
        {valueLabel}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АВАТАР — общий компонент
// ═══════════════════════════════════════════════════════════════

function Avatar({ user, size = 40, className = '' }) {
  const px = `${size}px`;
  const initial = (user?.name || '?')[0]?.toUpperCase() || '?';
  if (user?.avatar) {
    return (
      <img
        src={user.avatar}
        alt={user.name}
        className={`rounded-lg object-cover flex-shrink-0 ${className}`}
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <div
      className={`rounded-lg flex items-center justify-center font-display text-white flex-shrink-0 ${className} ${
        user?.isAdmin ? 'bg-gradient-to-br from-[#FFB800] to-[#A87800]' : 'bg-gradient-to-br from-[#E83A2C] to-[#8B0F08]'
      }`}
      style={{ width: px, height: px, fontSize: size * 0.4 }}
    >
      {initial}
    </div>
  );
}

// Загрузка аватара с файла → base64 (с ресайзом до 256px)
async function fileToAvatarDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('Нет файла'));
    if (!file.type.startsWith('image/')) return reject(new Error('Это не изображение'));
    if (file.size > 5 * 1024 * 1024) return reject(new Error('Файл больше 5 МБ'));
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Ресайз до 256x256
        const canvas = document.createElement('canvas');
        const targetSize = 256;
        const scale = Math.min(targetSize / img.width, targetSize / img.height, 1);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          resolve(dataUrl);
        } catch (e) { reject(e); }
      };
      img.onerror = () => reject(new Error('Не удалось загрузить изображение'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function AvatarUpload({ user, onChange, size = 80 }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToAvatarDataUrl(file);
      await onChange(dataUrl);
    } catch (err) {
      alert('Ошибка загрузки: ' + err.message);
    } finally {
      setBusy(false);
      e.target.value = ''; // сброс input чтобы можно было выбрать тот же файл
    }
  };

  return (
    <div className="relative inline-block group">
      <Avatar user={user} size={size} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="absolute inset-0 rounded-lg bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white"
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
      </button>
      {user?.avatar && (
        <button
          onClick={async () => { await onChange(''); }}
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E83A2C] hover:bg-[#FF4A3C] text-white flex items-center justify-center"
          title="Удалить аватар"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: НАСТРОЙКИ РЕГИСТРАЦИИ
// ═══════════════════════════════════════════════════════════════

function AdminSettings({ navigate, currentUser }) {
  const { settings, updateSettings, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditSystemSettings')) {
    return <PermissionGate user={currentUser} perm="canEditSystemSettings" navigate={navigate} label="системных настроек" />;
  }

  const Row = ({ title, hint, value, onChange, accent = '#FFB800' }) => (
    <div className="flex items-center justify-between gap-3 py-3 border-t border-[#2A2E36] first:border-t-0">
      <div>
        <div className="text-sm text-white font-bold">{title}</div>
        {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
      </div>
      <InlineToggle value={value} onChange={onChange} accent={accent} />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="НАСТРОЙКИ" subtitle="Регистрация и поля" />

      <div className="p-5 space-y-4">
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-2">ОСНОВНОЕ</div>
          <Row
            title="Разрешить самостоятельную регистрацию"
            hint="Если выключено, аккаунты создаёт только администратор"
            value={settings.allowRegistration}
            onChange={v => updateSettings({ allowRegistration: v })}
          />
        </div>

        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-2">ОБЯЗАТЕЛЬНЫЕ ПОЛЯ ПРИ РЕГИСТРАЦИИ</div>
          <Row
            title="ФИО (фамилия + имя)"
            hint="Минимум 2 слова. Рекомендуется оставить включённым."
            value={settings.requireFullName !== false}
            onChange={v => updateSettings({ requireFullName: v })}
          />
          <Row
            title="Email"
            hint="Email используется как уникальный логин"
            value={settings.requireEmail !== false}
            onChange={v => updateSettings({ requireEmail: v })}
          />
          <Row
            title="Пароль"
            hint="Минимум 6 символов. Без пароля вход возможен по одному email."
            value={settings.requirePassword !== false}
            onChange={v => updateSettings({ requirePassword: v })}
          />
          <Row
            title="Пол"
            hint="Выбор мужской / женский"
            value={!!settings.requireGender}
            onChange={v => updateSettings({ requireGender: v })}
          />
          <Row
            title="Телефон"
            hint="Для связи и контактного справочника"
            value={!!settings.requirePhone}
            onChange={v => updateSettings({ requirePhone: v })}
          />
          <Row
            title="Дата рождения"
            hint="Нужна для возраста и напоминаний о ДР"
            value={settings.requireBirthDate !== false}
            onChange={v => updateSettings({ requireBirthDate: v })}
          />
        </div>

        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ПРИВЕТСТВЕННОЕ СООБЩЕНИЕ</div>
          <InlineTextarea
            value={settings.welcomeMessage}
            onChange={v => updateSettings({ welcomeMessage: v })}
            placeholder="Текст, который видят все новые стажёры"
            rows={3}
          />
        </div>

        <button
          onClick={() => navigate('adminStartScreen')}
          className="w-full bg-[#15171B] hover:bg-[#FFB800]/10 border border-[#2A2E36] hover:border-[#FFB800] text-white py-4 rounded-xl flex items-center justify-between transition group"
        >
          <div className="flex items-center gap-3 pl-2">
            <Palette className="w-5 h-5 text-[#FFB800]" />
            <div className="text-left">
              <div className="font-bold text-sm">Тема и стартовый экран</div>
              <div className="text-xs text-gray-500">Цвета, темы, тексты логина</div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#FFB800] mr-2" />
        </button>

        <div className="bg-[#0A1F12] border border-[#00C781]/30 rounded-xl p-4 text-sm text-gray-300 leading-relaxed">
          <CheckCircle2 className="w-4 h-4 text-[#00C781] inline mr-2" />
          Все изменения применяются мгновенно. Новые регистрации сразу учитывают актуальные настройки.
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// АДМИН: КОНТАКТНАЯ ИНФОРМАЦИЯ — редактор
// ═══════════════════════════════════════════════════════════════

function AdminContactsEditor({ navigate, showToast, currentUser }) {
  const { contacts, updateContacts, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditContacts')) {
    return <PermissionGate user={currentUser} perm="canEditContacts" navigate={navigate} label="редактирования контактов" />;
  }

  const patch = (patchObj) => updateContacts({ ...contacts, ...patchObj });

  const addSectionAt = (insertIdx = contacts.sections.length) => {
    const next = [...contacts.sections];
    next.splice(insertIdx, 0, {
      id: genId('sec'),
      title: 'Новый раздел',
      icon: '📞',
      color: '#FFB800',
      items: [],
    });
    return patch({ sections: next });
  };
  const updSection = (i, patchObj) => {
    const next = contacts.sections.map((s, idx) => idx === i ? { ...s, ...patchObj } : s);
    return patch({ sections: next });
  };
  const reorderSections = (from, to) => {
    const next = [...contacts.sections];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ sections: next });
  };
  const removeSection = async (i) => {
    if (!(await confirm('Удалить раздел контактов?', { danger: true }))) return;
    return patch({ sections: contacts.sections.filter((_, x) => x !== i) });
  };

  const addItemAt = (si, insertIdx) => {
    const items = [...contacts.sections[si].items];
    if (insertIdx === undefined) insertIdx = items.length;
    items.splice(insertIdx, 0, { id: genId('it'), label: 'Должность / роль', value: '' });
    return updSection(si, { items });
  };
  const updItem = (si, ii, patchObj) => {
    const items = contacts.sections[si].items.map((it, idx) => idx === ii ? { ...it, ...patchObj } : it);
    return updSection(si, { items });
  };
  const reorderItems = (si) => (from, to) => {
    const items = [...contacts.sections[si].items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    return updSection(si, { items });
  };
  const removeItem = (si, ii) => updSection(si, { items: contacts.sections[si].items.filter((_, x) => x !== ii) });

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <div className="flex items-center justify-between">
		  <AdminHeader back={() => navigate('adminContent')} title="КОНТАКТЫ" subtitle="Справочник телефонов" />
		  <button
			onClick={() => {
			  updateContacts(contacts);
			  showToast?.('Контакты сохранены', 'success');
			}}
			className="bg-[#00C781] hover:bg-[#00D78F] text-black font-mono text-xs tracking-wider px-4 py-2 rounded-lg transition flex items-center gap-2"
		  >
			<Save className="w-3.5 h-3.5" /> СОХРАНИТЬ
		  </button>
		</div>

      <div className="p-5 space-y-4">
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ВСТУПИТЕЛЬНЫЙ ТЕКСТ</div>
          <InlineTextarea
            value={contacts.intro || ''}
            onChange={v => patch({ intro: v })}
            placeholder="Краткое описание для стажёров"
            rows={3}
          />
        </div>

        {contacts.sections.length === 0 ? (
          <button
            onClick={() => addSectionAt(0)}
            className="w-full bg-[#15171B] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] py-12 rounded-xl flex flex-col items-center gap-2 transition"
          >
            <Plus className="w-8 h-8" />
            <span className="text-sm">Добавить первый раздел</span>
          </button>
        ) : (
          <>
            <AddBlockButton onAdd={() => addSectionAt(0)} label="раздел" />
            <DraggableList
              items={contacts.sections.map((s, i) => ({ ...s, _id: s.id || `sec-${i}` }))}
              idKey="_id"
              onReorder={reorderSections}
              renderItem={(section, si, dragProps) => {
                const items = section.items.map((it, ii) => ({ ...it, _id: it.id || `it-${si}-${ii}` }));
                return (
                  <>
                    <div className="bg-[#15171B] border-l-4 rounded-2xl p-4 space-y-3 transition" style={{ borderLeftColor: section.color || '#FFB800', borderTop: '1px solid #2A2E36', borderRight: '1px solid #2A2E36', borderBottom: '1px solid #2A2E36' }}>
                      <div className="flex items-center gap-2">
                        <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] p-1`}>
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <InlineText
                          value={section.icon}
                          onChange={v => updSection(si, { icon: v })}
                          placeholder="📞"
                          size="2xl"
                          className="text-3xl"
                        />
                        <div className="flex-1 min-w-0">
                          <InlineText
                            value={section.title}
                            onChange={v => updSection(si, { title: v })}
                            placeholder="Название раздела"
                            size="lg"
                            className="font-bold text-white block"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          {['#E83A2C', '#FFB800', '#00C781', '#00E5FF', '#A855F7'].map(c => (
                            <button
                              key={c}
                              onClick={() => updSection(si, { color: c })}
                              className={`w-5 h-5 rounded transition ${section.color === c ? 'ring-2 ring-white' : ''}`}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                        </div>
                        <IconBtn icon={Trash2} onClick={() => removeSection(si)} danger />
                      </div>

                      <div className="pl-8 space-y-1">
                        {items.length === 0 ? (
                          <button
                            onClick={() => addItemAt(si, 0)}
                            className="text-xs text-gray-500 hover:text-[#FFB800] flex items-center gap-1 py-1 transition"
                          >
                            <Plus className="w-3 h-3" /> Добавить контакт
                          </button>
                        ) : (
                          <>
                            <AddBlockButton onAdd={() => addItemAt(si, 0)} label="контакт" />
                            <DraggableList
                              items={items}
                              idKey="_id"
                              onReorder={reorderItems(si)}
                              renderItem={(it, ii, idp) => (
                                <>
                                  <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-2.5 hover:border-[#FFB800]/40 transition group">
                                    <div className="flex items-center gap-2">
                                      <div {...idp} className={`${idp.className} text-gray-700 hover:text-[#FFB800] p-1`}>
                                        <GripVertical className="w-3 h-3" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <InlineText
                                          value={it.label}
                                          onChange={v => updItem(si, ii, { label: v })}
                                          placeholder="Должность / описание"
                                          size="sm"
                                          className="text-gray-300 block"
                                        />
                                      </div>
                                      <InlineText
                                        value={it.value}
                                        onChange={v => updItem(si, ii, { value: v })}
                                        placeholder="+7 (___) ___-__-__"
                                        className="text-white font-mono"
                                      />
                                      <button
                                        onClick={() => removeItem(si, ii)}
                                        className="text-gray-600 hover:text-[#E83A2C] p-1 opacity-0 group-hover:opacity-100 transition"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  <AddBlockButton onAdd={() => addItemAt(si, ii + 1)} label="контакт" />
                                </>
                              )}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <AddBlockButton onAdd={() => addSectionAt(si + 1)} label="раздел" />
                  </>
                );
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// СТАЖЁР: КОНТАКТЫ — просмотр
// ═══════════════════════════════════════════════════════════════

function ContactsScreen({ user, navigate, users }) {
  const { contacts: globalContacts } = useContent();
  const [contacts, setContacts] = useState(globalContacts);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch('/api/kv/mafia:content:contacts:v1?shared=true', { credentials: 'include' });
        if (!resp.ok) return;
        const json = await resp.json();
        if (!cancelled && json?.value) {
          setContacts(json.value);
        }
      } catch (e) { /* ignore */ }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Дни рождения сотрудников: сортируем по «ближайшему ДР»
  const birthdaysList = (users || [])
    .filter(u => u.birthDate)
    .map(u => ({ user: u, days: daysUntilBirthday(u.birthDate), age: calcAge(u.birthDate) }))
    .filter(b => b.days !== null)
    .sort((a, b) => a.days - b.days);

  const todayBirthdays = birthdaysList.filter(b => b.days === 0);

  return (
    <div className="max-w-2xl mx-auto pb-32 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('dashboard')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НА ГЛАВНУЮ
        </button>
        <div className="font-mono text-[10px] themed-text-accent tracking-[0.2em]">КОНТАКТЫ</div>
        <h1 className="font-display text-3xl tracking-wider text-white mt-0.5">СПРАВОЧНИК</h1>
      </div>

      <div className="py-5 space-y-5">
        {contacts.intro && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 text-sm text-gray-300 leading-relaxed whitespace-pre-line">
            {contacts.intro}
          </div>
        )}

        {/* Сегодняшние ДР — баннер */}
        {todayBirthdays.length > 0 && (
          <div className="bg-gradient-to-br from-[#FFB800]/20 to-[#E83A2C]/10 border border-[#FFB800]/40 rounded-2xl p-4 pop-in">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🎉</span>
              <div className="font-display text-lg tracking-wider text-white">СЕГОДНЯ ДР!</div>
            </div>
            <div className="space-y-2">
              {todayBirthdays.map(b => (
                <div key={b.user.id} className="flex items-center gap-3">
                  <Avatar user={b.user} size={36} />
                  <div className="flex-1">
                    <div className="font-bold text-white text-sm">{b.user.name}</div>
                    <div className="text-xs themed-text-accent">{b.age != null ? `Исполняется ${b.age}` : 'С днём рождения!'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Разделы контактов */}
        {contacts.sections.map(sec => (
          <div key={sec.id} className="bg-[#15171B] border-l-4 rounded-2xl p-4" style={{ borderLeftColor: sec.color, borderTop: '1px solid #2A2E36', borderRight: '1px solid #2A2E36', borderBottom: '1px solid #2A2E36' }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{sec.icon}</span>
              <div className="font-display text-lg tracking-wider text-white">{sec.title}</div>
            </div>
            <div className="space-y-1.5">
              {sec.items.length === 0 ? (
                <div className="text-xs text-gray-600 font-mono">— пусто —</div>
              ) : sec.items.map(it => (
                <a
                  key={it.id}
                  href={it.value && it.value.match(/^[\d\s()+\-]{3,}$/) ? `tel:${it.value.replace(/[^\d+]/g, '')}` : undefined}
                  className="flex items-center justify-between gap-3 px-3 py-2 bg-[#0A0B0D] border border-[#2A2E36] rounded-lg hover:border-[#FFB800]/40 transition group"
                >
                  <span className="text-sm text-gray-300 truncate">{it.label}</span>
                  <span className="text-sm font-mono text-white group-hover:themed-text-accent">{it.value || '—'}</span>
                </a>
              ))}
            </div>
          </div>
        ))}

        {/* Дни рождения коллег */}
        {birthdaysList.length > 0 && (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🎂</span>
              <div className="font-display text-lg tracking-wider text-white">ДНИ РОЖДЕНИЯ КОЛЛЕГ</div>
            </div>
            <div className="space-y-1.5">
              {birthdaysList.slice(0, 50).map(b => (
                <div key={b.user.id} className="flex items-center gap-3 px-3 py-2 bg-[#0A0B0D] border border-[#2A2E36] rounded-lg">
                  <Avatar user={b.user} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{b.user.name}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{formatBirthDay(b.user.birthDate)} · {b.age != null ? `${b.age} лет` : ''}</div>
                  </div>
                  <div className="text-[10px] font-mono tracking-wider" style={{ color: b.days === 0 ? '#FFB800' : b.days <= 7 ? '#00E5FF' : '#666' }}>
                    {b.days === 0 ? 'СЕГОДНЯ' : b.days === 1 ? 'ЗАВТРА' : `через ${b.days} дн.`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <BottomNav active="contacts" navigate={navigate} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: РЕДАКТОР EXIT-ИНТЕРВЬЮ (вопросы)
// ═══════════════════════════════════════════════════════════════

function AdminExitInterviewEditor({ navigate, showToast, currentUser }) {
  const { exitQuestions, updateExitQuestions, hasPerm } = useContent();
  const confirm = useConfirm();
  if (!hasPerm(currentUser, 'canEditExitQuestions')) {
    return <PermissionGate user={currentUser} perm="canEditExitQuestions" navigate={navigate} label="редактирования exit-анкеты" />;
  }

  const patch = (patchObj) => updateExitQuestions({ ...exitQuestions, ...patchObj });

  const addQuestionAt = (insertIdx = exitQuestions.questions.length) => {
    const next = [...exitQuestions.questions];
    next.splice(insertIdx, 0, { id: genId('eq'), text: 'Новый вопрос', type: 'text' });
    return patch({ questions: next });
  };
  const updQuestion = (i, patchObj) => {
    const next = exitQuestions.questions.map((q, idx) => idx === i ? { ...q, ...patchObj } : q);
    return patch({ questions: next });
  };
  const reorderQuestions = (from, to) => {
    const next = [...exitQuestions.questions];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return patch({ questions: next });
  };
  const removeQuestion = async (i) => {
    if (!(await confirm('Удалить вопрос?', { danger: true }))) return;
    return patch({ questions: exitQuestions.questions.filter((_, x) => x !== i) });
  };

  const questions = exitQuestions.questions.map((q, i) => ({ ...q, _id: q.id || `q-${i}` }));

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="EXIT-АНКЕТА" subtitle="Опрос при уходе с должности" />

      <div className="p-5 space-y-4">
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ВСТУПИТЕЛЬНЫЙ ТЕКСТ ДЛЯ СТАЖЁРА</div>
          <InlineTextarea
            value={exitQuestions.intro || ''}
            onChange={v => patch({ intro: v })}
            placeholder="Текст-обращение перед вопросами"
            rows={6}
          />
        </div>

        <div className="font-display text-lg tracking-wider text-white px-1">ВОПРОСЫ · {questions.length}</div>

        {questions.length === 0 ? (
          <button
            onClick={() => addQuestionAt(0)}
            className="w-full bg-[#15171B] hover:bg-[#FFB800]/10 border border-dashed border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] py-12 rounded-xl flex flex-col items-center gap-2 transition"
          >
            <Plus className="w-8 h-8" />
            <span className="text-sm">Добавить первый вопрос</span>
          </button>
        ) : (
          <>
            <AddBlockButton onAdd={() => addQuestionAt(0)} label="вопрос" />
            <DraggableList
              items={questions}
              idKey="_id"
              onReorder={reorderQuestions}
              renderItem={(q, i, dragProps) => (
                <>
                  <div className="bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800]/40 rounded-xl p-4 transition">
                    <div className="flex items-start gap-2">
                      <div {...dragProps} className={`${dragProps.className} text-gray-600 hover:text-[#FFB800] p-1 mt-1`}>
                        <GripVertical className="w-4 h-4" />
                      </div>
                      <div className="w-8 h-8 rounded bg-[#0A0B0D] flex items-center justify-center font-mono text-xs text-gray-500 flex-shrink-0">
                        {(i + 1).toString().padStart(2, '0')}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <InlineTextarea
                          value={q.text}
                          onChange={v => updQuestion(i, { text: v })}
                          placeholder="Текст вопроса"
                          rows={2}
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500 font-mono tracking-wider">ТИП:</span>
                          <InlineSelect
                            value={q.type}
                            onChange={v => updQuestion(i, { type: v })}
                            options={[
                              { value: 'text', label: 'Текст' },
                              { value: 'rating10', label: 'Оценка 1-10' },
                              { value: 'yesno', label: 'Да / Нет' },
                            ]}
                          />
                        </div>
                      </div>
                      <IconBtn icon={Trash2} onClick={() => removeQuestion(i)} danger />
                    </div>
                  </div>
                  <AddBlockButton onAdd={() => addQuestionAt(i + 1)} label="вопрос" />
                </>
              )}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: ОТВЕТЫ НА EXIT-ИНТЕРВЬЮ
// ═══════════════════════════════════════════════════════════════

function AdminExitResponses({ navigate, showToast, currentUser }) {
  const { exitResponses, exitQuestions, deleteExitResponse, hasPerm } = useContent();
  const confirm = useConfirm();
  const [openId, setOpenId] = useState(null);
  if (!hasPerm(currentUser, 'canSeeExitResponses')) {
    return <PermissionGate user={currentUser} perm="canSeeExitResponses" navigate={navigate} label="просмотра ответов exit" />;
  }

  const sorted = [...exitResponses].sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
  const open = sorted.find(r => r.id === openId);

  const onDelete = async (id) => {
    if (!(await confirm('Удалить эту анкету безвозвратно?', { danger: true }))) return;
    await deleteExitResponse(id);
    setOpenId(null);
    showToast('Анкета удалена', 'error');
  };

  if (open) {
    return (
      <div className="max-w-3xl mx-auto pb-10 fade-in">
        <AdminHeader back={() => setOpenId(null)} title="ОТВЕТЫ" subtitle={open.fullName || 'без имени'} />
        <div className="p-5 space-y-4">
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5 space-y-2">
            <Field label="ФИО" value={open.fullName || '—'} />
            <Field label="Дата рождения" value={open.birthDate ? `${formatBirthDay(open.birthDate)} ${new Date(open.birthDate).getFullYear()}` : '—'} />
            <Field label="Email" value={open.email || '—'} />
            <Field label="Должность" value={open.role || '—'} />
            <Field label="Заполнено" value={new Date(open.submittedAt).toLocaleString('ru')} />
          </div>

          <div className="space-y-3">
            {(open.answers || []).map((a, i) => (
              <div key={a.questionId || i} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
                <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-1">ВОПРОС {i + 1}</div>
                <div className="text-sm text-gray-400 mb-2">{a.question}</div>
                <div className="bg-[#0A0B0D] border border-[#2A2E36] rounded-lg p-3 text-sm text-white whitespace-pre-line">
                  {a.answer || <span className="text-gray-600 italic">— не отвечено —</span>}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => onDelete(open.id)}
            className="w-full bg-[#1F0A0A] hover:bg-[#E83A2C]/10 border border-[#E83A2C]/40 hover:border-[#E83A2C] text-[#E83A2C] font-mono text-xs tracking-wider py-3 rounded-lg flex items-center justify-center gap-2 transition"
          >
            <Trash2 className="w-4 h-4" /> УДАЛИТЬ АНКЕТУ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="EXIT-АНКЕТЫ" subtitle="База ответов" />

      <div className="py-5 space-y-3">
        <div className="text-xs text-gray-600 font-mono tracking-wider px-1">
          ВСЕГО АНКЕТ: {sorted.length} · ВОПРОСОВ В ШАБЛОНЕ: {exitQuestions.questions.length}
        </div>

        {sorted.length === 0 ? (
          <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-12 text-center">
            <FileText className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <div className="text-gray-500 text-sm">Анкет пока нет</div>
            <div className="text-[10px] text-gray-600 font-mono tracking-wider mt-1">КОГДА КТО-ТО ПРОЙДЁТ EXIT-ИНТЕРВЬЮ, ОНО ПОЯВИТСЯ ЗДЕСЬ</div>
          </div>
        ) : sorted.map(r => (
          <button
            key={r.id}
            onClick={() => setOpenId(r.id)}
            className="w-full bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800]/40 rounded-xl p-4 text-left transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E83A2C] to-[#8B0F08] text-white flex items-center justify-center font-display text-base flex-shrink-0">
                {(r.fullName || '?')[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white truncate">{r.fullName || 'без ФИО'}</div>
                <div className="text-[10px] text-gray-500 font-mono truncate">
                  {r.role || '—'} · {r.email || '—'}
                </div>
                <div className="text-[10px] text-gray-600 font-mono tracking-wider mt-0.5">
                  {new Date(r.submittedAt).toLocaleDateString('ru')} · {(r.answers || []).filter(a => a.answer).length}/{(r.answers || []).length} отв.
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// СТАЖЁР: ЗАПОЛНЕНИЕ EXIT-ИНТЕРВЬЮ
// ═══════════════════════════════════════════════════════════════

function ExitInterviewScreen({ user, navigate }) {
  const { exitQuestions, addExitResponse } = useContent();
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const setAnswer = (qid, v) => setAnswers(a => ({ ...a, [qid]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const resp = {
        id: 'ex_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
        userId: user.id,
        fullName: user.name,
        birthDate: user.birthDate || '',
        email: user.email || '',
        role: user.role || '',
        submittedAt: Date.now(),
        answers: exitQuestions.questions.map(q => ({
          questionId: q.id,
          question: q.text,
          type: q.type,
          answer: answers[q.id] || '',
        })),
      };
      await addExitResponse(resp);
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-5">
        <div className="max-w-md text-center bg-[#15171B] border border-[#00C781]/40 rounded-2xl p-8 fade-in">
          <CheckCircle2 className="w-12 h-12 text-[#00C781] mx-auto mb-4" />
          <div className="font-display text-2xl tracking-wider text-white mb-2">СПАСИБО</div>
          <div className="text-sm text-gray-400 mb-6">Анкета сохранена. Руководство получит твои ответы и постарается улучшить условия для следующих сотрудников.</div>
          <button onClick={() => navigate('dashboard')} className="bg-[#FFB800] hover:bg-[#FFC820] text-black font-mono text-xs tracking-wider px-6 py-3 rounded-lg">
            НА ГЛАВНУЮ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-32 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('dashboard')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НА ГЛАВНУЮ
        </button>
        <div className="font-mono text-[10px] themed-text-accent tracking-[0.2em]">EXIT-ИНТЕРВЬЮ</div>
        <h1 className="font-display text-2xl tracking-wider text-white mt-0.5">АНКЕТА ПРИ УХОДЕ</h1>
      </div>

      <div className="py-5 space-y-5">
        {exitQuestions.intro && (
          <div className="bg-[#15171B] border border-[#FFB800]/30 rounded-xl p-4 text-sm text-gray-300 leading-relaxed whitespace-pre-line">
            {exitQuestions.intro}
          </div>
        )}

        <div className="bg-[#0A1F12] border border-[#00C781]/30 rounded-xl p-3 text-xs text-gray-400 flex items-start gap-2">
          <Info className="w-4 h-4 text-[#00C781] flex-shrink-0 mt-0.5" />
          <div>
            ФИО (<span className="text-white">{user.name}</span>){user.birthDate ? <>, дата рождения (<span className="text-white">{formatBirthDay(user.birthDate)}</span>)</> : ''} и email сохранятся автоматически вместе с ответами.
          </div>
        </div>

        <div className="space-y-4">
          {exitQuestions.questions.map((q, i) => (
            <div key={q.id} className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded bg-[#FFB800]/20 themed-text-accent flex items-center justify-center font-mono text-xs flex-shrink-0">
                  {i + 1}
                </div>
                <div className="text-sm text-white leading-relaxed flex-1">{q.text}</div>
              </div>

              {q.type === 'text' && (
                <textarea
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  placeholder="Напиши ответ…"
                  rows={4}
                  className="w-full bg-[#0A0B0D] border border-[#2A2E36] focus:border-[#FFB800] rounded-lg p-3 text-white text-sm outline-none resize-y leading-relaxed"
                />
              )}

              {q.type === 'rating10' && (
                <div>
                  <div className="grid grid-cols-10 gap-1">
                    {Array.from({ length: 10 }, (_, n) => n + 1).map(n => (
                      <button
                        key={n}
                        onClick={() => setAnswer(q.id, String(n))}
                        className={`py-2 rounded text-sm font-mono transition ${
                          answers[q.id] === String(n)
                            ? 'themed-bg-accent'
                            : 'bg-[#0A0B0D] border border-[#2A2E36] text-gray-400 hover:border-gray-500 hover:text-white'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-600 font-mono mt-1 px-1">
                    <span>совсем не устраивает</span>
                    <span>полностью устраивает</span>
                  </div>
                </div>
              )}

              {q.type === 'yesno' && (
                <div className="grid grid-cols-2 gap-2">
                  {[{ id: 'yes', label: 'Да' }, { id: 'no', label: 'Нет' }].map(o => (
                    <button
                      key={o.id}
                      onClick={() => setAnswer(q.id, o.id)}
                      className={`py-3 rounded-lg text-sm font-mono tracking-wider transition ${
                        answers[q.id] === o.id
                          ? (o.id === 'yes' ? 'bg-[#00C781] text-black' : 'bg-[#E83A2C] text-white')
                          : 'bg-[#0A0B0D] border border-[#2A2E36] text-gray-400 hover:border-gray-500 hover:text-white'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#E83A2C] hover:bg-[#FF4A3C] disabled:bg-gray-800 disabled:text-gray-600 text-white font-mono text-sm tracking-wider py-3.5 rounded-lg flex items-center justify-center gap-2 hover-lift transition glow-red"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          ОТПРАВИТЬ АНКЕТУ
        </button>

        <div className="text-[10px] text-gray-700 font-mono text-center tracking-wider">
          Незаполненные вопросы можно пропустить — отправляй когда будешь готов(а).
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// АДМИН: РЕДАКТОР СТАРТОВОГО ЭКРАНА И ТЕМЫ
// ═══════════════════════════════════════════════════════════════

function AdminStartScreenEditor({ navigate, currentUser }) {
  const { settings, updateSettings, hasPerm } = useContent();
  if (!hasPerm(currentUser, 'canEditSystemSettings')) {
    return <PermissionGate user={currentUser} perm="canEditSystemSettings" navigate={navigate} label="редактирования темы" />;
  }
  const accentPresets = [
    { id: 'gold', label: 'Классика', color: '#FFB800' },
    { id: 'fuchsia', label: 'Фуксия', color: '#E91E63' },
    { id: 'magenta', label: 'Маджента', color: '#D946EF' },
    { id: 'mint', label: 'Мята', color: '#10B981' },
    { id: 'azure', label: 'Лазурь', color: '#0EA5E9' },
    { id: 'orange', label: 'Оранж', color: '#F97316' },
    { id: 'violet', label: 'Лиловый', color: '#8B5CF6' },
  ];

  return (
    <div className="max-w-3xl mx-auto pb-10 fade-in">
      <AdminHeader back={() => navigate('adminContent')} title="ТЕМА И СТАРТ" subtitle="Оформление и тексты" />

      <div className="py-5 space-y-5">
        {/* Тема */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">ТЕМА ОФОРМЛЕНИЯ</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => updateSettings({ theme: 'classic' })}
              className={`p-4 rounded-xl border text-left transition ${
                settings.theme === 'classic' ? 'themed-border-accent themed-bg-accent-soft' : 'border-[#2A2E36] bg-[#0A0B0D] hover:border-gray-600'
              }`}
            >
              <div className="text-2xl mb-1">🎩</div>
              <div className="font-bold text-white">Классика</div>
              <div className="text-[10px] text-gray-500">Мафиозный стиль</div>
            </button>
            <button
              onClick={() => updateSettings({ theme: 'newyear' })}
              className={`p-4 rounded-xl border text-left transition ${
                settings.theme === 'newyear' ? 'themed-border-accent themed-bg-accent-soft' : 'border-[#2A2E36] bg-[#0A0B0D] hover:border-gray-600'
              }`}
            >
              <div className="text-2xl mb-1">❄️🎄</div>
              <div className="font-bold text-white">Новогодняя</div>
              <div className="text-[10px] text-gray-500">Снег и праздник</div>
            </button>
          </div>
        </div>

        {/* Цвет акцента */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl p-5">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider mb-3">АКЦЕНТНЫЙ ЦВЕТ</div>
          <div className="text-xs text-gray-500 mb-3">
            Заменит золотой на выбранный — кнопки регистрации, активные плитки, ярлыки рангов.
          </div>
          <div className="grid grid-cols-4 gap-2">
            {accentPresets.map(p => (
              <button
                key={p.id}
                onClick={() => updateSettings({ accentColor: p.color })}
                className={`p-3 rounded-xl border text-center transition ${
                  settings.accentColor === p.color ? 'border-white ring-2 ring-white/30' : 'border-[#2A2E36] hover:border-gray-600'
                }`}
                style={{ background: p.color + '20' }}
              >
                <div className="w-8 h-8 mx-auto mb-1 rounded-full" style={{ background: p.color }} />
                <div className="text-xs text-white">{p.label}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-gray-500 font-mono tracking-wider">СВОЙ ЦВЕТ:</span>
            <input
              type="color"
              value={settings.accentColor || '#FFB800'}
              onChange={e => updateSettings({ accentColor: e.target.value })}
              className="w-12 h-9 bg-[#0A0B0D] border border-[#2A2E36] rounded cursor-pointer"
            />
            <span className="text-xs font-mono text-white">{settings.accentColor || '#FFB800'}</span>
          </div>
        </div>

        {/* Тексты стартового экрана */}
        <div className="bg-[#15171B] border border-[#2A2E36] rounded-2xl py-5 space-y-3">
          <div className="font-mono text-[10px] text-gray-500 tracking-wider">ТЕКСТЫ СТАРТОВОГО ЭКРАНА</div>

          <LabeledInline label="Заголовок (большой)" value={settings.loginTitle} onChange={v => updateSettings({ loginTitle: v })} placeholder="MAFIA" />
          <LabeledInline label="Подзаголовок" value={settings.loginSubtitle} onChange={v => updateSettings({ loginSubtitle: v })} placeholder="ACADEMY" />
          <LabeledInline label="Подпись под лого" value={settings.loginTagline} onChange={v => updateSettings({ loginTagline: v })} placeholder="БАЗА ПОДГОТОВКИ" />

          <div className="pt-2 border-t border-[#2A2E36]"></div>

          <LabeledInline label="Плейсхолдер email" value={settings.loginEmailPlaceholder} onChange={v => updateSettings({ loginEmailPlaceholder: v })} placeholder="ivan@example.com" />
          <LabeledInline label="Плейсхолдер пароля" value={settings.loginPasswordPlaceholder} onChange={v => updateSettings({ loginPasswordPlaceholder: v })} placeholder="••••••" />
          <LabeledInline label="Плейсхолдер ФИО" value={settings.loginNamePlaceholder} onChange={v => updateSettings({ loginNamePlaceholder: v })} placeholder="Иванов Иван Иванович" />

          <div className="pt-2 border-t border-[#2A2E36]"></div>

          <LabeledInline label="Подсказка под выбором направления" value={settings.registerHint} onChange={v => updateSettings({ registerHint: v })} placeholder="" />
          <LabeledInline label="Сообщение про первого админа" value={settings.firstAdminHint} onChange={v => updateSettings({ firstAdminHint: v })} placeholder="" />

          <div className="pt-2 border-t border-[#2A2E36]"></div>

          <LabeledInline label="Текст кнопки «Войти»" value={settings.loginButtonText} onChange={v => updateSettings({ loginButtonText: v })} placeholder="ВОЙТИ" />
          <LabeledInline label="Текст кнопки «Зарегистрироваться»" value={settings.registerButtonText} onChange={v => updateSettings({ registerButtonText: v })} placeholder="СОЗДАТЬ АККАУНТ" />
        </div>

        <div className="bg-[#0A1F12] border border-[#00C781]/30 rounded-xl p-4 text-sm text-gray-300 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#00C781] flex-shrink-0 mt-0.5" />
          <span>Все изменения применяются мгновенно — открой стартовый экран в новой вкладке и обнови, чтобы увидеть.</span>
        </div>
      </div>
    </div>
  );
}

function LabeledInline({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] font-mono text-gray-600 tracking-wider mb-1">{label.toUpperCase()}</label>
      <InlineText
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="text-white block"
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// СТАЖЁР: ДАШБОРД-СТАТИСТИКА (сравнение и ТОПы)
// ═══════════════════════════════════════════════════════════════

function TraineeStatsScreen({ user, users, navigate }) {
  const { courses, getCourseFor } = useContent();
  const [traineeProgress, setTraineeProgress] = useState({});

  const trainees = users.filter(u => !u.isAdmin);

  useEffect(() => {
    const loadProgress = async () => {
      const map = {};
      
      // Загружаем XP всех стажёров
      try {
        const resp = await fetch('/api/stats/trainees-xp', { credentials: 'include' });
        if (resp.ok) {
          const json = await resp.json();
          for (const t of json.trainees) {
            map[t.userId] = { xp: t.xp };
          }
        }
      } catch {}
      
      // Для текущего пользователя загружаем полный прогресс
      if (user && !user.isAdmin) {
        try {
          const key = STORAGE_KEYS.PROGRESS(user.id);
          const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${user.id}`, { credentials: 'include' });
          if (resp.ok) {
            const json = await resp.json();
            if (json?.value) map[user.id] = json.value;
          }
        } catch {}
      }
      
      setTraineeProgress(map);
    };
    
    loadProgress();
    const interval = setInterval(loadProgress, 30000);
    return () => clearInterval(interval);
  }, [users, user]);

  // Считаем метрики
  const stats = trainees.map(u => {
    const p = traineeProgress[u.id];
    const course = getCourseFor(u);
    let xp = p?.xp || 0;
    let totalQ = 0, errors = 0, modulesPassed = 0;
    if (p && course) {
      for (const mod of course.modules) {
        const t = p.tests?.[mod.id];
        if (t?.passed) modulesPassed++;
        if (t) {
          const qCount = t.totalQuestions || mod.test?.questions?.length || 0;
          totalQ += qCount;
          errors += Math.max(0, qCount - (t.correct || 0));
        }
      }
    }
    const accuracy = totalQ > 0 ? Math.round(((totalQ - errors) / totalQ) * 100) : 0;
    return { user: u, course, xp, totalQ, errors, accuracy, modulesPassed, totalModules: course?.modules?.length || 0 };
  });

  const myStats = stats.find(s => s.user.id === user.id) || { xp: 0, errors: 0, accuracy: 0, modulesPassed: 0, totalModules: 0 };

  const topByXP = [...stats].sort((a, b) => b.xp - a.xp).slice(0, 10);
  const myRankByXP = topByXP.findIndex(s => s.user.id === user.id) + 1;

  const topByAccuracy = [...stats].filter(s => s.totalQ > 0).sort((a, b) => b.accuracy - a.accuracy).slice(0, 10);
  const myRankByAcc = topByAccuracy.findIndex(s => s.user.id === user.id) + 1;

  return (
    <div className="max-w-3xl mx-auto pb-32 fade-in">
      <div className="pl-safe pr-safe pt-safe-5 px-5 pb-3 sticky top-0 bg-[#0A0B0D]/95 backdrop-blur z-10 border-b border-[#15171B]">
        <button onClick={() => navigate('dashboard')} className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mb-2 font-mono tracking-wider">
          <ChevronLeft className="w-4 h-4" /> НА ГЛАВНУЮ
        </button>
        <div className="font-mono text-[10px] themed-text-accent tracking-[0.2em]">СТАТИСТИКА</div>
        <h1 className="font-display text-3xl tracking-wider text-white mt-0.5">МОЙ ПРОГРЕСС</h1>
      </div>

      <div className="py-5 space-y-5">
        {/* Мои показатели */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBlock icon={Zap} label="МОИ XP" value={myStats.xp.toLocaleString('ru')} accent={'var(--accent)'} />
          <StatBlock icon={Target} label="ТОЧНОСТЬ" value={`${myStats.accuracy}%`} accent="#00E5FF" />
          <StatBlock icon={CheckCircle2} label="МОДУЛЕЙ" value={`${myStats.modulesPassed}/${myStats.totalModules}`} accent="#00C781" />
          <StatBlock icon={AlertCircle} label="ОШИБОК" value={myStats.errors} accent="#E83A2C" />
        </div>

        {/* Моя позиция */}
        {trainees.length > 1 && (
          <div className="bg-gradient-to-br from-[#FFB800]/10 to-[#E83A2C]/10 border themed-border-accent-soft rounded-2xl p-5">
            <div className="font-display text-lg tracking-wider text-white mb-3">МОЁ МЕСТО В РЕЙТИНГЕ</div>
            <div className="grid grid-cols-2 gap-3">
              <RankCard label="по XP" rank={myRankByXP} total={topByXP.length} />
              <RankCard label="по точности" rank={myRankByAcc} total={topByAccuracy.length} />
            </div>
          </div>
        )}

        {/* ТОП по XP */}
        <DashboardSection icon={Trophy} title="ТОП ПО XP" accent={'var(--accent)'}>
          {topByXP.length === 0 ? <EmptyMini>Никто ещё не получал XP</EmptyMini> : topByXP.map((s, i) => (
            <LeaderRow
              key={s.user.id}
              rank={i + 1}
              s={s}
              valueLabel={`${s.xp.toLocaleString('ru')} XP`}
              accent={'var(--accent)'}
              highlight={s.user.id === user.id}
            />
          ))}
        </DashboardSection>

        {/* ТОП по точности */}
        <DashboardSection icon={Target} title="ТОП ПО ТОЧНОСТИ" accent="#00E5FF">
          {topByAccuracy.length === 0 ? <EmptyMini>Пока никто не отвечал на тесты</EmptyMini> : topByAccuracy.map((s, i) => (
            <LeaderRow
              key={s.user.id}
              rank={i + 1}
              s={s}
              valueLabel={`${s.accuracy}%`}
              accent="#00E5FF"
              highlight={s.user.id === user.id}
            />
          ))}
        </DashboardSection>
      </div>
      <BottomNav active="stats" navigate={navigate} />
    </div>
  );
}

function TraineeMessagesScreen({ user, navigate, showToast }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const containerRef = useRef(null);
  const emojiRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!showEmoji) return;
    const onClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showEmoji]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const key = STORAGE_KEYS.MESSAGES(user.id);
        const resp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${user.id}`, { credentials: 'include' });
        if (resp.ok) {
          const json = await resp.json();
          if (!cancelled && Array.isArray(json?.value)) setMessages(json.value);
        }
      } catch {} finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    load();
    // Основной канал — сокет; опрос оставлен как страховка, но реже
    const interval = setInterval(load, 15000);
    const onRt = (e) => {
      const d = e.detail;
      if ((d?.type === 'new_message' || d?.type === 'messages_updated') && d.chatUserId === user.id) load();
    };
    realtimeBus?.addEventListener('rt', onRt);
    return () => { cancelled = true; clearInterval(interval); realtimeBus?.removeEventListener('rt', onRt); };
  }, [user.id]);

  // Уведомления о сообщениях гасим, раз чат открыт
  const notifCtx = useNotifications();
  const unreadMsgNotif = (notifCtx?.notifications || []).filter(n => n.type === 'message' && !n.read).length;
  useEffect(() => {
    if (unreadMsgNotif > 0) notifCtx?.markNotificationsReadWhere?.(n => n.type === 'message');
  }, [unreadMsgNotif]); // eslint-disable-line react-hooks/exhaustive-deps

  // Отмечаем входящие как прочитанные, когда чат открыт — у руководителя появится «✓»
  useEffect(() => {
    const unread = messages.filter(m => m.from !== user.id && !m.read);
    if (!unread.length) return;
    const updated = messages.map(m => (m.from !== user.id && !m.read) ? { ...m, read: true } : m);
    setMessages(updated);
    fetch(`/api/kv/${encodeURIComponent(STORAGE_KEYS.MESSAGES(user.id))}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: updated, shared: false, owner_id: user.id }),
    }).catch(() => {});
  }, [messages, user.id]);

  // Автопрокрутка к последнему сообщению
  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, loading]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const key = STORAGE_KEYS.MESSAGES(user.id);
      const existingResp = await fetch(`/api/kv/${encodeURIComponent(key)}?shared=false&owner_id=${user.id}`, { credentials: 'include' });
      let existing = [];
      if (existingResp.ok) {
        const json = await existingResp.json();
        if (Array.isArray(json?.value)) existing = json.value;
      }
      const msg = {
        id: 'msg_' + Date.now(),
        from: user.id,
        fromName: user.name,
        text: text.trim(),
        at: Date.now(),
        read: false,
      };
      await fetch(`/api/kv/${encodeURIComponent(key)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: [...existing, msg], shared: false, owner_id: user.id }),
      });
      setMessages(prev => [...prev, msg]);
      setText('');
    } catch (e) {
      showToast?.('Не удалось отправить', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto chat-screen with-nav flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
        <BottomNav active="messages" navigate={navigate} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto chat-screen with-nav flex flex-col fade-in">
      <div className="pt-safe px-4 py-3 border-b border-[#2A2E36] flex items-center gap-3 bg-[#0A0B0D]/95 backdrop-blur sticky top-0 z-10">
        <button onClick={() => navigate('dashboard')} className="min-touch -ml-2 flex items-center justify-center text-gray-400 hover:text-white" aria-label="На главную">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="font-display text-xl tracking-wider text-white">Сообщения</div>
          <div className="text-xs text-gray-500">Чат с руководителями</div>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-1">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <div className="text-gray-600 text-sm">Нет сообщений</div>
              <div className="text-xs text-gray-700 mt-1">Напишите первое сообщение</div>
            </div>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isMe = msg.from === user.id;
            const showDate = i === 0 || new Date(msg.at).toDateString() !== new Date(messages[i-1].at).toDateString();
            const bubbleClass = `chat-bubble ${isMe ? 'is-mine' : 'is-theirs'}`;
            return (
              <div key={msg.id}>
                {showDate && (
                  <div className="text-center my-3">
                    <span className="text-[9px] font-mono text-gray-600 bg-[#15171B] px-3 py-1 rounded-full">
                      {new Date(msg.at).toLocaleDateString('ru', { day: 'numeric', month: 'long' })}
                    </span>
                  </div>
                )}
                <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-1`}>
                  <div className={bubbleClass}>
                    {!isMe && <div className="font-bold text-xs text-[#FFB800] mb-1">{msg.fromName}</div>}
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                    <div className={`text-[9px] mt-1 ${isMe ? 'text-red-200' : 'text-gray-500'} flex justify-end`}>
                      {new Date(msg.at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="p-3 border-t border-[#2A2E36] bg-[#0A0B0D] relative">
        <div className="flex items-end gap-2">
          <div className="relative" ref={emojiRef}>
            <button
              onClick={() => setShowEmoji(v => !v)}
              className="w-10 h-10 rounded-xl bg-[#15171B] border border-[#2A2E36] hover:border-[#FFB800] text-gray-400 hover:text-[#FFB800] flex items-center justify-center flex-shrink-0 transition"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                <line x1="9" y1="9" x2="9.01" y2="9"/>
                <line x1="15" y1="9" x2="15.01" y2="9"/>
              </svg>
            </button>
            {showEmoji && (
              <div className="absolute bottom-full left-0 mb-2 z-50">
                <EmojiPicker
                  onEmojiClick={(emojiData) => {
                    setText(prev => prev + emojiData.emoji);
                    setShowEmoji(false);
                  }}
                  theme="dark"
                  width={Math.min(320, (typeof window !== 'undefined' ? window.innerWidth : 360) - 24)}
                  height={380}
                />
              </div>
            )}
          </div>
          
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Напишите сообщение..."
            rows={1}
            className="flex-1 bg-[#15171B] border border-[#2A2E36] focus:border-[#FFB800] rounded-xl px-4 py-3 text-white text-sm outline-none resize-none max-h-32"
            style={{ minHeight: 42 }}
          />
          <button
            onClick={send}
            disabled={busy || !text.trim()}
            className="w-10 h-10 rounded-xl bg-[#FFB800] hover:bg-[#FFC820] disabled:bg-gray-800 disabled:text-gray-600 text-black flex items-center justify-center flex-shrink-0 transition"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <BottomNav active="messages" navigate={navigate} />
    </div>
  );
}

function RankCard({ label, rank, total }) {
  if (!rank) return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 text-center">
      <div className="text-xs text-gray-500 font-mono tracking-wider mb-1">{label.toUpperCase()}</div>
      <div className="text-gray-600 text-sm">не в рейтинге</div>
    </div>
  );
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  return (
    <div className="bg-[#15171B] border border-[#2A2E36] rounded-xl p-4 text-center">
      <div className="text-xs text-gray-500 font-mono tracking-wider mb-1">{label.toUpperCase()}</div>
      <div className="font-display text-3xl text-white">
        {medal ? <span>{medal}</span> : <>#{rank}</>}
      </div>
      <div className="text-[10px] text-gray-600 font-mono mt-1">из {total}</div>
    </div>
  );
}
